// Reminder: remember to comment additions/edits for future maintainers.
import { Engine } from './engine.js';
import { Input } from './input.js';
import { Character } from './character.js';
import { fitCanvasToDisplaySize, setMaxDprCap, getMaxDprCap, lerp, lockCanvasSize } from './utils.js';
import { AudioManager, AUDIO_KIND_MUSIC, AUDIO_KIND_SFX } from './systems/AudioManager.js';
import { AssetLoader } from './systems/AssetLoader.js';
import { SaveManager } from './systems/SaveManager.js';
import { GameState } from './game/GameState.js';
import { ErrorHandler } from './systems/ErrorHandler.js';
import { DevTools } from './systems/DevTools.js';
import { SkyRenderer } from './world/SkyRenderer.js';
import { CollisionSystem } from './physics/CollisionSystem.js';
import { PlayerPhysics } from './physics/PlayerPhysics.js';
import { DiggingSystem } from './world/DiggingSystem.js';
import { ParticleSystem } from './systems/ParticleSystem.js';
import { AUDIO, RENDERING, PLAYER } from './game/config.js';

// === DEBUG FLAGS ===
// Set to true to spawn with sword equipped for testing. Set to false for production.
const DEBUG_START_WITH_SWORD = false;
// ==================

// Core constants
const TILE_SIZE = 12; // px per tile

const canvas = document.getElementById('game');
const ctx = canvas.getContext('2d');
// Global render scale (zoom): >1 zooms in, making everything appear bigger
const SCALE_STEPS = RENDERING.SCALE_STEPS;
let VIEW_SCALE = (() => {
  try {
    const v = parseFloat(localStorage.getItem('viewScale'));
    if (Number.isFinite(v) && v >= 0.75 && v <= 3) return v;
  } catch {}
  return RENDERING.DEFAULT_VIEW_SCALE; // default zoom (125%)
})();
const input = new Input(canvas);

const world = {
  width: () => (canvas.width / (canvas.__dpr || window.devicePixelRatio || 1)) / VIEW_SCALE,
  height: () => (canvas.height / (canvas.__dpr || window.devicePixelRatio || 1)) / VIEW_SCALE,
  // Make the ground fill roughly the bottom third of the screen
  get groundH() { return Math.round(this.height() / 3); },
};

const player = new Character(PLAYER.INITIAL_X, PLAYER.INITIAL_Y);
let playerStuckFrames = 0; // Track how many frames player has been stuck (for unstuck system)
// Last on-surface position; used as the save/resume point (we always resume on the surface,
// since dug terrain isn't persisted — restoring underground would drop the player into rock).
let lastSurfaceX = 48;
let lastSurfaceY = 0;
let cameraX = 0; // world-to-screen x offset
// Sky rendering now handled by SkyRenderer - these are compatibility accessors
let cloudDrift = 0; // deprecated - use skyRenderer
let cloudCamY = 0; // deprecated - use skyRenderer
const CLOUD_IDLE_SPEED = 64; // pixels per second (for legacy cloud functions)
const CLOUD_IDLE_FACTOR = 0.6; // per-layer multiplier (for legacy cloud functions)
let cameraY = 0; // vertical camera for world (ground/trees/items/player)
let verticalFollowActive = false; // only move ground when truly high up
let bottomFollowActive = false;   // follow when truly near the bottom
let undergroundCameraBias = 0;    // eased offset when digging deep underground
let playerIsUnderground = false;  // cached flag for assists/behaviors
let landingAnchorRatio = null;    // Screen position ratio where player landed from air (prevents snap-back)
let previousHeightFactor = 0;     // Track previous frame's height factor for landing detection
let wasHighInAir = false;         // Track if player was recently high enough to use air thresholds
let previousOnGround = true;      // Track previous frame's onGround state for landing detection
let jumpOffThresholds = null;     // {right, left} thresholds at moment of jump (prevents snap on takeoff)

// Levitate system (underground escape)
let levitateState = 'idle'; // 'idle', 'ascending', 'surfacing', 'arcing'
let levitateTimer = 0;
let levitateSurfaceY = 0; // Y position where we reached surface
let levitateArcStartX = 0; // X position where arc started
let levitateWindParticles = []; // { x, y, vy, life, dur, alpha }

// Underground mobility helpers
// Initialize AudioManager with music tracks
const musicTracks = [
  'music_assets/background_music_1.mp3',
  'music_assets/background_music_2.mp3',
  'music_assets/background_music_3.mp3',
  'music_assets/background_music_4.mp3',
  'music_assets/background_music_5.mp3',
  'music_assets/background_music_6.mp3',
  'music_assets/background_music_7.mp3',
  'music_assets/background_music_8.mp3',
  'music_assets/background_music_9.mp3',
  'music_assets/background_music_10.mp3',
  'music_assets/background_music_11.mp3',
  'music_assets/background_music-12.mp3',
];

// Underground music tracks (for very deep caves)
const undergroundMusicTracks = [
  'music_assets/backgroundmusic_underground_1.mp3',
  'music_assets/backgroundmusic_underground_2.mp3',
];

const audioManager = new AudioManager(musicTracks);
const saveManager = new SaveManager();
const errorHandler = new ErrorHandler();
const devTools = new DevTools();
const skyRenderer = new SkyRenderer();
const particles = new ParticleSystem(); // impact sparks + dash trails (see systems/ParticleSystem.js)
// Clear cloud cache on startup to ensure latest generation logic is used
skyRenderer.clearCloudCache();
// Expose clear function to window for debugging (can be removed in production if desired)
window.clearCloudCache = () => {
  skyRenderer.clearCloudCache();
};

// Audio unlock toast
let audioUnlockToastVisible = false;
let audioUnlockToastTimeout = null;

// Setup audio unlock callbacks
audioManager.onAudioUnlockNeeded = () => {
  audioUnlockToastVisible = true;
  // Auto-hide after 8 seconds in case user doesn't click
  clearTimeout(audioUnlockToastTimeout);
  audioUnlockToastTimeout = setTimeout(() => {
    audioUnlockToastVisible = false;
  }, 8000);
};

audioManager.onAudioUnlocked = () => {
  audioUnlockToastVisible = false;
  clearTimeout(audioUnlockToastTimeout);
};

// Refactored physics and digging systems
const collisionSystem = new CollisionSystem(TILE_SIZE);
const playerPhysics = new PlayerPhysics(TILE_SIZE);
const diggingSystem = new DiggingSystem(TILE_SIZE);

// Compatibility helpers for legacy code
const volumeSettings = audioManager.volumeSettings;
function clamp01(v) { return audioManager.clamp01(v); }
function setAudioBaseVolume(audio, base, kind) { return audioManager.setAudioBaseVolume(audio, base, kind); }
function refreshManagedAudioVolumes() { return audioManager.refreshManagedAudioVolumes(); }
const volumeKeys = {
  master: 'volumeMaster',
  music: 'volumeMusic',
  sfx: 'volumeSfx'
};

// Legacy compatibility - these will be updated gradually
let musicMuted = false;
// Touch-hint UI
const isTouchEnv = !!(window.matchMedia && window.matchMedia('(hover:none) and (pointer:coarse)').matches);
const touchHintEl = document.getElementById('touch-hint');
let touchHintActive = isTouchEnv && !!touchHintEl;
let hintStartTS = (typeof performance !== 'undefined' ? performance.now() : Date.now());
const HINT_MIN_SHOW_SEC = 3.5; // keep hint longer before allowing dismiss
const HINT_FORCE_HIDE_SEC = 10; // absolute cap
let userGestureSeen = false;
let hintActionCount = 0;
let hintPrevLeft = false, hintPrevRight = false, hintPrevJump = false, hintPrevInteract = false;
const touchHintStartX = 0; // not used directly; we use first-frame capture below
let firstUpdateRan = false;
const controlsOverlay = document.getElementById('controls-overlay');
const controlsListEl = controlsOverlay ? controlsOverlay.querySelector('.controls-list') : null;
const controlsBtn = document.getElementById('controls-btn');
const optionsBtn = document.getElementById('options-btn');
const optionsPanel = document.getElementById('options-panel');
const layoutToolbarBtn = document.getElementById('layout-toggle-btn');
const muteToolbarBtn = document.getElementById('mute-toolbar-btn');
const controlGuidesEl = document.getElementById('control-guides');
const CONTROL_GUIDE_STORAGE_KEY = 'controlGuides';
const defaultGuideState = { move: false, interact: false, item: false, ascend: false };
let controlGuideState = { ...defaultGuideState };
try {
  const storedGuides = localStorage.getItem(CONTROL_GUIDE_STORAGE_KEY);
  if (storedGuides) {
    const parsed = JSON.parse(storedGuides);
    if (parsed && typeof parsed === 'object') controlGuideState = { ...defaultGuideState, ...parsed };
  }
} catch {}
let controlGuideActive = null;

let worldToasts = [];

// Centralized base volumes for common sounds
const VOLUME_BASES = {
  birds: 0.04,
  undergroundAmbience: 0.25,
  caveAmbience: 0.18,
  caveRumble: 0.12,
  titanMusic: 0.40, // Normalized from 0.7 to match standard music levels
  shrineSfx: 0.35,
  button: 0.8,
  plant: 0.35,
  puzzle: 0.30,
  dialogueBlip: 0.7
};

function persistControlGuideState() {
  try { localStorage.setItem(CONTROL_GUIDE_STORAGE_KEY, JSON.stringify(controlGuideState)); } catch {}
}

function getMovementGuideText() {
  const layout = (input && input.layout) || 'WASD';
  const leftKey = layout === 'WASD' ? 'A' : 'Q';
  const rightKey = 'D';
  return `Move with ${leftKey}/${rightKey} or ←/→`;
}

function getInteractGuideText() {
  return 'Press S or ↓ to interact';
}

function getItemGuideText() {
  return 'Press S or ↓ to use your equipped item';
}

function getAscendGuideText() {
  return 'Press C to ascend back to the surface';
}

function getGuideMessage(kind) {
  if (kind === 'move') return getMovementGuideText();
  if (kind === 'interact') return getInteractGuideText();
  if (kind === 'item') return getItemGuideText();
  if (kind === 'ascend') return getAscendGuideText();
  return '';
}

function showControlGuide(kind, message) {
  if (!controlGuidesEl || controlGuideState[kind]) return;
  const text = message || getGuideMessage(kind);
  controlGuideActive = kind;
  controlGuidesEl.textContent = text;
  controlGuidesEl.dataset.kind = kind;
  controlGuidesEl.setAttribute('aria-label', text);
  controlGuidesEl.classList.add('visible');
}

function hideControlGuide(kind) {
  if (!controlGuidesEl || controlGuideActive !== kind) return;
  controlGuideActive = null;
  controlGuidesEl.classList.remove('visible');
  controlGuidesEl.textContent = '';
  delete controlGuidesEl.dataset.kind;
  controlGuidesEl.removeAttribute('aria-label');
}

function completeControlGuide(kind) {
  if (controlGuideState[kind]) return;
  controlGuideState[kind] = true;
  if (controlGuideActive === kind) hideControlGuide(kind);
  persistControlGuideState();
}

function refreshActiveGuideText() {
  if (!controlGuidesEl || !controlGuideActive) return;
  const text = getGuideMessage(controlGuideActive);
  controlGuidesEl.textContent = text;
  controlGuidesEl.setAttribute('aria-label', text);
}

function addWorldToast(x, y, text, duration = 2.6) {
  if (!text) return;
  worldToasts.push({ x, y, text, life: 0, dur: Math.max(0.5, duration) });
}

function updateWorldToasts(dt) {
  if (!worldToasts.length) return;
  for (const t of worldToasts) {
    t.life += dt;
  }
  worldToasts = worldToasts.filter(t => t.life < t.dur);
}

function drawWorldToasts(ctx) {
  if (!worldToasts.length) return;
  ctx.save();
  ctx.font = '16px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  for (const t of worldToasts) {
    const alpha = Math.max(0, Math.min(1, 1 - t.life / t.dur));
    if (alpha <= 0) continue;
    const screenX = Math.round((t.x - cameraX) * VIEW_SCALE);
    const screenY = Math.round((t.y + cameraY) * VIEW_SCALE);
    const paddingX = 10;
    const paddingY = 6;
    const textWidth = Math.ceil(ctx.measureText(t.text).width);
    const width = textWidth + paddingX * 2;
    const height = 26;
    ctx.globalAlpha = alpha * 0.9;
    ctx.fillStyle = 'rgba(12,18,30,0.75)';
    ctx.beginPath();
    ctx.roundRect?.(screenX - width / 2, screenY - height, width, height, 6);
    if (!ctx.roundRect) ctx.fillRect(screenX - width / 2, screenY - height, width, height);
    else ctx.fill();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = '#f4faff';
    ctx.fillText(t.text, screenX - textWidth / 2, screenY - height / 2 + 4);
  }
  ctx.restore();
}
const masterVolumeSlider = document.getElementById('volume-master');
const musicVolumeSlider = document.getElementById('volume-music');
const sfxVolumeSlider = document.getElementById('volume-sfx');
const controlsTitleEl = controlsOverlay ? controlsOverlay.querySelector('#controls-title') : null;
let controlsOverlayVisible = false;
let optionsMenuOpen = false;

// --- Intro Wisp (screen-space swirl + title) ---
const wispImg = new Image();
let wispReady = false; wispImg.onload = () => { wispReady = true; };
wispImg.src = 'img_assets/wisp.png';
const wispFramePaths = [
  'animation_assets/wisp/wisp1.png',
  'animation_assets/wisp/wisp2.png',
  'animation_assets/wisp/wisp3.png',
  'animation_assets/wisp/wisp4.png'
];
const wispFrames = wispFramePaths.map(path => {
  const img = new Image();
  img.src = path;
  return img;
});
function getReadyWispFrames() {
  const ready = wispFrames.filter(img => img && img.naturalWidth && img.naturalHeight);
  if (ready.length) return ready;
  return (wispReady && wispImg.naturalWidth) ? [wispImg] : [];
}

function ensureForestBirdsAudio() {
  return audioManager.getNamedAudio('forestBirds', 'ambiance_sfx/forestbirds-319791.mp3', {
    loop: true,
    baseVolume: VOLUME_BASES.birds,
    kind: AUDIO_KIND_SFX,
    preload: 'auto'
  });
}
const SHRINE_REPOSITION_OFFSET = 720;
const ARCH_SPACING_EXTRA = 280;
let wispIntro = {
  active: true,
  t: 0,
  // Narrative copy
  title: '..and so your quest begins.',
  lore: ''
};
const groveLegend = {
  seen: false,
  active: false,
  t: 0,
  duration: 4
};
const SHOW_DEBUG_UI = (() => {
  try {
    const params = new URLSearchParams(window.location.search);
    const val = params.get('debugUI');
    if (val === '0' || val === 'false') return false;
  } catch {}
  return true;
})();
// Depth-based forest atmosphere toggle
const FOREST_FOG_COLOR = 'rgba(18,32,44,';
const FOREST_VIGNETTE_COLOR = 'rgba(4,8,12,';
let debugBypassCorruptTitan = false;
const DEBUG_SPAWN_OVERRIDE = (() => {
  try {
    const params = new URLSearchParams(window.location.search);
    const preset = params.get('debugSpawn');
    const xParam = params.get('spawnX');
    const yParam = params.get('spawnY');
    const parsedX = xParam != null ? parseFloat(xParam) : null;
    const parsedY = yParam != null ? parseFloat(yParam) : null;
    if (preset || Number.isFinite(parsedX)) {
      return { preset, x: parsedX, y: parsedY };
    }
  } catch {}
  return null;
})();

// --- Friendly Goblin NPC + dialogue system ---
const GOBLIN_TARGET_H = 72;
const GOBLIN_SPAWN_X = 2100 + SHRINE_REPOSITION_OFFSET + ARCH_SPACING_EXTRA;
// Pre-spawn so the goblin is visible before the player arrives.
const goblin = { x: GOBLIN_SPAWN_X, w: GOBLIN_TARGET_H, h: GOBLIN_TARGET_H, facing: -1, overlap: false, yOffset: 0, talkRadius: 120, spawned: true, spawnTime: 4, runFromLeft: false, runStartX: 0, runTargetX: 0, runSpeed: 0 };
const goblinImg = new Image();
let goblinReady = false;
let goblinBounds = null;
goblinImg.onload = () => {
  goblinReady = true;
  goblinBounds = computeImageOpaqueBounds(goblinImg);
  const aspect = (goblinImg.naturalWidth && goblinImg.naturalHeight) ? (goblinImg.naturalWidth / goblinImg.naturalHeight) : 1;
  goblin.h = GOBLIN_TARGET_H;
  goblin.w = Math.max(1, Math.round(GOBLIN_TARGET_H * aspect));
  const scaleY = goblin.h / Math.max(1, goblinImg.naturalHeight || goblin.h);
  const bottom = goblinBounds ? goblinBounds.bottom || 0 : 0;
  goblin.yOffset = Math.round(bottom * scaleY);
};
goblinImg.src = 'img_assets/goblin.png';
goblin.runIn = false;
goblin.runSpeed = 0;
goblin.runTarget = null;
goblin.runToastShown = false;
const goblinDialoguePages = [
  "Hey there, don't smack or slash or anything!\nI know I look like your typical bad goblin but I'm a nice guy.\nMy name's Bwak.",
  "I'm actually looking for a blob just like you to help me out.\nEver since the shiny lights left, the woods have been a mess and I don't like that so much.\nLook at all these spiders running out of the deep forest!\nWhen these guys go, you know something's up.",
  "Anyway I need you to get rid of this 'small' titan that's blocking the entrance to my crib.\nI'll make it worth your while."
];
const goblinWifeImg = new Image();
let goblinWifeReady = false;
let goblinWifeBounds = null;
goblinWifeImg.onload = () => { goblinWifeReady = true; goblinWifeBounds = computeImageOpaqueBounds(goblinWifeImg); };
goblinWifeImg.src = 'img_assets/goblinwife.png';

const goblinSonImg = new Image();
let goblinSonReady = false;
let goblinSonBounds = null;
goblinSonImg.onload = () => { goblinSonReady = true; goblinSonBounds = computeImageOpaqueBounds(goblinSonImg); };
goblinSonImg.src = 'img_assets/goblinson.png';

// Goblin family (wife and son) - spawn when corrupt titan is defeated
// EDIT GOBLIN FAMILY SIZE HERE - Adjust base size for wife and son
const GOBLIN_FAMILY_TARGET_H = 60; // Wife's height - change this value to scale wife size
const goblinWife = { spawned: false, x: 0, y: 0, w: GOBLIN_FAMILY_TARGET_H, h: GOBLIN_FAMILY_TARGET_H, walkSpeed: 80, targetX: 0, yOffset: 0 };
const goblinSon = { spawned: false, x: 0, y: 0, w: GOBLIN_FAMILY_TARGET_H * 0.8, h: GOBLIN_FAMILY_TARGET_H * 0.8, walkSpeed: 90, targetX: 0, yOffset: 0 }; // Son is 0.8x wife's size - change 0.8 to adjust son's size relative to wife
let goblinFamilySpawned = false;
let goblinFamilyJetbackSpawned = false;
let goblinFamilyThanksQueued = false;
const goblinFamilyThanksDialogue = [
  "Thanks, Blob! Knew I could count on you. Here's a toolbox, a trader left this in our crib a long time ago. see ya!"
];

const rockTitanDialoguePages = [
  "Ahead lies the deep forest.\nBeware. Abandoned by the wisp, darkness has been allowed to creep in.",
  "The heart of the wood hides a terrible secret."
];
const dialogueState = {
  active: false,
  npcId: null,
  pages: [],
  pageIndex: 0,
  charProgress: 0,
  visibleChars: 0,
  history: [],
  historyVisible: false,
  cooldown: 0
};
const DIALOGUE_TYPE_SPEED = 48; // chars per second
const DIALOGUE_FAST_SPEED = 260;
// Dialogue blip audio managed by AudioManager
function getDialogueBlipAudio() {
  return audioManager.getNamedAudio('dialogueBlip', 'sfx_assets/dialogue_blip.ogg', {
    loop: true,
    baseVolume: VOLUME_BASES.dialogueBlip,
    kind: AUDIO_KIND_SFX,
    preload: 'auto'
  });
}
let dialogueBlipPlaying = false;
let forestBirdsAudio = null;
let forestBirdsPauseTimer = null;
let deepForestAmbienceActive = false;
let deepForestDebugMsg = null;
let deepForestDebugTimer = 0;

// Ambiance state
let currentAmbianceState = 'none'; // 'none', 'forest', 'underground', 'cave'
let undergroundAmbianceAudio = null;
let ambianceNeedsGesture = false;

// Underground music for very deep caves
let undergroundMusicAudio = null;
let undergroundMusicIndex = -1; // -1 means not yet selected
let isUndergroundMusicActive = false;
function startDialogueBlip() {
  if (dialogueBlipPlaying) return;
  const dialogueBlipAudio = getDialogueBlipAudio();
  if (!dialogueBlipAudio) return;
  try {
    dialogueBlipAudio.currentTime = 0;
    const p = dialogueBlipAudio.play();
    dialogueBlipPlaying = true;
    if (p && typeof p.catch === 'function') {
      p.catch(() => { dialogueBlipPlaying = false; });
    }
  } catch {
    dialogueBlipPlaying = false;
  }
}
function stopDialogueBlip() {
  if (!dialogueBlipPlaying) return;
  const dialogueBlipAudio = getDialogueBlipAudio();
  if (!dialogueBlipAudio) return;
  try {
    dialogueBlipAudio.pause();
    dialogueBlipAudio.currentTime = 0;
  } catch {}
  dialogueBlipPlaying = false;
}
function startDialogue(npcId, pages) {
  if (!pages || !pages.length) return;
  stopDialogueBlip();
  dialogueState.active = true;
  dialogueState.npcId = npcId;
  dialogueState.pages = pages;
  dialogueState.pageIndex = 0;
  dialogueState.charProgress = 0;
  dialogueState.visibleChars = 0;
  dialogueState.history = [];
  dialogueState.historyVisible = false;
  dialogueState.cooldown = 0;
}
function endDialogue() {
  stopDialogueBlip();

  // Spawn jetback after rock titan dialogue completes
  if (dialogueState.npcId === 'rock_titan' && !rockTitan.dialogueComplete) {
    rockTitan.dialogueComplete = true;
    if (!jetback.picked) {
      spawnJetbackAt(rockTitan.x + 200); // Spawn to the right of the rock titan
    }
  }

  dialogueState.active = false;
  dialogueState.npcId = null;
  dialogueState.pages = [];
  dialogueState.pageIndex = 0;
  dialogueState.charProgress = 0;
  dialogueState.visibleChars = 0;
  dialogueState.history = [];
  dialogueState.historyVisible = false;
  dialogueState.cooldown = 0.3;
}
function consumeWorldInteract() {
  if (dialogueState.active) return false;
  if (dialogueState.cooldown > 0) return false;
  const used = input.consumeInteract();
  if (used) onPlayerInteracted();
  return used;
}

function consumeDialogueInput() {
  if (!input) return false;
  if (dialogueState && dialogueState.cooldown > 0) return false;
  if (!input.hasKeyboard) {
    return input.consumeInteract();
  }
  const used = input.consumeDialogue();
  // Clear any leftover generic interact input so dialogue presses don't trigger other actions
  input.consumeInteract();
  return used;
}

function onPlayerInteracted() {
  completeControlGuide('interact');
  if (!controlGuideState.item) {
    if (selectedWeapon || axe.equipped || pickaxe.equipped || sword.equipped || raygun.equipped || builder.equipped) {
      completeControlGuide('item');
    }
  }
}

function ensureUndergroundAmbianceAudio() {
  return audioManager.getNamedAudio('undergroundAmbiance', 'ambiance_sfx/deep-cave-159876.mp3', {
    loop: true,
    baseVolume: VOLUME_BASES.undergroundAmbience,
    kind: AUDIO_KIND_SFX,
    preload: 'auto'
  });
}

function ensureUndergroundMusicAudio() {
  // Pick a random underground track if not already selected
  if (undergroundMusicIndex === -1 && undergroundMusicTracks.length > 0) {
    undergroundMusicIndex = Math.floor(Math.random() * undergroundMusicTracks.length);
  }

  const track = undergroundMusicTracks[Math.max(0, undergroundMusicIndex) % undergroundMusicTracks.length];
  return audioManager.getNamedAudio('undergroundMusic', track, {
    loop: true,
    baseVolume: 0, // Start at 0, will fade in
    kind: AUDIO_KIND_MUSIC,
    preload: 'auto'
  });
}

function setAmbianceState(newState) {
  if (apocalypseActive && newState !== 'none') {
    newState = 'none';
  }
  if (newState === currentAmbianceState) return;

  const oldState = currentAmbianceState;
  currentAmbianceState = newState;

  // Handle transitions - stop old ambiance
  if (oldState === 'forest') {
    // Fade out forest birds
    const forestBirdsAudio = audioManager.namedAudio.get('forestBirds')?.audio;
    if (forestBirdsAudio) {
      audioManager.fadeAudio(forestBirdsAudio, 0, 350, AUDIO_KIND_SFX);
      forestBirdsPauseTimer = setTimeout(() => {
        audioManager.stopNamedAudio('forestBirds');
        forestBirdsPauseTimer = null;
      }, 380);
    }
  } else if (oldState === 'underground') {
    // Fade out underground ambiance
    const undergroundAmbianceAudio = audioManager.namedAudio.get('undergroundAmbiance')?.audio;
    if (undergroundAmbianceAudio) {
      audioManager.fadeAudio(undergroundAmbianceAudio, 0, 350, AUDIO_KIND_SFX);
      setTimeout(() => {
        audioManager.stopNamedAudio('undergroundAmbiance');
      }, 380);
    }
  } else if (oldState === 'cave') {
    // Cave ambiance is managed by enterSurfaceCave/exitSurfaceCave
    // Just update state, don't touch the audio here
  }

  // Start new ambiance
  if (newState === 'forest') {
    // DISABLED: Forest birds ambiance
    // const birds = ensureForestBirdsAudio();
    // if (birds) {
    //   if (forestBirdsPauseTimer) { clearTimeout(forestBirdsPauseTimer); forestBirdsPauseTimer = null; }
    //   setAudioBaseVolume(birds, 0, AUDIO_KIND_SFX);
    //   const p = birds.play?.();
    //   if (p && typeof p.catch === 'function') {
    //     p.catch(() => {
    //       ambianceNeedsGesture = true;
    //     });
    //   }
    //   if (p && typeof p.then === 'function') {
    //     p.then(() => {
    //       audioManager.fadeAudio(birds, VOLUME_BASES.birds, 400, AUDIO_KIND_SFX);
    //     });
    //   } else {
    //     audioManager.fadeAudio(birds, VOLUME_BASES.birds, 400, AUDIO_KIND_SFX);
    //   }
    // }
  } else if (newState === 'underground') {
    const underground = ensureUndergroundAmbianceAudio();
    if (underground) {
      setAudioBaseVolume(underground, 0, AUDIO_KIND_SFX);
      const p = underground.play?.();
      if (p && typeof p.then === 'function') {
        // Single chain: avoid an unhandled rejection when ambiance autoplay is blocked.
        p.then(() => {
          audioManager.fadeAudio(underground, 0.18, 400, AUDIO_KIND_SFX);
        }).catch(() => {
          ambianceNeedsGesture = true;
        });
      } else {
        audioManager.fadeAudio(underground, 0.18, 400, AUDIO_KIND_SFX);
      }
    }
  } else if (newState === 'cave') {
    // Stop forest/underground when entering cave
    const forestBirdsAudio = audioManager.namedAudio.get('forestBirds')?.audio;
    if (forestBirdsAudio) {
      audioManager.fadeAudio(forestBirdsAudio, 0, 350, AUDIO_KIND_SFX);
      setTimeout(() => {
        audioManager.stopNamedAudio('forestBirds');
      }, 380);
    }
    const undergroundAmbianceAudio = audioManager.namedAudio.get('undergroundAmbiance')?.audio;
    if (undergroundAmbianceAudio) {
      audioManager.fadeAudio(undergroundAmbianceAudio, 0, 350, AUDIO_KIND_SFX);
      setTimeout(() => {
        audioManager.stopNamedAudio('undergroundAmbiance');
      }, 380);
    }
  }
  // 'none' state means no ambiance playing
}

function stopAllAmbianceImmediately() {
  // Stop all ambiance audio using AudioManager
  audioManager.stopNamedAudio('forestBirds');
  audioManager.stopNamedAudio('undergroundAmbiance');
  audioManager.stopNamedAudio('undergroundMusic');
  audioManager.stopNamedAudio('caveRumble');
  // Note: caveAmbience shares the same audio as undergroundAmbiance, so no separate stop needed
}

function stopAllMusic() {
  try {
    audioManager.pauseBackgroundMusic();
    audioManager.musicWasPlaying = false;
    if (audioManager.musicAudio) {
      audioManager.musicAudio.pause?.();
      audioManager.musicAudio.currentTime = 0;
    }
  } catch {}
}

function startTitanMusic() {
  try {
    if (!titanMusic1) {
      titanMusic1 = new Audio('music_assets/titanmusic_1.mp3');
      titanMusic1.preload = 'auto';
      setAudioBaseVolume(titanMusic1, VOLUME_BASES.titanMusic, AUDIO_KIND_MUSIC);
    }
    if (!titanMusic2) {
      titanMusic2 = new Audio('music_assets/titanmusic_2.mp3');
      titanMusic2.preload = 'auto';
      setAudioBaseVolume(titanMusic2, VOLUME_BASES.titanMusic, AUDIO_KIND_MUSIC);
    }
    const playTrack2 = () => {
      if (!titanMusic2) return;
      titanMusic2.currentTime = 0;
      titanMusic2.loop = false;
      titanMusic2.onended = () => {
        try { titanMusic1.currentTime = 0; titanMusic1.play?.(); } catch {}
      };
      titanMusic2.play?.();
    };
    titanMusic1.onended = () => playTrack2();
    titanMusic1.loop = false;
    titanMusic1.currentTime = 0;
    titanMusic1.play?.();
    titanMusicPlaying = true;
  } catch {}
}

function stopTitanMusic() {
  try { titanMusic1?.pause?.(); titanMusic1.currentTime = 0; } catch {}
  try { titanMusic2?.pause?.(); titanMusic2.currentTime = 0; } catch {}
  titanMusicPlaying = false;
}

// Deep forest audio transition zone (in pixels before deep forest starts)
const DEEP_FOREST_AUDIO_TRANSITION_DISTANCE = 700;
const DEEP_FOREST_AMBIANCE_VOLUME = 0.24; // Max ambiance volume in deep forest (3x base)
const DEEP_FOREST_MUSIC_MULTIPLIER = 0.5; // Music volume multiplier in deep forest

// Underground audio transition zones
const UNDERGROUND_TRANSITION_START_DEPTH = 200; // Depth where transition begins
const UNDERGROUND_TRANSITION_DISTANCE = 350; // Distance over which transition occurs (200-550)
const UNDERGROUND_AMBIANCE_VOLUME = 0.50; // Max cave ambiance volume when deep underground
const UNDERGROUND_MUSIC_MULTIPLIER = 1.0; // Music volume multiplier when deep underground (match surface music loudness)

// Very deep underground music crossfade
const DEEP_UNDERGROUND_START_DEPTH = 1000; // Depth where deep music transition begins
const DEEP_UNDERGROUND_TRANSITION_DISTANCE = 400; // Distance for crossfade (1000-1400)

function updateUndergroundAudioTransition(playerDepth) {
  // Calculate shallow transition factor (0 = at surface, 1 = at moderate depth)
  let transitionFactor = 0;
  if (playerDepth <= UNDERGROUND_TRANSITION_START_DEPTH) {
    transitionFactor = 0;
  } else if (playerDepth >= UNDERGROUND_TRANSITION_START_DEPTH + UNDERGROUND_TRANSITION_DISTANCE) {
    transitionFactor = 1;
  } else {
    const depthIntoTransition = playerDepth - UNDERGROUND_TRANSITION_START_DEPTH;
    transitionFactor = depthIntoTransition / UNDERGROUND_TRANSITION_DISTANCE;
  }

  // Apply smooth easing to make transition feel more natural
  const easedFactor = transitionFactor * transitionFactor * (3 - 2 * transitionFactor); // smoothstep

  // Calculate deep underground music crossfade factor
  let deepMusicFactor = 0;
  if (playerDepth >= DEEP_UNDERGROUND_START_DEPTH) {
    if (playerDepth >= DEEP_UNDERGROUND_START_DEPTH + DEEP_UNDERGROUND_TRANSITION_DISTANCE) {
      deepMusicFactor = 1;
    } else {
      const depthIntoDeep = playerDepth - DEEP_UNDERGROUND_START_DEPTH;
      deepMusicFactor = depthIntoDeep / DEEP_UNDERGROUND_TRANSITION_DISTANCE;
    }
  }

  // Apply smoothstep easing to deep music crossfade
  const easedDeepFactor = deepMusicFactor * deepMusicFactor * (3 - 2 * deepMusicFactor);

  // Ensure underground ambiance audio is ready
  const underground = ensureUndergroundAmbianceAudio();

  // Update forest birds volume (fade down as we go deeper)
  if (forestBirdsAudio && currentAmbianceState === 'forest') {
    const baseForestVolume = VOLUME_BASES.birds;
    const targetForestVolume = baseForestVolume * (1 - easedFactor);
    setAudioBaseVolume(forestBirdsAudio, targetForestVolume, AUDIO_KIND_SFX);
  }

  // Update underground ambiance volume (fade up as we go deeper)
  if (underground) {
    const targetUndergroundVolume = UNDERGROUND_AMBIANCE_VOLUME * easedFactor;
    setAudioBaseVolume(underground, targetUndergroundVolume, AUDIO_KIND_SFX);

    // Start playing if needed and volume is significant
    if (easedFactor > 0.1 && underground.paused) {
      const p = underground.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => { ambianceNeedsGesture = true; });
      }
    }

    // Pause if back at surface
    if (easedFactor < 0.05 && !underground.paused) {
      try {
        underground.pause();
        underground.currentTime = 0;
      } catch {}
    }
  }

  // Handle deep underground music crossfade
  if (deepMusicFactor > 0) {
    const undergroundMusic = ensureUndergroundMusicAudio();

    // Crossfade: main music fades out, underground music fades in
    if (audioManager.musicAudio && !musicMuted) {
      const baseVolume = AUDIO.MUSIC_BASE_VOLUME * UNDERGROUND_MUSIC_MULTIPLIER;
      const mainMusicVolume = baseVolume * (1 - easedDeepFactor);
      setAudioBaseVolume(audioManager.musicAudio, mainMusicVolume, AUDIO_KIND_MUSIC);
    }

    if (undergroundMusic) {
      const undergroundMusicVolume = AUDIO.MUSIC_BASE_VOLUME * easedDeepFactor;
      setAudioBaseVolume(undergroundMusic, undergroundMusicVolume, AUDIO_KIND_MUSIC);

      // Start playing underground music when crossfade begins
      if (easedDeepFactor > 0.05 && undergroundMusic.paused) {
        const p = undergroundMusic.play();
        if (p && typeof p.catch === 'function') {
          p.catch(() => { ambianceNeedsGesture = true; });
        }
        isUndergroundMusicActive = true;
      }

      // Stop underground music when back to moderate depth
      if (easedDeepFactor < 0.02 && !undergroundMusic.paused) {
        try {
          undergroundMusic.pause();
          undergroundMusic.currentTime = 0;
          isUndergroundMusicActive = false;
        } catch {}
      }
    }
  } else {
    // Not in deep zone - use normal music volume reduction
    if (audioManager.musicAudio && !musicMuted) {
      const musicMultiplier = 1 - (1 - UNDERGROUND_MUSIC_MULTIPLIER) * easedFactor;
      const baseVolume = AUDIO.MUSIC_BASE_VOLUME;
      const targetMusicVolume = baseVolume * musicMultiplier;
      setAudioBaseVolume(audioManager.musicAudio, targetMusicVolume, AUDIO_KIND_MUSIC);
    }

    // Ensure underground music is stopped when not deep
    const undergroundMusicAudio = audioManager.namedAudio.get('undergroundMusic')?.audio;
    if (undergroundMusicAudio && !undergroundMusicAudio.paused) {
      audioManager.stopNamedAudio('undergroundMusic');
      isUndergroundMusicActive = false;
    }
  }
}

function updateDeepForestAudioTransition(playerX) {
  const dfStart = deepForestStartX();
  if (!Number.isFinite(dfStart)) return;

  // Calculate distance from player to deep forest start
  const distanceToDeepForest = dfStart - playerX;

  // Calculate transition factor (0 = not in transition, 1 = fully in deep forest)
  let transitionFactor = 0;
  if (distanceToDeepForest <= 0) {
    // Player is in deep forest
    transitionFactor = 1;
  } else if (distanceToDeepForest < DEEP_FOREST_AUDIO_TRANSITION_DISTANCE) {
    // Player is in transition zone
    transitionFactor = 1 - (distanceToDeepForest / DEEP_FOREST_AUDIO_TRANSITION_DISTANCE);
  }

  // Apply smooth easing to make transition feel more natural
  const easedFactor = transitionFactor * transitionFactor * (3 - 2 * transitionFactor); // smoothstep

  // DISABLED: Forest birds ambiance volume transition
  // const forestBirdsAudio = audioManager.namedAudio.get('forestBirds')?.audio;
  // if (forestBirdsAudio && currentAmbianceState === 'forest') {
  //   const baseAmbianceVolume = VOLUME_BASES.birds;
  //   const targetAmbianceVolume = baseAmbianceVolume + (DEEP_FOREST_AMBIANCE_VOLUME - baseAmbianceVolume) * easedFactor;
  //   setAudioBaseVolume(forestBirdsAudio, targetAmbianceVolume, AUDIO_KIND_SFX);
  // }

  // Update music volume (full -> half in deep forest)
  if (audioManager.musicAudio && !musicMuted) {
    const musicMultiplier = 1 - (1 - DEEP_FOREST_MUSIC_MULTIPLIER) * easedFactor;
    const targetMusicVolume = AUDIO.MUSIC_BASE_VOLUME * musicMultiplier;
    setAudioBaseVolume(audioManager.musicAudio, targetMusicVolume, AUDIO_KIND_MUSIC);
  }

  // Update debug message
  const wasInDeepForest = deepForestAmbienceActive;
  const isInDeepForest = transitionFactor >= 0.9;
  if (isInDeepForest !== wasInDeepForest) {
    deepForestAmbienceActive = isInDeepForest;
    deepForestDebugMsg = isInDeepForest ? 'Entering Deep Forest' : 'Leaving Deep Forest';
    deepForestDebugTimer = 1.8;
  }
}

// Starfield (screen-space positions in the sky region)
let stars = [];
const MAX_STARS = 140;
function drawStars(ctx, w, skyH, darkness) {
  const target = Math.max(0, Math.round(MAX_STARS * Math.pow(darkness, 1.1)));
  // Add stars up to target
  while (stars.length < target) {
    stars.push({ x: Math.random() * w, y: Math.random() * Math.max(1, skyH), a: 0.6 + Math.random() * 0.4, s: Math.random() * 1.0 + 0.5 });
  }
  // Remove extra if target shrinks
  if (stars.length > target) stars.length = target;
  if (!stars.length) return;
  ctx.save();
  for (const st of stars) {
    const twinkle = 0.5 + 0.5 * Math.sin((performance.now?.() || Date.now()) * 0.003 + st.x * 0.01 + st.y * 0.02);
    ctx.globalAlpha = st.a * Math.max(0.1, darkness) * twinkle;
    ctx.fillStyle = '#dbeaff';
    ctx.fillRect(Math.round(st.x), Math.round(st.y), st.s, st.s);
  }
  ctx.restore();
}

// Moon sprite
const moonImg = new Image();
let moonReady = false;
let moonRect = null;
moonImg.onload = () => { moonReady = true; };
moonImg.src = 'img_assets/moon.png';
function drawMoon(ctx, w, skyH, darkness) {
  if (!moonReady) { moonRect = null; return; }
  const t = Math.max(0, Math.min(1, (darkness - 0.20) / 0.45));
  if (t <= 0) { moonRect = null; return; }
  const targetW = 120;
  const aspect = moonImg.naturalWidth && moonImg.naturalHeight ? (moonImg.naturalWidth / moonImg.naturalHeight) : 1;
  const dw = targetW;
  const dh = Math.max(1, Math.round(targetW / Math.max(0.1, aspect)));
  const x = Math.round(w - dw - 80);
  const y = Math.round(48);
  ctx.save();
  ctx.globalAlpha = 0.18 + 0.37 * t;
  ctx.drawImage(moonImg, x, y, dw, dh);
  ctx.restore();
  moonRect = { x, y, w: dw, h: dh };
}

// Shooting stars (screen-space within sky region)
let shootingStars = []; // { x,y,vx,vy,life,dur }
let shootingTimer = 0;
let shootingNext = 8 + Math.random() * 12;
function updateShooting(dt, w, h) {
  // Spawn only when high/dark enough
  const ascend = Math.max(0, -cameraY);
  const darkK = Math.max(0, Math.min(1, ascend / Math.max(1, h * 1.4))); // require higher altitude
  shootingTimer += dt;
  const groundTopScreen = (h - world.groundH) + cameraY;
  if (darkK > 0.7 && shootingTimer >= shootingNext && shootingStars.length < 2) {
    shootingTimer = 0;
    shootingNext = 6 + Math.random() * 10;
    const y = 30 + Math.random() * Math.max(40, groundTopScreen * 0.6);
    const startLeft = Math.random() < 0.7;
    const x = startLeft ? -80 : (w + 80);
    const dir = startLeft ? 1 : -1;
    const speed = 280 + Math.random() * 220;
    const vy = 40 + Math.random() * 60;
    shootingStars.push({ x, y, vx: dir * speed, vy, life: 0, dur: 1.8 + Math.random() * 0.9 });
  }
  // Integrate
  for (const s of shootingStars) {
    s.life += dt;
    s.x += s.vx * dt;
    s.y += s.vy * dt;
  }
  shootingStars = shootingStars.filter(s => s.life < s.dur && s.x > -200 && s.x < w + 200 && s.y > -100 && s.y < h + 100);
}
/**
 * Draws shooting stars in the sky
 * @param {CanvasRenderingContext2D} ctx - The canvas rendering context
 * @param {number} w - Canvas width
 * @param {number} h - Canvas height
 */
function drawShootingStars(ctx, w, h) {
  if (!shootingStars.length) return;
  const groundTopScreen = (h - world.groundH) + cameraY;
  ctx.save();
  ctx.beginPath();
  for (const s of shootingStars) {
    const t = Math.min(1, s.life / s.dur);
    const a = (1 - t) * 0.9;
    const len = 60;
    const x2 = s.x - (s.vx / Math.max(1, Math.hypot(s.vx, s.vy))) * len;
    const y2 = s.y - (s.vy / Math.max(1, Math.hypot(s.vx, s.vy))) * len;
    ctx.strokeStyle = `rgba(220,240,255,${a})`;
    ctx.lineWidth = 2;
    // only draw within sky area
    if (s.y < groundTopScreen - 2) {
      ctx.moveTo(s.x, s.y);
      ctx.lineTo(x2, y2);
    }
  }
  ctx.stroke();
  ctx.restore();
}

// --- Storm/Aurora/Comet/Floating Debris systems (Level 2/3) ---
const SKY_DARKNESS_SCALE = Infinity; // disable sky darkening with altitude for now
function skyDarkness(h) {
  // Compute based on player's altitude above the ground surface (world-space),
  // scaled so you must travel several screen-heights to reach deep-sky effects.
  const groundTopWorld = h - world.groundH;
  const playerCenterY = player.y + player.h * 0.5;
  const altitude = Math.max(0, groundTopWorld - playerCenterY); // px above ground
  const screens = altitude / Math.max(1, h);
  // Ease to make transition smoother near start and end
  const k = Math.max(0, Math.min(1, screens / SKY_DARKNESS_SCALE));
  return k * k * (3 - 2 * k); // smoothstep
}

// Lightning flashes
let lightning = []; // { x, y, life, dur, seed }
let lightningTimer = 0;
let lightningNext = 5 + Math.random() * 8;
function updateStorm(dt, w, h) {
  const darkK = skyDarkness(h);
  const level2 = darkK >= 0.55 && darkK < 0.9; // trigger only when much higher up
  if (!level2) { lightning = lightning.filter(e => e.life < e.dur); return; }
  lightningTimer += dt;
  if (lightningTimer >= lightningNext && lightning.length < 2) {
    lightningTimer = 0;
    lightningNext = 4 + Math.random() * 8;
    const groundTop = (h - world.groundH) + cameraY;
    // spawn near upper sky band
    const y = Math.max(30, Math.random() * Math.max(60, groundTop * 0.6));
    const x = Math.random() * w;
    lightning.push({ x, y, life: 0, dur: 0.25 + Math.random() * 0.25, seed: (Math.random()*1e9)|0 });
  }
  for (const e of lightning) e.life += dt;
  lightning = lightning.filter(e => e.life < e.dur);
}
/**
 * Draws lightning bolts in the sky
 * @param {CanvasRenderingContext2D} ctx - The canvas rendering context
 * @param {number} w - Canvas width
 * @param {number} h - Canvas height
 */
function drawLightning(ctx, w, h) {
  if (!lightning.length) return;
  const groundTop = (h - world.groundH) + cameraY;
  ctx.save();
  for (const e of lightning) {
    if (e.y >= groundTop - 2) continue; // sky only
    const t = Math.min(1, e.life / e.dur);
    const alpha = (1 - t) * 0.6;
    const rng = seededPRNG(e.seed);
    // bolt params
    const segs = 6 + Math.floor(rng() * 4);
    const len = 80 + rng() * 140;
    const sway = 18 + rng() * 10;
    let x = e.x, y = e.y;
    ctx.strokeStyle = `rgba(200,240,255,${alpha})`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(x, y);
    for (let i = 0; i < segs; i++) {
      x += (rng() - 0.5) * sway;
      y += len / segs;
      if (y >= groundTop - 2) break;
      ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.restore();
}

function drawApocalypseSkyTint(ctx, w, h) {
  if (!apocalypseActive && apocalypseSky <= 0.01) return;
  const t = apocalypseActive ? apocalypseSky : Math.max(0, apocalypseSky - 0.5);
  ctx.save();
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, `rgba(140,60,30,${0.55 * t})`);
  grad.addColorStop(1, `rgba(200,80,40,${0.4 * t})`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();
}

function drawApocalypseWorld(ctx, w, h) {
  if (!apocalypseActive) return;
  const groundTop = h - world.groundH;
  // Meteors
  ctx.save();
  for (const m of apocMeteors) {
    if (m.landed) continue;
    if (meteorReady && meteorImg.naturalWidth) {
      const aspect = meteorImg.naturalWidth / meteorImg.naturalHeight;
      const dh = m.r * 2;
      const dw = dh * aspect;
      ctx.drawImage(meteorImg, Math.round(m.x - dw / 2), Math.round(m.y - dh / 2), dw, dh);
    } else {
      ctx.fillStyle = '#8a5b3a';
      ctx.beginPath();
      ctx.arc(m.x, m.y, m.r, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  // Embers (draw before titans for layering)
  for (const e of apocEmbers) {
    const t = e.life / e.dur;
    // Color shift from bright orange to dark red as they rise and fade
    const brightness = Math.floor(255 * (1 - t * 0.5));
    const red = brightness;
    const green = Math.floor(brightness * 0.4);
    ctx.fillStyle = `rgba(${red},${green},0,${e.alpha * 0.9})`;
    ctx.fillRect(Math.round(e.x - e.size / 2), Math.round(e.y - e.size / 2), e.size, e.size);
  }
  // Ash piles from burned NPCs/objects
  if (apocAshes.length) {
    ctx.save();
    ctx.fillStyle = 'rgba(40, 40, 40, 0.9)';
    for (const a of apocAshes) {
      const ashW = 16;
      const ashH = 8;
      ctx.beginPath();
      ctx.ellipse(a.x, a.y, ashW / 2, ashH / 2, 0, 0, Math.PI * 2);
      ctx.ellipse(a.x, a.y - 2, ashW / 3, ashH / 3, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }
  // Titans - draw from back to front (furthest first for proper layering)
  const sortedTitans = [...apocTitans].sort((a, b) => b.x - a.x); // Sort by x descending (furthest first)
  for (const t of sortedTitans) {
    if (titangiantReady && titanFramesReady >= TITAN_FRAME_COUNT) {
      const currentFrame = titanFrames[t.frame];
      if (currentFrame && currentFrame.naturalWidth) {
        const bx = Math.round(t.x - t.w / 2);
        // Stepped bob synced with frame animation (discrete, not smooth)
        const bob = t.bobOffset || 0; // Discreet bob that matches step frames
        const by = Math.round(t.y + bob); // Use stored y position (includes bottomOffset for ground contact)
        // Flip horizontally to face left
        ctx.save();
        ctx.translate(bx + t.w, by);
        ctx.scale(-1, 1);
        ctx.drawImage(currentFrame, 0, 0, t.w, t.h);
        ctx.restore();
      } else {
        // Fallback to first frame if current frame not loaded
        ctx.fillStyle = 'rgba(120,60,40,0.8)';
        ctx.fillRect(t.x - t.w / 2, t.y, t.w, t.h);
      }
    } else {
      ctx.fillStyle = 'rgba(120,60,40,0.8)';
      ctx.fillRect(t.x - t.w / 2, t.y, t.w, t.h);
    }
  }
  // Landed meteors
  for (const m of apocMeteors) {
    if (!m.landed) continue;
    if (meteorGroundReady && meteorGroundImg.naturalWidth) {
      const aspect = meteorGroundImg.naturalWidth / meteorGroundImg.naturalHeight;
      const dh = m.r * 2.2;
      const dw = dh * aspect;
      const y = m.landY - dh + (meteorGroundImg.naturalHeight ? (meteorGroundImg.naturalHeight * 0.15) * (dh / meteorGroundImg.naturalHeight) : 0);
      ctx.drawImage(meteorGroundImg, Math.round(m.x - dw / 2), Math.round(y), dw, dh);
    } else {
      ctx.fillStyle = '#623727';
      ctx.beginPath();
      ctx.arc(m.x, m.landY, m.r * 1.6, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  ctx.restore();
}

function drawGameOverOverlay(ctx, screenW, screenH) {
  ctx.save();
  ctx.globalAlpha = 0.8;
  ctx.fillStyle = 'rgba(0,0,0,0.65)';
  ctx.fillRect(0, 0, screenW, screenH);
  ctx.globalAlpha = 1;
  ctx.fillStyle = '#f6d2b0';
  ctx.font = '28px "Palatino Linotype", "Book Antiqua", Georgia, serif';
  const title = 'Game Over';
  const titleW = ctx.measureText(title).width;
  const titleX = Math.round(screenW / 2 - titleW / 2);
  const titleY = Math.round(screenH / 2 - 30);
  ctx.fillText(title, titleX, titleY);

  const btnW = 180;
  const btnH = 44;
  const btnX = Math.round(screenW / 2 - btnW / 2);
  const btnY = Math.round(screenH / 2 + 10);
  ctx.fillStyle = '#7b2b24';
  ctx.fillRect(btnX, btnY, btnW, btnH);
  ctx.strokeStyle = '#f6d2b0';
  ctx.lineWidth = 2;
  ctx.strokeRect(btnX, btnY, btnW, btnH);
  ctx.fillStyle = '#f6d2b0';
  ctx.font = '20px "Palatino Linotype", "Book Antiqua", Georgia, serif';
  const label = 'Respawn? (E)';
  const lw = ctx.measureText(label).width;
  ctx.fillText(label, Math.round(btnX + btnW / 2 - lw / 2), Math.round(btnY + btnH / 2 + 7));
  gameOverBtnRect = { x: btnX, y: btnY, w: btnW, h: btnH };
  ctx.restore();
}

// Audio unlock toast
function drawAudioUnlockToast(ctx, screenW, screenH) {
  if (!audioUnlockToastVisible) return;

  ctx.save();
  const message = '🔊 Press any key or tap to enable sound';
  const padding = 20;
  const height = 50;

  ctx.font = '16px system-ui, -apple-system, sans-serif';
  const textWidth = ctx.measureText(message).width;
  const width = textWidth + padding * 2;

  const x = Math.round((screenW - width) / 2);
  const y = 20;

  // Background with slight transparency
  ctx.fillStyle = 'rgba(30, 30, 35, 0.95)';
  ctx.beginPath();
  if (ctx.roundRect) {
    ctx.roundRect(x, y, width, height, 8);
  } else {
    ctx.rect(x, y, width, height);
  }
  ctx.fill();

  // Border
  ctx.strokeStyle = '#5aa7e6';
  ctx.lineWidth = 2;
  ctx.stroke();

  // Text
  ctx.fillStyle = '#ffffff';
  ctx.fillText(message, x + padding, y + height / 2 + 5);

  ctx.restore();
}

// Auroras
function drawAuroras(ctx, w, h) {
  const darkK = skyDarkness(h);
  if (darkK < 0.7) return;
  const groundTop = (h - world.groundH) + cameraY;
  const baseY = Math.max(10, groundTop * 0.18);
  const bands = 2;
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (let b = 0; b < bands; b++) {
    const amp = 30 + b * 18;
    const col = b === 0 ? 'rgba(120,220,255,0.10)' : 'rgba(120,255,180,0.08)';
    ctx.strokeStyle = col;
    ctx.lineWidth = 10;
    ctx.beginPath();
    for (let x = -40; x <= w + 40; x += 14) {
      const t = (x + performance.now() * 0.02 + b * 600) * 0.01;
      const y = baseY + Math.sin(t) * amp + Math.cos(t * 0.37) * (amp * 0.35) + b * 16;
      if (x === -40) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.restore();
}

// Comets (distinct from shooting stars)
let comets = []; // { x,y,vx,vy,life,dur }
let cometTimer = 0, cometNext = 10 + Math.random()*15;
function updateComets(dt, w, h) {
  const darkK = skyDarkness(h);
  if (darkK < 0.7) { comets = comets.filter(c => c.life < c.dur); return; }
  cometTimer += dt;
  const groundTop = (h - world.groundH) + cameraY;
  if (cometTimer >= cometNext && comets.length < 2) {
    cometTimer = 0; cometNext = 12 + Math.random() * 20;
    const fromLeft = Math.random() < 0.5;
    const x = fromLeft ? -120 : (w + 120);
    const y = Math.max(20, Math.random() * Math.max(60, groundTop * 0.5));
    const speed = 200 + Math.random() * 180;
    const dir = fromLeft ? 1 : -1;
    const vy = 20 + Math.random() * 40;
    comets.push({ x, y, vx: dir * speed, vy, life: 0, dur: 2.5 + Math.random() * 1.5 });
  }
  for (const c of comets) { c.life += dt; c.x += c.vx * dt; c.y += c.vy * dt; }
  comets = comets.filter(c => c.life < c.dur && c.x > -200 && c.x < w + 200);
}
function drawComets(ctx, w, h) {
  if (!comets.length) return;
  const groundTop = (h - world.groundH) + cameraY;
  ctx.save();
  for (const c of comets) {
    if (c.y >= groundTop - 2) continue;
    const t = Math.min(1, c.life / c.dur);
    const a = (1 - t) * 0.8;
    const len = 120;
    const hyp = Math.max(1, Math.hypot(c.vx, c.vy));
    const x2 = c.x - (c.vx / hyp) * len;
    const y2 = c.y - (c.vy / hyp) * len;
    ctx.strokeStyle = `rgba(180,220,255,${a})`;
    ctx.lineWidth = 3;
    ctx.beginPath(); ctx.moveTo(c.x, c.y); ctx.lineTo(x2, y2); ctx.stroke();
  }
  ctx.restore();
}

const debrisImgSrcs = [
  'img_assets/debris_1.png',
  'img_assets/debris_2.png',
  'img_assets/debris_3.png',
  'img_assets/debris_4.png',
  'img_assets/debris_5.png',
  'img_assets/debris_6.png',
  'img_assets/debris_7.png',
  'img_assets/debris_8.png',
  'img_assets/debris_9.png'
];
const debrisImgs = debrisImgSrcs.map(src => { const img = new Image(); img.src = src; return img; });

// Floating debris (parallax motes)
let floatDebris = []; // { x,y,rot,vr,life,imgIndex,scale,alpha,drift }
let debrisSpawned = false;
function ensureDebris(w, h) {
  if (debrisSpawned) return;
  const darkK = skyDarkness(h);
  if (darkK < 0.7) return;
  debrisSpawned = true;
  const groundTop = (h - world.groundH) + cameraY;
  for (let i = 0; i < 8; i++) {
    const x = Math.random() * w;
    const y = Math.max(30, Math.random() * Math.max(60, groundTop * 0.6));
    const imgIndex = Math.floor(Math.random() * debrisImgs.length);
    floatDebris.push({
      x,
      y,
      rot: Math.random() * Math.PI * 2,
      vr: (Math.random() * 0.3 - 0.15),
      life: 0,
      imgIndex,
      scale: 0.15 + Math.random() * 0.15,
      alpha: 0.18 + Math.random() * 0.14,
      drift: (Math.random() * 2 - 1) * 8
    });
  }
}
function updateDebris(dt, w, h) {
  const darkK = skyDarkness(h);
  const groundTop = (h - world.groundH) + cameraY;
  const active = darkK >= 0.7;
  if (active) ensureDebris(w, h);
  for (const d of floatDebris) {
    d.life += dt;
    d.rot += d.vr * dt;
    d.y += Math.sin(d.life * 0.6 + d.imgIndex) * 6 * dt;
    d.x += Math.cos(d.life * 0.4 + d.drift) * 4 * dt;
  }
  // keep within sky bounds
  floatDebris = floatDebris.filter(d => d.y < groundTop - 4);
}
function drawDebris(ctx, w, h) {
  if (!floatDebris.length) return;
  const groundTop = (h - world.groundH) + cameraY;
  const moon = moonRect;
  const circleIntersectsRect = (cx, cy, r, rect) => {
    const nearestX = Math.max(rect.x, Math.min(cx, rect.x + rect.w));
    const nearestY = Math.max(rect.y, Math.min(cy, rect.y + rect.h));
    const dx = cx - nearestX;
    const dy = cy - nearestY;
    return dx * dx + dy * dy <= r * r;
  };
  ctx.save();
  for (const d of floatDebris) {
    if (d.y >= groundTop - 2) continue;
    const img = debrisImgs[d.imgIndex | 0];
    let radius;
    let drawW; let drawH; let fallbackSize; let useImg = false;
    if (img && img.naturalWidth && img.naturalHeight) {
      drawW = img.naturalWidth * d.scale;
      drawH = img.naturalHeight * d.scale;
      radius = Math.max(drawW, drawH) * 0.6;
      useImg = true;
    } else {
      fallbackSize = 8 * d.scale;
      radius = fallbackSize * 0.7;
    }
    if (moon && circleIntersectsRect(d.x, d.y, radius, moon)) continue;
    ctx.save();
    ctx.translate(d.x, d.y);
    ctx.rotate(d.rot);
    ctx.globalAlpha = d.alpha;
    if (useImg) {
      ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);
    } else {
      ctx.fillStyle = 'rgba(160,190,255,0.18)';
      ctx.fillRect(-fallbackSize / 2, -fallbackSize / 2, fallbackSize, fallbackSize);
    }
    ctx.restore();
  }
  ctx.restore();
}

// --- Feature flags ---
const USE_TILE_TERRAIN = true; // Always use tile-based terrain (legacy columnar removed)
const SIDE_ONLY = true; // TEMP: render character using side/profile only
// Underground digging configuration
const UNDERGROUND_ASSISTS = false; // Assists disabled (wall/ledge climbs removed)
const DEBUG_DRAW_SENSORS = false; // lightweight overlay for feet/ceiling probes (off by default)
const FIXED_TIMESTEP = 1/60; // 60 FPS physics timestep for deterministic collision

// --- Axe collectible (using image asset) ---
const AXE_PRE_SHRINE_OFFSET = 160; // place the axe along the path between arch and shrine
const axe = { x: 0, picked: false, equipped: false, spawned: false, spawnTime: 0, floatPhase: 0 };
const axeImg = new Image();
let axeReady = false;
axeImg.onload = () => { axeReady = true; };
axeImg.src = 'img_assets/axe.png';
let axeOverlap = false;

// --- Forest Shrine (spawns helicohat) ---
const HELICOHAT_SHRINE_X = 10000; // Deep forest placement
const HELICOHAT_SHRINE_H = 120; // Shrine size control (height in px)
const HELICOHAT_SHRINE_GROUND_SINK = 23; // Push shrine down so it visibly touches ground
const HELICOHAT_SHRINE_CLEAR_RADIUS = 120; // Exclusion zone around shrine
const HELICOHAT_SHRINE_CLEAR_RADIUS_TREES = 260; // Extra exclusion for tree/obstacle spawning
const forestShrine = {
  x: HELICOHAT_SHRINE_X,
  w: HELICOHAT_SHRINE_H,
  h: HELICOHAT_SHRINE_H,
  overlap: false,
  yOffset: 0,
  interactRadius: 100,
  activated: false,
  glowProgress: 0 // 0 to 1 transition from normal to glow
};
const forestShrineImg = new Image();
const forestShrineGlowImg = new Image();
let forestShrineReady = false;
let forestShrineGlowReady = false;
let forestShrineBounds = null;
function refreshShrineCloudMetrics() {
  const img = cloudImgs?.[SHRINE_CLOUD_IMG_INDEX];
  if (!img || !img.naturalWidth) return false;
  const scale = SHRINE_CLOUD_SCALE * CLOUD_IMG_BASE_SCALE;
  const w = img.naturalWidth * scale;
  const h = img.naturalHeight * scale;
  const bounds = cloudImgBounds?.[SHRINE_CLOUD_IMG_INDEX] || null;
  let hitOffsetX = -w / 2;
  let hitOffsetY = 0;
  let hitW = w;
  let hitH = h;
  if (bounds && typeof bounds.minX === 'number') {
    hitOffsetX = -w / 2 + bounds.minX * scale;
    hitOffsetY = bounds.minY * scale;
    const bw = (bounds.maxX - bounds.minX + 1) * scale;
    const bh = (bounds.maxY - bounds.minY + 1) * scale;
    hitW = Math.max(1, bw);
    hitH = Math.max(1, bh);
    // Expand a little to be forgiving
    const expand = 2 * scale;
    hitOffsetX -= expand;
    hitOffsetY -= expand;
    hitW += expand * 2;
    hitH += expand * 2;
  }
  shrineCloud.w = w;
  shrineCloud.h = h;
  shrineCloud.hitOffsetX = hitOffsetX;
  shrineCloud.hitOffsetY = hitOffsetY;
  shrineCloud.hitW = hitW;
  shrineCloud.hitH = hitH;
  return true;
}
forestShrineImg.onload = () => {
  forestShrineReady = true;
  forestShrineBounds = computeImageOpaqueBounds(forestShrineImg);
  const aspect = (forestShrineImg.naturalWidth && forestShrineImg.naturalHeight) ? (forestShrineImg.naturalWidth / forestShrineImg.naturalHeight) : 1;
  forestShrine.h = HELICOHAT_SHRINE_H;
  forestShrine.w = Math.max(1, Math.round(HELICOHAT_SHRINE_H * aspect));
  const scaleY = forestShrine.h / Math.max(1, forestShrineImg.naturalHeight || forestShrine.h);
  const bottom = forestShrineBounds ? forestShrineBounds.bottom || 0 : 0;
  forestShrine.yOffset = Math.round(bottom * scaleY) + HELICOHAT_SHRINE_GROUND_SINK;
};
forestShrineImg.src = 'img_assets/forestshrine.png';
forestShrineGlowImg.onload = () => { forestShrineGlowReady = true; };
forestShrineGlowImg.src = 'img_assets/forestshrine_glow.png';

// Static cloud above shrine (bounceable platform)
const shrineCloud = {
  spawned: false,
  x: 0,
  y: 0,
  w: 140, // updated when cloud assets are ready
  h: 70,
  hitOffsetX: 0,
  hitOffsetY: 0,
  hitW: 140,
  hitH: 70
};
const SHRINE_CLOUD_IMG_INDEX = 1; // cloud2.png
const SHRINE_CLOUD_SCALE = 1.0; // relative to base cloud scale
const SHRINE_CLOUD_GAP = -10; // vertical gap above shrine top

// --- Helico Hat collectible (spawned by shrine) ---
const SPAWN_HELICOHAT = false; // Spawns after shrine activation
const HAT_SPAWN_X = HELICOHAT_SHRINE_X + 120; // Spawns in front of shrine
const hat = { x: HAT_SPAWN_X, picked: false, equipped: false, spawnTime: 0, spawned: false, floatPhase: Math.random() * Math.PI * 2 };
const hatImg = new Image();
let hatReady = false;
hatImg.onload = () => { hatReady = true; };
hatImg.src = 'img_assets/helicohat.png';
let hatOverlap = false;
let hatSpin = 0; // radians (accumulated angle)
let hatSpinRemain = 0; // radians remaining to complete queued full rotations
// Vertical adjustment for equipped helico hat (positive = lower)
const HAT_Y_OFFSET = 28;
const HAT_CLEAR_RADIUS = 140;
function isNearHat(x, extra = 0) {
  if (!hat.spawned || hat.picked) return false;
  return Math.abs((x || 0) - (hat?.x || 0)) <= HAT_CLEAR_RADIUS + Math.max(0, extra);
}

// Helper to check if near helicohat shrine
function isNearHelicohatShrine(x, extra = 0) {
  return Math.abs((x || 0) - HELICOHAT_SHRINE_X) <= HELICOHAT_SHRINE_CLEAR_RADIUS + Math.max(0, extra);
}

function isNearSurfaceRuin(x, extra = 0) {
  const dist = Math.max(0, extra);
  for (const r of ruins) {
    if (!r || typeof r.x !== 'number') continue;
    if (Math.abs(x - r.x) <= Math.max(dist, (r.targetH || 0) * 0.5)) return true;
  }
  return false;
}

// --- WIP NPC (end of current game) ---
const WIP_NPC_CLEAR_RADIUS = 300; // Empty grove around WIP NPC
function isNearWipNpc(x, extra = 0) {
  return Math.abs((x || 0) - WIP_NPC_SPAWN_X) <= WIP_NPC_CLEAR_RADIUS + Math.max(0, extra);
}

// ---
const WIP_NPC_SPAWN_X = 11200; // A bit after the blob
const WIP_NPC_TARGET_H = 70;
const wipNpc = {
  x: WIP_NPC_SPAWN_X,
  w: WIP_NPC_TARGET_H,
  h: WIP_NPC_TARGET_H,
  facing: -1,
  overlap: false,
  yOffset: 0,
  talkRadius: 100
};
const wipNpcImg = new Image();
let wipNpcReady = false;
let wipNpcBounds = null;
wipNpcImg.onload = () => {
  wipNpcReady = true;
  wipNpcBounds = computeImageOpaqueBounds(wipNpcImg);
  const aspect = (wipNpcImg.naturalWidth && wipNpcImg.naturalHeight) ? (wipNpcImg.naturalWidth / wipNpcImg.naturalHeight) : 1;
  wipNpc.h = WIP_NPC_TARGET_H;
  wipNpc.w = Math.max(1, Math.round(WIP_NPC_TARGET_H * aspect));
  const scaleY = wipNpc.h / Math.max(1, wipNpcImg.naturalHeight || wipNpc.h);
  const bottom = wipNpcBounds ? wipNpcBounds.bottom || 0 : 0;
  wipNpc.yOffset = Math.round(bottom * scaleY);
};
wipNpcImg.src = 'img_assets/WIP-npc.png';
const wipBannerImg = new Image();
let wipBannerReady = false;
wipBannerImg.onload = () => { wipBannerReady = true; };
wipBannerImg.src = 'img_assets/WIP-banner.png';
const wipNpcDialogue = [
  "hello, wanderer! you have reached the end of the current game - check later for more!"
];
const wipNpcApocalypseDialogue = [
  "well you done messed now haven’t ya. Run little bloop!"
];

// --- Underground Cave NPC (helper for levitation controls) ---
const undergroundNpcImg = new Image();
let undergroundNpcReady = false;
let undergroundNpcBounds = null;
undergroundNpcImg.onload = () => {
  undergroundNpcReady = true;
  undergroundNpcBounds = computeImageOpaqueBounds(undergroundNpcImg);
};
undergroundNpcImg.src = 'img_assets/npcyel.png';
const undergroundNpcDialogue = [
  "Hello.\nYou're far from the surface.\nRemember, if you want to get back up, press C and jump!\nGood luck down here!"
];
let undergroundNpcs = []; // { x, y, w, h, facing, overlap, talkRadius }

// Axe fun spin on jump
let axeSpin = 0; // current radians
let axeSpinRemain = 0; // queued radians to spin
const AXE_SPIN_SPEED = 20; // rad/s (quick full rotation)
let lastOnGround = true; // track jump edge

// --- Verdant Archway & Shrine ---
const ARCH_SPAWN_X = 1400;
const SHRINE_SPAWN_X = ARCH_SPAWN_X + SHRINE_REPOSITION_OFFSET;
const arch = { x: ARCH_SPAWN_X };
const shrine = {
  x: SHRINE_SPAWN_X,
  activated: false,
  overlap: false,
  hp: 0,
  maxHp: 0,
  damageFlash: 0
};
const SHRINE_MAX_HP = 1; // single axe hit now cleanses
// Controls vertical placement relative to the ground surface (px).
// Positive values push the shrine down, negative lift it up.
const SHRINE_GROUND_OFFSET = 6;
const archLeftImg = new Image();
const archRightImg = new Image();
let archLeftReady = false, archRightReady = false;
let archLeftBounds = null, archRightBounds = null;
archLeftImg.onload = () => { archLeftReady = true; archLeftBounds = computeImageOpaqueBounds(archLeftImg); };
archRightImg.onload = () => { archRightReady = true; archRightBounds = computeImageOpaqueBounds(archRightImg); };
archLeftImg.src = 'img_assets/archleft.png';
archRightImg.src = 'img_assets/archright.png';
const ARCH_CLEAR_LEFT = 260;
const ARCH_CLEAR_RIGHT = 260;
const ARCH_TARGET_H = 392;
const ARCH_GROUND_OFFSET = 18;
const ARCH_LEFT_Y_OFFSET = 35;
const ARCH_RIGHT_Y_OFFSET = 41;
const ARCH_LEFT_X_OFFSET = 34;
const ARCH_RIGHT_X_OFFSET = -228;
const shrineCorruptImg = new Image();
const shrineFreedImg = new Image();
let shrineCorruptReady = false, shrineFreedReady = false;
let shrineCorruptBounds = null, shrineFreedBounds = null;
shrineCorruptImg.onload = () => { shrineCorruptReady = true; shrineCorruptBounds = computeImageOpaqueBounds(shrineCorruptImg); };
shrineFreedImg.onload = () => { shrineFreedReady = true; shrineFreedBounds = computeImageOpaqueBounds(shrineFreedImg); };
shrineCorruptImg.src = 'img_assets/shrinecorrupt.png';
shrineFreedImg.src = 'img_assets/shrinefreed.png';
shrine.maxHp = SHRINE_MAX_HP;
shrine.hp = SHRINE_MAX_HP;
const barrierVinesImg = new Image();
let barrierVinesReady = false;
let barrierVinesBounds = null;
barrierVinesImg.onload = () => { barrierVinesReady = true; barrierVinesBounds = computeImageOpaqueBounds(barrierVinesImg); };
barrierVinesImg.src = 'img_assets/barriervines.png';
const BARRIER_VINES_TARGET_H = 360;
const BARRIER_VINES_INITIAL_OFFSET = 180;
const BARRIER_VINES_GROUND_OFFSET = 18;
const BARRIER_VINES_BLOCK_WIDTH = 110;
const BARRIER_VINES_GAP = 28;
const BARRIER_VINES_FADE_SPEED = 1.6;
const barrierVines = {
  x: SHRINE_SPAWN_X + BARRIER_VINES_INITIAL_OFFSET,
  fade: 1,
  fading: false,
  removed: false
};
if (!axe.picked) {
  axe.spawned = true;
  axe.x = SHRINE_SPAWN_X - AXE_PRE_SHRINE_OFFSET;
  axe.spawnTime = 0;
  axe.floatPhase = Math.random() * Math.PI * 2;
}
const SHRINE_TARGET_H = 220;
let shrineBloomTrees = [];
let verdantGrassActive = false; // becomes true after activating the shrine
let grassReveal = 0; // world radius from shrine.x that is revealed (animated)
const GRASS_GROW_SPEED = 800; // px per second per side
const POST_SHRINE_TREE_BUFFER = 320;
const POST_SHRINE_TREE_MIN_SPACING = 150;
let shrineSfx = null;
try {
  shrineSfx = new Audio('sfx_assets/verdant_shrine_pressed.wav');
  setAudioBaseVolume(shrineSfx, VOLUME_BASES.shrineSfx, AUDIO_KIND_SFX);
} catch {
  shrineSfx = null;
}

// --- Jetback collectible (backpack on/off) ---
const JETBACK_SPAWN_X = 200; // a bit after the pickaxe
const jetback = { x: JETBACK_SPAWN_X, picked: false, equipped: false, active: false, spawnTime: 0, floatPhase: Math.random() * Math.PI * 2, spawned: false };
const jetImgOff = new Image();
const jetImgOn = new Image();
let jetOffReady = false, jetOnReady = false;
jetImgOff.onload = () => { jetOffReady = true; };
jetImgOn.onload = () => { jetOnReady = true; };
jetImgOff.src = 'img_assets/jetback_off.png';
jetImgOn.src = 'img_assets/jetback_on.png';
let jetbackOverlap = false;

// --- Spaceship ---
// Spaceship sits past the "do not press" button (DNP is at WIP_NPC_SPAWN_X + 400) so it can't
// be reached instantly, but remains available for testing the spaceflight/moon arc.
const SPACESHIP_SPAWN_X = WIP_NPC_SPAWN_X + 1000;
const spaceship = {
  x: SPACESHIP_SPAWN_X,
  mode: 'idle',        // 'idle' | 'entering' | 'occupied'
  barT: 0,             // time elapsed in cinematic bar animation
  barDur: 1.0,         // total bar animation duration (seconds)
  linePhase: 0,        // 0-1 scrolling phase for speed lines
  flightX: 0,          // world-space flight position
  flightY: 0,
  flightVX: 0,
  flightVY: 0,
  baseX: 0,            // base world-X (camera tracks this)
  baseY: 0,            // base world-Y that auto-ascends
  vpOffX: 0,           // viewport offset X (player-controlled screen position)
  vpOffY: 0,           // viewport offset Y
  emptyActive: false,  // empty ship drifting after eject
  emptyX: 0,
  emptyY: 0,
  emptyVY: 0,
  emptyTimer: 0,
};
let spaceshipOverlap = false;

// Atmospheric zones (altitude above ground in world-px)
const ATMO_ZONES = [
  // [ name,           minAlt,  overlayRGB,       maxOverlayAlpha ]
  [ 'Troposphere',         0,   null,              0.00 ],
  [ 'Upper Troposphere',8000,   [12, 18,  75],     0.22 ],
  [ 'Stratosphere',   18000,   [ 6, 10,  50],     0.58 ],
  [ 'Mesosphere',     36000,   [ 3,  4,  22],     0.82 ],
  [ 'Thermosphere',   56000,   [ 1,  2,   8],     0.94 ],
  [ 'Space',          82000,   [ 0,  0,   0],     1.00 ],
];
// Star field (screen-normalised 0-1, generated once)
const SPACE_STARS = (() => {
  const rng = i => { let s = (i * 2654435761) >>> 0; s ^= s >> 15; s *= 0x2c1b3c6d; s ^= s >> 12; s *= 0x297a2d39; return (s >>> 0) / 0xffffffff; };
  return Array.from({ length: 240 }, (_, i) => ({
    x:   rng(i * 7),     y:   rng(i * 7 + 1),
    r:   0.4 + rng(i * 7 + 2) * 1.8,
    br:  0.45 + rng(i * 7 + 3) * 0.55,
    tw:  rng(i * 7 + 4) * Math.PI * 2,
    tws: 0.7 + rng(i * 7 + 5) * 2.2,
  }));
})();
let spaceshipAltitude = 0;      // updated each frame during flight
let spaceshipZoneIdx  = 0;      // current ATMO_ZONES index
let spaceshipZoneLabelText = '';
let spaceshipZoneLabelT    = 0;
const ZONE_LABEL_DUR = 3.0;
const spaceshipOffImg   = new Image();
const spaceshipEquipImg = new Image();
const spaceshipEmptyImg = new Image();
let spaceshipOffReady = false, spaceshipEquipReady = false, spaceshipEmptyReady = false;
spaceshipOffImg.onload   = () => { spaceshipOffReady   = true; };
spaceshipEquipImg.onload = () => { spaceshipEquipReady = true; };
spaceshipEmptyImg.onload = () => { spaceshipEmptyReady = true; };
spaceshipOffImg.src   = 'img_assets/spaceship/spaceshipoff.png';
spaceshipEquipImg.src = 'img_assets/spaceship/spaceshipequip.png';
spaceshipEmptyImg.src = 'img_assets/spaceship/spaceshipempty.png';
// Space debris images (reuse existing assets)
const spaceDebrisImgs = [
  'img_assets/meteor.png',
  'img_assets/debris_1.png',
  'img_assets/debris_4.png', 'img_assets/debris_5.png', 'img_assets/debris_6.png',
].map(src => { const i = new Image(); i.src = src; return i; });
const spaceMoonImg = new Image();
spaceMoonImg.src = 'img_assets/moon.png';
const transEarthImg = new Image();
transEarthImg.src = 'img_assets/earth.webp';
const transMoonImg = new Image();
transMoonImg.src = 'img_assets/moon.webp';

// Pre-load at startup so it's decoded and ready to play instantly
const sonicBoomAudio = new Audio('music_assets/sonicboom.mp3');
sonicBoomAudio.loop = true;
sonicBoomAudio.preload = 'auto';
function ensureSonicBoomAudio() { return sonicBoomAudio; }

// --- Sky Platforms (platformmagic_empty) ---
const platformImg = new Image();
let platformReady = false;
let platformImgBounds = null; // { top, bottom, left, right } in source pixels
platformImg.onload = () => { platformReady = true; platformImgBounds = computeImageOpaqueBounds(platformImg); };
platformImg.src = 'img_assets/platformmagic_empty.png';
// Static sky platforms by 2D chunks (world-space)
const platformChunkW = 1800; // horizontal chunk size
const platformChunkH = 1400; // vertical chunk size
const platformsByCell = new Map(); // key: `${cx},${cy}` => array of { x, y, w, h }
let legacyPlatform = null; // always-spawned platform near the early area
const PLATFORM_MOVE_SPEED = 24; // px/s platform carry speed when standing on it
let standingOnPlatform = false; // set each frame when landing/standing on a platform
let standingOnPlank = false;    // true when directly supported by a player-built plank
let currentPlatformCarry = null; // platform currently carrying player (if any)
const SKY_CHEST_PLATFORM_Y_OFFSET = 55; // Adjust chest vertical placement relative to platform top (px, positive = lower into platform)

// Sky chests (spawn on some platforms)
const chestClosedImg = new Image(); const chestOpenImg = new Image();
let chestClosedReady = false, chestOpenReady = false;
chestClosedImg.onload = () => { chestClosedReady = true; };
chestOpenImg.onload = () => { chestOpenReady = true; };
chestClosedImg.src = 'img_assets/chest.png';
chestOpenImg.src = 'img_assets/chest_open.png';
let skyChests = []; // { x,y,w,h,open:boolean,fade:number,platform:ref }
function spawnChestOnPlatform(p) {
  // Place chest centered horizontally on platform, sitting on top
  const img = chestClosedImg;
  const ready = chestClosedReady || (img && img.naturalWidth);
  const targetW = 56; const dw = targetW; const aspect = (img.naturalWidth && img.naturalHeight) ? (img.naturalWidth / img.naturalHeight) : 1;
  const dh = Math.max(12, Math.round(targetW / Math.max(0.1, aspect)));
  const top = p.y - p.h / 2;
  // Position chest so bottom edge touches top edge of platform (no +1 offset)
  const ch = {
    x: Math.round(p.x - dw / 2),
    y: Math.round(top - dh + SKY_CHEST_PLATFORM_Y_OFFSET),
    w: dw,
    h: dh,
    open: false,
    fade: 1,
    platform: p, // Reference to parent platform so chest can move with it
    contents: Math.random() < 0.5
      ? { kind: 'diamonds', amount: 1 + Math.floor(Math.random() * 3) } // 1-3 diamonds
      : { kind: 'wood', amount: 10 + Math.floor(Math.random() * 21) } // 10-30 wood
  };
  skyChests.push(ch);
}

// --- Decorative trees in world-space chunks (image-based) ---
const chunkSize = 420; // world units per chunk
const treesByChunk = new Map(); // key: chunk index, val: array of trees with world x
const midgroundTreesByChunk = new Map();
const backgroundTreesByChunk = new Map();
let treePieces = []; // debris pieces from chopped trees
const TREE_GROUND_SINK = 3; // Sink trees slightly into the ground so they visually touch firmly
const TREE_DENSITY_BASE = 1 / 120; // baseline trees per pixel (increased from 1/140)
const TREE_DENSITY_MAX_BOOST = 1.2; // max additional density multiplier as forest thickens (increased from 0.75)
const TREE_DENSITY_RAMP_OFFSET = 160; // px past the titan before density starts increasing
const TREE_DENSITY_RAMP_LENGTH = 2600; // distance over which density reaches forest-edge boost
const TREE_DENSITY_HEART_OFFSET = 820; // extra distance before the forest heart thickens further
const TREE_DENSITY_HEART_EXTRA = 0.8; // added density boost once inside the forest heart (increased from 0.45)
const TREE_DENSITY_HEART_RAMP = 1600; // distance to reach the heart's extra density
const TREE_DENSITY_MAX_TOTAL = 1 + TREE_DENSITY_MAX_BOOST + TREE_DENSITY_HEART_EXTRA;
let treeDensityRampOrigin = 5200 + SHRINE_REPOSITION_OFFSET; // updated once titan placement is known
const ORANGE_TREE_MIN_BOOST = 1.22;
const ORANGE_TREE_SPAWN_CHANCE = 0.08;
const OAK_TREE_MIN_BOOST = 1.32;
const OAK_TREE_SPAWN_CHANCE = 0.07;
const CORRUPTED_TREE_MIN_BOOST = 1.40;
const CORRUPTED_TREE_SPAWN_CHANCE = 0.05;
const SPECIAL_TREE_CLEAR_RADIUS = 68;

// Tree art: variants (including deep-forest orange tree)
const TREE_IMG_SRC = [
  'img_assets/tree1.png',
  'img_assets/tree2.png',
  'img_assets/tree3.png',
  'img_assets/orangetree.png',
  'img_assets/oaktree.png',
  'img_assets/oaktreefade.png',
  'img_assets/corruptedtree.png'
];
const treeImgs = TREE_IMG_SRC.map(src => {
  const img = new Image();
  img.src = src;
  return img;
});
let treeImgsReady = 0;
const treeImgBounds = new Array(treeImgs.length).fill(null);
treeImgs.forEach((img, i) => {
  const ready = () => {
    treeImgsReady++;
    treeImgBounds[i] = computeImageOpaqueBounds(img);
  };
  if (img.complete && img.naturalWidth) ready();
  else img.addEventListener('load', ready, { once: true });
});
const ORANGE_TREE_INDEX = TREE_IMG_SRC.indexOf('img_assets/orangetree.png');
const OAK_TREE_INDEX = TREE_IMG_SRC.indexOf('img_assets/oaktree.png');
const OAK_TREE_FADE_INDEX = TREE_IMG_SRC.indexOf('img_assets/oaktreefade.png');
const CORRUPTED_TREE_INDEX = TREE_IMG_SRC.indexOf('img_assets/corruptedtree.png');
const BIRD_PERCH_TREE_IMG_INDICES = new Set([TREE_IMG_SRC.indexOf('img_assets/tree1.png'), TREE_IMG_SRC.indexOf('img_assets/tree3.png'), OAK_TREE_INDEX].filter(i => i >= 0));
const BIRD_PERCH_FOOT_OFFSET = 0; // Pixels to sink bird feet into the tree top for visible contact

// Deep-forest layered trees (midground + background) disabled
const MIDGROUND_TREE_IMG_SRC = [];
const midgroundTreeImgs = [];
const midgroundTreeBounds = [];
const BACKGROUND_TREE_IMG_SRC = [];
const backgroundTreeImgs = [];
const backgroundTreeBounds = [];

// Tree chunk art for debris
const treeChunkImgs = [new Image(), new Image(), new Image(), new Image(), new Image(), new Image()];
let treeChunkReady = 0;
treeChunkImgs.forEach((im, i)=>{ im.onload = ()=> { treeChunkReady++; }; });
treeChunkImgs[0].src = 'img_assets/treechunk1.png';
treeChunkImgs[1].src = 'img_assets/treechunk2.png';
treeChunkImgs[2].src = 'img_assets/treechunk3.png';
treeChunkImgs[3].src = 'img_assets/treechunk4.png';
treeChunkImgs[4].src = 'img_assets/treechunk5.png';
treeChunkImgs[5].src = 'img_assets/treechunk6.png';

// --- Underground foliage test ---
const FOLIAGE_TOGGLE = false;
const FOLIAGE_IMG_SRC = [
  'img_assets/foliage_element/2foliag5.png',
  'img_assets/foliage_element/2foliage1.png',
  'img_assets/foliage_element/2foliage2.png',
  'img_assets/foliage_element/2foliage3.png',
  'img_assets/foliage_element/2foliage4.png',
  'img_assets/foliage_element/2foliage6.png',
  'img_assets/foliage_element/foliageelement1.png',
  'img_assets/foliage_element/foliageelement2.png',
  'img_assets/foliage_element/foliageelement3.png',
  'img_assets/foliage_element/foliageelement4.png',
  'img_assets/foliage_element/foliageelement5.png',
  'img_assets/foliage_element/foliageelement6.png',
  'img_assets/foliage_element/foliageelement7.png',
  'img_assets/foliage_element/foliageelement8.png',
  'img_assets/foliage_element/foliageelement9.png',
  'img_assets/foliage_element/foliageelement10.png',
  'img_assets/foliage_element/foliageelement11.png'
];
const foliageImgs = FOLIAGE_IMG_SRC.map(src => {
  const img = new Image();
  img.src = src;
  return img;
});
let foliageImgsReady = 0;
foliageImgs.forEach((img) => {
  if (img.complete && img.naturalWidth) foliageImgsReady += 1;
  else img.addEventListener('load', () => { foliageImgsReady += 1; }, { once: true });
});
const foliageByChunk = new Map();
const FOLIAGE_CHUNK_SIZE = 360;
const FOLIAGE_DEPTH_MIN = 70;
const FOLIAGE_DEPTH_MAX = 240;
const FOLIAGE_MIN_COUNT = 2;
const FOLIAGE_MAX_COUNT = 5;

// --- Chopped logs HUD ---
const logImg = new Image();
let logReady = false;
logImg.onload = () => { logReady = true; };
logImg.src = 'img_assets/log.png';
let choppedCount = 0; // Start with 0 logs - chop trees to get wood

// Diamonds (cave loot)
const diamondImg = new Image();
let diamondReady = false;
diamondImg.onload = () => { diamondReady = true; };
diamondImg.src = 'img_assets/diamong.png';
const debris2Img = new Image();
let debris2Ready = false;
debris2Img.onload = () => { debris2Ready = true; };
debris2Img.src = 'img_assets/debris_2.png';
const debris7Img = new Image();
let debris7Ready = false;
debris7Img.onload = () => { debris7Ready = true; };
debris7Img.src = 'img_assets/debris_7.png';
let diamonds = []; // { x, y, w, h, picked }
let diamondCount = 0;
let caveSpawns = []; // queued spawns from cave generation: { cx, cy, tx, ty, type: 'diamond'|'spider'|'ruins'|'chest', realized?:bool }
let undergroundChests = []; // { x, y, w, h, open, fade, contents }
let undergroundRuins = []; // { x, y, targetH, inCave }

// Wisp Feathers (deep forest collectible)
const featherImg = new Image();
let featherReady = false;
featherImg.onload = () => { featherReady = true; };
featherImg.src = 'img_assets/feather.png';
let featherCount = 0;
const FEATHER_CHUNK_W = 420;
const FEATHER_VIEW_MARGIN = 520;
const FEATHER_MIN_ALTITUDE = 80;
const FEATHER_MAX_ALTITUDE = 150;
const FEATHER_SPAWN_CHANCE = 0.0; // disabled: feathers now tied to ruins
const FEATHER_EXTRA_CHANCE = 0.0;
const FEATHER_FLOAT_SPEED_MIN = 0.6;
const FEATHER_FLOAT_SPEED_MAX = 1.15;
const FEATHER_GLOW_SPEED_MIN = 0.45;
const FEATHER_GLOW_SPEED_MAX = 0.9;
const FEATHER_SPAWN_OFFSET = 240; // distance past titan before feathers appear
const FEATHER_BOB_MIN = 12;
const FEATHER_BOB_MAX = 22;
const FEATHER_SWAY_MIN = 8;
const FEATHER_SWAY_MAX = 18;
let featherChunks = new Map(); // chunk index -> list of feathers in that span
let feathers = []; // active feather refs for quick iteration

// Silk icon (for HUD and toasts)
const silkImg = new Image();
let silkReady = false;
silkImg.onload = () => { silkReady = true; };
silkImg.src = 'img_assets/silk.png';

// --- Digging (pickaxe) - Unified Tile System ---
const GROUND_EXTRA = 20000; // much deeper ground body for long-range digging
const AXE_CHOP_HOLD_RATE = 0.18; // faster auto-chops while holding interact
let axeChopCooldown = 0;
// Physics accumulator for fixed timestep
let physicsAccumulator = 0;

// Legacy columnar system removed - using unified tile system only

function seededPRNG(seed) {
  // mulberry32
  let t = (seed >>> 0) + 0x6D2B79F5;
  return function() {
    t |= 0; t = (t + 0x6D2B79F5) | 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function randRange(rng, min, max) { return min + (max - min) * rng(); }

// moved dirtPattern/ensureDirtPattern earlier (near randRange)

function generateFoliageChunk(index) {
  const arr = [];
  const rng = seededPRNG((0xF01A6 ^ (index * 0x45d9f3b)) >>> 0);
  const count = FOLIAGE_MIN_COUNT + Math.floor(rng() * (FOLIAGE_MAX_COUNT - FOLIAGE_MIN_COUNT + 1));
  const startX = index * FOLIAGE_CHUNK_SIZE;
  for (let i = 0; i < count; i++) {
    const imgIndex = Math.floor(rng() * Math.max(1, FOLIAGE_IMG_SRC.length));
    const x = randRange(rng, startX + 16, startX + FOLIAGE_CHUNK_SIZE - 16);
    const depth = randRange(rng, FOLIAGE_DEPTH_MIN, FOLIAGE_DEPTH_MAX);
    const scale = 0.10 + rng() * 0.12; // much smaller foliage
    const jitter = (rng() - 0.5) * 6;
    arr.push({ x, depth, imgIndex, scale, jitter });
  }
  return arr;
}

function ensureFoliageInView(camX, w) {
  if (!FOLIAGE_TOGGLE) return;
  const margin = Math.max(480, w * 0.75);
  const start = Math.floor((camX - margin) / FOLIAGE_CHUNK_SIZE) - 1;
  const end = Math.floor((camX + w + margin) / FOLIAGE_CHUNK_SIZE) + 1;
  for (let idx = start; idx <= end; idx++) {
    if (!foliageByChunk.has(idx)) foliageByChunk.set(idx, generateFoliageChunk(idx));
  }
  // prune
  for (const key of Array.from(foliageByChunk.keys())) {
    if (key < start - 2 || key > end + 2) foliageByChunk.delete(key);
  }
}

function drawFoliage(ctx, w, h) {
  if (!FOLIAGE_TOGGLE || foliageImgsReady <= 0) return;
  ensureFoliageInView(cameraX, w);
  if (!foliageByChunk.size) return;
  const groundTop = h - world.groundH;
  for (const [, list] of foliageByChunk) {
    for (const f of list) {
      const img = foliageImgs[f.imgIndex % foliageImgs.length];
      if (!img || !img.naturalWidth || !img.naturalHeight) continue;
      const dw = img.naturalWidth * f.scale;
      const dh = img.naturalHeight * f.scale;
      const drawX = f.x + (f.jitter || 0);
      const drawY = groundTop + f.depth - dh;
      ctx.drawImage(img, Math.round(drawX - dw / 2), Math.round(drawY), Math.max(1, Math.round(dw)), Math.max(1, Math.round(dh)));
    }
  }
}

function featherSpawnStartX() {
  const titanX = (rockTitan && typeof rockTitan.x === 'number') ? rockTitan.x : (treeDensityRampOrigin || 0);
  return titanX + TREE_DENSITY_RAMP_OFFSET + FEATHER_SPAWN_OFFSET;
}

function spawnFeatherAtRuins(x, h, ruinsH) {
  if (!featherReady) return null;
  const targetH = 30;
  const aspect = featherImg.naturalWidth && featherImg.naturalHeight
    ? (featherImg.naturalWidth / featherImg.naturalHeight)
    : 0.55;
  const dh = targetH;
  const dw = Math.max(12, Math.round(targetH * aspect));
  const groundTop = h - world.groundH;
  const centerY = groundTop - Math.max(70, ruinsH * 0.35);
  const feather = {
    id: crypto.randomUUID ? crypto.randomUUID() : `ruin-feather-${Math.random().toString(36).slice(2)}`,
    chunk: Math.floor(x / FEATHER_CHUNK_W),
    baseX: x,
    baseY: centerY,
    x,
    y: centerY,
    w: dw,
    h: dh,
    bobT: Math.random() * Math.PI * 2,
    swayT: Math.random() * Math.PI * 2,
    swayAmp: randRange(Math.random, FEATHER_SWAY_MIN, FEATHER_SWAY_MAX),
    bobAmp: randRange(Math.random, FEATHER_BOB_MIN, FEATHER_BOB_MAX),
    floatSpeed: randRange(Math.random, FEATHER_FLOAT_SPEED_MIN, FEATHER_FLOAT_SPEED_MAX),
    glowSpeed: randRange(Math.random, FEATHER_GLOW_SPEED_MIN, FEATHER_GLOW_SPEED_MAX),
    phase: randRange(Math.random, 0, Math.PI * 2),
    phaseOffset: randRange(Math.random, 0, Math.PI * 2),
    glowPhase: randRange(Math.random, 0, Math.PI * 2),
    glowStrength: 0.4,
    collected: false,
    removed: false
  };
  if (!featherChunks.has(feather.chunk)) featherChunks.set(feather.chunk, []);
  featherChunks.get(feather.chunk).push(feather);
  feathers.push(feather);
  return feather.id;
}

function ensureFeathersInView(camX, w, h) {
  const margin = Math.max(FEATHER_VIEW_MARGIN, w * 0.6);
  const start = Math.floor((camX - margin) / FEATHER_CHUNK_W) - 1;
  const end = Math.floor((camX + w + margin) / FEATHER_CHUNK_W) + 1;
  for (let idx = start; idx <= end; idx++) {
    if (!featherChunks.has(idx)) {
      const chunkFeathers = generateFeatherChunk(idx, w, h);
      featherChunks.set(idx, chunkFeathers);
      if (chunkFeathers.length) {
        for (const f of chunkFeathers) feathers.push(f);
      }
    }
  }
  // prune chunks far away to keep memory bounded
  for (const key of Array.from(featherChunks.keys())) {
    if (key < start - 2 || key > end + 2) {
      const arr = featherChunks.get(key) || [];
      for (const f of arr) { f.removed = true; }
      featherChunks.delete(key);
    }
  }
  if (feathers.length) {
    feathers = feathers.filter(f => !f.collected && !f.removed && featherChunks.has(f.chunk));
  }
}

function generateFeatherChunk(index, w, h) {
  // Feathers now spawn only on ruins; disable chunk-based generation.
  return [];
}

function treeDensityBoostAt(midX, maxBoost = TREE_DENSITY_MAX_BOOST) {
  const rampStart = treeDensityRampOrigin + TREE_DENSITY_RAMP_OFFSET;
  if (midX <= rampStart) return 1;
  const primaryT = Math.max(0, Math.min(1, (midX - rampStart) / Math.max(1, TREE_DENSITY_RAMP_LENGTH)));
  // Smoothstep for a gentle transition at the forest edge
  const primarySmooth = primaryT * primaryT * (3 - 2 * primaryT);
  let boost = 1 + primarySmooth * maxBoost;

  const heartStart = rampStart + TREE_DENSITY_HEART_OFFSET;
  if (midX > heartStart) {
    const heartT = Math.max(0, Math.min(1, (midX - heartStart) / Math.max(1, TREE_DENSITY_HEART_RAMP)));
    const heartSmooth = heartT * heartT * (3 - 2 * heartT);
    boost += heartSmooth * TREE_DENSITY_HEART_EXTRA;
  }

  return boost;
}

function forestDepthFactor(midX) {
  const boost = treeDensityBoostAt(midX, TREE_DENSITY_MAX_BOOST);
  const maxBoost = Math.max(1.0001, TREE_DENSITY_MAX_TOTAL);
  return Math.max(0, Math.min(1, (boost - 1) / (maxBoost - 1)));
}

function drawForestFogOverlay(ctx, screenW, screenH) {
  const playerCenter = player.x + player.w * 0.5;
  const depth = forestDepthFactor(playerCenter);
  if (depth <= 0.01) return;

  // Don't draw forest fog when player is high above ground (prevents dark overlay at high altitude)
  const h = world.height();
  const groundTopWorld = h - world.groundH;
  const playerCenterY = player.y + player.h * 0.5;
  const altitudeAboveGround = groundTopWorld - playerCenterY;
  // Only draw fog when player is within ~2 screen heights of ground level
  if (altitudeAboveGround > h * 2) return;

  const groundTopScreen = (world.height() - world.groundH + cameraY) * VIEW_SCALE;
  const fogAlpha = 0.22 * depth;
  const vignetteAlpha = 0.42 * depth;

  ctx.save();
  // Cool, desaturated fog from bottom upward
  const fogGrad = ctx.createLinearGradient(0, groundTopScreen * 0.6, 0, screenH);
  fogGrad.addColorStop(0, `${FOREST_FOG_COLOR}0)`);
  fogGrad.addColorStop(1, `${FOREST_FOG_COLOR}${fogAlpha})`);
  ctx.fillStyle = fogGrad;
  ctx.fillRect(0, 0, screenW, screenH);

  // Vignette to tighten edges as you go deeper
  const cx = screenW * 0.5;
  const cy = screenH * 0.55;
  const radius = Math.max(screenW, screenH) * 0.75;
  const vigGrad = ctx.createRadialGradient(cx, cy, radius * 0.15, cx, cy, radius);
  vigGrad.addColorStop(0, `${FOREST_VIGNETTE_COLOR}0)`);
  vigGrad.addColorStop(1, `${FOREST_VIGNETTE_COLOR}${vignetteAlpha})`);
  ctx.fillStyle = vigGrad;
  ctx.fillRect(0, 0, screenW, screenH);
  ctx.restore();
}

function ensureChunksInView(camX, w) {
  const margin = 160;
  const start = Math.floor((camX - margin) / chunkSize);
  const end = Math.floor((camX + w + margin) / chunkSize);
  for (let i = start; i <= end; i++) {
    if (!treesByChunk.has(i)) treesByChunk.set(i, generateChunk(i));
  }
  // prune far-away chunks to keep memory bounded
  for (const key of Array.from(treesByChunk.keys())) {
    if (key < start - 3 || key > end + 3) treesByChunk.delete(key);
  }
}

function ensureTreeChunksForRange(xMin, xMax) {
  const startIdx = Math.floor(Math.min(xMin, xMax) / chunkSize);
  const endIdx = Math.floor(Math.max(xMin, xMax) / chunkSize);
  for (let i = startIdx; i <= endIdx; i++) {
    if (!treesByChunk.has(i)) treesByChunk.set(i, generateChunk(i));
  }
}

function generateChunk(index) {
  const rng = seededPRNG(0x9E3779B9 ^ (index | 0));
  const startX = index * chunkSize;
  const endX = startX + chunkSize;
  if (arch && typeof arch.x === 'number' && endX <= arch.x) {
    return [];
  }
  const chunkMid = startX + chunkSize * 0.5;
  const densityBoost = treeDensityBoostAt(chunkMid);
  const density = TREE_DENSITY_BASE * densityBoost; // trees per pixel, scales deeper in forest
  const jitter = 0.9 + rng() * 0.2; // light randomness keeps pattern organic
  const count = Math.max(2, Math.round(chunkSize * density * jitter));
  const list = [];
  let orangeSpawned = false;
  let oakSpawned = false;
  let corruptedSpawned = false;
  for (let i = 0; i < count; i++) {
    const x = Math.round(randRange(rng, startX + 16, endX - 16));
    const scale = randRange(rng, 0.8, 1.25);
    let img = Math.floor(rng() * Math.max(1, ORANGE_TREE_INDEX)) % Math.max(1, ORANGE_TREE_INDEX);
    let kind = 'standard';
    const canSpawnOrange = !orangeSpawned && densityBoost >= ORANGE_TREE_MIN_BOOST;
    const canSpawnOak = !oakSpawned && densityBoost >= OAK_TREE_MIN_BOOST;
    const canSpawnCorrupted = !corruptedSpawned && densityBoost >= CORRUPTED_TREE_MIN_BOOST;
    if (canSpawnOrange && rng() < ORANGE_TREE_SPAWN_CHANCE) {
      img = ORANGE_TREE_INDEX;
      kind = 'orange';
      orangeSpawned = true;
    } else if (canSpawnOak && rng() < OAK_TREE_SPAWN_CHANCE) {
      img = OAK_TREE_INDEX;
      kind = 'oak';
      oakSpawned = true;
    } else if (canSpawnCorrupted && rng() < CORRUPTED_TREE_SPAWN_CHANCE) {
      img = CORRUPTED_TREE_INDEX;
      kind = 'corrupted';
      corruptedSpawned = true;
    }
    const maxHp = kind === 'oak' ? 3 : 1;
    list.push({ x, scale, img, kind, hp: maxHp, maxHp, felled: false, fallT: 0, fallDir: 1 });
  }
const zones = [];
for (const trap of plantTraps) {
  zones.push({ x: trap.x, left: PLANT_TRAP_TARGET_W * 0.5, right: PLANT_TRAP_TARGET_W * 0.7 });
}
if (arch && typeof arch.x === 'number') zones.push({ x: arch.x, left: ARCH_CLEAR_LEFT, right: ARCH_CLEAR_RIGHT });
if (shrine && typeof shrine.x === 'number') zones.push({ x: shrine.x, left: 220, right: 220 });
// Keep helicohat shrine area clear of trees/objects
zones.push({ x: HELICOHAT_SHRINE_X, left: HELICOHAT_SHRINE_CLEAR_RADIUS_TREES, right: HELICOHAT_SHRINE_CLEAR_RADIUS_TREES });
if (!barrierVines.removed && typeof barrierVines.x === 'number') {
  syncBarrierVinesPlacement(getBarrierVinesDrawInfo());
  zones.push({ x: barrierVines.x, left: 120, right: 180 });
}
if (cave && cave.width) zones.push({ x: cave.x, left: cave.width * 0.35, right: cave.width * 0.85 });
if (enterableCave && enterableCave.width) {
  const clear = getEnterableCaveClearRadius();
  zones.push({ x: enterableCave.x, left: clear, right: clear });
}
if (hat.spawned && !hat.picked) {
  zones.push({ x: hat.x, left: HAT_CLEAR_RADIUS, right: HAT_CLEAR_RADIUS });
}
zones.push({ x: WIP_NPC_SPAWN_X, left: WIP_NPC_CLEAR_RADIUS, right: WIP_NPC_CLEAR_RADIUS });
if (SHOW_DNP_BUTTON && dnpBtn && dnpBtn.x) zones.push({ x: dnpBtn.x, left: 100, right: 100 }); // Prevent trees on donotpress button
zones.push({ x: SPACESHIP_SPAWN_X, left: 200, right: 200 }); // Keep trees off the spaceship at its new spot
if (rockTitan && rockTitan.x) zones.push({ x: rockTitan.x, left: 240, right: 120 });
for (const r of ruins) {
  const half = Math.max(120, (r?.targetH || 180) * 0.7);
  zones.push({ x: r.x, left: half, right: half });
}
for (const m of mushrooms) {
  const half = Math.max(70, (m?.targetH || 60) * 0.8);
  zones.push({ x: m.x, left: half, right: half });
}
  if (zones.length) {
    const filtered = list.filter(t => zones.every(z => {
      const dx = (t.x || 0) - z.x;
      return !(dx >= -z.left && dx <= z.right);
    }));
    list.splice(0, list.length, ...filtered);
  }

  if (shrine && typeof shrine.x === 'number') {
    const minTreeX = shrine.x + POST_SHRINE_TREE_BUFFER;
    const filtered = list.filter(t => (t?.x || 0) >= minTreeX);
    list.splice(0, list.length, ...filtered);
  }

  if (list.length > 1) {
    list.sort((a, b) => (a.x || 0) - (b.x || 0));
    const spaced = [];
    for (const t of list) {
      if (!spaced.length || Math.abs((t.x || 0) - (spaced[spaced.length - 1].x || 0)) >= POST_SHRINE_TREE_MIN_SPACING) {
        spaced.push(t);
      }
    }
    list.splice(0, list.length, ...spaced);
  }

  const specials = list.filter(t => t && (t.kind === 'orange' || t.kind === 'oak' || t.kind === 'corrupted'));
  if (specials.length) {
    const filtered = [];
    for (const t of list) {
      if (t && t.kind === 'standard') {
        let clash = false;
        for (const s of specials) {
          if (Math.abs((s?.x || 0) - (t.x || 0)) < SPECIAL_TREE_CLEAR_RADIUS) {
            clash = true;
            break;
          }
        }
        if (clash) continue;
      }
      filtered.push(t);
    }
    list.splice(0, list.length, ...filtered);
  }
  // Keep shrine area clear of trees for visibility
  try {
    const CLEAR_R = 200; // px around shrine center for custom bloom area
    list.splice(0, list.length, ...list.filter(t => Math.abs((t.x || 0) - (shrine?.x || -1e9)) > CLEAR_R));
  } catch {}
  if (typeof CORRUPT_TITAN_WORLD_X === 'number' && Number.isFinite(CORRUPT_TITAN_WORLD_X)) {
    const CLEAR_LEFT = 60;
    const CLEAR_RIGHT = 260;
    list.splice(0, list.length, ...list.filter(t => {
      const tx = (t?.x || 0);
      if (tx >= CORRUPT_TITAN_WORLD_X - CLEAR_LEFT && tx <= CORRUPT_TITAN_WORLD_X + CLEAR_RIGHT) return false;
      return true;
    }));
  }
  list.sort((a, b) => ((a?.x) || 0) - ((b?.x) || 0));
  return list;
}

function clearTreesNear(centerX, halfWidth) {
  const left = Math.max(0, halfWidth || 0);
  const right = Math.max(0, halfWidth || 0);
  for (const [key, arr] of treesByChunk) {
    const filtered = arr.filter(t => {
      const dx = (t?.x || 0) - centerX;
      return dx < -left || dx > right;
    });
    if (filtered.length !== arr.length) treesByChunk.set(key, filtered);
  }
}

// Midground tree spawning controls below: adjust margin/spacing or depth thresholds here
function ensureMidgroundTreesInView(camX, w, h) {
  // Midground trees disabled - using denser normal trees instead
  // const margin = 220;
  // const start = Math.floor((camX - margin) / chunkSize) - 1;
  // const end = Math.floor((camX + w + margin) / chunkSize) + 1;
  // for (let idx = start; idx <= end; idx++) {
  //   if (!midgroundTreesByChunk.has(idx)) {
  //     midgroundTreesByChunk.set(idx, generateMidgroundChunk(idx, w, h));
  //   }
  // }
  // for (const key of Array.from(midgroundTreesByChunk.keys())) {
  //   if (key < start - 2 || key > end + 2) midgroundTreesByChunk.delete(key);
  // }
}

// Background tree spawning controls: tweak margins/thresholds for silhouette frequency
function ensureBackgroundTreesInView(camX, w, h) {
  const margin = 260;
  // Background trees disabled - using denser normal trees instead
  // const start = Math.floor((camX - margin) / chunkSize) - 1;
  // const end = Math.floor((camX + w + margin) / chunkSize) + 1;
  // for (let idx = start; idx <= end; idx++) {
  //   if (!backgroundTreesByChunk.has(idx)) {
  //     backgroundTreesByChunk.set(idx, generateBackgroundChunk(idx, w, h));
  //   }
  // }
  // for (const key of Array.from(backgroundTreesByChunk.keys())) {
  //   if (key < start - 2 || key > end + 2) backgroundTreesByChunk.delete(key);
  // }
}

function generateMidgroundChunk(index, w, h) {
  const startX = index * chunkSize;
  const endX = startX + chunkSize;
  const chunkMid = startX + chunkSize * 0.5;
  const depth = forestDepthFactor(chunkMid);
  if (depth <= 0.12) return [];
  const rng = seededPRNG(0x7f4a7c15 ^ (index | 0));
  const maxCount = 3; // increase for denser midground columns
  let count = Math.floor(depth * maxCount);
  if (rng() < depth * 0.8) count += 1;
  count = Math.min(maxCount + 1, count);
  if (count <= 0 && depth > 0.2) count = 1;
  if (count <= 0) return [];

  const list = [];
  for (let i = 0; i < count; i++) {
    const baseX = Math.round(randRange(rng, startX + 60, endX - 60));
    if (list.some(t => Math.abs(t.baseX - baseX) < 160)) continue; // spacing clamp between trees
    const imgIndex = midgroundTreeImgs.length ? Math.floor(rng() * midgroundTreeImgs.length) : 0;
    const scaleBase = randRange(rng, 0.30, 0.50); // much smaller - between background and foreground
    const scale = scaleBase * (1 + depth * 0.20); // depth-based amplification
    const parallax = 0.55; // positioned between background (0.35) and foreground (1.0)
    const sink = 12; // pushes sprite into the ground slightly
    const lift = randRange(rng, 8, 22); // vertical jitter for variation
    list.push({
      chunk: index,
      baseX,
      imgIndex,
      scale,
      parallax,
      sink,
      lift,
      alpha: 0.45 + depth * 0.25 // semi-transparent for depth
    });
  }
  return list;
}

function generateBackgroundChunk(index, w, h) {
  const startX = index * chunkSize;
  const endX = startX + chunkSize;
  const chunkMid = startX + chunkSize * 0.5;
  const depth = forestDepthFactor(chunkMid);
  if (depth <= 0.18) return [];
  const rng = seededPRNG(0x4c1f3d97 ^ (index | 0));
  const maxCount = 3; // increased density since trees are now smaller and more transparent
  let count = Math.floor(depth * (maxCount + 1) * 0.7);
  if (rng() < depth * 0.7) count += 1;
  count = Math.min(maxCount + 1, count);
  if (count <= 0 && depth > 0.35) count = 1;
  if (count <= 0) return [];

  const list = [];
  for (let i = 0; i < count; i++) {
    const baseX = Math.round(randRange(rng, startX + 80, endX - 80));
    if (list.some(t => Math.abs(t.baseX - baseX) < 140)) continue; // smaller spacing for smaller background trees
    const imgIndex = backgroundTreeImgs.length ? Math.floor(rng() * backgroundTreeImgs.length) : 0;
    const scaleBase = randRange(rng, 0.12, 0.20); // much smaller for distant background trees
    const scale = scaleBase * (1 + depth * 0.15); // deeper forest → slightly larger
    const parallax = 0.35; // much lower = farther back, more depth
    const sink = 14; // sink towards horizon
    const lift = randRange(rng, 8, 24); // gentle vertical jitter - higher for background
    list.push({
      chunk: index,
      baseX,
      imgIndex,
      scale,
      parallax,
      sink,
      lift,
      alpha: 0.25 + depth * 0.2 // much more transparent for distant background
    });
  }
  return list;
}

function drawMidgroundTrees(ctx, w, h, groundH) {
  if (!midgroundTreeImgs.length) return;
  ensureMidgroundTreesInView(cameraX, w, h);
  const groundY = h - groundH;
  for (const [, arr] of midgroundTreesByChunk) {
    if (!arr || !arr.length) continue;
    for (const tree of arr) {
      const img = midgroundTreeImgs[tree.imgIndex | 0];
      if (!img || !img.naturalHeight) continue;
      const naturalH = img.naturalHeight || 1;
      const naturalW = img.naturalWidth || 1;
      const scale = Math.max(0.1, tree.scale || 1);
      const dh = Math.round(naturalH * scale);
      const dw = Math.max(4, Math.round(dh * (naturalW / naturalH)));
      const parallax = Math.max(0, Math.min(1, tree.parallax ?? 0.78));
      const worldX = cameraX + (tree.baseX - cameraX) * parallax;
      const screenX = worldX - cameraX;
      if (screenX + dw < -260 || screenX > w + 260) continue;
      const b = midgroundTreeBounds[tree.imgIndex | 0] || { bottom: 0 };
      const scaleY = dh / naturalH;
      const yAdjust = Math.round((b.bottom || 0) * scaleY);
      const sink = tree.sink ?? 0;
      const lift = tree.lift ?? 0;
      const drawX = Math.round(worldX - dw / 2);
      const drawY = Math.round(groundY - dh + yAdjust + sink - lift);
      const alpha = Math.max(0.3, Math.min(1, tree.alpha ?? 0.8));
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.drawImage(img, drawX, drawY, dw, dh);
      ctx.restore();
    }
  }
  ctx.globalAlpha = 1;
}

function drawBackgroundTrees(ctx, w, h, groundH) {
  if (!backgroundTreeImgs.length) return;
  ensureBackgroundTreesInView(cameraX, w, h);
  const groundY = h - groundH;
  for (const [, arr] of backgroundTreesByChunk) {
    if (!arr || !arr.length) continue;
    for (const tree of arr) {
      const img = backgroundTreeImgs[tree.imgIndex | 0];
      if (!img || !img.naturalHeight) continue;
      const naturalH = img.naturalHeight || 1;
      const naturalW = img.naturalWidth || 1;
      const scale = Math.max(0.1, tree.scale || 1);
      const dh = Math.round(naturalH * scale);
      const dw = Math.max(6, Math.round(dh * (naturalW / naturalH)));
      const parallax = Math.max(0, Math.min(1, tree.parallax ?? 0.6));
      const worldX = cameraX + (tree.baseX - cameraX) * parallax;
      const screenX = worldX - cameraX;
      if (screenX + dw < -320 || screenX > w + 320) continue;
      const b = backgroundTreeBounds[tree.imgIndex | 0] || { bottom: 0 };
      const scaleY = dh / naturalH;
      const yAdjust = Math.round((b.bottom || 0) * scaleY);
      const sink = tree.sink ?? 0;
      const lift = tree.lift ?? 0;
      const drawX = Math.round(worldX - dw / 2);
      const drawY = Math.round(groundY - dh + yAdjust + sink - lift);
      const alpha = Math.max(0.25, Math.min(0.95, tree.alpha ?? 0.7));
      ctx.save();
      ctx.globalAlpha = alpha;
      // Add atmospheric perspective tint (subtle blue/purple for distance)
      ctx.filter = 'brightness(0.9) saturate(0.7)';
      ctx.drawImage(img, drawX, drawY, dw, dh);
      ctx.filter = 'none';
      ctx.restore();
    }
  }
  ctx.globalAlpha = 1;
}

function drawTrees(ctx, w, h, groundH) {
  const groundY = h - groundH;
  ensureChunksInView(cameraX, w);
  for (const [, arr] of treesByChunk) {
    for (const t of arr) {
      if (t.felled) continue; // hide felled trees (replaced by particles)
      if (t.x < cameraX - 80 || t.x > cameraX + w + 80) continue; // cull offscreen
      drawTree(ctx, t, groundY);
    }
  }
}

function getTreeDrawMetrics(t, groundY) {
  const img = treeImgs[t.img | 0];
  if (!img || !img.naturalHeight || !img.naturalWidth) return null;
  const s = Math.max(0.5, t.scale || 1);
  const isOrange = t.kind === 'orange';
  const isOak = t.kind === 'oak';
  const targetH = (isOak ? 160 : isOrange ? 124 : 110) * s; // special trees stand taller
  const dh = Math.round(targetH);
  const aspect = img.naturalWidth / img.naturalHeight;
  const dw = Math.max(10, Math.round(dh * aspect));
  const b = treeImgBounds[t.img | 0] || { bottom: 0, top: 0 };
  const scaleY = dh / (img.naturalHeight || 1);
  const yAdjust = Math.round((b.bottom || 0) * scaleY);
  const drawX = Math.round(t.x - dw / 2);
  const drawY = Math.round(groundY - dh + yAdjust + TREE_GROUND_SINK);
  const topVisibleY = drawY + Math.round((b.top || 0) * scaleY);
  return { img, dw, dh, drawX, drawY, topVisibleY };
}

function drawTree(ctx, t, groundY) {
  const metrics = getTreeDrawMetrics(t, groundY);
  if (!metrics) return;
  const { img, dw, dh, drawX: x, drawY: y } = metrics;
  if (typeof t.maxHp !== 'number' || t.maxHp <= 0) t.maxHp = 1;
  if (typeof t.hp !== 'number' || t.hp > t.maxHp) t.hp = t.maxHp;
  ctx.drawImage(img, x, y, dw, dh);
  if (t.kind === 'oak' && t.hitFlash && OAK_TREE_FADE_INDEX >= 0) {
    const fadeImg = treeImgs[OAK_TREE_FADE_INDEX];
    if (fadeImg && fadeImg.naturalHeight) {
      ctx.save();
      ctx.globalAlpha = Math.max(0.15, Math.min(0.85, t.hitFlash * 3.5));
      ctx.drawImage(fadeImg, x, y, dw, dh);
      ctx.restore();
    }
  }
  // Cache draw dimensions for hit tests
  t._dw = dw; t._dh = dh;
  if (t.maxHp > 1 && t.hp >= 0 && t.hp < t.maxHp) {
    const barW = Math.max(28, Math.round(dw * 0.45));
    const barH = 4;
    const barX = Math.round(t.x - barW / 2);
    const barY = Math.round(y - 12);
    const pct = Math.max(0, Math.min(1, t.hp / t.maxHp));
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    ctx.fillRect(barX - 1, barY - 1, barW + 2, barH + 2);
    ctx.fillStyle = '#4b2a18';
    ctx.fillRect(barX, barY, barW, barH);
    ctx.fillStyle = '#f3c969';
    ctx.fillRect(barX, barY, Math.round(barW * pct), barH);
  }
}

function drawTriangle(ctx, cx, topY, width, height) {
  ctx.beginPath();
  ctx.moveTo(cx, topY);
  ctx.lineTo(cx - width / 2, topY + height);
  ctx.lineTo(cx + width / 2, topY + height);
  ctx.closePath();
  ctx.fill();
}

function drawCircle(ctx, cx, cy, r) {
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
}

function drawAxe(ctx, w, h, groundH) {
  if (!axeReady || !axe.spawned || axe.picked) return;
  // Target on-screen width in logical pixels; maintains image aspect ratio
  const targetW = 42;
  const aspect = axeImg.naturalWidth && axeImg.naturalHeight ? (axeImg.naturalWidth / axeImg.naturalHeight) : 1;
  const dw = targetW;
  const dh = Math.max(1, Math.round(targetW / aspect));
  const groundTop = h - groundH;
  const fade = Math.max(0, Math.min(1, axe.spawnTime / 0.75));
  if (fade <= 0.01) return;
  const bob = Math.sin(axe.floatPhase * 2.4) * 3 - 3;
  const x = Math.round(axe.x - dw / 2);
  const y = Math.round(groundTop - dh + bob);
  // Frustum cull for a bit of efficiency
  if (x + dw < cameraX - 80 || x > cameraX + w + 80) return;
  ctx.save();
  ctx.globalAlpha = fade;
  ctx.shadowColor = 'rgba(150,200,255,0.45)';
  ctx.shadowBlur = 16 * fade;
  ctx.drawImage(axeImg, x, y, dw, dh);
  ctx.restore();
}

function drawHat(ctx, w, h, groundH) {
  if (!hatReady) return;
  const targetW = 36;
  const aspect = hatImg.naturalWidth && hatImg.naturalHeight ? (hatImg.naturalWidth / hatImg.naturalHeight) : 1;
  const dw = targetW;
  const dh = Math.max(1, Math.round(targetW / aspect));
  const groundTop = h - groundH;
  const fade = Math.max(0, Math.min(1, (hat.spawnTime || 0) / 0.75));
  if (fade <= 0.01) return;
  const bob = Math.sin((hat.floatPhase || 0) * 2.4) * 3 - 3;
  const x = Math.round(hat.x - dw / 2);
  const y = Math.round(groundTop - dh + bob);
  if (x + dw < cameraX - 80 || x > cameraX + w + 80) return;
  ctx.save();
  ctx.globalAlpha = fade;
  // EDIT HELICOHAT FACING HERE - Add/remove mirroring to flip direction
  // To mirror: uncomment the translate and scale lines below, and change drawImage to use -dw/2, -dh/2 offsets
  ctx.shadowColor = 'rgba(150,200,255,0.35)';
  ctx.shadowBlur = 12 * fade;
  // ctx.translate(x + dw / 2, y + dh / 2);
  // ctx.scale(-1, 1);
  ctx.drawImage(hatImg, x, y, dw, dh); // No mirror version: (x, y, dw, dh) | Mirror version: (-dw/2, -dh/2, dw, dh)
  ctx.restore();
}

function drawHeldAxe(ctx) {
  if (!axeReady) return;
  const targetW = 32; // slightly larger when equipped
  const aspect = axeImg.naturalWidth && axeImg.naturalHeight ? (axeImg.naturalWidth / axeImg.naturalHeight) : 1;
  const dw = targetW;
  const dh = Math.max(1, Math.round(targetW / aspect));
  // Pull the axe slightly into the body (overlap) and a bit higher.
  // Use a single horizontal control; bake the previous right-side nudge into offsetX.
  const inward = dw * 0.20;
  const rightNudge = dw * 0.50; // was applied later as sideAdjust; consolidated here
  const offsetX = (player.facing === 1)
    ? (player.w - inward - rightNudge)
    : (-dw + inward);
  // Position slightly higher on the body
  const offsetY = player.h * 0.02;
  const px = player.x + offsetX;
  const py = player.y + offsetY;
  // subtle swing when holding Interact
  const swinging = axe.equipped && input.interactHeld;
  const time = performance.now ? performance.now() : Date.now();
  const swingAng = swinging ? (Math.sin(time / 110) * (Math.PI / 14)) : 0; // ~±12.8°
  // rotate around a pivot near the handle
  const handleOX = dw * 0.25;
  const handleOY = dh * 0.85;
  ctx.save();
  // Flip when facing RIGHT (asset default faces left)
  const flip = (player.facing === 1);
  const pivotOX = flip ? (dw - handleOX) : handleOX;
  ctx.translate(Math.round(px + pivotOX), Math.round(py + handleOY));
  // Compute angle so left-facing chop is explicitly inverted relative to right
  const angle = (player.facing === -1) ? -(swingAng + axeSpin) : (swingAng + axeSpin);
  // Important: rotate BEFORE scale so horizontal flip doesn't invert rotation sign
  ctx.rotate(angle);
  if (flip) ctx.scale(-1, 1);
  ctx.drawImage(axeImg, -pivotOX, -handleOY, dw, dh);
  ctx.restore();
}

function drawHeldHat(ctx) {
  if (!hatReady) return;
  const targetW = 30; // larger when equipped for better visibility
  const aspect = hatImg.naturalWidth && hatImg.naturalHeight ? (hatImg.naturalWidth / hatImg.naturalHeight) : 1;
  const dw = targetW;
  const dh = Math.max(1, Math.round(targetW / aspect));
  const px = Math.round(player.x + player.w / 2);
  // Align hat to the visible top of the character sprite (accounts for larger sprite size)
  let py;
  try {
    // Choose base sprite: side-only when flag is on
    const sprites = Character.sprites;
    const movingHoriz = Math.abs(player.vx) > 10;
    const useSide = SIDE_ONLY || (player.state === 'walk' || (player.state === 'jump' && movingHoriz));
    const baseImg = useSide ? sprites.side : sprites.front;
    if (baseImg && baseImg.naturalWidth) {
      const bounds = Character.getOpaqueBounds ? Character.getOpaqueBounds(baseImg) : null;
      const scale = 1.8;
      const scalePxY = (player.h * scale) / baseImg.naturalHeight;
      const opaqueBottom = bounds ? (bounds.top + bounds.height) : baseImg.naturalHeight;
      const spriteTopY = player.y + player.h - opaqueBottom * scalePxY;
      // Place hat so its bottom sits just above the head top (slight embed for cohesion)
      const bottomOffset = HAT_Y_OFFSET; // tweak with HAT_Y_OFFSET
      py = Math.round(spriteTopY - dh + bottomOffset + (player.state === 'walk' ? -1 : 0));
    } else {
      // Fallback
      py = Math.round(player.y - dh + 3 + HAT_Y_OFFSET + (player.state === 'walk' ? -1 : 0));
    }
  } catch {
    py = Math.round(player.y - dh + 3 + HAT_Y_OFFSET + (player.state === 'walk' ? -1 : 0));
  }
  ctx.save();
  ctx.translate(px, py + dh / 2);
  ctx.rotate(hatSpin);
  ctx.drawImage(hatImg, -dw / 2, -dh / 2, dw, dh);
  ctx.restore();
}

// --- Pickaxe item ---
const SPAWN_PICKAXE = false; // Pickaxe only spawns in cave as reward
const PICKAXE_SPAWN_X = 500; // keep same spawn position
const pickaxe = { x: PICKAXE_SPAWN_X, picked: false, equipped: false, spawnTime: 0, floatPhase: Math.random() * Math.PI * 2 };
const pickaxeImg = new Image();
let pickaxeReady = false;
pickaxeImg.onload = () => { pickaxeReady = true; };
pickaxeImg.src = 'img_assets/pickaxe.png';
let pickaxeOverlap = false;
let pickaxeDigAnim = 0;
let pickaxeDigMode = 'idle'; // 'idle' | 'down' | 'horizontal' | 'up'
let pickaxeDigTimer = 0;

// --- Sword item ---
const SPAWN_SWORD = false;
const SWORD_SPAWN_X = 760; // a bit after the jetback
const sword = { x: SWORD_SPAWN_X, picked: false, equipped: false, spawnTime: 0, floatPhase: Math.random() * Math.PI * 2 };
const swordImg = new Image();
let swordReady = false;
swordImg.onload = () => { swordReady = true; };
swordImg.src = 'img_assets/sword.png';
let swordOverlap = false;
// sword swing animation
const SWORD_SWING_DUR = 0.22; // seconds per swing
const SWORD_DASH_SPEED = 550; // horizontal dash speed (increased for more distance)
const SWORD_DASH_DUR = 0.18; // dash lasts for most of swing
let swordSwingT = 0; // 0..1 progress
let swordSwingActive = false;
let swordSwingHit = false; // guard to trigger one impact per swing
let swordDashActive = false; // dash velocity boost active
let swordSlashArc = null; // visual slash arc effect { startT, facing }

// --- Raygun item ---
const SPAWN_RAYGUN = false;
const RAYGUN_SPAWN_X = 1040; // after DNP
const raygun = { x: RAYGUN_SPAWN_X, picked: false, equipped: false, spawnTime: 0, floatPhase: Math.random() * Math.PI * 2 };
const raygunImg = new Image();
let raygunReady = false;
raygunImg.onload = () => { raygunReady = true; };
raygunImg.src = 'img_assets/raygun.png';
let raygunOverlap = false;
// Raygun blasts
let blasts = []; // { x,y,vx,life,dur,r }
const RAYGUN_SPEED = 900; // px/s
const RAYGUN_COOLDOWN = 0.18; // s between shots
let raygunCooldown = 0;

function triggerApocalypse() {
  if (apocalypseActive) return;
  apocalypseActive = true;
  // Stop spawning spiders during apocalypse
  caveSpawns = caveSpawns.filter(s => s?.type !== 'spider');
  gameOverActive = false;
  gameOverBtnRect = null;
  gameOverPending = false;
  gameOverBurnT = 0;
  stopAllMusic();
  stopAllAmbianceImmediately();
  try {
    const undergroundMusicAudio = audioManager.namedAudio.get('undergroundMusic')?.audio;
    if (undergroundMusicAudio) {
      undergroundMusicAudio.pause();
      undergroundMusicAudio.currentTime = 0;
      isUndergroundMusicActive = false;
    }
  } catch {}
  stopTitanMusic();
  musicMuted = false;
  audioManager.setMusicMuted(false);
  startTitanMusic();
  setAmbianceState('none');
  apocTitans = [];
  apocMeteors = [];
  apocFires = [];
  apocEmbers = [];
  apocAshes = [];
  apocBurnedEntities.clear();
  audioManager.musicWasPlaying = false;
  apocalypseSky = 0;
  apocRumbleStarted = false;
  apocHitCooldown = 0;
  apocTitanTimer = 0;
  apocTitanNext = 7; // initial delay before first titan appears on the right
}

function respawnFromApocalypse() {
  apocalypseActive = false;
  gameOverActive = false;
  gameOverBtnRect = null;
  gameOverPending = false;
  gameOverBurnT = 0;
  apocTitans = [];
  apocMeteors = [];
  apocFires = [];
  apocEmbers = [];
  apocAshes = [];
  apocBurnedEntities.clear();
  apocalypseSky = 0;
  apocRumbleStarted = false;
  apocHitCooldown = 0;
  forestShrine.burning = false;
  forestShrine.overlap = false;
  forestShrine.interactRadius = 100;
  wipNpc.burning = false;
  wipNpc.overlap = false;
  wipNpc.talkRadius = 100;
  stopTitanMusic();
  musicMuted = false;
  audioManager.setMusicMuted(false);
  try { audioManager.startBackgroundMusic(); } catch {}
  // Ambiance will be set by main loop based on player position
  const gx = deepForestStartX() + 40;
  const groundTop = world.height() - world.groundH;
  player.x = gx;
  player.y = groundTop - player.h;
  player.vx = 0; player.vy = 0; player.onGround = true;
  cameraX = Math.max(0, player.x - world.width() * 0.4);
  cameraY = 0;
  skyRenderer.setCloudCamY(cameraY);
}

function apocSpawnTitan(w, h, offset = 0) {
  if (!titangiantReady || !titangiantImg.naturalWidth) return;
  const targetH = 520; // Bigger titans (was 420)
  const aspect = titangiantImg.naturalWidth / titangiantImg.naturalHeight;
  const dw = targetH * aspect;
  const groundTop = h - world.groundH;
  const scaleY = titangiantImg.naturalHeight ? targetH / titangiantImg.naturalHeight : 1;
  const scaleX = titangiantImg.naturalWidth ? dw / titangiantImg.naturalWidth : 1;
  const bottomOffset = titangiantBounds ? (titangiantBounds.bottom || 0) * scaleY : 0;
  // Calculate opaque bounds for tighter hitbox
  const boundsLeft = titangiantBounds ? (titangiantBounds.left || 0) * scaleX : 0;
  const boundsRight = titangiantBounds ? (titangiantBounds.right || 0) * scaleX : 0;
  const boundsTop = titangiantBounds ? (titangiantBounds.top || 0) * scaleY : 0;
  const boundsWidth = dw - boundsLeft - boundsRight;
  const boundsHeight = targetH - boundsTop - bottomOffset;
  const startX = cameraX + w + 200 + offset;
  const TITAN_GROUND_SINK = 20; // Additional sink into ground for proper contact
  const titan = {
    x: startX,
    y: groundTop - targetH + bottomOffset + TITAN_GROUND_SINK, // Lowered titans closer to ground
    w: dw,
    h: targetH,
    // Opaque bounds for collision
    boundsLeft,
    boundsTop,
    boundsWidth,
    boundsHeight,
    speed: 60, // Faster march to raise urgency
    // Animation state
    frame: Math.floor(Math.random() * TITAN_FRAME_COUNT), // Start on random frame for variety
    frameTimer: Math.random() * TITAN_FRAME_DURATION, // Random offset in cycle
    bobOffset: 0 // Stepped bob offset (calculated per frame)
  };
  apocTitans.push(titan);
  // DISABLED: Cave rumble during apocalypse
  // try {
  //   if (!apocRumbleStarted) {
  //     const rumble = caveRumble.ensure();
  //     if (rumble) {
  //       const p = rumble.play?.();
  //       setAudioBaseVolume(rumble, 0.16, AUDIO_KIND_SFX);
  //       if (p && typeof p.catch === 'function') p.catch(()=>{});
  //     }
  //     apocRumbleStarted = true;
  //   }
  // } catch {}
}

function apocSpawnMeteor(w, h) {
  const spawnX = cameraX + (Math.random() * (w + 600)) - 300;
  const spawnY = cameraY - 400 - Math.random() * 200;
  const speed = 280 + Math.random() * 220;
  const angle = (Math.random() * 0.4) - 0.2;
  const vx = speed * Math.sin(angle);
  const vy = speed * Math.cos(angle);
  const r = 18 + Math.random() * 20;
  apocMeteors.push({ x: spawnX, y: spawnY, vx, vy, r, life: 0, dur: 6, landed: false, landY: 0 });
}

function apocAddFire(x, groundTop) {
  const r = 36 + Math.random() * 24;
  apocFires.push({ x, y: groundTop - r * 0.2, r, life: 0, dur: 10 + Math.random() * 6 });

  // Spawn initial burst of embers
  for (let i = 0; i < 20; i++) {
    apocSpawnEmber(x + (Math.random() - 0.5) * r * 2, groundTop);
  }
}

function apocSpawnEmber(x, y) {
  const vx = (Math.random() - 0.5) * 60; // Horizontal drift
  const vy = -(80 + Math.random() * 120); // Rise speed
  const size = 3 + Math.random() * 5; // 3-8px
  const dur = 1.5 + Math.random() * 2.5; // 1.5-4 seconds lifetime
  apocEmbers.push({ x, y, vx, vy, life: 0, dur, size, alpha: 1 });
}

function apocBurnTrees(rect) {
  for (const [key, arr] of treesByChunk) {
    const filtered = arr.filter(t => {
      const tw = 30;
      const th = (t?.scale || 1) * 120;
      const tx = (t?.x || 0) - tw / 2;
      const ty = (world.height() - world.groundH) - th;
      const overlaps = !(tx + tw < rect.x || rect.x + rect.w < tx || ty + th < rect.y || rect.y + rect.h < ty);
      if (overlaps) particles.spawnSparks(tx + tw / 2, ty + th / 2, 18, ['#ffca7a', '#ff9f4d', '#f76b1c']);
      return !overlaps;
    });
    treesByChunk.set(key, filtered);
  }
}

function apocHandlePlayerHit(fromTitan) {
  if (gameOverPending || gameOverActive) return;
  // Check hit cooldown to prevent rapid successive hits
  if (apocHitCooldown > 0) return;

  // Set cooldown: longer for titans (2s), shorter for meteors/fires (0.8s)
  apocHitCooldown = fromTitan ? 2.0 : 0.8;

  if (fromTitan) {
    // Instant death on titan contact - explode into fire and turn to ash
    playerHitFlash = PLAYER_HIT_FLASH_DUR;
    player.vx = 0; // No knockback - just die in place
    player.vy = 0;
    player.onGround = true;
    gameOverPending = true;
    gameOverBurnT = GAME_OVER_BURN_DUR;
    gameOverActive = false;
    gameOverBtnRect = null;
    // Large fire explosion - radial burst
    particles.spawnSparks(player.x + player.w / 2, player.y + player.h / 2, 30, ['#ffca7a', '#ff9f4d', '#f76b1c', '#ff6b1c']);
    // Additional upward fire burst
    for (let i = 0; i < 15; i++) {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.8;
      const speed = 100 + Math.random() * 150;
      const vx = Math.cos(angle) * speed;
      const vy = Math.sin(angle) * speed;
      particles.sparks.push({
        x: player.x + player.w / 2,
        y: player.y + player.h / 2,
        vx,
        vy,
        life: 0,
        dur: 0.6 + Math.random() * 0.4,
        size: 1 + Math.random() * 2,
        color: ['#ffca7a', '#ff9f4d', '#f76b1c'][Math.floor(Math.random() * 3)]
      });
    }
  } else {
    // Meteor/fire hit: Knockback only (no death)
    playerHitFlash = PLAYER_HIT_FLASH_DUR;
    const dir = Math.random() < 0.5 ? -1 : 1;
    player.vx = dir * 260;
    player.vy = -320;
    player.onGround = false;
  }
}

function updateApocalypse(dt, w, h) {
  if (!apocalypseActive) return;
  apocalypseSky = Math.min(1, apocalypseSky + dt * 0.15);
  // Update hit cooldown
  if (apocHitCooldown > 0) apocHitCooldown = Math.max(0, apocHitCooldown - dt);
  apocTitanTimer += dt;
  if (apocTitanTimer >= apocTitanNext) {
    apocTitanTimer = 0;
    apocTitanNext = Infinity; // no subsequent waves; initial wall only
    const spacing = 140;
    const count = Math.ceil((w + 1400) / spacing); // denser wall
    for (let i = 0; i < count; i++) {
      apocSpawnTitan(w, h, i * spacing);
    }
    // add trailing titans behind the wall to suggest endlessness
    const trailCount = 14;
    const trailSpacing = 120;
    for (let i = 0; i < trailCount; i++) {
      apocSpawnTitan(w, h, count * spacing + i * trailSpacing);
    }
  }
  // Intensify meteor frequency over time
  const meteorChance = Math.min(0.6, 0.12 + apocTitans.length * 0.04);
  if (Math.random() < meteorChance * dt) apocSpawnMeteor(w, h);

  const groundTop = h - world.groundH;
  const playerRect = { x: player.x, y: player.y, w: player.w, h: player.h };

  // Update titans
  for (const t of apocTitans) {
    // Update walk animation frame
    t.frameTimer += dt;
    if (t.frameTimer >= TITAN_FRAME_DURATION) {
      t.frameTimer -= TITAN_FRAME_DURATION;
      t.frame = (t.frame + 1) % TITAN_FRAME_COUNT;
    }

    // Only move during step frames (1 and 2), not idle frames (0 and 3)
    // This creates jagged movement that matches the animation
    if (t.frame === 1 || t.frame === 2) {
      t.x -= t.speed * dt;
    }
    // Stepped bob: sync with frame changes for jagged walk feel
    // Frames 0 and 3 are idle (titan-1 and titan-4 are identical), frames 1 and 2 are steps
    // Create discrete bob values for each frame instead of smooth sine
    const frameBobValues = [0, -4, -4, 0]; // Down on step frames (1,2), neutral on idle frames (0,3)
    const currentBob = frameBobValues[t.frame];
    const nextBob = frameBobValues[(t.frame + 1) % TITAN_FRAME_COUNT];
    // Interpolate between current and next frame's bob for slight smoothing
    const frameProgress = t.frameTimer / TITAN_FRAME_DURATION;
    t.bobOffset = currentBob + (nextBob - currentBob) * frameProgress;
    // Use opaque bounds for tighter, more accurate hitbox
    const bx = t.x - t.w / 2; // Base X position
    const by = t.y; // Base Y position
    const hitboxX = bx + t.boundsLeft;
    const hitboxY = by + t.boundsTop;
    const hitboxW = t.boundsWidth;
    const hitboxH = t.boundsHeight;
    const rect = { x: hitboxX, y: hitboxY, w: hitboxW, h: hitboxH };
    if (!(rect.x + rect.w < playerRect.x || rect.x > playerRect.x + playerRect.w || rect.y + rect.h < playerRect.y || rect.y > playerRect.y + playerRect.h)) {
      apocHandlePlayerHit(true);
      return;
    }
    // Use full rect for burning trees (more forgiving/dramatic)
    const fullRect = { x: t.x - t.w / 2, y: t.y, w: t.w, h: t.h };
    apocBurnTrees(fullRect);
    // burn NPCs on contact
    const burnNpcRect = (r) => !(r.x + r.w < rect.x || r.x > rect.x + rect.w || r.y + r.h < rect.y || r.y > rect.y + rect.h);
    const igniteEntity = (r, tag, storeFn) => {
      if (!burnNpcRect(r) || (tag && apocBurnedEntities.has(tag))) return false;
      if (tag) apocBurnedEntities.add(tag);
      const cx = r.x + r.w / 2;
      const cy = r.y + r.h / 2;
      apocAddFire(cx, groundTop);
      apocAshes.push({ x: cx, y: groundTop });
      particles.spawnSparks(cx, cy, 16, ['#ffca7a', '#f76b1c']);
      if (typeof storeFn === 'function') storeFn();
      return true;
    };
    const groundTop = h - world.groundH;
    if (goblin && goblin.spawned) {
      const g = getGoblinRect(h);
      igniteEntity(g, 'goblin', () => {
        goblin.spawned = false;
        goblin.overlap = false;
        apocAshes.push({ x: g.x + g.w / 2, y: groundTop });
      });
    }
    if (goblinFamilySpawned) {
      const wRect = getGoblinWifeRect(h);
      const sRect = getGoblinSonRect(h);
      igniteEntity(wRect, 'goblin_wife', () => { goblinFamilySpawned = false; apocAshes.push({ x: wRect.x + wRect.w / 2, y: groundTop }); });
      igniteEntity(sRect, 'goblin_son', () => { goblinFamilySpawned = false; apocAshes.push({ x: sRect.x + sRect.w / 2, y: groundTop }); });
    }
    if (!gameOverActive && forestShrineReady && forestShrine.h > 0) {
      const fsH = forestShrine.h;
      const fsW = forestShrine.w;
      const fsRect = { x: forestShrine.x - fsW / 2, y: groundTop - fsH, w: fsW, h: fsH };
      igniteEntity(fsRect, 'forest_shrine', () => {
        forestShrine.burning = true;
        forestShrine.overlap = false;
        forestShrine.interactRadius = 0;
        shrineCloud.spawned = false;
        apocAshes.push({ x: forestShrine.x, y: groundTop });
      });
    }
    if (wipNpcReady && wipNpc.h > 0) {
      const wRect = { x: wipNpc.x - wipNpc.w / 2, y: groundTop - wipNpc.h + (wipNpc.yOffset || 0), w: wipNpc.w, h: wipNpc.h };
      igniteEntity(wRect, 'wip_npc', () => {
        wipNpc.burning = true;
        wipNpc.overlap = false;
        wipNpc.talkRadius = 0;
        apocAshes.push({ x: wRect.x + wRect.w / 2, y: groundTop });
      });
    }
  }
  apocTitans = apocTitans.filter(t => t.x + t.w > cameraX - 800);

  // Update meteors
  for (const m of apocMeteors) {
    if (!m.landed) {
      m.life += dt;
      m.vy += 600 * dt;
      m.x += m.vx * dt;
      m.y += m.vy * dt;
      if (m.y + m.r >= groundTop) {
        m.landed = true;
        m.landY = groundTop;
        apocAddFire(m.x, groundTop);
        apocBurnTrees({ x: m.x - m.r, y: groundTop - m.r, w: m.r * 2, h: m.r });
        particles.spawnSparks(m.x, groundTop, 16, ['#ffb46b', '#ffdf8a', '#f5601a']);
      }
    }
  }
  apocMeteors = apocMeteors.filter(m => m.life < m.dur && m.x > cameraX - 800 && m.x < cameraX + w + 800);

  // Update fires and spawn embers
  for (const f of apocFires) {
    f.life += dt;
    // Continuously spawn embers from active fires
    if (Math.random() < 0.8 * dt) { // ~80% chance per second
      apocSpawnEmber(f.x + (Math.random() - 0.5) * f.r, groundTop);
    }
  }
  apocFires = apocFires.filter(f => f.life < f.dur);

  // Update embers
  for (const e of apocEmbers) {
    e.life += dt;
    e.x += e.vx * dt;
    e.y += e.vy * dt;
    e.vy += 20 * dt; // Slight upward deceleration (buoyancy)
    e.alpha = Math.max(0, 1 - (e.life / e.dur)); // Fade out over lifetime
  }
  apocEmbers = apocEmbers.filter(e => e.life < e.dur && e.alpha > 0.01);

  // Player hits with meteors/fires
  for (const m of apocMeteors) {
    if (m.landed) continue;
    const dx = (m.x - (player.x + player.w / 2));
    const dy = (m.y - (player.y + player.h / 2));
    const dist2 = dx * dx + dy * dy;
    // Tighter hitbox: use smaller collision radius (meteor radius + small player margin)
    const collisionRadius = m.r * 0.7 + 8; // Reduced from m.r + player.dimension*0.5
    if (dist2 < collisionRadius ** 2) {
      apocHandlePlayerHit(false);
      break;
    }
  }
  for (const f of apocFires) {
    const dx = Math.abs(f.x - (player.x + player.w / 2));
    const dy = Math.abs((groundTop - f.r * 0.5) - (player.y + player.h / 2));
    if (dx < f.r && dy < f.r) {
      apocHandlePlayerHit(false);
      break;
    }
  }
  // Screen shake based on nearest titan
  let minDist = Infinity;
  for (const t of apocTitans) {
    const dx = Math.abs((t.x - t.w * 0.5) - player.x);
    minDist = Math.min(minDist, dx);
  }
  const shakeIntensity = Math.max(0, 1 - Math.min(minDist, 600) / 600) * 8;
  apocShakeX = (Math.random() - 0.5) * shakeIntensity;
  apocShakeY = (Math.random() - 0.5) * shakeIntensity * 0.6;
}

// --- Do Not Press button (fun interactive prop) ---
const SHOW_DNP_BUTTON = true;
const DNP_SPAWN_X = WIP_NPC_SPAWN_X + 400; // Much further after the WIP NPC/banner
const dnpBtn = { x: DNP_SPAWN_X };
const dnpImg = new Image();
let dnpReady = false;
dnpImg.onload = () => { dnpReady = true; };
dnpImg.src = 'img_assets/donotpressbutton.png';
let dnpOverlap = false;
let buttonPressSfx = null;
try {
  buttonPressSfx = new Audio('sfx_assets/button_press.mp3');
  buttonPressSfx.preload = 'auto';
  setAudioBaseVolume(buttonPressSfx, VOLUME_BASES.button, AUDIO_KIND_SFX);
} catch {}
let apocalypseActive = false;
let gameOverActive = false;
let gameOverBtnRect = null;
let gameOverPending = false;
let gameOverBurnT = 0;
const GAME_OVER_BURN_DUR = 1.3;
let titanMusic1 = null;
let titanMusic2 = null;
let titanMusicPlaying = false;
// Titan walk animation (4 frames: 1 and 4 are identical for pause)
const titanFrames = [];
const titanBounds = [];
let titanFramesReady = 0;
const TITAN_FRAME_COUNT = 4;
const TITAN_FRAME_DURATION = 0.7; // seconds per frame (2.8s total cycle - very slow giant walk)
for (let i = 1; i <= TITAN_FRAME_COUNT; i++) {
  const img = new Image();
  img.onload = () => {
    titanBounds[i - 1] = computeImageOpaqueBounds(img);
    titanFramesReady++;
  };
  img.src = `img_assets/titananimation/titan-${i}.png`;
  titanFrames.push(img);
}
// Legacy single image reference for compatibility
const titangiantImg = titanFrames[0];
let titangiantReady = false;
let titangiantBounds = null;
// Ready when all frames loaded
const checkTitanReady = setInterval(() => {
  if (titanFramesReady >= TITAN_FRAME_COUNT) {
    titangiantReady = true;
    titangiantBounds = titanBounds[0]; // Use first frame bounds for spawn calculations
    clearInterval(checkTitanReady);
  }
}, 50);
const meteorImg = new Image();
let meteorReady = false;
meteorImg.onload = () => { meteorReady = true; };
meteorImg.src = 'img_assets/meteor.png';
const meteorGroundImg = new Image();
let meteorGroundReady = false;
meteorGroundImg.onload = () => { meteorGroundReady = true; };
meteorGroundImg.src = 'img_assets/meteor_ground.png';
let apocTitans = []; // { x,y,w,h,speed }
let apocTitanTimer = 0;
let apocTitanNext = 6;
let apocMeteors = []; // { x,y,vx,vy,r,life,dur,landed }
let apocFires = []; // { x,y,r,life,dur }
let apocEmbers = []; // { x,y,vx,vy,life,dur,size,alpha } - Rising embers from fires
let apocBurnedEntities = new Set(); // track entities already ignited
let apocAshes = []; // { x, y }
let apocalypseSky = 0; // 0..1 tint
let apocShakeX = 0;
let apocShakeY = 0;
let apocRumbleStarted = false;
let apocHitCooldown = 0; // Cooldown timer to prevent rapid successive hits

// --- Enemies: Spiders, Titans, and Treemonsters ---
const spiderImg = new Image();
let spiderReady = false; let spiderBounds = null;
spiderImg.onload = () => { spiderReady = true; spiderBounds = computeImageOpaqueBounds(spiderImg); };
spiderImg.src = 'img_assets/spider.png';

// Old enemy titans removed - only apocalypse titans exist now

const treeMonsterImg = new Image();
let treeMonsterReady = false; let treeMonsterBounds = null;
treeMonsterImg.onload = () => { treeMonsterReady = true; treeMonsterBounds = computeImageOpaqueBounds(treeMonsterImg); };
treeMonsterImg.src = 'img_assets/treemonster.png';

// --- Corrupt Titan animation tests (three blending variants) ---
const ENABLE_CORRUPT_TITAN_TESTS = false; // dev toggle
const corruptTitanFramePaths = [
  'animation_assets/corrupttitanstand/corruptitan_01.png',
  'animation_assets/corrupttitanstand/corruptitan_03.png',
  'animation_assets/corrupttitanstand/corruptitan_06.png'
];
const corruptTitanFrames = [];
const corruptTitanFrameBounds = new Array(corruptTitanFramePaths.length).fill(null);
const corruptTitanFrameMeta = new Array(corruptTitanFramePaths.length).fill(null);
let corruptTitanFrameReadyCount = 0;
let corruptTitanFramesLoaded = false;
let corruptTitanFootBaseline = 0;
let corruptTitanTests = [];
let corruptTitanTestsReady = false;
let corruptTitanGuard = null;
let corruptTitanGuardReady = false;
const CORRUPT_TITAN_INTRO_FRAME_INDEX = 0; // first frame (corruptitan_01) shown before the titan awakens
const CORRUPT_TITAN_IDLE_FRAME_INDEX = corruptTitanFramePaths.length - 1; // hold on the final frame once intro finishes
const CORRUPT_TITAN_FINAL_FRAME_INDEX = CORRUPT_TITAN_IDLE_FRAME_INDEX;
const CORRUPT_TITAN_TEST_TARGET_H = 220;
const CORRUPT_TITAN_TEST_SPACING = 240;
const CORRUPT_TITAN_BASE_OFFSET = 220; // distance in front of the cave mouth
const CORRUPT_TITAN_FRAME_DUR = 0.25;  // per-frame duration during wake-up
const CORRUPT_TITAN_IDLE_HOLD = 0.35;   // pause before settling into idle pose
const CORRUPT_TITAN_CROSSFADE_WINDOW = 0.35;
const CORRUPT_TITAN_AFTERIMAGE_LIFE = 0.6;
const CORRUPT_TITAN_AFTERIMAGE_ALPHA = 0.32;
const CORRUPT_TITAN_BLUR_PX = 3;
const CORRUPT_TITAN_LAST_FRAME_GROUND_OFFSET = 6;
const CORRUPT_TITAN_ATTACK_MIN_DELAY = 4.5;
const CORRUPT_TITAN_ATTACK_MAX_DELAY = 7.8;

// --- Corrupt titan gameplay tuning (easily tweakable) ---
const CORRUPT_TITAN_MAX_HP = 18;                // Axe hits required before the titan collapses
const CORRUPT_TITAN_AXE_DAMAGE = 1;             // Damage per axe swing (adjust to shorten/lengthen fight)
const CORRUPT_TITAN_SWORD_DAMAGE = 0.65;        // Damage dealt by the sword arc
const CORRUPT_TITAN_RAYGUN_DAMAGE = 0.35;       // Damage dealt per raygun blast hit
const CORRUPT_TITAN_BODY_BLOCK_INSET = 24;      // Pixels inside the titan's body that remain impassable while alive
const CORRUPT_TITAN_ATTACK_COOLDOWN = 2.0;      // Cooldown (s) shared by all attacks once one finishes

// Vine attack trigger zone (player standing in front of the titan)
const CORRUPT_TITAN_VINE_TRIGGER_DELAY = 1.0;   // Seconds the player must stand inside the trigger zone
const CORRUPT_TITAN_VINE_TRIGGER_RECT = {       // Rectangle relative to the titan body (see comments below)
  forwardOffset: 0,   // Distance in front of the titan (toward the player) where the zone starts
  width: 220,          // Width of the trigger zone extending away from the titan
  height: 210,         // Height above the ground included in the trigger zone
  groundPadding: 0     // How much to sink the trigger zone into the ground for leniency
};

// Vine attack timings & hitbox
const CORRUPT_TITAN_VINE_WINDUP = 1.0;          // Seconds spent glowing before the vines lash out
const CORRUPT_TITAN_VINE_ACTIVE = 0.6;          // Duration of the attack animation
const CORRUPT_TITAN_VINE_DAMAGE_WINDOW = { start: 0.18, end: 0.48 }; // Portion of the active window that deals damage
const CORRUPT_TITAN_VINE_DAMAGE_SOURCE = { minX: 0, maxX: 650, minY: 334, maxY: 985 }; // Source image pixels for hitbox (minX pulled back to include titan body)
const CORRUPT_TITAN_VINE_IMAGE_X_OFFSET = -20;     // Horizontal offset (pixels) to nudge the vine attack sprite left/right
const CORRUPT_TITAN_VINE_KNOCK = { x: 280, y: 420 }; // Knock-back applied on hit

// Slam attack trigger (player hugging the titan)
const CORRUPT_TITAN_SLAM_TRIGGER_DELAY = 0;
const CORRUPT_TITAN_SLAM_TRIGGER_RECT = null;
const CORRUPT_TITAN_SLAM_WINDUP = 0;
const CORRUPT_TITAN_SLAM_ACTIVE = 0;
const CORRUPT_TITAN_SLAM_DAMAGE_WINDOW = { start: 0, end: 0 };
const CORRUPT_TITAN_SLAM_DAMAGE_SOURCE = null;
const CORRUPT_TITAN_SLAM_KNOCK = { x: 0, y: 0 };

const CORRUPT_TITAN_GLOW_COLOR = '#b97cff';    // Purple glow colour used during attack windups
const CORRUPT_TITAN_TEST_MODES = [
  { mode: 'crossfade', label: 'Crossfade blend' },
  { mode: 'afterimage', label: 'Afterimage trail' },
  { mode: 'blur', label: 'Blurred blend' }
];
const CORRUPT_TITAN_GUARD_MODE = 'afterimage';
const CORRUPT_TITAN_GUARD_TARGET_H = 230;
const CORRUPT_TITAN_GUARD_AFTERIMAGE_CAP = 14;
const CORRUPT_TITAN_FINAL_SCALE = 1.20;
const CORRUPT_TITAN_TRIGGER_DISTANCE = 520;
let corruptTitanAttackImg = null;
let corruptTitanAttackBounds = null;
let corruptTitanAttackMeta = null;
let corruptTitanAttackReady = false;
let corruptTitanAttackSelfImg = null;
let corruptTitanAttackSelfReady = false;
let corruptTitanAttackSelfBounds = null;
let corruptTitanAttackSelfMeta = null;

function buildCorruptTitanAttackMeta(img, bounds, xOffset = 0) {
  if (!img || !img.naturalHeight || !bounds) return null;
  const baseline = corruptTitanFootBaseline || 0;
  const footOffset = (typeof bounds.bottomCenter === 'number'
    ? (bounds.bottomCenter - img.naturalWidth / 2)
    : 0) - baseline;
  return {
    img,
    bounds,
    footOffsetPx: footOffset + xOffset,
    groundOffset: 0
  };
}

function prepareCorruptTitanMeta() {
  const baseIdx = CORRUPT_TITAN_IDLE_FRAME_INDEX;
  const baseImg = corruptTitanFrames[baseIdx];
  const baseBounds = corruptTitanFrameBounds[baseIdx];
  if (!baseImg || !baseImg.naturalWidth || !baseBounds) return;
  const baseline = (typeof baseBounds.bottomCenter === 'number' ? (baseBounds.bottomCenter - baseImg.naturalWidth / 2) : 0);
  corruptTitanFootBaseline = baseline;
  for (let i = 0; i < corruptTitanFrames.length; i++) {
    const img = corruptTitanFrames[i];
    const bounds = corruptTitanFrameBounds[i];
    if (!img || !bounds || !img.naturalHeight) continue;
    const footOffset = (typeof bounds.bottomCenter === 'number' ? (bounds.bottomCenter - img.naturalWidth / 2) : 0) - baseline;
    corruptTitanFrameMeta[i] = {
      img,
      bounds,
      footOffsetPx: footOffset,
      groundOffset: (i === baseIdx ? CORRUPT_TITAN_LAST_FRAME_GROUND_OFFSET : 0)
    };
  }
  if (corruptTitanAttackReady && corruptTitanAttackBounds) {
    corruptTitanAttackMeta = buildCorruptTitanAttackMeta(corruptTitanAttackImg, corruptTitanAttackBounds, CORRUPT_TITAN_VINE_IMAGE_X_OFFSET);
  }
  if (corruptTitanAttackSelfReady && corruptTitanAttackSelfBounds) {
    corruptTitanAttackSelfMeta = buildCorruptTitanAttackMeta(corruptTitanAttackSelfImg, corruptTitanAttackSelfBounds);
  }
}

function markCorruptTitanFramesReady() {
  corruptTitanFrameReadyCount = Math.min(corruptTitanFramePaths.length, corruptTitanFrameReadyCount + 1);
  prepareCorruptTitanMeta();
  if (corruptTitanFrameReadyCount >= corruptTitanFramePaths.length) {
    corruptTitanFramesLoaded = true;
    corruptTitanTestsReady = false; // reinitialize with fresh bounds
  }
}

corruptTitanFramePaths.forEach((src, idx) => {
  const img = new Image();
  const onLoad = () => {
    corruptTitanFrameBounds[idx] = computeImageOpaqueBounds(img);
    markCorruptTitanFramesReady();
  };
  img.src = src;
  if (img.complete && img.naturalWidth) onLoad();
  else img.addEventListener('load', onLoad, { once: true });
  corruptTitanFrames.push(img);
});

corruptTitanAttackImg = new Image();
const onCorruptTitanAttackLoad = () => {
  corruptTitanAttackBounds = computeImageOpaqueBounds(corruptTitanAttackImg);
  corruptTitanAttackReady = true;
  prepareCorruptTitanMeta();
};
corruptTitanAttackImg.src = 'animation_assets/corrupttitanstand/vineattack.png';
if (corruptTitanAttackImg.complete && corruptTitanAttackImg.naturalWidth) onCorruptTitanAttackLoad();
else corruptTitanAttackImg.addEventListener('load', onCorruptTitanAttackLoad, { once: true });

corruptTitanAttackSelfImg = new Image();
const onCorruptTitanAttackSelfLoad = () => {
  corruptTitanAttackSelfBounds = computeImageOpaqueBounds(corruptTitanAttackSelfImg);
  corruptTitanAttackSelfReady = true;
  prepareCorruptTitanMeta();
};
corruptTitanAttackSelfImg.src = 'animation_assets/corrupttitanstand/groundattack.png';
if (corruptTitanAttackSelfImg.complete && corruptTitanAttackSelfImg.naturalWidth) onCorruptTitanAttackSelfLoad();
else corruptTitanAttackSelfImg.addEventListener('load', onCorruptTitanAttackSelfLoad, { once: true });

let enemies = []; // { kind: 'spider'|'titan'|'tree_monster', x, y, w, h, speed, hp?, maxHp? }
let enemySpawnTimer = 0;
let enemyNextSpawn = 3 + Math.random() * 4; // seconds

// --- Plant traps: stationary hazards on the surface ---
const plantTrapFrameSrcs = ['img_assets/plant_trap.png', 'img_assets/plant_trap_2.png'];
const plantTrapFrames = plantTrapFrameSrcs.map(src => {
  const img = new Image();
  img.src = src;
  return img;
});
const plantTrapFrameBounds = new Array(plantTrapFrameSrcs.length).fill(null);
plantTrapFrames.forEach((img, idx) => {
  img.onload = () => { plantTrapFrameBounds[idx] = computeImageOpaqueBounds(img); };
});
const PLANT_TRAP_TARGET_W = 50;
const plantTraps = [
  { x: 2460 + SHRINE_REPOSITION_OFFSET + ARCH_SPACING_EXTRA * 1.2, animT: Math.random() * 1.6, frame: 0, cooldown: 0, facing: 1 },
  { x: 2740 + SHRINE_REPOSITION_OFFSET + ARCH_SPACING_EXTRA * 1.45, animT: Math.random() * 1.6, frame: 0, cooldown: 0, facing: 1 },
  { x: 3020 + SHRINE_REPOSITION_OFFSET + ARCH_SPACING_EXTRA * 1.7, animT: Math.random() * 1.6, frame: 0, cooldown: 0, facing: 1 }
];
const PLAYER_HIT_FLASH_DUR = 0.35;
let playerHitFlash = 0;
let playerStickyTime = 0;
let plantBiteAudio = null;
try {
  plantBiteAudio = new Audio('sfx_assets/plant_bite.mp3');
  plantBiteAudio.preload = 'auto';
  setAudioBaseVolume(plantBiteAudio, VOLUME_BASES.plant, AUDIO_KIND_SFX);
} catch {
  plantBiteAudio = null;
}

let puzzleUnlockAudio = null;
try {
  puzzleUnlockAudio = new Audio('sfx_assets/unlock.mp3');
  puzzleUnlockAudio.preload = 'auto';
  setAudioBaseVolume(puzzleUnlockAudio, VOLUME_BASES.puzzle, AUDIO_KIND_SFX);
} catch {
  puzzleUnlockAudio = null;
}

// Cave for goblin family (non-enterable)
const caveImg = new Image();
let caveReady = false; let caveBounds = null;
caveImg.onload = () => { caveReady = true; caveBounds = computeImageOpaqueBounds(caveImg); };
caveImg.src = 'img_assets/cave.png';
const cave = { x: 3600 + SHRINE_REPOSITION_OFFSET + ARCH_SPACING_EXTRA * 2.4, width: 220 };
const CORRUPT_TITAN_WORLD_X = cave.x - CORRUPT_TITAN_BASE_OFFSET;

// Enterable deep-forest cave (new)
const caveEntranceImg = new Image();
let caveEntranceReady = false; let caveEntranceBounds = null;
caveEntranceImg.onload = () => { caveEntranceReady = true; caveEntranceBounds = computeImageOpaqueBounds(caveEntranceImg); };
caveEntranceImg.src = 'img_assets/caveentrance.png';
const caveEntranceInteriorImg = new Image();
let caveEntranceInteriorReady = false; let caveEntranceInteriorBounds = null;
caveEntranceInteriorImg.onload = () => { caveEntranceInteriorReady = true; caveEntranceInteriorBounds = computeImageOpaqueBounds(caveEntranceInteriorImg); };
caveEntranceInteriorImg.src = 'img_assets/caveentrance_interior.png';
// caveInteriorImg removed - unused asset
const ENTERABLE_CAVE_OFFSET = 1400; // distance after stone titan
const enterableCave = { x: 8500, width: 220 }; // CAVE ELEMENT POSITION ADJUSTMENT entrance x/width
const ENTERABLE_CAVE_SURFACE_BUFFER = 240; // keep ambient spawns clear of the deep-forest cave
function getEnterableCaveClearRadius(extra = 0) {
  const baseW = (enterableCave && enterableCave.width) ? enterableCave.width : 220;
  return baseW * 0.5 + ENTERABLE_CAVE_SURFACE_BUFFER + Math.max(0, extra);
}
function isNearEnterableCave(x, extra = 0) {
  if (!enterableCave || !Number.isFinite(enterableCave.x)) return false;
  return Math.abs(x - enterableCave.x) <= getEnterableCaveClearRadius(extra);
}
const CAVE_INTERIOR_WIDTH = 1200; // Wider cave for more space
const CAVE_CEILING_RISE = 260; // height from ground line to main ceiling arc
const CAVE_VISUAL_OFFSET_X = 0; // CAVE ELEMENT POSITION ADJUSTMENT background shift (negative = left)
let insideCave = false;
let insideMoon = false;
let moonTransition = null; // null | { t: number }
let moonLanding = null;   // null | { phase, t, shipX, shipY, shipVY }
const MOON_TRANSITION_ALT = 20000; // DEBUG: lower for testing (production: 82000)
const MOON_TRANS_DUR = 7.5;
const MOON_LAND_X = 500;  // world-x where the ship lands on the moon
let caveEnterOverlap = false;
let caveExitOverlap = false;
let caveMusicWasPlaying = false;
let caveRewardPickups = []; // { kind: 'pickaxe'|'sword', x, y, picked }
let cavePromptMessage = '';
let cavePromptAnchor = null; // { x, y } anchor for cave prompts (e.g., knight)
let caveHintMarkers = []; // [{ x, y }]

function getPedestalDims(targetH = 68) {
  const aspect = pedestalImg.naturalWidth && pedestalImg.naturalHeight
    ? pedestalImg.naturalWidth / pedestalImg.naturalHeight : 1;
  const height = targetH;
  const width = height * aspect;
  const scale = pedestalImg.naturalHeight ? height / pedestalImg.naturalHeight : 1;
  const bottomOffset = pedestalBounds ? (pedestalBounds.bottom || 0) * scale : 0;
  const topOffset = pedestalBounds ? (pedestalBounds.top || 0) * scale : 0;
  return { width, height, bottomOffset, topOffset };
}

function getPuzzleItemDimensions(item) {
  const img = cavePuzzleItemImgs[item.key];
  const targetH = item.targetH || 52;
  const aspect = img && img.naturalWidth && img.naturalHeight ? (img.naturalWidth / img.naturalHeight) : 1;
  const height = targetH;
  const width = height * aspect;
  return { width, height };
}

function getPedestalSurfaceY(ped, groundTop) {
  const dims = getPedestalDims();
  const y = groundTop - dims.height + dims.bottomOffset;
  const inset = Math.max(dims.topOffset, dims.height * 0.3);
  return y + inset;
}

function resetCavePuzzleState(groundTop) {
  const pedStart = 130; // CAVE ELEMENT POSITION ADJUSTMENT pedestals start
  const pedSpacing = 100; // CAVE ELEMENT POSITION ADJUSTMENT pedestals spacing
  const itemStart = pedStart + pedSpacing * 5 + 60; // CAVE ELEMENT POSITION ADJUSTMENT loose items start
  const itemSpacing = 42; // CAVE ELEMENT POSITION ADJUSTMENT loose items spacing
  const shuffledItems = [...CAVE_PUZZLE_ITEMS].filter(c => c.key !== 'sword').sort(() => Math.random() - 0.5);
  cavePuzzleState = {
    pedestals: Array.from({ length: 6 }, (_, i) => ({
      x: pedStart + i * pedSpacing,
      y: groundTop,
      itemId: i === 0 ? 'pitem-sword' : null
    })),
    items: CAVE_PUZZLE_ITEMS.map(cfg => {
      if (cfg.key === 'sword') {
        return { id: 'pitem-sword', key: cfg.key, label: cfg.label, targetH: cfg.targetH,
          x: pedStart, y: groundTop, state: 'pedestal', pedestalIndex: 0, locked: true };
      }
      const gi = shuffledItems.findIndex(s => s.key === cfg.key);
      return { id: `pitem-${cfg.key}`, key: cfg.key, label: cfg.label, targetH: cfg.targetH,
        x: itemStart + gi * itemSpacing, y: groundTop, state: 'ground', pedestalIndex: null };
    }),
    carriedItemId: null,
    solved: false,
    rewardsSpawned: false,
    prompt: ''
  };
  caveRewardPickups = [];
}

function findPuzzleItemById(id) {
  return cavePuzzleState.items.find(it => it.id === id);
}

function checkCavePuzzleSolved() {
  const wasSolved = cavePuzzleState.solved;
  const filled = cavePuzzleState.pedestals.map(p => p.itemId).filter(Boolean);
  if (filled.length !== CAVE_PUZZLE_SOLUTIONS[0].length) {
    cavePuzzleState.solved = false;
    return;
  }
  const matchesSolution = CAVE_PUZZLE_SOLUTIONS.some(solution => {
    for (let i = 0; i < solution.length; i++) {
      const ped = cavePuzzleState.pedestals[i];
      const item = ped && ped.itemId ? findPuzzleItemById(ped.itemId) : null;
      if (!item || item.key !== solution[i]) return false;
    }
    return true;
  });
  if (!matchesSolution) {
    cavePuzzleState.solved = false;
    return;
  }
  cavePuzzleState.solved = true;

  // Play unlock sound when puzzle is first solved
  if (!wasSolved && cavePuzzleState.solved) {
    try {
      if (puzzleUnlockAudio) {
        puzzleUnlockAudio.currentTime = 0;
        const p = puzzleUnlockAudio.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      }
    } catch {}
  }
}

function spawnCaveRewards(groundTop) {
  if (cavePuzzleState.rewardsSpawned) return;
  cavePuzzleState.rewardsSpawned = true;
  const baseX = caveKnight.x - 150; // CAVE ELEMENT POSITION ADJUSTMENT reward start offset
  const spacing = 60; // CAVE ELEMENT POSITION ADJUSTMENT reward spacing
  caveRewardPickups = [
    { kind: 'pickaxe', x: baseX, y: groundTop, picked: false },
    { kind: 'sword', x: baseX + spacing, y: groundTop, picked: false }
  ];
}

function getPuzzleItemRect(item, groundTop) {
  const { width, height } = getPuzzleItemDimensions(item);
  const baseX = item.x;
  let baseY = groundTop - height;
  if (item.state === 'pedestal' && typeof item.pedestalIndex === 'number') {
    const ped = cavePuzzleState.pedestals[item.pedestalIndex];
    const surfaceY = getPedestalSurfaceY(ped, groundTop);
    baseY = surfaceY - height;
  }
  return { x: baseX - width / 2, y: baseY, w: width, h: height };
}

function getCarriedPuzzleItemRect(item) {
  const { width, height } = getPuzzleItemDimensions(item);
  const px = player.x + player.w * 0.5;
  const py = player.y + player.h * 0.15 - height;
  return { x: px - width / 2, y: py, w: width, h: height };
}

function drawCaveRiddleText(ctx, groundTop) {
  // === TEXT CONTENT ===
  const headerText = 'The ancient forest knights knew the following order:';
  const leftColumn = [
    '1. The staff must be at one end.',
    '2. The axe must be next to the sword.',
    '3. The mace comes before the hammer.'
  ];
  const rightColumn = [
    '4. The bow comes after the sword.',
    '5. The hammer and axe are each next to an end.',
    '6. The mace must be next to the bow.'
  ];

  // === POSITION CONTROLS === CAVE FONT EDIT
  const RIDDLE_START_X = 250;           // Left margin
  const RIDDLE_START_Y = -120;         // Offset from (groundTop - CAVE_CEILING_RISE). Negative = higher up
  const RIDDLE_COLUMN_GAP = 320;       // Horizontal space between columns

  // === SIZE CONTROLS === CAVE FONT EDIT
  const RIDDLE_FONT_SIZE = 12;         // Font size in pixels
  const RIDDLE_LINE_HEIGHT = 24;       // Vertical spacing between lines
  const RIDDLE_HEADER_GAP = 8;         // Space between header and columns

  // === COLOR CONTROLS === CAVE FONT EDIT
  const RIDDLE_SHADOW_COLOR = 'rgba(10, 15, 20, 0.7)';        // Dark shadow (carved depth)
  const RIDDLE_MAIN_COLOR = 'rgba(208, 95, 58, 0.95)';      // Aged bronze/copper
  const RIDDLE_HIGHLIGHT_COLOR = 'rgba(220, 190, 150, 0.25)'; // Subtle highlight (relief)

  ctx.save();
  const startX = RIDDLE_START_X;
  const startY = Math.max(14, groundTop - CAVE_CEILING_RISE + RIDDLE_START_Y);

  // Ancient carved stone text style
  ctx.font = `${RIDDLE_FONT_SIZE}px "Cinzel", "Palatino Linotype", "Book Antiqua", Georgia, serif`;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  // Helper function to draw text with carved effect
  const drawCarvedText = (text, x, y) => {
    // Dark shadow for depth (carved effect)
    ctx.fillStyle = RIDDLE_SHADOW_COLOR;
    ctx.fillText(text, x + 1, y + 1);

    // Main text in aged bronze/copper color
    ctx.fillStyle = RIDDLE_MAIN_COLOR;
    ctx.fillText(text, x, y);

    // Subtle highlight for carved relief effect
    ctx.fillStyle = RIDDLE_HIGHLIGHT_COLOR;
    ctx.fillText(text, x - 0.5, y - 0.5);
  };

  // Draw header
  let currentY = startY;
  drawCarvedText(headerText, startX, currentY);
  currentY += RIDDLE_LINE_HEIGHT + RIDDLE_HEADER_GAP;

  // Draw left column (items 1-3)
  leftColumn.forEach((line, idx) => {
    const y = currentY + idx * RIDDLE_LINE_HEIGHT;
    drawCarvedText(line, startX, y);
  });

  // Draw right column (items 4-6)
  rightColumn.forEach((line, idx) => {
    const y = currentY + idx * RIDDLE_LINE_HEIGHT;
    drawCarvedText(line, startX + RIDDLE_COLUMN_GAP, y);
  });

  ctx.restore();
}

function drawCavePuzzle(ctx, groundTop) {
  const pedDims = getPedestalDims();
  ctx.save();
  for (const ped of cavePuzzleState.pedestals) {
    const y = groundTop - pedDims.height + pedDims.bottomOffset;
    const x = ped.x - pedDims.width / 2;
    if (pedestalReady && pedestalImg.naturalWidth) {
      ctx.drawImage(pedestalImg, Math.round(x), Math.round(y), pedDims.width, pedDims.height);
    } else {
      ctx.fillStyle = '#203145';
      ctx.fillRect(Math.round(x), Math.round(y), pedDims.width, pedDims.height);
    }
  }

  for (const item of cavePuzzleState.items) {
    if (item.state === 'carried') continue;
    const rect = getPuzzleItemRect(item, groundTop);
    const img = cavePuzzleItemImgs[item.key];
    ctx.save();
    if (img && cavePuzzleItemReady[item.key]) {
      ctx.drawImage(img, Math.round(rect.x), Math.round(rect.y), rect.w, rect.h);
    } else {
      ctx.fillStyle = '#aac2dd';
      ctx.fillRect(Math.round(rect.x), Math.round(rect.y), rect.w, rect.h);
    }
    ctx.restore();
  }
  ctx.restore();
}

function drawCavePuzzleText(ctx, groundTop) {
  const startX = CAVE_TEXT_START_X; // CAVE TEXT ADJUSTMENT X
  const startY = Math.max(12, groundTop - CAVE_CEILING_RISE + CAVE_TEXT_START_Y); // CAVE TEXT ADJUSTMENT Y
  const colWidth = CAVE_TEXT_COL_WIDTH; // CAVE TEXT ADJUSTMENT column width
  const colSpacing = CAVE_TEXT_COL_SPACING; // CAVE TEXT ADJUSTMENT column spacing
  const lineH = CAVE_TEXT_LINE_H; // CAVE TEXT ADJUSTMENT line spacing
  const cols = CAVE_TEXT_COLS; // CAVE TEXT ADJUSTMENT columns
  const targetH = CAVE_TEXT_TARGET_H; // CAVE TEXT ADJUSTMENT consistent height for all images
  ctx.save();
  cavePuzzleTextImgs.forEach((img, idx) => {
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    const x = startX + col * (colWidth + colSpacing);
    const y = startY + row * lineH;
    if (img && img.naturalWidth && img.naturalHeight) {
      // Scale all images to consistent height, preserving aspect ratio
      const aspect = img.naturalWidth / img.naturalHeight;
      const dh = targetH;
      const dw = dh * aspect;
      // Left-align images within their column for consistent positioning
      const dx = x;
      const dy = y;
      ctx.drawImage(img, dx, dy, dw, dh);
    }
  });
  ctx.restore();
}

function drawCarriedPuzzleItem(ctx) {
  if (!cavePuzzleState.carriedItemId) return;
  const item = findPuzzleItemById(cavePuzzleState.carriedItemId);
  if (!item) return;
  const rect = getCarriedPuzzleItemRect(item);
  const img = cavePuzzleItemImgs[item.key];
  ctx.save();
  ctx.globalAlpha = 0.95;
  if (img && cavePuzzleItemReady[item.key]) {
    ctx.drawImage(img, Math.round(rect.x), Math.round(rect.y), rect.w, rect.h);
  } else {
    ctx.fillStyle = '#d2e1f2';
    ctx.fillRect(Math.round(rect.x), Math.round(rect.y), rect.w, rect.h);
  }
  ctx.restore();
}

function drawCaveRewards(ctx, groundTop) {
  for (const reward of caveRewardPickups) {
    if (reward.picked) continue;
    let img, targetW;
    if (reward.kind === 'pickaxe') {
      img = pickaxeImg;
      targetW = 40;
    } else if (reward.kind === 'sword') {
      img = swordImg;
      targetW = 44;
    }
    if (!img || !img.naturalWidth) continue;
    const aspect = img.naturalWidth / img.naturalHeight;
    const dw = targetW;
    const dh = Math.max(1, Math.round(targetW / aspect));
    const x = Math.round(reward.x - dw / 2);
    const y = Math.round(groundTop - dh);
    ctx.drawImage(img, x, y, dw, dh);
  }
}
const knightImg = new Image();
let knightReady = false;
let knightBounds = null;
knightImg.onload = () => { knightReady = true; knightBounds = computeImageOpaqueBounds(knightImg); };
knightImg.src = 'img_assets/knight.png';
const caveKnight = { x: CAVE_INTERIOR_WIDTH * 0.81, targetH: 135, overlap: false, facing: -1, praised: false }; // CAVE ELEMENT POSITION ADJUSTMENT knight
const knightDialogue = {
  intro: "greetings. i am graven the knight. solve my riddle, and i shall bestow upon thou the greatest of rewards.",
  solved: "well done, adventurer. many a great mind has failed to solve this challenge. you are truly special. enjoy your rewards, and be on your way."
};

function getKnightHintMessage() {
  const pedestals = cavePuzzleState.pedestals;
  const filledPedestals = pedestals.filter(p => p.itemId);
  if (!filledPedestals.length) return knightDialogue.intro;

  const correctLabels = [];
  for (let i = 0; i < pedestals.length; i++) {
    const ped = pedestals[i];
    if (!ped.itemId) continue;
    const item = findPuzzleItemById(ped.itemId);
    if (!item) continue;
    const isCorrect = CAVE_PUZZLE_SOLUTIONS.some(sol => sol[i] === item.key);
    if (isCorrect) correctLabels.push(item.label.toLowerCase());
  }

  if (correctLabels.length === 0) {
    return "hmm... none of the weapons placed thus far are in their rightful position. look to the riddle once more.";
  }
  const names = correctLabels.join(', ');
  const verb = correctLabels.length === 1 ? 'stands' : 'stand';
  const place = correctLabels.length === 1 ? 'its place' : 'their places';
  return `the ${names} ${verb} in ${place}. the rest still wander astray.`;
}

const pedestalImg = new Image();
let pedestalReady = false;
let pedestalBounds = null;
pedestalImg.onload = () => { pedestalReady = true; pedestalBounds = computeImageOpaqueBounds(pedestalImg); };
pedestalImg.src = 'img_assets/pedestal.png';

const CAVE_PUZZLE_TEXT_SRCS = [
  'img_assets/cavepuzzle/text/puzzle_1.png',
  'img_assets/cavepuzzle/text/puzzle_2.png',
  'img_assets/cavepuzzle/text/puzzle_3.png',
  'img_assets/cavepuzzle/text/puzzle_4.png',
  'img_assets/cavepuzzle/text/puzzle_5.png',
  'img_assets/cavepuzzle/text/puzzle_6.png',
  'img_assets/cavepuzzle/text/puzzle_7.png'
];
const cavePuzzleTextImgs = CAVE_PUZZLE_TEXT_SRCS.map(src => {
  const img = new Image();
  img.src = src;
  return img;
});
// CAVE TEXT ADJUSTMENT: tweak these to move/scale puzzle text overlays
const CAVE_TEXT_START_X = 24;   // left offset
const CAVE_TEXT_START_Y = -70;  // relative to (groundTop - CAVE_CEILING_RISE)
const CAVE_TEXT_TARGET_H = 32;  // consistent rendered height for all text images
const CAVE_TEXT_LINE_H = 42;    // vertical spacing between lines (increased for better readability)
const CAVE_TEXT_COL_WIDTH = 280; // width per column (increased to accommodate wider text)
const CAVE_TEXT_COL_SPACING = 20; // horizontal gap between columns
const CAVE_TEXT_COLS = 3;       // number of columns across

const CAVE_PUZZLE_ITEMS = [
  { key: 'sword', label: 'Sword', src: 'img_assets/cavepuzzle/sword.png', targetH: 52 },
  { key: 'axe', label: 'Axe', src: 'img_assets/cavepuzzle/axe.png', targetH: 54 },
  { key: 'mace', label: 'Mace', src: 'img_assets/cavepuzzle/mace.png', targetH: 52 },
  { key: 'bow', label: 'Bow', src: 'img_assets/cavepuzzle/bow.png', targetH: 50 },
  { key: 'hammer', label: 'Hammer', src: 'img_assets/cavepuzzle/hammer.png', targetH: 54 },
  { key: 'staff', label: 'Staff', src: 'img_assets/cavepuzzle/staff.png', targetH: 64 },
];
const CAVE_PUZZLE_CORRECT_ORDER = ['sword', 'axe', 'mace', 'bow', 'hammer', 'staff'];
const CAVE_PUZZLE_SOLUTIONS = [
  ['sword', 'axe', 'mace', 'bow', 'hammer', 'staff'],
  ['sword', 'axe', 'bow', 'mace', 'hammer', 'staff'],
];
const cavePuzzleItemImgs = {};
const cavePuzzleItemReady = {};
CAVE_PUZZLE_ITEMS.forEach(({ key, src }) => {
  const img = new Image();
  img.onload = () => { cavePuzzleItemReady[key] = true; };
  img.src = src;
  cavePuzzleItemImgs[key] = img;
});
let cavePuzzleState = { pedestals: [], items: [], carriedItemId: null, solved: false, rewardsSpawned: false, prompt: '' };
const caveAmbience = {
  audio: null,
  ready: false,
  ensure() {
    // Reuse undergroundAmbianceAudio to avoid loading duplicate file
    if (this.audio) return this.audio;
    this.audio = ensureUndergroundAmbianceAudio();
    if (this.audio) {
      // Update volume for cave context
      setAudioBaseVolume(this.audio, VOLUME_BASES.caveAmbience, AUDIO_KIND_SFX);
      this.ready = true;
    } else {
      this.ready = false;
    }
    return this.audio;
  }
};
const caveRumble = {
  audio: null,
  ensure() {
    if (this.audio) return this.audio;
    this.audio = audioManager.getNamedAudio('caveRumble', 'ambiance_sfx/mystic-forest-ambient-23812.mp3', {
      loop: true,
      baseVolume: VOLUME_BASES.caveRumble,
      kind: AUDIO_KIND_SFX,
      preload: 'auto'
    });
    return this.audio;
  }
};

const rockTitanBaseImg = new Image();
const rockTitanAwareImg = new Image();
let rockTitanBaseReady = false, rockTitanAwareReady = false;
let rockTitanBaseBounds = null, rockTitanAwareBounds = null;
rockTitanBaseImg.onload = () => { rockTitanBaseReady = true; rockTitanBaseBounds = computeImageOpaqueBounds(rockTitanBaseImg); };
rockTitanAwareImg.onload = () => { rockTitanAwareReady = true; rockTitanAwareBounds = computeImageOpaqueBounds(rockTitanAwareImg); };
rockTitanBaseImg.src = 'img_assets/rocktitan.png';
rockTitanAwareImg.src = 'img_assets/rocktitan_look.png';
const ROCK_TITAN_TARGET_H = 260;
const ROCK_TITAN_VISION_RANGE = 420;
const ROCK_TITAN_CLOSE_TRIGGER = 120;
const ROCK_TITAN_SINK = 18;
const ROCK_TITAN_TALK_RADIUS = 240;
const rockTitan = { x: 5200 + SHRINE_REPOSITION_OFFSET + ARCH_SPACING_EXTRA * 3.2, attentive: false, overlap: false, _rect: null, dialogueComplete: false };
treeDensityRampOrigin = rockTitan.x;

// --- Deep Forest Elements (after rock titan) ---
// Spores (ambient elements)
const sporeGreenImg = new Image();
const sporeYellowImg = new Image();
let sporeGreenReady = false, sporeYellowReady = false;
sporeGreenImg.onload = () => { sporeGreenReady = true; };
sporeYellowImg.onload = () => { sporeYellowReady = true; };
sporeGreenImg.src = 'img_assets/sporegreen.png';
sporeYellowImg.src = 'img_assets/sporeyellow.png';

// Ruins (ambient background element)
const ruinsOverrunImg = new Image();
let ruinsOverrunReady = false;
let ruinsOverrunBounds = null;
ruinsOverrunImg.onload = () => {
  ruinsOverrunReady = true;
  ruinsOverrunBounds = computeImageOpaqueBounds(ruinsOverrunImg);
};
ruinsOverrunImg.src = 'img_assets/ruinsoverrun.png';

// Mushroom obstacle (idle/active states)
const mushroomIdleImg = new Image();
const mushroomActiveImg = new Image();
let mushroomIdleReady = false, mushroomActiveReady = false;
let mushroomIdleBounds = null, mushroomActiveBounds = null;
const ENABLE_MUSHROOMS = false;
const MUSHROOM_GROUND_INSET = 6;
const MUSHROOM_IDLE_ALPHA = 0.55;
mushroomIdleImg.onload = () => {
  mushroomIdleReady = true;
  mushroomIdleBounds = computeImageOpaqueBounds(mushroomIdleImg);
};
mushroomActiveImg.onload = () => {
  mushroomActiveReady = true;
  mushroomActiveBounds = computeImageOpaqueBounds(mushroomActiveImg);
};
mushroomIdleImg.src = 'img_assets/mushroomidle.png';
mushroomActiveImg.src = 'img_assets/mushroomactive.png';

// Bird idle state
const birdIdleImg = new Image();
let birdIdleReady = false;
let birdIdleBounds = null;
birdIdleImg.onload = () => { birdIdleReady = true; birdIdleBounds = computeImageOpaqueBounds(birdIdleImg); };
birdIdleImg.src = 'animation_assets/bird/birdidle.png';

// Deer animations
const deerIdleImg = new Image();
const deerRunFrames = [];
let deerIdleReady = false;
let deerRunFramesReady = 0;
const DEER_RUN_FRAME_COUNT = 4;
deerIdleImg.onload = () => { deerIdleReady = true; };
deerIdleImg.src = 'animation_assets/deer/deer_idle.png';
for (let i = 1; i <= DEER_RUN_FRAME_COUNT; i++) {
  const img = new Image();
  img.onload = () => { deerRunFramesReady++; };
  img.src = `animation_assets/deer/deer_run_0${i}.png`;
  deerRunFrames.push(img);
}
const DEER_IDLE_HEIGHT = 90; // Idle sprite render height (px)
const DEER_RUNNING_HEIGHT = DEER_IDLE_HEIGHT - 20; // Separate running sprite height (px; taller than idle)
const DEER_RIDER_Y_OFFSET = 40; // Player vertical offset while riding (px; negative lifts the player)
const DEER_RIDDEN_SPEED = 300; // Pixels per second when a player is mounted (intentionally fast)
const DEER_IDLE_Y_OFFSET = 20; // Vertical offset for idle deer (px)
const DEER_RUNNING_Y_OFFSET = 2; // Vertical offset for running deer (px; adjust independently)

function getDeerRenderHeight(deer) {
  const idleH = Number.isFinite(deer?.idleH) ? deer.idleH : DEER_IDLE_HEIGHT;
  const runH = Number.isFinite(deer?.runH) ? deer.runH : DEER_RUNNING_HEIGHT;
  return deer?.state === 'running' ? runH : idleH;
}

function getDeerYOffset(deer) {
  const idleOff = Number.isFinite(deer?.idleYOffset) ? deer.idleYOffset : DEER_IDLE_Y_OFFSET;
  const runOff = Number.isFinite(deer?.runYOffset) ? deer.runYOffset : DEER_RUNNING_Y_OFFSET;
  return deer?.state === 'running' ? runOff : idleOff;
}

function getDeerRunSpeed(deer) {
  return deer?.rider ? DEER_RIDDEN_SPEED : (deer?.runSpeed ?? 0);
}

// Deer2 removed - keeping only main deer

// Deep forest start position (after rock titan)
const DEEP_FOREST_START_OFFSET = 300; // distance past rock titan
function deepForestStartX() {
  return rockTitan.x + DEEP_FOREST_START_OFFSET;
}

function applyDebugSpawn() {
  // Check URL parameters for debug item spawning
  // Usage: ?item=axe, ?item=pickaxe, ?item=sword, ?item=raygun, ?item=builder
  const urlParams = new URLSearchParams(window.location.search);
  const debugItem = urlParams.get('item');

  if (debugItem) {
    const itemMap = {
      'axe': axe,
      'pickaxe': pickaxe,
      'sword': sword,
      'raygun': raygun,
      'builder': builder
    };

    const item = itemMap[debugItem.toLowerCase()];
    if (item) {
      item.picked = true;
      item.equipped = true;
      equipWeapon(debugItem.toLowerCase());
      updateInventoryUI();
      // Debug spawned with item (disabled for production)
    } else {
      console.warn(`[DEBUG] Unknown item: ${debugItem}. Valid items: axe, pickaxe, sword, raygun, builder`);
    }
  }

  if (urlParams.has('moon')) {
    enterMoon();
  }

  if (urlParams.has('transition')) {
    // Debug: skip straight to the moon transition animation
    spaceship.mode = 'occupied';
    spaceshipAltitude = MOON_TRANSITION_ALT;
    startMoonTransition();
  }

  // Apply debug sword spawn if enabled (legacy)
  if (DEBUG_START_WITH_SWORD && !debugItem) {
    sword.picked = true;
    sword.equipped = true;
    equipWeapon('sword');
    updateInventoryUI();
  }

  if (!DEBUG_SPAWN_OVERRIDE) return;
  let targetX = Number.isFinite(DEBUG_SPAWN_OVERRIDE.x) ? DEBUG_SPAWN_OVERRIDE.x : null;
  const usingPreset = DEBUG_SPAWN_OVERRIDE.preset === 'deepforest';
  if (!Number.isFinite(targetX) && usingPreset) {
    targetX = deepForestStartX() + 720; // land well past the entrance pillar/stop block
  }
  if (Number.isFinite(targetX)) {
    // Remove shrine barrier so teleport past it doesn't snap us back
    barrierVines.removed = true;
    barrierVines.fade = 0;
    barrierVines.fading = false;
    shrine.activated = true;
    // Disable corrupt titan guard so it doesn't block or teleport during debug spawn
    debugBypassCorruptTitan = true;
    corruptTitanGuardReady = false;
    corruptTitanGuard = null;
  }
  if (!Number.isFinite(targetX)) return;
  const groundTop = world.height() - world.groundH;
  const targetY = Number.isFinite(DEBUG_SPAWN_OVERRIDE.y)
    ? DEBUG_SPAWN_OVERRIDE.y
    : groundTop - player.h;
  player.x = targetX;
  player.y = targetY;
  player.vx = 0;
  player.vy = 0;
  player.onGround = true;
  cameraX = Math.max(0, targetX - world.width() * 0.5);
  cameraY = 0;
  skyRenderer.setCloudCamY(cameraY);
}

// Ambient elements arrays
let spores = [];
let ruins = [];
let mushrooms = [];
let idleBirds = [];
let deers = [];

function plantTrapFrameInfo(idx) {
  const img = plantTrapFrames[idx] || null;
  if (img && img.naturalWidth && img.naturalHeight) {
    const aspect = img.naturalWidth / img.naturalHeight;
    const width = PLANT_TRAP_TARGET_W;
    const height = Math.max(1, width / Math.max(0.1, aspect));
    const bounds = plantTrapFrameBounds[idx];
    const bottomOffset = bounds && img.naturalHeight ? Math.round((bounds.bottom || 0) * (height / img.naturalHeight)) : 0;
    return { img, width, height, bottomOffset };
  }
  return { img, width: PLANT_TRAP_TARGET_W, height: PLANT_TRAP_TARGET_W, bottomOffset: 0 };
}

function updatePlantTraps(dt, w, h) {
  if (!plantTraps.length) return;
  const groundTopY = h - world.groundH;
  const playerRect = { x: player.x, y: player.y, w: player.w, h: player.h };
  for (const trap of plantTraps) {
    if (trap.dead) continue;
    trap.animT += dt;
    const cycle = 2.4;
    if (trap.animT >= cycle) {
      trap.animT -= cycle;
    }
    trap.frame = (trap.animT < cycle / 2) ? 0 : 1;
    const playerCenterX = player.x + player.w * 0.5;
    trap.facing = (playerCenterX < trap.x) ? -1 : 1;
    if (trap.cooldown > 0) trap.cooldown = Math.max(0, trap.cooldown - dt);

    const info = plantTrapFrameInfo(trap.frame);
    const rect = {
      x: trap.x - info.width / 2,
      y: groundTopY - info.height + info.bottomOffset,
      w: info.width,
      h: info.height
    };
    const hitboxMarginX = info.width * 0.20;
    const hitboxMarginY = info.height * 0.25;
    const hitRect = {
      x: rect.x + hitboxMarginX,
      y: rect.y + hitboxMarginY,
      w: rect.w - hitboxMarginX * 2,
      h: rect.h - hitboxMarginY * 2
    };
    if (playerRect.x + playerRect.w <= hitRect.x || hitRect.x + hitRect.w <= playerRect.x || playerRect.y + playerRect.h <= hitRect.y || hitRect.y + hitRect.h <= playerRect.y) {
      continue;
    }
    if (trap.cooldown > 0) continue;
    trap.cooldown = 0.6;
    playerHitFlash = PLAYER_HIT_FLASH_DUR;
    const center = player.x + player.w * 0.5;
    const dir = (center < trap.x) ? -1 : 1;
    const KNOCK_X = 280;
    const KNOCK_Y = 420;
    player.vx = dir * KNOCK_X;
    player.vy = -KNOCK_Y;
    player.onGround = false;
    if (typeof player.airJumpUsed !== 'undefined') player.airJumpUsed = false;
    if (plantBiteAudio) {
      try {
        plantBiteAudio.currentTime = 0;
        const playPromise = plantBiteAudio.play?.();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      } catch {}
    }
  }
}

function updateRockTitan(dt, w, h) {
  if (!rockTitanBaseReady) return;
  const info = rockTitanFrameInfo(rockTitan.attentive) || rockTitanFrameInfo(false);
  if (!info) return;
  const halfW = info.width / 2;
  const playerCenter = player.x + player.w * 0.5;
  const inVision = (playerCenter <= rockTitan.x) && (playerCenter >= rockTitan.x - ROCK_TITAN_VISION_RANGE);
  const closeEnough = (playerCenter >= rockTitan.x - ROCK_TITAN_CLOSE_TRIGGER);
  const titanLeft = rockTitan.x - halfW;
  const titanRight = rockTitan.x + halfW;
  const playerOverlap = !(player.x + player.w < titanLeft || player.x > titanRight);
  if (inVision && closeEnough && rockTitanAwareReady) {
    rockTitan.attentive = true;
  } else if (!playerOverlap) {
    rockTitan.attentive = false;
  }
}

function pickBirdPerch(perchMinX, perchMaxX, groundTopY) {
  ensureTreeChunksForRange(perchMinX, perchMaxX);
  const candidates = [];
  for (const [, arr] of treesByChunk) {
    for (const t of arr) {
      if (!t || t.felled) continue;
      const idx = t.img | 0;
      if (!BIRD_PERCH_TREE_IMG_INDICES.has(idx)) continue;
      const tx = t.x || 0;
      if (tx < perchMinX || tx > perchMaxX) continue;
      const metrics = getTreeDrawMetrics(t, groundTopY);
      if (!metrics) continue;
      candidates.push({ tree: t, metrics });
    }
  }
  if (!candidates.length) return null;
  return candidates[Math.floor(Math.random() * candidates.length)];
}

// --- Deep Forest Ambient Elements ---
function ensureDeepForestElements(camX, w, h) {
  const dfStart = deepForestStartX();
  const viewLeft = camX - w;
  const viewRight = camX + w * 2;
  const groundTopY = h - world.groundH;

  // Only spawn if we're near or past deep forest start
  if (viewRight < dfStart) return;

  // Spores (small, discreet ambient elements)
  if (sporeGreenReady && sporeYellowReady && spores.length < 12) {
    const spawnChance = 0.015;
    if (Math.random() < spawnChance) {
      const isGreen = Math.random() < 0.5;
      const x = viewRight + Math.random() * 400;
      if (x > dfStart && !spores.find(s => Math.abs(s.x - x) < 60)) {
        const size = 12 + Math.random() * 18; // 12-30px
        const y = -80 - Math.random() * 120; // floating in air
        const driftAmp = 4 + Math.random() * 8;
        const driftSpeed = 0.3 + Math.random() * 0.5;
        const opacity = 0.6 + Math.random() * 0.3;
        spores.push({
          x, y, size,
          isGreen,
          driftT: Math.random() * Math.PI * 2,
          driftAmp,
          driftSpeed,
          opacity
        });
      }
    }
  }

  // Ruins (background element)
  if (ruinsOverrunReady && ruins.length < 2) {
    const spawnChance = 0.004;
    if (Math.random() < spawnChance) {
      const x = viewRight + Math.random() * 600;
      const blockedByCave = isNearEnterableCave(x);
      const blockedByHat = isNearHat(x, 80);
      const blockedByShrine = isNearHelicohatShrine(x, 80);
      const blockedByWipNpc = isNearWipNpc(x, 80);
      if (x > dfStart + 200 && !blockedByCave && !blockedByHat && !blockedByShrine && !blockedByWipNpc && !ruins.find(r => Math.abs(r.x - x) < 300)) {
        const targetH = 140 + Math.random() * 60; // 140-200px
        ruins.push({ x, targetH, featherId: null });
        clearTreesNear(x, Math.max(120, targetH * 0.7));
        spawnFeatherAtRuins(x, h, targetH);
      }
    }
  }

  // Mushrooms (ground obstacles)
  if (ENABLE_MUSHROOMS && mushroomIdleReady && mushroomActiveReady && mushrooms.length < 5) {
    const spawnChance = 0.01;
    if (Math.random() < spawnChance) {
      const x = viewRight + Math.random() * 400;
      const blockedByHat = isNearHat(x, 60);
      const blockedByShrine = isNearHelicohatShrine(x, 60);
      const blockedByWipNpc = isNearWipNpc(x, 60);
      if (x > dfStart + 100 && !blockedByHat && !blockedByShrine && !blockedByWipNpc && !mushrooms.find(m => Math.abs(m.x - x) < 150)) {
        const targetH = 50 + Math.random() * 30; // 50-80px
        const cycleTime = 4 + Math.random() * 3; // 4-7 seconds per cycle
        const activeRatio = 0.4; // 40% of time active
        mushrooms.push({
          x,
          targetH,
          timer: Math.random() * cycleTime,
          cycleTime,
          activeRatio,
          cooldown: 0
        });
        clearTreesNear(x, Math.max(70, targetH * 0.8));
      }
    }
  }

  // Idle birds (perched on select trees, take off when player approaches)
  if (birdIdleReady && birdFramesReady >= BIRD_FRAME_COUNT && idleBirds.length < 3) {
    const spawnChance = 0.003;
    if (Math.random() < spawnChance) {
      const x = viewRight + Math.random() * 500;
      const perchRangeStart = viewRight;
      const perchRangeEnd = viewRight + 600;
      const perch = pickBirdPerch(perchRangeStart, perchRangeEnd, groundTopY);
      if (perch) {
        const perchCenterX = perch.tree?.x ?? x;
        const blockedByCave = isNearEnterableCave(perchCenterX, 20);
        const blockedByHat = isNearHat(perchCenterX, 60);
        const blockedByShrine = isNearHelicohatShrine(perchCenterX, 60);
        const blockedByRuins = isNearSurfaceRuin(perchCenterX, 120);
        const blockedByWipNpc = isNearWipNpc(perchCenterX, 60);
        if (perchCenterX > dfStart + 150 && !blockedByCave && !blockedByHat && !blockedByShrine && !blockedByRuins && !blockedByWipNpc && !idleBirds.find(b => b.state !== 'flying' && Math.abs(b.x - perchCenterX) < 200)) {
          const targetH = 38 + Math.random() * 8; // 38-46px
          const triggerDist = 140 + Math.random() * 60; // 140-200px
          const treeTopY = typeof perch.metrics.topVisibleY === 'number' ? perch.metrics.topVisibleY : perch.metrics.drawY; // y of the top visible pixels
          const perchJitterX = (Math.random() - 0.5) * Math.min(perch.metrics.dw * 0.3, 40);
          const perchX = (perch.tree?.x || x) + perchJitterX;
          const birdBottom = (birdIdleBounds && birdIdleImg.naturalHeight)
            ? Math.round((birdIdleBounds.bottom || birdIdleImg.naturalHeight) * (targetH / Math.max(1, birdIdleImg.naturalHeight)))
            : targetH;
          const perchY = Math.round(treeTopY - birdBottom + BIRD_PERCH_FOOT_OFFSET); // align feet to tree top
          idleBirds.push({
            x: perchX,
            targetH,
            state: 'idle', // 'idle' or 'flying'
            triggerDist,
            frame: 0,
            ftime: 0,
            vx: 0,
            y0: 0,
            t: 0,
            driftAmp: 6 + Math.random() * 8,
            driftSpeed: 0.8 + Math.random() * 0.8,
            facing: Math.random() < 0.5 ? -1 : 1,
            perchY,
            perchTree: perch.tree
          });
        }
      }
    }
  }

  // Deer (idle, runs when player approaches)
  if (deerIdleReady && deerRunFramesReady >= DEER_RUN_FRAME_COUNT && deers.length < 2) {
    const spawnChance = 0.002;
    if (Math.random() < spawnChance) {
      const x = viewRight + Math.random() * 600;
      const blockedByCave = isNearEnterableCave(x, 40);
      const blockedByHat = isNearHat(x, 80);
      const blockedByShrine = isNearHelicohatShrine(x, 80);
      const blockedByWipNpc = isNearWipNpc(x, 80);
      const blockedByHelicoShrine = isNearHelicohatShrine(x, HELICOHAT_SHRINE_CLEAR_RADIUS_TREES);
      if (x > dfStart + 250 && !blockedByCave && !blockedByHat && !blockedByShrine && !blockedByWipNpc && !blockedByHelicoShrine && !deers.find(d => d.type === 'deer1' && Math.abs(d.x - x) < 300)) {
        // EDIT DEER SPAWN SIZE HERE - Adjust base deer size (idle) and run size separately
        const idleH = DEER_IDLE_HEIGHT; // Fixed idle size (no variation) to prevent floating
        const runH = DEER_RUNNING_HEIGHT; // Run animation height (kept separate so sprites can differ)
        // EDIT IDLE DEER Y POSITION HERE - Adjust vertical position of idle deer (positive = lower, negative = higher)
        const idleYOffset = DEER_IDLE_Y_OFFSET; // Vertical offset for idle deer (pixels)
        const runYOffset = DEER_RUNNING_Y_OFFSET; // Vertical offset for running deer (pixels)
        const triggerDist = 180 + Math.random() * 80; // 180-260px
        const runSpeed = 220 + Math.random() * 80; // 220-300 px/s
        deers.push({
          type: 'deer1',
          x,
          idleH,
          runH,
          idleYOffset,
          runYOffset,
          state: 'idle', // 'idle' or 'running'
          triggerDist,
          runSpeed,
          frame: 0,
          ftime: 0,
          facing: -1 // faces left initially
        });
      }
    }
  }

  // Deer2 removed - keeping only main deer

  // Cleanup off-screen elements
  const cleanupMargin = w * 2;
  spores = spores.filter(s => s.x > viewLeft - cleanupMargin && s.x < viewRight + cleanupMargin);
  ruins = ruins.filter(r => r.x > viewLeft - cleanupMargin && r.x < viewRight + cleanupMargin && !isNearEnterableCave(r.x) && !isNearHat(r.x, 60) && !isNearWipNpc(r.x, 60));
  mushrooms = ENABLE_MUSHROOMS
    ? mushrooms.filter(m => m.x > viewLeft - cleanupMargin && m.x < viewRight + cleanupMargin)
    : [];
  // Birds and deer: only check distance when filtering, allow them to move through spawn-blocked zones
  idleBirds = idleBirds.filter(b => {
    // Flying birds can go anywhere until they're far off-screen
    if (b.state === 'flying') {
      return b.x > viewLeft - cleanupMargin && b.x < viewRight + cleanupMargin;
    }
    // Idle birds: check spawn zones since they're stationary
    return b.x > viewLeft - cleanupMargin && b.x < viewRight + cleanupMargin && !isNearEnterableCave(b.x, 20) && !isNearHat(b.x, 60) && !isNearWipNpc(b.x, 60);
  });
  deers = deers.filter(d => {
    // Running deer can go anywhere until they're far off-screen
    if (d.state === 'running') {
      return d.x > viewLeft - cleanupMargin;
    }
    // Idle deer: check spawn zones since they're stationary
    return d.x > viewLeft - cleanupMargin && d.x < viewRight + cleanupMargin && !isNearEnterableCave(d.x, 40) && !isNearHat(d.x, 80) && !isNearWipNpc(d.x, 80) && !isNearHelicohatShrine(d.x, HELICOHAT_SHRINE_CLEAR_RADIUS_TREES);
  });
}

function updateDeepForestElements(dt, w, h) {
  const playerCenterX = player.x + player.w * 0.5;
  const playerCenterY = player.y + player.h * 0.5;
  const groundTopY = h - world.groundH;

  // Update spores (gentle drifting)
  for (const s of spores) {
    s.driftT += dt * s.driftSpeed;
  }

  if (ENABLE_MUSHROOMS) {
    // Update mushrooms (cycle between idle and active)
    for (const m of mushrooms) {
      m.timer += dt;
      if (m.timer >= m.cycleTime) m.timer -= m.cycleTime;
      if (m.cooldown > 0) m.cooldown -= dt;

      const isActive = (m.timer / m.cycleTime) < m.activeRatio;
      if (isActive && mushroomActiveReady) {
        // Check collision with player when active
        const info = mushroomActiveBounds || {};
        const aspect = mushroomActiveImg.naturalWidth && mushroomActiveImg.naturalHeight
          ? mushroomActiveImg.naturalWidth / mushroomActiveImg.naturalHeight : 1;
        const width = m.targetH * aspect;
        const height = m.targetH;
        const bottomOffset = info.bottom ? Math.round(info.bottom * (height / mushroomActiveImg.naturalHeight)) : 0;

        const rect = {
          x: m.x - width / 2,
          y: groundTopY - height + bottomOffset + MUSHROOM_GROUND_INSET,
          w: width,
          h: height
        };

        // Check if player is overlapping
        const playerRect = { x: player.x, y: player.y, w: player.w, h: player.h };
        const overlaps = !(playerRect.x + playerRect.w < rect.x || rect.x + rect.w < playerRect.x ||
                           playerRect.y + playerRect.h < rect.y || rect.y + rect.h < playerRect.y);

        if (overlaps && m.cooldown <= 0) {
          // Push player back
          m.cooldown = 0.8;
          playerHitFlash = PLAYER_HIT_FLASH_DUR;
          const dir = (playerCenterX < m.x) ? -1 : 1;
          player.vx = dir * 240;
          player.vy = -350;
          player.onGround = false;
          if (typeof player.airJumpUsed !== 'undefined') player.airJumpUsed = false;
        }
      }
    }
  } else {
    mushrooms = [];
  }

  // Update idle birds
  for (const b of idleBirds) {
    if (b.state === 'idle') {
      const dist = Math.abs(playerCenterX - b.x);
      const perchLost = b.perchTree && b.perchTree.felled;
      if (dist < b.triggerDist || perchLost) {
        // Take off!
        b.state = 'flying';
        const height = b.targetH;
        const perchBase = (typeof b.perchY === 'number') ? b.perchY : (groundTopY - height + 5);
        b.y0 = perchBase + height / 2; // start at perch contact
        const awayFromPlayer = (b.x > playerCenterX) ? 1 : -1;
        b.vx = awayFromPlayer * (100 + Math.random() * 80);
        b.facing = awayFromPlayer;
        b.vy = -(60 + Math.random() * 40); // upward takeoff
        b.frame = 0;
        b.ftime = 0;
      }
    } else if (b.state === 'flying') {
      // Fly away
      b.x += b.vx * dt;
      if (typeof b.vy === 'number') {
        b.y0 += b.vy * dt;
      }
      b.t += dt;
      b.ftime += dt;
      const ANIM_FPS = 12;
      while (b.ftime >= 1 / ANIM_FPS) {
        b.ftime -= 1 / ANIM_FPS;
        b.frame = (b.frame + 1) % BIRD_FRAME_COUNT;
      }
    }
  }

  // Update deer
  for (const d of deers) {
    if (d.type === 'deer1') {
      // Riding: clamp player on top if mounted
      if (d.rider) {
        const deerH = getDeerRenderHeight(d); // Match render height per state
        const rideSpeed = getDeerRunSpeed(d);
        const wantsLeft = input.leftHeld && !input.rightHeld;
        const wantsRight = input.rightHeld && !input.leftHeld;
        if (wantsLeft) d.facing = -1;
        else if (wantsRight) d.facing = 1;
        player.x = d.x - player.w / 2 - (d.facing === 1 ? 4 : -4);
        player.y = (h - world.groundH) - deerH - player.h + getDeerYOffset(d) + DEER_RIDER_Y_OFFSET; // pixel-to-pixel contact with tuneable offset
        player.vx = rideSpeed * d.facing;
        player.vy = 0;
        player.onGround = true;
        if (input.jumpHeld || input.consumeJump()) {
          d.rider = null;
          player.vy = -420;
          player.onGround = false;
        }
      }
      if (d.state === 'idle') {
        const dist = Math.abs(playerCenterX - d.x);
        if (dist < d.triggerDist) {
          d.state = 'running';
          d.facing = (d.x > playerCenterX) ? 1 : -1; // run away from player
          d.rider = null;
        }
      } else if (d.state === 'running') {
        const speed = getDeerRunSpeed(d);
        d.x += d.facing * speed * dt;
        d.ftime += dt;
        const ANIM_FPS = 10;
        while (d.ftime >= 1 / ANIM_FPS) {
          d.ftime -= 1 / ANIM_FPS;
          d.frame = (d.frame + 1) % DEER_RUN_FRAME_COUNT;
        }
      }
    }
    // Deer2 removed - keeping only main deer
  }
}

function drawDeepForestElements(ctx, w, h) {
  const groundTopY = h - world.groundH;

  // Draw ruins (background layer, before trees)
  for (const r of ruins) {
    if (!ruinsOverrunReady || !ruinsOverrunImg.naturalWidth) continue;
    const aspect = ruinsOverrunImg.naturalWidth / ruinsOverrunImg.naturalHeight;
    const width = r.targetH * aspect;
    const height = r.targetH;
    const x = Math.round(r.x - width / 2);
    const bottomOffset = ruinsOverrunBounds ? Math.round(ruinsOverrunBounds.bottom * (height / ruinsOverrunImg.naturalHeight)) : 0;
    const y = Math.round(groundTopY - height + bottomOffset);

    ctx.save();
    ctx.globalAlpha = 0.4; // faded background element
    ctx.drawImage(ruinsOverrunImg, x, y, width, height);
    ctx.restore();
  }

  // Draw underground ruins (in caves)
  for (const r of undergroundRuins) {
    if (!ruinsOverrunReady || !ruinsOverrunImg.naturalWidth) continue;
    const aspect = ruinsOverrunImg.naturalWidth / ruinsOverrunImg.naturalHeight;
    const width = r.targetH * aspect;
    const height = r.targetH;
    const x = Math.round(r.x - width / 2);
    const bottomOffset = ruinsOverrunBounds ? Math.round(ruinsOverrunBounds.bottom * (height / ruinsOverrunImg.naturalHeight)) : 0;
    const y = Math.round(r.y - height + bottomOffset);

    ctx.save();
    ctx.globalAlpha = 0.3; // slightly more faded for underground ambiance
    ctx.drawImage(ruinsOverrunImg, x, y, width, height);
    ctx.restore();
  }

  // Draw spores (midground layer, ambient)
  for (const s of spores) {
    const img = s.isGreen ? sporeGreenImg : sporeYellowImg;
    if (!img.naturalWidth) continue;
    const drift = s.driftAmp * Math.sin(s.driftT);
    const x = Math.round(s.x - s.size / 2);
    const y = Math.round(groundTopY + s.y + drift - s.size / 2);

    ctx.save();
    ctx.globalAlpha = s.opacity;
    ctx.drawImage(img, x, y, s.size, s.size);
    ctx.restore();
  }

  if (ENABLE_MUSHROOMS) {
    // Draw mushrooms (ground layer)
    for (const m of mushrooms) {
      const isActive = (m.timer / m.cycleTime) < m.activeRatio;
      const img = isActive ? mushroomActiveImg : mushroomIdleImg;
      const bounds = isActive ? mushroomActiveBounds : mushroomIdleBounds;

      if (!img || !img.naturalWidth) continue;
      const aspect = img.naturalWidth / img.naturalHeight;
      const width = m.targetH * aspect;
      const height = m.targetH;
      const bottomOffset = bounds ? Math.round(bounds.bottom * (height / img.naturalHeight)) : 0;
      const x = Math.round(m.x - width / 2);
      const y = Math.round(groundTopY - height + bottomOffset + MUSHROOM_GROUND_INSET);

      ctx.save();
      ctx.globalAlpha = isActive ? 1.0 : MUSHROOM_IDLE_ALPHA;
      ctx.drawImage(img, x, y, width, height);
      ctx.restore();
    }
  }

}

function drawDeepForestAnimals(ctx, w, h) {
  const groundTopY = h - world.groundH;

  // Draw idle/flying birds
  for (const b of idleBirds) {
    if (b.state === 'idle' && birdIdleReady && birdIdleImg.naturalWidth) {
      const aspect = birdIdleImg.naturalWidth / birdIdleImg.naturalHeight;
      const width = b.targetH * aspect;
      const height = b.targetH;
      const x = Math.round(b.x);
      const y = Math.round((typeof b.perchY === 'number') ? b.perchY : (groundTopY - height + 5));

      ctx.save();
      ctx.translate(x, y + height / 2);
      if (b.facing < 0) ctx.scale(-1, 1);
      ctx.drawImage(birdIdleImg, -width / 2, -height / 2, width, height);
      ctx.restore();
    } else if (b.state === 'flying') {
      const img = birdFrames[b.frame];
      if (!img || !img.naturalWidth) continue;
      const s = b.targetH / img.naturalHeight;
      const dw = img.naturalWidth * s;
      const dh = img.naturalHeight * s;
      const px = Math.round(b.x);
      const drift = b.driftAmp * Math.sin(b.t * b.driftSpeed);
      const py = Math.round((b.y0 != null ? b.y0 : b.y) + drift);

      ctx.save();
      ctx.translate(px, py);
      if (b.facing > 0) ctx.scale(-1, 1);
      ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
      ctx.restore();
    }
  }

  // Draw deer
  for (const d of deers) {
    if (d.type === 'deer1') {
      let img;
      if (d.state === 'idle' && deerIdleReady) {
        img = deerIdleImg;
      } else if (d.state === 'running' && deerRunFramesReady >= DEER_RUN_FRAME_COUNT) {
        img = deerRunFrames[d.frame];
      }

      if (!img || !img.naturalWidth) continue;
      const aspect = img.naturalWidth / img.naturalHeight;
      // EDIT RUNNING DEER SIZE HERE - Adjust DEER_RUNNING_HEIGHT to size the run animation independently
      const height = getDeerRenderHeight(d); // Deer render height per state
      const width = height * aspect;
      const x = Math.round(d.x);
      // Apply Y offset (set during spawn)
      const y = Math.round(groundTopY - height + getDeerYOffset(d)); // Deer ground position with offset

      ctx.save();
      ctx.translate(x, y + height / 2);
      if (d.facing < 0) ctx.scale(-1, 1);
      ctx.drawImage(img, -width / 2, -height / 2, width, height);
      ctx.restore();
    }
    // Deer2 removed - keeping only main deer
  }
}

function drawCave(ctx, w, h) {
  const img = caveImg;
  const bounds = caveBounds;
  if (!img || !(img.naturalWidth && img.naturalHeight)) return;
  const aspect = img.naturalWidth / img.naturalHeight;
  const targetW = cave.width || 260;
  const dw = targetW;
  const dh = Math.max(10, Math.round(targetW / Math.max(0.1, aspect)));
  const groundTop = h - world.groundH;
  const x = Math.round(cave.x - dw / 2);
  if (x + dw < cameraX - 600 || x > cameraX + w + 600) return;
  let y = groundTop - dh;
  if (bounds && img.naturalHeight) {
    const scaleY = dh / img.naturalHeight;
    y += Math.round((bounds.bottom || 0) * scaleY);
  }
  const sink = 14;
  ctx.drawImage(img, x, Math.round(y + sink), dw, dh);
}

function drawEnterableCave(ctx, w, h) {
  const img = caveEntranceReady ? caveEntranceImg : caveImg;
  const bounds = caveEntranceReady ? caveEntranceBounds : caveBounds;
  if (!img || !(img.naturalWidth && img.naturalHeight)) return;
  const aspect = img.naturalWidth / img.naturalHeight;
  const targetW = enterableCave.width || 240;
  const dw = targetW;
  const dh = Math.max(10, Math.round(targetW / Math.max(0.1, aspect)));
  const groundTop = h - world.groundH;
  const x = Math.round(enterableCave.x - dw / 2);
  if (x + dw < cameraX - 600 || x > cameraX + w + 600) return;
  let y = groundTop - dh;
  if (bounds && img.naturalHeight) {
    const scaleY = dh / img.naturalHeight;
    y += Math.round((bounds.bottom || 0) * scaleY);
  }
  const sink = 6; // smaller sink so entrance sits higher
  ctx.drawImage(img, x, Math.round(y + sink), dw, dh);
}

function drawCaveInteriorScene(ctx, w, h) {
  ctx.save();

  const decoOffsetX = CAVE_VISUAL_OFFSET_X; // CAVE ELEMENT POSITION ADJUSTMENT background offset
  ctx.save();
  ctx.translate(decoOffsetX, 0);

  const drawW = Math.max(w, CAVE_INTERIOR_WIDTH);
  const groundH = world.groundH;
  const groundTop = h - groundH;
  const ceilingY = Math.max(32, groundTop - CAVE_CEILING_RISE);

  // Cave background gradient
  const bgGrad = ctx.createLinearGradient(0, 0, 0, h);
  bgGrad.addColorStop(0, '#0d141f');
  bgGrad.addColorStop(0.45, '#111c2a');
  bgGrad.addColorStop(1, '#0f1825');
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, drawW, h);

  // Soft light spill from the entrance
  const lightGrad = ctx.createRadialGradient(110, groundTop - 48, 60, 110, groundTop - 48, 360);
  lightGrad.addColorStop(0, 'rgba(74,111,148,0.25)');
  lightGrad.addColorStop(0.55, 'rgba(40,63,89,0.20)');
  lightGrad.addColorStop(1, 'rgba(12,18,28,0.05)');
  ctx.fillStyle = lightGrad;
  ctx.fillRect(0, 0, drawW, h);

  // Layered stone bands for a carved look
  ctx.save();
  for (let i = 0; i < 6; i++) {
    const bandY = ceilingY + (groundTop - ceilingY) * (i / 6);
    ctx.fillStyle = `rgba(36,55,70,${0.2 + i * 0.08})`;
    ctx.beginPath();
    ctx.moveTo(0, bandY + Math.sin(i * 1.7) * 8);
    for (let xPos = 0; xPos <= drawW; xPos += 120) {
      const wobble = Math.sin(xPos * 0.01 + i * 0.8) * 18 + Math.cos(xPos * 0.015 + i) * 10;
      ctx.lineTo(xPos, bandY + wobble);
    }
    ctx.lineTo(drawW, groundTop + 140);
    ctx.lineTo(0, groundTop + 140);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();

  // Darken edges to frame the interior
  const sideGradLeft = ctx.createLinearGradient(0, 0, 180, 0);
  sideGradLeft.addColorStop(0, 'rgba(5,8,12,0.55)');
  sideGradLeft.addColorStop(1, 'rgba(5,8,12,0.0)');
  ctx.fillStyle = sideGradLeft;
  ctx.fillRect(0, ceilingY - 16, 220, groundTop - ceilingY + 200);
  const sideGradRight = ctx.createLinearGradient(drawW, 0, drawW - 180, 0);
  sideGradRight.addColorStop(0, 'rgba(5,8,12,0.55)');
  sideGradRight.addColorStop(1, 'rgba(5,8,12,0.0)');
  ctx.fillStyle = sideGradRight;
  ctx.fillRect(drawW - 220, ceilingY - 16, 220, groundTop - ceilingY + 200);

  // Stalactites
  ctx.save();
  ctx.fillStyle = '#0b121b';
  const stalCount = 7;
  for (let i = 0; i < stalCount; i++) {
    const cx = 40 + (i / (stalCount - 1)) * (drawW - 80);
    const spikeH = 70 + (i % 3) * 16 + Math.sin(i * 1.1) * 10;
    const spikeW = 24 + (i % 2) * 8;
    ctx.beginPath();
    ctx.moveTo(cx - spikeW * 0.55, ceilingY - 8);
    ctx.lineTo(cx + spikeW * 0.55, ceilingY - 8);
    ctx.lineTo(cx, ceilingY + spikeH);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();

  // Stalagmites
  ctx.save();
  ctx.fillStyle = '#0e1722';
  const miteCount = 6;
  for (let i = 0; i < miteCount; i++) {
    const cx = 30 + (i / (miteCount - 1)) * (drawW - 60);
    const spikeH = 50 + (i % 3) * 14 + Math.cos(i * 1.3) * 6;
    const spikeW = 30 + (i % 2) * 8;
    ctx.beginPath();
    ctx.moveTo(cx - spikeW * 0.5, groundTop + 6);
    ctx.lineTo(cx + spikeW * 0.5, groundTop + 6);
    ctx.lineTo(cx, groundTop - spikeH);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();

  // Floor and pathway
  const floorGrad = ctx.createLinearGradient(0, groundTop - 14, 0, h);
  floorGrad.addColorStop(0, '#1b2734');
  floorGrad.addColorStop(1, '#0f151f');
  ctx.fillStyle = floorGrad;
  ctx.fillRect(0, groundTop - 14, drawW, h - (groundTop - 14));

  const pathGrad = ctx.createLinearGradient(0, groundTop - 6, 0, groundTop + 90);
  pathGrad.addColorStop(0, 'rgba(128,164,196,0.12)');
  pathGrad.addColorStop(1, 'rgba(64,93,122,0.08)');
  ctx.fillStyle = pathGrad;
  ctx.fillRect(0, groundTop - 6, drawW, Math.min(h - (groundTop - 6), 140));

  // Pebble speckles to keep texture on the ground line
  ctx.save();
  ctx.fillStyle = 'rgba(178,200,220,0.12)';
  for (let i = 0; i < 42; i++) {
    const noise = Math.sin(i * 12.9898) * 43758.5453;
    const n = noise - Math.floor(noise);
    const px = (n * drawW) % drawW;
    const py = groundTop + ((i % 7) * 6);
    const size = 1 + (i % 3);
    ctx.fillRect(Math.round(px), Math.round(py), size, size);
  }
  ctx.restore();

  // Exit tunnel on left (opening back to outside) with matching entrance art
  const doorImg = caveEntranceInteriorReady ? caveEntranceInteriorImg : (caveEntranceReady ? caveEntranceImg : caveImg);
  const doorBounds = caveEntranceInteriorReady ? caveEntranceInteriorBounds : (caveEntranceReady ? caveEntranceBounds : caveBounds);
  if (doorImg && doorImg.naturalWidth) {
    const aspect = doorImg.naturalWidth / doorImg.naturalHeight;
    const targetW = Math.min(180, (enterableCave.width || 220) * 0.8);
    const dw = targetW;
    const dh = Math.max(10, Math.round(targetW / Math.max(0.1, aspect)));
    const scaleY = doorImg.naturalHeight ? dh / doorImg.naturalHeight : 1;
    const bottomOffset = (doorBounds ? doorBounds.bottom || 0 : 0) * scaleY;
    const y = groundTop - dh + bottomOffset;
    ctx.save();
    ctx.translate(dw * 0.6, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(doorImg, 0, Math.round(y), dw, dh);
    ctx.restore();
  }

  // Ground line aligned with outside ground level
  ctx.strokeStyle = 'rgba(90,118,146,0.45)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, groundTop);
  ctx.lineTo(drawW, groundTop);
  ctx.stroke();

  drawCaveRiddleText(ctx, groundTop);
  ctx.restore(); // end decoOffsetX for background/riddle/door

  drawCavePuzzle(ctx, groundTop);
  drawCaveRewards(ctx, groundTop);

  // Knight NPC at the back (no bob, grounded)
  if (knightReady && knightImg.naturalWidth) {
    const aspect = knightImg.naturalWidth / knightImg.naturalHeight;
    const dh = caveKnight.targetH;
    const dw = Math.round(dh * aspect);
    const bx = Math.round(caveKnight.x - dw / 2); // CAVE ELEMENT POSITION ADJUSTMENT knight X anchor
    const by = Math.round(groundTop - dh + 17); // CAVE ELEMENT POSITION ADJUSTMENT knight Y (increase to lower)
    ctx.save();
    if (caveKnight.facing > 0) {
      ctx.translate(bx + dw / 2, 0);
      ctx.scale(-1, 1);
      ctx.translate(-(bx + dw / 2), 0);
    }
    ctx.drawImage(knightImg, bx, by, dw, dh);
    ctx.restore();
  }

  // Draw player in cave space (camera locked to origin)
  ctx.save();
  // Draw player or ash pile if burning/dead
  if (gameOverPending || gameOverActive) {
    // Player is dead - draw ash pile instead
    const ashProgress = gameOverBurnT > 0 ? (1 - (gameOverBurnT / GAME_OVER_BURN_DUR)) : 1; // Full opacity when game over
    const groundY = player.y + player.h;
    const ashW = 16;
    const ashH = 8;
    const ashX = player.x + player.w / 2 - ashW / 2;
    const ashY = groundY - ashH; // Settled on ground

    // Draw ash pile (simple dark gray mound)
    ctx.save();
    ctx.globalAlpha = Math.min(1, ashProgress * 2); // Fade in, then stay visible
    ctx.fillStyle = '#3a3a3a';
    ctx.beginPath();
    ctx.ellipse(ashX + ashW / 2, ashY + ashH, ashW / 2, ashH / 2, 0, 0, Math.PI * 2);
    ctx.fill();
    // Darker center
    ctx.fillStyle = '#2a2a2a';
    ctx.beginPath();
    ctx.ellipse(ashX + ashW / 2, ashY + ashH - 2, ashW / 3, ashH / 3, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  } else {
    player.draw(ctx);
    // Draw equipped items in cave
    if (sword.equipped) {
      drawSwordSlashArc(ctx);
      drawHeldSword(ctx);
    }
    if (axe.equipped) drawHeldAxe(ctx);
    if (pickaxe.equipped) drawHeldPickaxe(ctx);
    if (raygun.equipped) drawHeldRaygun(ctx);
    if (builder.equipped) drawHeldBuilder(ctx);
  }
  ctx.restore();

  // Draw carried puzzle item (follows player)
  drawCarriedPuzzleItem(ctx);

  ctx.restore();
}

function enterSurfaceCave() {
  insideCave = true;
  caveExitOverlap = false;
  // Reset audio to base levels when entering cave
  const forestBirdsAudio = audioManager.namedAudio.get('forestBirds')?.audio;
  if (forestBirdsAudio) setAudioBaseVolume(forestBirdsAudio, VOLUME_BASES.birds, AUDIO_KIND_SFX);
  const undergroundAmbianceAudio = audioManager.namedAudio.get('undergroundAmbiance')?.audio;
  if (undergroundAmbianceAudio) {
    setAudioBaseVolume(undergroundAmbianceAudio, VOLUME_BASES.undergroundAmbience, AUDIO_KIND_SFX);
    try {
      undergroundAmbianceAudio.pause();
      undergroundAmbianceAudio.currentTime = 0;
    } catch {}
  }
  if (audioManager.musicAudio) setAudioBaseVolume(audioManager.musicAudio, AUDIO.MUSIC_BASE_VOLUME, AUDIO_KIND_MUSIC);
  setAmbianceState('cave');
  if (apocalypseActive) {
    // Block cave ambience during apocalypse
    if (caveAmbience.audio) {
      try { caveAmbience.audio.pause(); caveAmbience.audio.currentTime = 0; } catch {}
    }
    if (caveRumble.audio) {
      try { caveRumble.audio.pause(); caveRumble.audio.currentTime = 0; } catch {}
    }
  }
  const musicAudio = audioManager.musicAudio;
  caveMusicWasPlaying = !!(musicAudio && !musicAudio.paused && !musicMuted && !musicAudio.muted);
  pauseBackgroundMusic();

  // Start underground music in the cave (not during apocalypse)
  if (!apocalypseActive) {
    const undergroundMusic = ensureUndergroundMusicAudio();
    if (undergroundMusic && !musicMuted) {
      const p = undergroundMusic.play?.();
      // Use slightly higher volume for cave music (1.2x base, normalized from 1.6x)
      setAudioBaseVolume(undergroundMusic, AUDIO.MUSIC_BASE_VOLUME * 1.2, AUDIO_KIND_MUSIC);
      if (p && typeof p.catch === 'function') p.catch(()=>{});
      isUndergroundMusicActive = true;
    }
  }

  // DISABLED: Cave ambiance
  // if (!apocalypseActive) {
  //   const caveAudio = caveAmbience.ensure();
  //   if (caveAudio) {
  //     const p = caveAudio.play?.();
  //     setAudioBaseVolume(caveAudio, VOLUME_BASES.caveAmbience, AUDIO_KIND_SFX);
  //     if (p && typeof p.catch === 'function') p.catch(()=>{});
  //   }
  //   const rumble = caveRumble.ensure();
  //   if (rumble) {
  //     const p = rumble.play?.();
  //     setAudioBaseVolume(rumble, VOLUME_BASES.caveRumble, AUDIO_KIND_SFX);
  //     if (p && typeof p.catch === 'function') p.catch(()=>{});
  //   }
  // }
  const groundTop = world.height() - world.groundH;
  resetCavePuzzleState(groundTop);
  cavePromptMessage = '';
  cavePromptAnchor = null;
  caveHintMarkers = [];
  caveKnight.overlap = false;
  caveKnight.praised = cavePuzzleState.solved;
  player.x = 80; // Start near entrance
  player.y = groundTop - player.h;
  player.vx = 0; player.vy = 0; player.onGround = true;
  cameraX = 0; cameraY = 0;
  skyRenderer.setCloudCamY(cameraY);
}

function exitSurfaceCave() {
  insideCave = false;
  caveEnterOverlap = false;
  caveExitOverlap = false;
  if (caveAmbience.audio) {
    try {
      caveAmbience.audio.pause();
      caveAmbience.audio.currentTime = 0;
    } catch {}
  }
  if (caveRumble.audio) {
    try {
      caveRumble.audio.pause();
      caveRumble.audio.currentTime = 0;
    } catch {}
  }

  // Stop underground music when exiting cave
  audioManager.stopNamedAudio('undergroundMusic');
  isUndergroundMusicActive = false;

  // Ambiance will be set by main loop based on player position
  cavePromptMessage = '';
  caveRewardPickups = [];
  if (caveMusicWasPlaying && !musicMuted) {
    resumeBackgroundMusic();
  }
  caveMusicWasPlaying = false;
  const groundTop = world.height() - world.groundH;
  player.x = enterableCave.x - (enterableCave.width || 220) * 0.5 - player.w - 8;
  player.y = groundTop - player.h;
  player.vx = 0; player.vy = 0; player.onGround = true;
  cameraX = Math.max(0, player.x - world.width() * 0.4);
  cameraY = 0;
  skyRenderer.setCloudCamY(cameraY);
}

// ============================================================
// SPACESHIP
// ============================================================

/** Returns { w, h } render size for a spaceship image at a fixed target width. */
function spaceshipRenderSize(img, targetW = 160) {
  if (img && img.naturalWidth > 0 && img.naturalHeight > 0) {
    return { w: targetW, h: Math.round(targetW / (img.naturalWidth / img.naturalHeight)) };
  }
  return { w: targetW, h: Math.round(targetW * 0.55) };
}

function enterSpaceship() {
  const w = world.width();
  const h = world.height();
  const groundTop = h - world.groundH;
  const sz = spaceshipRenderSize(spaceshipOffImg.naturalWidth > 0 ? spaceshipOffImg : spaceshipEquipImg, 240);
  spaceship.mode = 'entering';
  spaceship.barT = 0;
  spaceship.linePhase = 0;
  // Match the exact parked position so the equip form appears seamlessly over the idle ship
  spaceship.flightX = spaceship.x;
  spaceship.flightY = groundTop - sz.h + 48;
  spaceship.baseX   = spaceship.flightX;
  spaceship.baseY   = spaceship.flightY;
  spaceship.vpOffX  = 0;
  spaceship.vpOffY  = 0;
  // Snap camera X so ship starts centered
  cameraX = Math.max(0, spaceship.flightX + sz.w / 2 - w / 2);
  spaceship.flightVX = 0;
  spaceship.flightVY = 0;
  // Start sonic boom immediately (before fading the old track so there's no gap)
  const boom = ensureSonicBoomAudio();
  if (!musicMuted) {
    try { boom.currentTime = 0; } catch {}
    const p = boom.play?.();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }
  pauseBackgroundMusic();
}

function exitSpaceship() {
  const sz = spaceshipRenderSize(spaceshipEquipImg, 240);
  // Launch empty shell upward
  spaceship.emptyActive = true;
  spaceship.emptyX = spaceship.flightX;
  spaceship.emptyY = spaceship.flightY;
  spaceship.emptyVY = Math.min(spaceship.flightVY, -600); // keep going fast
  spaceship.emptyTimer = 0;
  // Eject player from ship center
  player.x = spaceship.flightX + sz.w / 2 - player.w / 2;
  player.y = spaceship.flightY + sz.h / 2 - player.h / 2;
  player.vx = 0;
  player.vy = 280; // tumble downward
  player.onGround = false;
  spaceship.mode = 'idle';
  // Stop sonic boom and resume background music
  if (sonicBoomAudio) {
    try { sonicBoomAudio.pause(); sonicBoomAudio.currentTime = 0; } catch {}
  }
  if (!musicMuted && audioManager.musicAudio) {
    try {
      const p = audioManager.musicAudio.play?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch {}
  }
}

function updateSpaceshipMode(dt, w, h) {
  const FLIGHT_ACCEL = 260;   // px/s² upward acceleration
  const FLIGHT_MAX   = 1350;  // max upward speed (px/s)
  const H_SPEED      = 210;   // horizontal nudge speed

  spaceship.linePhase = (spaceship.linePhase + dt * 1.6) % 1;

  if (spaceship.mode === 'entering') {
    spaceship.barT += dt;
    // Keep camera centered on the real ship during the enter animation
    const eSz = spaceshipRenderSize(spaceshipOffImg.naturalWidth > 0 ? spaceshipOffImg : spaceshipEquipImg, 240);
    const entCamX = spaceship.flightX + eSz.w / 2 - w / 2;
    const entCamY = h * 0.55 - spaceship.flightY - eSz.h / 2;
    cameraX += (entCamX - cameraX) * Math.min(1, dt * 10);
    cameraY += (entCamY - cameraY) * Math.min(1, dt * 10);
    cameraX = Math.max(0, cameraX);
    if (spaceship.barT >= spaceship.barDur) {
      spaceship.mode = 'occupied';
      spaceship.flightVY = -380; // initial burst
      spaceship.flightTimer = 0; // reset for takeoff shake
      spaceship.shakeX = 0; spaceship.shakeY = 0;
    }
    return;
  }

  if (spaceship.mode === 'occupied') {
    const sz = spaceshipRenderSize(spaceshipEquipImg, 240);

    // ── Base ascent: always accelerates upward, player has no speed control ──
    spaceship.flightVY = Math.max(spaceship.flightVY - FLIGHT_ACCEL * dt, -FLIGHT_MAX);
    spaceship.baseY   += spaceship.flightVY * dt;

    // ── Viewport offset: all 4 directions reposition ship on screen ──
    // (background/camera tracks baseY at constant rate; offset just moves ship within viewport)
    const VP_SPEED = 420;                   // px/s screen repositioning speed (snappy for dodging)
    const VP_MAX_X = w  * 0.38;            // max horizontal offset (world units)
    const VP_MAX_Y = h  * 0.44;            // max vertical offset (world units)
    if (input.leftHeld)  spaceship.vpOffX -= VP_SPEED * dt;
    if (input.rightHeld) spaceship.vpOffX += VP_SPEED * dt;
    if (input.jumpHeld)  spaceship.vpOffY -= VP_SPEED * dt;
    if (input.downHeld)  spaceship.vpOffY += VP_SPEED * dt;
    spaceship.vpOffX = Math.max(-VP_MAX_X, Math.min(VP_MAX_X, spaceship.vpOffX));
    spaceship.vpOffY = Math.max(-VP_MAX_Y, Math.min(VP_MAX_Y, spaceship.vpOffY));

    // Actual world position = base + viewport offset
    spaceship.flightX = spaceship.baseX + spaceship.vpOffX;
    spaceship.flightY = spaceship.baseY + spaceship.vpOffY;
    // Keep off the world's left edge. (Don't clamp to [0, w - sz.w]: baseX is the ship's
    // world-X, which is far from origin now that the ship spawns deep in the level — the old
    // screen-range clamp yanked the ship thousands of px away from the camera and hid it.
    // vpOffX is already bounded, so the ship stays on screen relative to the camera.)
    spaceship.flightX = Math.max(0, spaceship.flightX);

    // Sync player position to ship center
    player.x = spaceship.flightX + sz.w / 2 - player.w / 2;
    player.y = spaceship.flightY + sz.h / 2 - player.h / 2;
    player.vx = 0; player.vy = 0;

    // Spawn thruster particles from the ship's exhaust (bottom-center)
    spawnSpaceshipThrust(spaceship.flightX + sz.w / 2, spaceship.flightY + sz.h, dt);

    // Ship shake: brief at takeoff, nearly imperceptible during sustained flight
    spaceship.flightTimer = (spaceship.flightTimer || 0) + dt;
    const takeoffShake = Math.max(0, 1 - spaceship.flightTimer / 2.0); // fades over 2s
    const speedFrac    = Math.min(1, Math.abs(spaceship.flightVY) / FLIGHT_MAX);
    const shakeMag     = (takeoffShake * 1.8 + speedFrac * 0.35);
    const now          = performance.now ? performance.now() / 1000 : 0;
    spaceship.shakeX   = Math.sin(now * 47 + 1.1) * shakeMag * 0.5;
    spaceship.shakeY   = Math.sin(now * 61 + 2.3) * shakeMag;

    // Camera tracks baseY (constant ascent rate), not flightY — so viewport offsets don't jerk camera
    // Ship sits at 60% from top by default, showing more of what's approaching above
    const targetCamY = h * 0.60 - spaceship.baseY - sz.h / 2;
    cameraY += (targetCamY - cameraY) * Math.min(1, dt * 6);
    skyRenderer.setCloudCamY(cameraY);
    const targetCamX = spaceship.baseX + sz.w / 2 - w / 2;
    cameraX += (targetCamX - cameraX) * Math.min(1, dt * 5);
    cameraX = Math.max(0, cameraX);

    // Tick space debris spawner (screen-space; derive dims from canvas)
    {
      const dpr = canvas.__dpr || window.devicePixelRatio || 1;
      updateSpaceDebris(dt, canvas.width / dpr, canvas.height / dpr);
    }

    // Trigger moon transition once high enough
    if (spaceshipAltitude >= MOON_TRANSITION_ALT) {
      startMoonTransition();
      return;
    }

    // E to eject
    if (consumeDialogueInput()) {
      exitSpaceship();
    }
  }
}

/**
 * Called every frame. Tracks altitude from player position so atmosphere, clouds,
 * and platforms persist correctly after ejecting the spaceship.
 */
function updateAltitude(dt) {
  const groundTop = world.height() - world.groundH;
  // Use player centre (synced to ship during flight, falls naturally after eject)
  spaceshipAltitude = Math.max(0, groundTop - (player.y + player.h * 0.5));

  // Zone detection — only label when ascending into a new zone
  let newZone = 0;
  for (let i = ATMO_ZONES.length - 1; i >= 0; i--) {
    if (spaceshipAltitude >= ATMO_ZONES[i][1]) { newZone = i; break; }
  }
  if (newZone > spaceshipZoneIdx) {
    spaceshipZoneIdx = newZone;
    spaceshipZoneLabelText = 'ENTERING ' + ATMO_ZONES[newZone][0].toUpperCase();
    spaceshipZoneLabelT = 0;
  } else if (newZone < spaceshipZoneIdx) {
    // Descending — update index silently so overlay fades correctly
    spaceshipZoneIdx = newZone;
  }

  // Cloud visibility: normal below 8000px, gone by 22000px
  const cloudFade = 1 - Math.min(1, Math.max(0, (spaceshipAltitude - 8000) / 14000));
  skyRenderer.cloudVisibility = cloudFade;

  // Zone label timer
  if (spaceshipZoneLabelT < ZONE_LABEL_DUR) spaceshipZoneLabelT += dt;
}

// Tick the zone label timer (called every frame from update)
function tickSpaceshipZoneLabel(dt) {
  // now a no-op — logic moved into updateAltitude
}

/** Draw the anime cinematic speed-bar transition (screen-space). */
function drawSpaceshipBar(ctx, screenW, screenH) {
  const t = spaceship.barT;
  const BAR_H = Math.round(screenH * 0.13);
  const easeOut = v => 1 - (1 - Math.min(1, Math.max(0, v))) ** 3;

  let inP, outP;
  if (t < 0.30)      { inP = t / 0.30; outP = 0; }
  else if (t < 0.80) { inP = 1;        outP = 0; }
  else               { inP = 1;        outP = (t - 0.80) / 0.20; }
  inP  = easeOut(inP);
  outP = easeOut(outP);
  const vis = inP * (1 - outP);

  // Bar slides in from left edge (grows rightward) and out to the right
  const barW = screenW * inP - screenW * outP;
  if (barW <= 0 || vis <= 0) return;

  const barY = Math.round(screenH * 0.5 - BAR_H * 0.5);

  ctx.save();

  // Solid black bar across the center
  ctx.globalAlpha = vis;
  ctx.fillStyle = '#07070d';
  ctx.fillRect(0, barY, barW, BAR_H);

  // A handful of bright white speed lines inside the bar
  const lines = [0.20, 0.42, 0.58, 0.78];
  for (let i = 0; i < lines.length; i++) {
    const lp = (spaceship.linePhase + i * 0.25) % 1;
    const lx = lp * (screenW + 160) - 80;
    const lw = 40 + (i % 3) * 30;
    const ly = barY + Math.round(BAR_H * lines[i]);
    ctx.globalAlpha = (0.55 + (i % 2) * 0.25) * vis;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(lx, ly, lw, i === 1 ? 2 : 1);
  }

  ctx.restore();
}

// ── Moon transition ──────────────────────────────────────────────────────────

function startMoonTransition() {
  spaceship.mode = 'transitioning';
  const sz = spaceshipRenderSize(spaceshipEquipImg, 240);
  // Capture actual screen-space center of the ship (overlay coords = screenW x screenH).
  // The overlay uses this to start the ship at its real position and glide it to centre.
  const startShipSX = Math.round((spaceship.flightX + sz.w / 2 - cameraX) * VIEW_SCALE);
  const startShipSY = Math.round((spaceship.flightY + sz.h / 2 + cameraY) * VIEW_SCALE);
  moonTransition = { t: 0, startShipSX, startShipSY };
}

function updateMoonTransition(dt) {
  if (!moonTransition) return;
  moonTransition.t = Math.min(MOON_TRANS_DUR, moonTransition.t + dt);
  if (moonTransition.t >= MOON_TRANS_DUR) {
    moonTransition = null;
    spaceship.mode = 'idle';
    enterMoon();
  }
}

function drawMoonTransitionOverlay(ctx, w, h) {
  if (!moonTransition) return;
  const t = moonTransition.t;
  const p = t / MOON_TRANS_DUR;
  const sm = x => x * x * (3 - 2 * x);

  // Phase markers
  const ZOOM_END   = 0.42; // zoom-out / reveal completes
  const TRAVEL_S   = 0.36; // ship starts moving toward moon
  const TRAVEL_E   = 0.82;
  const FLIP_S     = 0.50; // ship starts rotating
  const FLIP_E     = 0.66;
  const ARRIVAL_S  = 0.74; // moon grows, earth fades
  const FADE_OUT_S = 0.87;

  // Dark space background fades in — starts transparent so flight view bleeds through
  const bgAlpha = sm(Math.min(1, p / 0.22));
  ctx.save();
  ctx.globalAlpha = bgAlpha;
  ctx.fillStyle = '#030308';
  ctx.fillRect(0, 0, w, h);
  ctx.restore();
  if (bgAlpha < 0.02) return;

  // Stars: fixed positions, start large+dim (close) and shrink to normal (far away)
  const zoomOutP = sm(Math.min(1, p / ZOOM_END));
  ctx.save();
  for (const s of SPACE_STARS) {
    const tw = Math.sin(t * s.tws + s.tw) * 0.5 + 0.5;
    // Fade in with bg, then brighten as we "zoom out"
    ctx.globalAlpha = bgAlpha * (0.25 + zoomOutP * 0.75) * s.br * (0.4 + 0.6 * tw);
    ctx.fillStyle = '#ffffff';
    // Stars start slightly larger (feels close) and settle to normal size
    const sz = s.r > 1.3 ? (zoomOutP < 1 ? 3 : 2) : (zoomOutP < 0.5 ? 2 : 1);
    ctx.fillRect(Math.round(s.x * w), Math.round(s.y * h), sz, sz);
  }
  ctx.globalAlpha = 1;
  ctx.restore();

  // glideP: drives Earth/Moon sliding in from edges (0→1 over first 52% of anim)
  const glideP = sm(Math.min(1, p / 0.52));
  // Earth fades out in the last third of the animation
  const earthFadeOut = sm(Math.max(0, Math.min(1, (p - 0.65) / 0.22)));

  // Earth — slides up from below viewport, starts large (close) and zooms out; stays settled
  if (transEarthImg.naturalWidth) {
    const eAsp = transEarthImg.naturalWidth / transEarthImg.naturalHeight;
    const eW = Math.round(w * (0.36 - glideP * 0.08));
    const eH = Math.round(eW / eAsp);
    const eY = (h + eH * 0.3) + ((h - eH * 0.52) - (h + eH * 0.3)) * glideP;
    ctx.save();
    ctx.globalAlpha = bgAlpha * glideP * (1 - earthFadeOut);
    ctx.drawImage(transEarthImg, Math.round(w / 2 - eW / 2), Math.round(eY), eW, eH);
    ctx.restore();
  }

  // Moon — slides down from above viewport, starts large (close) and zooms out; stays settled
  if (transMoonImg.naturalWidth) {
    const mAsp = transMoonImg.naturalWidth / transMoonImg.naturalHeight;
    const mW = Math.round(w * (0.32 - glideP * 0.08));
    const mH = Math.round(mW / mAsp);
    const mY = (-mH * 1.1) + ((-mH * 0.48) - (-mH * 1.1)) * glideP;
    ctx.save();
    ctx.globalAlpha = bgAlpha * glideP;
    ctx.drawImage(transMoonImg, Math.round(w / 2 - mW / 2), Math.round(mY), mW, mH);
    ctx.restore();
  }

  // Ship — starts matching actual flight render size (240*VIEW_SCALE), shrinks to ~41px
  const travelP   = sm(Math.max(0, Math.min(1, (p - TRAVEL_S) / (TRAVEL_E - TRAVEL_S))));
  const flipP     = sm(Math.max(0, Math.min(1, (p - FLIP_S)   / (FLIP_E   - FLIP_S))));
  const shipRenderW = Math.round(240 * VIEW_SCALE + (41 - 240 * VIEW_SCALE) * zoomOutP);
  const sz = spaceshipRenderSize(spaceshipEquipImg, shipRenderW);

  // Canonical animation positions (closer to Earth at start, stops at moon surface)
  const SHIP_START_Y = h * 0.68;
  const SHIP_END_Y   = h * 0.09;

  // Centering phase (p 0→0.22): ship glides from its actual screen position to w*0.5, SHIP_START_Y
  const centerP = sm(Math.min(1, p / 0.22));
  const startSX = moonTransition.startShipSX ?? Math.round(w * 0.5);
  const startSY = moonTransition.startShipSY ?? SHIP_START_Y;
  const shipX = Math.round(startSX + (w * 0.5 - startSX) * centerP);
  const centerPhaseY = startSY + (SHIP_START_Y - startSY) * centerP;
  const shipScreenY  = Math.round(centerPhaseY + (SHIP_END_Y - SHIP_START_Y) * travelP);

  // Full alpha during centering so ship stays visible as the overlay fades in over the world
  ctx.save();
  ctx.globalAlpha = p < 0.22 ? 1 : bgAlpha;
  ctx.translate(shipX, shipScreenY);
  ctx.rotate(flipP * Math.PI);
  if (spaceshipEquipReady && sz.w > 0) {
    ctx.drawImage(spaceshipEquipImg, Math.round(-sz.w / 2), Math.round(-sz.h / 2), sz.w, sz.h);
  }
  ctx.restore();

  // Fade to moon-surface grey at the very end
  if (p > FADE_OUT_S) {
    const fo = sm(Math.min(1, (p - FADE_OUT_S) / (1 - FADE_OUT_S)));
    ctx.save();
    ctx.globalAlpha = fo;
    ctx.fillStyle = '#b8b4a8';
    ctx.fillRect(0, 0, w, h);
    ctx.restore();
  }
}

// ── Moon level ───────────────────────────────────────────────────────────────

function enterMoon() {
  const h = world.height();
  const groundTop = h - world.groundH;
  insideMoon = true;
  // Fade sonic boom out as the ship descends, then resume normal music
  try { audioManager.fadeAudio(sonicBoomAudio, 0, 1800, AUDIO_KIND_MUSIC); } catch {}
  setTimeout(() => {
    try { sonicBoomAudio.pause(); sonicBoomAudio.currentTime = 0; } catch {}
    if (!musicMuted) resumeBackgroundMusic();
  }, 1900);
  // Park player off-screen during landing; it will be placed once the ship opens
  player.x = MOON_LAND_X - player.w / 2;
  player.y = groundTop - player.h;
  player.vx = 0; player.vy = 0; player.onGround = true;
  // Setup landing ship arriving from top
  const sz = spaceshipRenderSize(spaceshipEquipImg, 180);
  moonLanding = { phase: 'descend', t: 0, shipX: MOON_LAND_X, shipY: -sz.h - 40, shipVY: 90 };
  // Pre-position camera so ship lands and player spawns at the same screen X — no snap later
  cameraX = Math.max(0, MOON_LAND_X - world.width() * 0.33);
  cameraY = 0;
  skyRenderer.setCloudCamY(0);
}

function exitMoon() {
  insideMoon = false;
  moonLanding = null;
}

function updateMoonLanding(dt, w, h) {
  if (!moonLanding) return;
  const groundTop = h - world.groundH;
  const sz = spaceshipRenderSize(spaceshipEquipImg, 180);
  const landedY = groundTop - sz.h + 22;
  moonLanding.t += dt;

  if (moonLanding.phase === 'descend') {
    moonLanding.shipVY = Math.min(moonLanding.shipVY + 260 * dt, 340);
    moonLanding.shipY += moonLanding.shipVY * dt;
    if (moonLanding.shipY >= landedY) {
      moonLanding.shipY = landedY;
      moonLanding.shipVY = -60; // small bounce
      moonLanding.phase = 'bounce';
      moonLanding.t = 0;
    }
  } else if (moonLanding.phase === 'bounce') {
    moonLanding.shipVY += 360 * dt;
    moonLanding.shipY += moonLanding.shipVY * dt;
    if (moonLanding.shipY >= landedY) {
      moonLanding.shipY = landedY;
      moonLanding.phase = 'settle';
      moonLanding.t = 0;
    }
  } else if (moonLanding.phase === 'settle') {
    if (moonLanding.t >= 0.65) { moonLanding.phase = 'open'; moonLanding.t = 0; }
  } else if (moonLanding.phase === 'open') {
    if (moonLanding.t >= 0.5) {
      // Place player and let them hop out
      player.x = MOON_LAND_X - player.w / 2;
      player.y = groundTop - player.h;
      player.vx = 0; player.vy = -160;
      player.onGround = false;
      cameraX = Math.max(0, MOON_LAND_X - w * 0.33); // consistent with landing camera
      moonLanding = null;
    }
  }
}

function updateMoon(dt, w, h) {
  if (moonLanding) {
    updateMoonLanding(dt, w, h);
    updatePickupToasts(dt);
    return;
  }
  player.update(dt, input, {
    width: 1e7,
    height: h,
    groundH: world.groundH,
    doubleJump: hat.equipped,
    speedMul: world.speedMul || 1,
    clampGround: true,
    gravityMul: 0.32,
  });
  const groundY = h - world.groundH - player.h;
  if (player.y > groundY) { player.y = groundY; player.vy = 0; player.onGround = true; }
  // Deadzone scroll: same feel as normal level (scroll right at 50%, left at 33%)
  const moonScreenX = player.x - cameraX;
  const moonRightEdge = w * 0.50;
  const moonLeftEdge  = w * 0.33;
  let moonTargetCamX = cameraX;
  if (moonScreenX > moonRightEdge) {
    moonTargetCamX = Math.max(0, player.x - moonRightEdge);
  } else if (moonScreenX < moonLeftEdge) {
    moonTargetCamX = Math.max(0, player.x - moonLeftEdge);
  }
  cameraX += (moonTargetCamX - cameraX) * (1 - Math.exp(-8 * dt));
  cameraY = 0;
  skyRenderer.setCloudCamY(0);
  updatePickupToasts(dt);
}

const _moonRand = seed => { const x = Math.sin(seed + 1) * 10000; return x - Math.floor(x); };

function drawMoonScene(ctx, w, h) {
  const groundTop = h - world.groundH;

  // Sky — near-black with faint blue tint
  ctx.fillStyle = '#06060f';
  ctx.fillRect(0, 0, w, h);

  // Stars — two parallax layers
  ctx.save();
  for (let layer = 0; layer < 2; layer++) {
    const parallax = layer === 0 ? 0.04 : 0.12;
    const count = layer === 0 ? 80 : 40;
    const CHUNK = 700;
    const ox = cameraX * parallax;
    const startCI = Math.floor((ox - 80) / CHUNK);
    const endCI   = Math.ceil((ox + w + 80) / CHUNK);
    for (let ci = startCI; ci <= endCI; ci++) {
      for (let i = 0; i < count; i++) {
        const seed = ci * 997 + i * 17 + layer * 4999;
        const sx = Math.round(_moonRand(seed)       * CHUNK + ci * CHUNK - ox);
        const sy = Math.round(_moonRand(seed + 1)   * (groundTop - 20));
        const br = _moonRand(seed + 2);
        const big = br > 0.92;
        ctx.globalAlpha = 0.35 + _moonRand(seed + 3) * 0.65;
        ctx.fillStyle = br > 0.75 ? '#cce4ff' : '#ffffff';
        ctx.fillRect(sx, sy, big ? 2 : 1, big ? 2 : 1);
      }
    }
  }
  ctx.globalAlpha = 1;
  ctx.restore();

  // Ground body
  ctx.fillStyle = '#5c5852';
  ctx.fillRect(0, groundTop, w, world.groundH);

  // World-space elements
  ctx.save();
  ctx.translate(-cameraX, 0);

  // Dust surface strip
  ctx.fillStyle = '#b8b4a8';
  ctx.fillRect(cameraX, groundTop, w, 7);

  // Sub-surface texture bands
  for (let i = 0; i < 3; i++) {
    ctx.fillStyle = `rgba(20,18,16,${0.07 + i * 0.04})`;
    ctx.fillRect(cameraX, groundTop + 7 + i * 10, w, 9);
  }

  // Player — hidden while ship is landing; pops out once landing done
  if (!moonLanding) player.draw(ctx);

  // Landing ship
  if (moonLanding) {
    const sz = spaceshipRenderSize(spaceshipEquipImg, 180);
    const sx = Math.round(moonLanding.shipX - sz.w / 2);
    const sy = Math.round(moonLanding.shipY);
    // Fade out during 'open' phase
    const alpha = moonLanding.phase === 'open'
      ? Math.max(0, 1 - moonLanding.t / 0.5)
      : 1;
    if (spaceshipEquipReady && alpha > 0) {
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.drawImage(spaceshipEquipImg, sx, sy, sz.w, sz.h);
      ctx.restore();
    }
    // Dust puff on touchdown
    if (moonLanding.phase === 'settle' && moonLanding.t < 0.08) {
      ctx.save();
      ctx.globalAlpha = 0.35 * (1 - moonLanding.t / 0.08);
      ctx.fillStyle = '#c8c4b8';
      ctx.beginPath();
      ctx.ellipse(moonLanding.shipX, groundTop + 4, 38, 8, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }

  ctx.restore();
}

/**
 * Draw the atmospheric colour overlay (world-scale ctx, after sky is painted).
 * Darkens and tints the sky toward black as the ship climbs through atmosphere layers.
 */
// ── Space debris dodge minigame ──────────────────────────────────────────────
// Debris images: debris_1-6 only (no meteor)
const spaceDebrisImgsOnly = spaceDebrisImgs.slice(1); // drop meteor.png (index 0)

// ── Space debris: screen-space with parallax depth layers ──────────────────
// depth 0.3 = far background (small, dim, slow)
// depth 1.0 = close foreground (large, bright, fast)
const spaceDebris = [];
let debrisSpawnTimer = 0;

function updateSpaceDebris(dt, screenW, screenH) {
  if (spaceship.mode !== 'occupied') { spaceDebris.length = 0; debrisSpawnTimer = 0; return; }
  const alt = spaceshipAltitude;
  if (alt < 18000) { spaceDebris.length = 0; return; }

  // Spawn rate: 0.5/s at entry → 1.8/s deep in space (sparser than before)
  const rate = 0.5 + Math.min(1.3, (alt - 18000) / 64000 * 1.3);
  debrisSpawnTimer += dt;
  const interval = 1 / rate;
  while (debrisSpawnTimer >= interval) {
    debrisSpawnTimer -= interval;
    _spawnDebrisPiece(screenW, screenH);
  }

  // Update in screen space; remove when off bottom edge
  for (let i = spaceDebris.length - 1; i >= 0; i--) {
    const d = spaceDebris[i];
    d.sx  += d.svx * dt;
    d.sy  += d.svy * dt;
    d.rot += d.rotSpd * dt;
    if (d.sy > screenH + d.h + 20) { spaceDebris.splice(i, 1); }
  }
}

function _spawnDebrisPiece(screenW, screenH) {
  const imgs = spaceDebrisImgsOnly.filter(i => i.complete && i.naturalWidth > 0);
  if (!imgs.length) return;
  const img = imgs[Math.floor(Math.random() * imgs.length)];

  // 65% chance: deep background (tiny, very dim, slow, rare feel)
  // 35% chance: mid/close foreground (larger, visible, fast)
  const isBg = Math.random() < 0.65;
  const depth = isBg
    ? 0.12 + Math.random() * 0.18   // 0.12–0.30: very far back
    : 0.55 + Math.random() * 0.45;  // 0.55–1.00: mid-close

  // Wide size variance in background for depth feel; foreground is chunky
  const baseW = isBg ? 48 : 38;
  const sizeVar = isBg
    ? 0.25 + Math.random() * 0.55   // bg: 12–26px after depth scale
    : 0.70 + Math.random() * 0.80;  // fg: 27–68px
  const dw = Math.round(baseW * sizeVar * (isBg ? 1 : depth));
  const dh = img.naturalHeight > 0 ? Math.round(dw / (img.naturalWidth / img.naturalHeight)) : dw;

  // Foreground debris is fast; background drifts noticeably slower
  const baseSvy = isBg
    ? 70 + Math.random() * 50        // bg: 70–120 px/s (clearly slower than fg)
    : 480 + Math.random() * 260;     // fg: 480–740 px/s (rockets past)

  spaceDebris.push({
    img, depth, isBg,
    sx:     Math.random() * (screenW + 60) - 30,
    sy:     -dh - Math.random() * screenH * 0.20,
    svx:    (Math.random() - 0.5) * (isBg ? 6 : 30) * depth,
    svy:    baseSvy,
    rot:    Math.random() * Math.PI * 2,
    rotSpd: (Math.random() - 0.5) * (isBg ? 0.3 : 2.0),
    w:      Math.max(1, dw), h: Math.max(1, dh),
    alpha:  isBg ? 0.08 + 0.10 * depth : 0.55 + 0.35 * depth,
  });
}

// Called from screen-space draw section (after world-scale ctx.restore)
function drawSpaceDebris(ctx) {
  if (spaceDebris.length === 0) return;
  ctx.save();
  // Sort far-to-near so close pieces render on top
  const sorted = [...spaceDebris].sort((a, b) => a.depth - b.depth);
  for (const d of sorted) {
    if (!d.img.complete || !d.img.naturalWidth) continue;
    ctx.globalAlpha = d.alpha;
    ctx.save();
    ctx.translate(d.sx + d.w / 2, d.sy + d.h / 2);
    ctx.rotate(d.rot);
    ctx.drawImage(d.img, -d.w / 2, -d.h / 2, d.w, d.h);
    ctx.restore();
  }
  ctx.restore();
}

function drawSpaceMoon(ctx, w, h) {
  // Moon appears as a large background element at Mesosphere+ (36000px)
  if (spaceshipAltitude < 28000 || !spaceMoonImg.complete || !spaceMoonImg.naturalWidth) return;
  const vis = Math.min(1, (spaceshipAltitude - 28000) / 12000);
  const moonW = 180, moonH = moonW * (spaceMoonImg.naturalHeight / spaceMoonImg.naturalWidth || 1);
  // Positioned top-right, drifts very slightly with camera for parallax feel
  const mx = w * 0.72 + cameraX * 0.02;
  const my = -cameraY * 0.04 + h * 0.12;
  ctx.save();
  ctx.globalAlpha = vis * 0.82;
  ctx.drawImage(spaceMoonImg, mx, my, moonW, moonH);
  ctx.restore();
}

function drawSpaceshipAtmosphere(ctx, w, h) {
  if (spaceshipAltitude < 200) return;
  const alt = spaceshipAltitude;

  // Find bracketing zones
  let lo = 0;
  for (let i = ATMO_ZONES.length - 1; i >= 0; i--) {
    if (alt >= ATMO_ZONES[i][1]) { lo = i; break; }
  }
  const hi = Math.min(lo + 1, ATMO_ZONES.length - 1);
  const zLo = ATMO_ZONES[lo], zHi = ATMO_ZONES[hi];
  const span = zHi[1] - zLo[1] || 1;
  const t = Math.min(1, Math.max(0, (alt - zLo[1]) / span));
  const smooth = t * t * (3 - 2 * t); // smoothstep

  const alpha = zLo[3] + (zHi[3] - zLo[3]) * smooth;
  if (alpha < 0.004) return;

  const cLo = zLo[2] || [12, 25, 90];
  const cHi = zHi[2] || cLo;
  const r = Math.round(cLo[0] + (cHi[0] - cLo[0]) * smooth);
  const g = Math.round(cLo[1] + (cHi[1] - cLo[1]) * smooth);
  const b = Math.round(cLo[2] + (cHi[2] - cLo[2]) * smooth);

  ctx.save();
  ctx.globalAlpha = Math.min(1, alpha);
  ctx.fillStyle = `rgb(${r},${g},${b})`;
  ctx.fillRect(0, 0, w, h);
  ctx.restore();
}

/**
 * Draw star field and zone label (screen-space, after world-scale ctx is restored).
 */
function drawSpaceshipSpaceEffects(ctx, screenW, screenH) {
  if (spaceshipAltitude < 8000) return;
  const alt = spaceshipAltitude;

  // Stars fade in starting from Stratosphere entry (~18000), fully visible by Mesosphere (~36000)
  const starAlpha = Math.min(1, Math.max(0, (alt - 14000) / 22000));
  if (starAlpha > 0.005) {
    const now = performance.now ? performance.now() / 1000 : 0;
    ctx.save();
    for (const s of SPACE_STARS) {
      const twinkle = 0.75 + 0.25 * Math.sin(now * s.tws + s.tw);
      const a = starAlpha * s.br * twinkle;
      ctx.globalAlpha = a;
      ctx.fillStyle = '#e8f0ff';
      ctx.beginPath();
      ctx.arc(s.x * screenW, s.y * screenH, s.r, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  // Speed lines — vertical star-streaks shooting downward (objects rushing past as ship ascends)
  if (spaceship.mode === 'occupied') {
    const speedFrac = Math.min(1, Math.abs(spaceship.flightVY || 0) / 1350);
    if (speedFrac > 0.05) {
      const now = performance.now ? performance.now() / 1000 : 0;
      ctx.save();
      const lineCount = 14;
      for (let i = 0; i < lineCount; i++) {
        const seed   = i * 1.618;
        const lx     = ((seed % 1) * screenW);
        // Each streak scrolls top→bottom at its own speed; when it exits bottom it wraps to top
        const speed  = 1.4 + (i % 5) * 0.55;  // 1.4–3.6 screens/s
        const phase  = (now * speed + seed) % 1;
        const len    = 60 + (i % 6) * 30;      // 60–210px — long enough to read as streaks
        const sy     = phase * (screenH + len) - len;
        // Fade at leading/trailing ends so they pop in and out cleanly
        const fade   = Math.min(1, Math.min(phase * 8, (1 - phase) * 8));
        const bright = 0.4 + 0.6 * ((i % 3) / 2);
        ctx.globalAlpha = fade * speedFrac * 0.20 * bright;
        ctx.strokeStyle  = i % 4 === 0 ? '#ffffff' : '#c0d8ff';
        ctx.lineWidth    = i % 5 === 0 ? 1.2 : 0.6;
        ctx.beginPath();
        ctx.moveTo(lx, sy);
        ctx.lineTo(lx, sy + len);
        ctx.stroke();
      }
      ctx.restore();
    }
    // Draw parallax space debris (screen-space)
    drawSpaceDebris(ctx);
  }

  // Zone labels removed — atmosphere mood change is sufficient
}

/** Draw parked spaceship (idle, world-space context). */
function drawSpaceshipIdle(ctx, w, h) {
  if (spaceship.mode !== 'idle' || !spaceshipOffReady) return;
  const groundTop = h - world.groundH;
  const sz  = spaceshipRenderSize(spaceshipOffImg, 240);
  ctx.drawImage(spaceshipOffImg, spaceship.x, groundTop - sz.h + 48, sz.w, sz.h);
}

/** Draw ship in flight (occupied mode, world-space context). */
function drawSpaceshipEquipped(ctx) {
  if (!spaceshipEquipReady) return;
  const sz = spaceshipRenderSize(spaceshipEquipImg, 240);
  const sx = (spaceship.shakeX || 0);
  const sy = (spaceship.shakeY || 0);
  ctx.drawImage(spaceshipEquipImg, spaceship.flightX + sx, spaceship.flightY + sy, sz.w, sz.h);
  drawSpaceshipThrust(ctx);
}

/**
 * Draw ship + character hop animation during 'entering' mode (world-space context).
 * The bar overlay's gap shows this directly — no separate close-up needed.
 */
function drawSpaceshipEntering(ctx) {
  if (!spaceshipEquipReady) return;
  const sz = spaceshipRenderSize(spaceshipEquipImg, 240);
  ctx.drawImage(spaceshipEquipImg, spaceship.flightX, spaceship.flightY, sz.w, sz.h);
}

/** Draw empty shell drifting upward after eject (world-space context). */
function drawSpaceshipEmptyShell(ctx) {
  if (!spaceship.emptyActive || !spaceshipEmptyReady) return;
  const sz = spaceshipRenderSize(spaceshipEmptyImg);
  ctx.drawImage(spaceshipEmptyImg, spaceship.emptyX, spaceship.emptyY, sz.w, sz.h);
}

// --- Spaceship thruster particles ---
const thrustParticles = [];
function spawnSpaceshipThrust(cx, bottomY, dt) {
  // ~55 particles/s, concentrated in a tight exhaust cone
  const count = Math.round(55 * dt + Math.random());
  for (let i = 0; i < count; i++) {
    const spread = 18 + Math.random() * 16;
    thrustParticles.push({
      x:    cx + (Math.random() - 0.5) * spread,
      y:    bottomY + Math.random() * 4,
      vx:   (Math.random() - 0.5) * 22,
      vy:   70 + Math.random() * 130,  // downward in world space
      life: 0.30 + Math.random() * 0.30,
      age:  0,
      r:    1.5 + Math.random() * 3.5,
      // fire hue: 0=white-core, 1=yellow, 2=orange, 3=deep-red
      tier: Math.random() < 0.15 ? 0 : (Math.random() < 0.35 ? 1 : (Math.random() < 0.55 ? 2 : 3)),
    });
  }
}
function updateThrustParticles(dt) {
  for (let i = thrustParticles.length - 1; i >= 0; i--) {
    const p = thrustParticles[i];
    p.age += dt;
    if (p.age >= p.life) { thrustParticles.splice(i, 1); continue; }
    p.x  += p.vx * dt;
    p.y  += p.vy * dt;
    p.vy += 200 * dt; // gravity widens exhaust plume
    p.r   = Math.max(0.2, p.r - 5 * dt);
  }
}
function drawSpaceshipThrust(ctx) {
  if (thrustParticles.length === 0) return;
  ctx.save();
  for (const p of thrustParticles) {
    const t = p.age / p.life; // 0=fresh, 1=dead
    // Colour transitions from white/yellow at birth → deep orange/red at death
    const alpha = (1 - t) ** 1.2 * 0.90;
    switch (p.tier) {
      case 0: ctx.fillStyle = `rgba(255,255,220,${alpha})`; break;   // white-hot core
      case 1: ctx.fillStyle = `hsla(42,100%,65%,${alpha})`; break;   // bright yellow-orange
      case 2: ctx.fillStyle = `hsla(22,100%,55%,${alpha})`; break;   // orange
      default: ctx.fillStyle = `hsla(5,95%,45%,${alpha})`;  break;   // deep red
    }
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function updateCaveInterior(dt, w, h) {
  const interiorW = Math.max(CAVE_INTERIOR_WIDTH, w, 280);
  const groundH = world.groundH;

  // Sword swing disabled in cave to prevent bugs
  // if (sword.equipped && consumeWorldInteract()) {
  //   swordSwingActive = true; swordSwingT = 0; swordSwingHit = false;
  //   swordDashActive = true;
  //   player.vx = player.facing * SWORD_DASH_SPEED;
  //   swordSlashArc = { startT: performance.now ? performance.now() : Date.now(), facing: player.facing };
  //   particles.spawnSparks(player.x + player.w / 2, player.y + player.h / 2, 6, ['#7dd3fc', '#38bdf8', '#0ea5e9']);
  // }

  // During sword dash, temporarily suppress horizontal input to prevent walk acceleration from overriding dash
  const savedLeftHeld = input.leftHeld;
  const savedRightHeld = input.rightHeld;
  if (swordDashActive) {
    input.leftHeld = false;
    input.rightHeld = false;
  }

  player.update(dt, input, { width: interiorW, height: h, groundH, doubleJump: hat.equipped, speedMul: world.speedMul || 1, clampGround: true });

  // Restore input state
  if (swordDashActive) {
    input.leftHeld = savedLeftHeld;
    input.rightHeld = savedRightHeld;
  }

  // Re-apply dash velocity after player.update
  if (swordDashActive) {
    player.vx = player.facing * SWORD_DASH_SPEED;
  }

  const minX = 12;
  const maxX = interiorW - player.w - 12;
  if (player.x < minX) player.x = minX;
  if (player.x > maxX) player.x = maxX;
  const groundY = h - groundH - player.h;
  if (player.y > groundY) { player.y = groundY; player.vy = 0; player.onGround = true; }
  cameraX = 0; cameraY = 0;
  skyRenderer.setCloudCamY(cameraY);
  const groundTop = h - groundH;
  const playerRect = { x: player.x, y: player.y, w: player.w, h: player.h };
  const playerCenterX = player.x + player.w * 0.5;
  cavePromptMessage = '';
  cavePromptAnchor = null;
  caveHintMarkers = [];

  // Update pickup toasts in cave
  updatePickupToasts(dt);

  // Knight dialogue
  const knightAspect = knightImg.naturalWidth && knightImg.naturalHeight ? (knightImg.naturalWidth / knightImg.naturalHeight) : 1;
  const knightH = caveKnight.targetH;
  const knightW = knightH * knightAspect;
  const knightScale = knightImg.naturalHeight ? (knightH / knightImg.naturalHeight) : 1;
  const knightBottomOffset = knightBounds ? (knightBounds.bottom || 0) * knightScale : 0;
  const knightRect = {
    x: caveKnight.x - knightW / 2,
    y: groundTop - knightH + knightBottomOffset,
    w: knightW,
    h: knightH
  };
  caveKnight._rect = knightRect; // Save for dialogue positioning
  caveKnight.overlap = rectsOverlap(playerRect, knightRect);

  // Puzzle interaction intent
  let puzzleIntent = null;
  const carriedItem = cavePuzzleState.carriedItemId ? findPuzzleItemById(cavePuzzleState.carriedItemId) : null;
  if (carriedItem) {
    let nearestPed = null;
    let nearestDist = Infinity;
    cavePuzzleState.pedestals.forEach((p, idx) => {
      const dx = Math.abs(playerCenterX - p.x);
      if (dx < 70 && dx < nearestDist) { nearestDist = dx; nearestPed = idx; }
    });
    if (nearestPed != null) {
      puzzleIntent = { type: 'place', pedestalIndex: nearestPed, itemId: carriedItem.id };
    } else {
      puzzleIntent = { type: 'drop', itemId: carriedItem.id };
    }
  } else {
    let best = null;
    let bestDist = Infinity;
    for (const item of cavePuzzleState.items) {
      if (item.state === 'carried') continue;
      if (item.locked) continue;
      const rect = getPuzzleItemRect(item, groundTop);
      if (!rectsOverlap(rect, playerRect)) continue;
      const dx = Math.abs((rect.x + rect.w / 2) - playerCenterX);
      if (dx < bestDist) { bestDist = dx; best = item; }
    }
    if (best) {
      puzzleIntent = { type: 'pickup', itemId: best.id };
    }
  }

  // Reward intent
  let rewardIntent = null;
  for (const reward of caveRewardPickups) {
    if (reward.picked) continue;
    let img, targetW;
    if (reward.kind === 'pickaxe') {
      img = pickaxeImg;
      targetW = 40;
    } else if (reward.kind === 'sword') {
      img = swordImg;
      targetW = 44;
    }
    if (!img || !img.naturalWidth) continue;
    const aspect = img.naturalWidth / img.naturalHeight;
    const dw = targetW;
    const dh = Math.max(1, Math.round(targetW / aspect));
    const rect = { x: reward.x - dw / 2, y: groundTop - dh, w: dw, h: dh };
    if (rectsOverlap(rect, playerRect)) {
      rewardIntent = { kind: reward.kind, reward };
      break;
    }
  }

  // Exit cave when near left edge
  caveExitOverlap = player.x <= minX + 36 && player.onGround;

  // Allow knight interaction even when rewards are present (player can talk to knight anytime)
  const knightIntent = caveKnight.overlap && !puzzleIntent && !dialogueState.active;

  // Prompt priority: exit > knight > reward > puzzle
  // Knight gets priority when actively overlapping so player can always talk to them
  if (caveExitOverlap) cavePromptMessage = 'Exit Cave (E)';
  else if (knightIntent) {
    cavePromptMessage = 'Talk (E)';
    cavePromptAnchor = { x: caveKnight.x, y: knightRect.y - 12 };
  }
  else if (rewardIntent) {
    // Show descriptive pickup message based on reward type
    if (rewardIntent.kind === 'pickaxe') cavePromptMessage = 'Pickup Pickaxe (S)';
    else if (rewardIntent.kind === 'sword') cavePromptMessage = 'Pickup Sword (S)';
    else cavePromptMessage = 'Pickup (S)';
  }
  else if (puzzleIntent) cavePromptMessage = '';
  else cavePromptMessage = '';

  // Handle reward pickup with S key (like other equippable items)
  // Use input.consumeInteract() directly to bypass dialogue cooldown
  if (rewardIntent && input.consumeInteract()) {
    onPlayerInteracted(); // Trigger interaction tracking
    const r = rewardIntent.reward;
    r.picked = true;
    if (rewardIntent.kind === 'pickaxe') {
      pickaxe.picked = true;
      equipWeapon('pickaxe');
      addPickupToast('pickaxe');
    } else if (rewardIntent.kind === 'sword') {
      sword.picked = true;
      equipWeapon('sword');
      addPickupToast('sword');
    }
    updateInventoryUI();
    return;
  }

  // Handle E interactions in priority order (exit, puzzle, knight)
  // Bypass dialogue cooldown for non-dialogue interactions (exit, puzzle)
  const hasInteractInput = !dialogueState.active && (input.hasKeyboard ? input.consumeDialogue() : input.consumeInteract());
  if (hasInteractInput) {
    // Consume the corresponding interact input to prevent double-triggers
    if (input.hasKeyboard) input.consumeInteract();
    if (caveExitOverlap) {
      exitSurfaceCave();
      return;
    }
    if (puzzleIntent) {
      if (puzzleIntent.type === 'pickup') {
        const item = findPuzzleItemById(puzzleIntent.itemId);
        if (item) {
          if (item.state === 'pedestal' && typeof item.pedestalIndex === 'number') {
            const ped = cavePuzzleState.pedestals[item.pedestalIndex];
            if (ped) ped.itemId = null;
          }
          item.state = 'carried';
          item.pedestalIndex = null;
          cavePuzzleState.carriedItemId = item.id;
          if (!cavePuzzleState.solved) checkCavePuzzleSolved();
        }
      } else if (puzzleIntent.type === 'drop') {
        const item = findPuzzleItemById(puzzleIntent.itemId);
        if (item) {
          item.state = 'ground';
          item.pedestalIndex = null;
          item.x = Math.max(minX + 20, Math.min(interiorW - 20, playerCenterX));
          cavePuzzleState.carriedItemId = null;
        }
      } else if (puzzleIntent.type === 'place') {
        const item = findPuzzleItemById(puzzleIntent.itemId);
        const ped = cavePuzzleState.pedestals[puzzleIntent.pedestalIndex];
        if (item && ped) {
          let swapId = null;
          if (ped.itemId && ped.itemId !== item.id) swapId = ped.itemId;
          ped.itemId = item.id;
          item.state = 'pedestal';
          item.pedestalIndex = puzzleIntent.pedestalIndex;
          item.x = ped.x;
          if (swapId) {
            const swapItem = findPuzzleItemById(swapId);
            if (swapItem) {
              swapItem.state = 'carried';
              swapItem.pedestalIndex = null;
              cavePuzzleState.carriedItemId = swapItem.id;
            }
          } else {
            cavePuzzleState.carriedItemId = null;
          }
          if (!cavePuzzleState.solved) checkCavePuzzleSolved();
          if (cavePuzzleState.solved) {
            spawnCaveRewards(groundTop);
            caveKnight.praised = true;
          }
        }
      }
      return;
    }
    if (knightIntent && dialogueState.cooldown <= 0) {
      let msg;
      if (cavePuzzleState.solved) {
        msg = knightDialogue.solved;
      } else if (cavePuzzleState.pedestals.some(p => p.itemId)) {
        msg = getKnightHintMessage();
      } else {
        msg = knightDialogue.intro;
      }
      startDialogue('cave_knight', [msg || '...']);
    }
  }

  if (!controlGuideState.interact) {
    if (caveExitOverlap) showControlGuide('interact', 'Press E to interact');
    else if (controlGuideActive === 'interact') hideControlGuide('interact');
  }
}

function drawDeepForestDebug(ctx, screenW, screenH) {
  const msg = deepForestDebugMsg || '';
  if (!msg) return;
  const fadeIn = Math.min(1, deepForestDebugTimer / 0.8);
  const fadeOut = Math.min(1, Math.max(0, (1.8 - deepForestDebugTimer) / 1.0));
  const alpha = Math.max(0, Math.min(1, fadeIn * fadeOut));
  if (alpha <= 0) return;
  ctx.save();
  ctx.globalAlpha = alpha;
  // Smaller font to show it's a subpart of Sundered Grove
  ctx.font = '22px Merriweather, Georgia, serif';
  // Similar styling to Sundered Grove but with a forest-green tint
  ctx.fillStyle = 'rgba(200, 240, 210, 0.92)';
  const textW = Math.ceil(ctx.measureText(msg).width);
  const x = Math.round((screenW - textW) / 2);
  const y = Math.round(screenH * 0.18);
  ctx.fillText(msg, x, y);
  ctx.restore();
}

function drawPlayerPosDebug(ctx, screenW) {
  if (!SHOW_DEBUG_UI) return;
  const msg = `X: ${player.x.toFixed(1)}  Y: ${player.y.toFixed(1)}  camX: ${cameraX.toFixed(1)} camY: ${cameraY.toFixed(1)}`;
  ctx.save();
  ctx.font = '14px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  const padX = 10;
  const padY = 6;
  const textW = Math.ceil(ctx.measureText(msg).width);
  const boxW = textW + padX * 2;
  const boxH = 24 + padY * 2;
  const x = Math.round(screenW / 2 - boxW / 2);
  const y = 8;
  ctx.fillStyle = 'rgba(0,0,0,0.65)';
  ctx.strokeStyle = 'rgba(255,255,255,0.35)';
  ctx.lineWidth = 1;
  roundRectPath(ctx, x, y, boxW, boxH, 10);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#d8f5ff';
  ctx.fillText(msg, x + padX, y + padY + 18);
  ctx.restore();
}

function rockTitanFrameInfo(attentive) {
  const img = (attentive && rockTitanAwareReady) ? rockTitanAwareImg : rockTitanBaseImg;
  const bounds = (attentive && rockTitanAwareReady) ? rockTitanAwareBounds : rockTitanBaseBounds;
  if (!img || !img.naturalWidth || !img.naturalHeight) return null;
  const aspect = img.naturalWidth / img.naturalHeight;
  const height = ROCK_TITAN_TARGET_H;
  const width = Math.max(10, Math.round(height * aspect));
  const bottomOffset = bounds && img.naturalHeight ? Math.round((bounds.bottom || 0) * (height / img.naturalHeight)) : 0;
  return { img, width, height, bottomOffset };
}

function drawRockTitan(ctx, w, h) {
  if (!rockTitanBaseReady) return;
  const info = rockTitanFrameInfo(rockTitan.attentive);
  if (!info) return;
  const groundTop = h - world.groundH;
  const x = Math.round(rockTitan.x - info.width / 2);
  if (x + info.width < cameraX - 600 || x > cameraX + w + 600) return;
  const y = Math.round(groundTop - info.height + info.bottomOffset + ROCK_TITAN_SINK);
  ctx.drawImage(info.img, x, y, info.width, info.height);
}

// --- Toolbox (Builder item) ---
const SPAWN_TOOLBOX = false; // Now a reward from the goblin family
const TOOLBOX_SPAWN_X = 1180; // after raygun
const toolbox = { x: TOOLBOX_SPAWN_X, picked: true, spawned: false, spawnTime: 0, floatPhase: Math.random() * Math.PI * 2 }; // Not spawned at start - reward from goblin family
const toolboxImg = new Image();
let toolboxReady = false; let toolboxBounds = null; toolboxImg.onload = () => { toolboxReady = true; toolboxBounds = computeImageOpaqueBounds(toolboxImg); };
toolboxImg.src = 'img_assets/toolbox.png';
let toolboxOverlap = false;

function advanceFloatingItem(item, dt, speed = 1) {
  if (!item || item.picked || item.spawned === false) return;
  item.spawnTime = (item.spawnTime || 0) + dt;
  const loop = Math.PI * 2;
  item.floatPhase = (item.floatPhase || 0) + dt * speed;
  if (item.floatPhase > loop) item.floatPhase -= loop * Math.floor(item.floatPhase / loop);
}

function spawnJetbackAt(x) {
  if (jetback.picked) return;
  jetback.spawned = true;
  jetback.x = x;
  jetback.spawnTime = 0;
  jetback.floatPhase = Math.random() * Math.PI * 2;
}

function drawPickaxe(ctx, w, h, groundH) {
  if (!pickaxeReady) return;
  const targetW = 42;
  const aspect = pickaxeImg.naturalWidth && pickaxeImg.naturalHeight ? (pickaxeImg.naturalWidth / pickaxeImg.naturalHeight) : 1;
  const dw = targetW;
  const dh = Math.max(1, Math.round(targetW / aspect));
  const groundTop = h - groundH;
  const fade = Math.max(0, Math.min(1, (pickaxe.spawnTime || 0) / 0.75));
  if (fade <= 0.01) return;
  const bob = Math.sin((pickaxe.floatPhase || 0) * 2.4) * 3 - 3;
  const x = Math.round(pickaxe.x - dw / 2);
  const y = Math.round(groundTop - dh + bob);
  if (x + dw < cameraX - 80 || x > cameraX + w + 80) return;
  ctx.save();
  ctx.globalAlpha = fade;
  ctx.shadowColor = 'rgba(150,200,255,0.35)';
  ctx.shadowBlur = 12 * fade;
  ctx.drawImage(pickaxeImg, x, y, dw, dh);
  ctx.restore();
}

function drawHeldPickaxe(ctx) {
  if (!pickaxeReady) return;
  const targetW = 30;
  const aspect = pickaxeImg.naturalWidth && pickaxeImg.naturalHeight ? (pickaxeImg.naturalWidth / pickaxeImg.naturalHeight) : 1;
  const dw = targetW;
  const dh = Math.max(1, Math.round(targetW / aspect));

  // --- PICKAXE POSITION ADJUSTMENTS ---
  // inward: pulls pickaxe toward character's center (increase = closer to body, decrease = farther out)
  const inward = dw * 0.50;
  // sideNudge: shifts pickaxe left/right (increase = farther from center, decrease = closer to center)
  const sideNudge = dw * 0.2;
  const baseX = player.facing === 1
    ? (player.x + player.w - inward - sideNudge)
    : (player.x - dw + inward + sideNudge);
  // baseY: vertical position (0.0 = top of character, 0.5 = middle, 1.0 = feet)
  // Currently 0.45 = slightly above middle (hand level)
  let baseY = player.y + player.h * 0.1;

  const handleOX = dw * 0.25;
  const handleOY = dh * 0.88;
  const flip = player.facing === 1;
  const pivotOX = flip ? (dw - handleOX) : handleOX;

  // --- PICKAXE ANGLE ADJUSTMENTS ---
  const idleTime = (typeof performance !== 'undefined' ? performance.now() : Date.now());
  const idleSway = Math.sin(idleTime / 280) * 0.08;
  // baseAngle: idle angle (higher value = more vertical, lower = more horizontal)
  // 0.35 = diagonal pointing down-forward
  let baseAngle = Math.PI * 0.1 + idleSway;

  if (pickaxeDigAnim > 0) {
    const swing = Math.sin(pickaxeDigTimer * 18) * 0.4 * pickaxeDigAnim;
    if (pickaxeDigMode === 'down') {
      // Down dig: angle (0.75 = pointing downward), Y offset (higher = lower on screen)
      baseAngle = Math.PI * -0.2 + swing;
      baseY += pickaxeDigAnim * 6;
    } else if (pickaxeDigMode === 'horizontal') {
      // Horizontal dig: angle (0.4 = diagonal), Y offset
      baseAngle = Math.PI * 0.4 + swing;
      baseY += pickaxeDigAnim * 2;
    } else if (pickaxeDigMode === 'up') {
      // Up dig: angle (0.05 = pointing upward), Y offset (negative = higher on screen)
      baseAngle = Math.PI * 0.05 + swing;
      baseY -= pickaxeDigAnim * 6;
    }
  }

  const angle = (player.facing === -1)
    ? -(baseAngle)
    : (baseAngle);

  ctx.save();
  ctx.translate(Math.round(baseX + pivotOX), Math.round(baseY + handleOY));
  ctx.rotate(angle);
  if (flip) ctx.scale(-1, 1);
  ctx.shadowColor = 'rgba(0,0,0,0.25)';
  ctx.shadowBlur = 3;
  ctx.drawImage(pickaxeImg, -pivotOX, -handleOY, dw, dh);
  ctx.restore();
}

function drawSword(ctx, w, h, groundH) {
  if (!swordReady) return;
  const targetW = 40;
  const aspect = swordImg.naturalWidth && swordImg.naturalHeight ? (swordImg.naturalWidth / swordImg.naturalHeight) : 1;
  const dw = targetW;
  const dh = Math.max(1, Math.round(targetW / aspect));
  const groundTop = h - groundH;
  const fade = Math.max(0, Math.min(1, (sword.spawnTime || 0) / 0.75));
  if (fade <= 0.01) return;
  const bob = Math.sin((sword.floatPhase || 0) * 2.4) * 3 - 3;
  const x = Math.round(sword.x - dw / 2);
  const y = Math.round(groundTop - dh + bob);
  if (x + dw < cameraX - 80 || x > cameraX + w + 80) return;
  ctx.save();
  ctx.globalAlpha = fade;
  ctx.shadowColor = 'rgba(150,200,255,0.35)';
  ctx.shadowBlur = 12 * fade;
  ctx.drawImage(swordImg, x, y, dw, dh);
  ctx.restore();
}

function drawRaygun(ctx, w, h, groundH) {
  if (!raygunReady) return;
  const targetW = 42;
  const aspect = raygunImg.naturalWidth && raygunImg.naturalHeight ? (raygunImg.naturalWidth / raygunImg.naturalHeight) : 1;
  const dw = targetW;
  const dh = Math.max(1, Math.round(targetW / aspect));
  const groundTop = h - groundH;
  const fade = Math.max(0, Math.min(1, (raygun.spawnTime || 0) / 0.75));
  if (fade <= 0.01) return;
  const bob = Math.sin((raygun.floatPhase || 0) * 2.4) * 3 - 3;
  const x = Math.round(raygun.x - dw / 2);
  const y = Math.round(groundTop - dh + bob);
  if (x + dw < cameraX - 80 || x > cameraX + w + 80) return;
  ctx.save();
  ctx.globalAlpha = fade;
  ctx.shadowColor = 'rgba(150,200,255,0.35)';
  ctx.shadowBlur = 12 * fade;
  ctx.drawImage(raygunImg, x, y, dw, dh);
  ctx.restore();
}

function drawToolbox(ctx, w, h, groundH) {
  if (!toolboxReady || !toolbox.spawned) return;
  const targetW = 46;
  const aspect = toolboxImg.naturalWidth && toolboxImg.naturalHeight ? (toolboxImg.naturalWidth / toolboxImg.naturalHeight) : 1;
  const dw = targetW;
  const dh = Math.max(1, Math.round(targetW / aspect));
  const groundTop = h - groundH;
  const fade = Math.max(0, Math.min(1, (toolbox.spawnTime || 0) / 0.75));
  if (fade <= 0.01) return;
  const bob = Math.sin((toolbox.floatPhase || 0) * 2.4) * 3 - 3;
  const x = Math.round(toolbox.x - dw / 2);
  let y = Math.round(groundTop - dh + bob);
  if (toolboxBounds && toolboxImg.naturalHeight) {
    const scaleY = dh / toolboxImg.naturalHeight;
    y += Math.round((toolboxBounds.bottom || 0) * scaleY);
  }
  if (x + dw < cameraX - 80 || x > cameraX + w + 80) return;
  ctx.save();
  ctx.globalAlpha = fade;
  ctx.shadowColor = 'rgba(150,200,255,0.35)';
  ctx.shadowBlur = 12 * fade;
  ctx.drawImage(toolboxImg, x, y, dw, dh);
  ctx.restore();
}

function drawPlantTraps(ctx, w, h) {
  if (!plantTraps.length) return;
  const groundTop = h - world.groundH;
  for (const trap of plantTraps) {
    if (trap.dead) continue;
    const info = plantTrapFrameInfo(trap.frame);
    const left = trap.x - info.width / 2;
    const right = trap.x + info.width / 2;
    if (right < cameraX - 200 || left > cameraX + w + 200) continue;
    const drawX = Math.round(left);
    const drawY = Math.round(groundTop - info.height + info.bottomOffset);
    if (info.img && info.img.naturalWidth && info.img.naturalHeight) {
      ctx.save();
      ctx.translate(Math.round(trap.x), drawY);
      if (trap.facing === -1) {
        ctx.scale(-1, 1);
      }
      ctx.drawImage(info.img, Math.round(-info.width / 2), 0, Math.round(info.width), Math.round(info.height));
      ctx.restore();
    } else {
      ctx.fillStyle = '#3b8f58';
      ctx.fillRect(drawX, drawY, Math.round(info.width), Math.round(info.height));
    }
  }
}

function drawHeldRaygun(ctx) {
  if (!raygunReady) return;
  const targetW = 32;
  const aspect = raygunImg.naturalWidth && raygunImg.naturalHeight ? (raygunImg.naturalWidth / raygunImg.naturalHeight) : 1;
  const dw = targetW;
  const dh = Math.max(1, Math.round(targetW / aspect));
  const handY = player.y + player.h * 0.56;
  const inward = dw * 0.18;
  const rightNudge = dw * 0.35;
  const offsetX = (player.facing === 1)
    ? (player.w - inward - rightNudge)
    : (-dw + inward);
  const px = player.x + offsetX;
  const py = handY - dh * 0.25;
  ctx.save();
  const flip = (player.facing === 1);
  if (flip) {
    ctx.translate(Math.round(px + dw), Math.round(py));
    ctx.scale(-1, 1);
    ctx.drawImage(raygunImg, 0, 0, dw, dh);
  } else {
    ctx.translate(Math.round(px), Math.round(py));
    ctx.drawImage(raygunImg, 0, 0, dw, dh);
  }
  ctx.restore();
}

function drawHeldBuilder(ctx) {
  if (!toolboxReady) return;
  const targetW = 30;
  const aspect = toolboxImg.naturalWidth && toolboxImg.naturalHeight ? (toolboxImg.naturalWidth / toolboxImg.naturalHeight) : 1;
  const dw = targetW;
  const dh = Math.max(1, Math.round(targetW / aspect));
  const handY = player.y + player.h * 0.52;
  const inward = dw * 0.18;
  const rightNudge = dw * 0.30;
  const offsetX = (player.facing === 1)
    ? (player.w - inward - rightNudge)
    : (-dw + inward);
  const px = player.x + offsetX;
  const py = handY - dh * 0.35;
  ctx.save();
  const flip = (player.facing === 1);
  if (flip) {
    ctx.translate(Math.round(px + dw), Math.round(py));
    ctx.scale(-1, 1);
    ctx.drawImage(toolboxImg, 0, 0, dw, dh);
  } else {
    ctx.translate(Math.round(px), Math.round(py));
    ctx.drawImage(toolboxImg, 0, 0, dw, dh);
  }
  ctx.restore();
}

function spawnBlast() {
  const y = player.y + player.h * 0.48;
  const x = player.facing === 1 ? (player.x + player.w) : player.x;
  const dir = player.facing === 1 ? 1 : -1;
  const r = 3;
  blasts.push({ x, y, vx: dir * RAYGUN_SPEED, life: 0, dur: 0.6, r });
}

function updateBlasts(dt, w, h) {
  raygunCooldown = Math.max(0, raygunCooldown - dt);
  // Move and collide
  const groundTopY = h - world.groundH;
  const stepPx = 4;
  const next = [];
  for (const b of blasts) {
    b.life += dt;
    const maxMove = Math.abs(b.vx * dt);
    let moved = 0;
    let x = b.x, y = b.y;
    let alive = (b.life < b.dur);
    while (alive && moved < maxMove) {
      const dx = Math.min(stepPx, maxMove - moved) * Math.sign(b.vx);
      x += dx; moved += Math.abs(dx);
      // collide with tiles only underground region
      // sample a small rect around the blast
      const rx = x - b.r, ry = y - b.r, rw = b.r * 2, rh = b.r * 2;
      if (rectOverlapsWorldSolid(w, h, rx, ry, rw, rh)) {
        particles.spawnSparks(x, y, 6);
        alive = false;
      }
      // collide with enemies; spiders die, titans take damage
      if (alive && enemies && enemies.length) {
        for (const e of enemies) {
          if (e.dead || (e.kind !== 'spider' && e.kind !== 'titan')) continue;
        const hb = getEnemyHitbox(e);
        if (x + b.r < hb.x || x - b.r > hb.x + hb.w || y + b.r < hb.y || y - b.r > hb.y + hb.h) continue;
          // Raygun damage: 1 per hit
          damageEnemy(e, 1, 'raygun');
          particles.spawnSparks(x, y, 8);
          alive = false; // blast dissipates on impact
          break;
        }
      }
      if (alive && hitCorruptTitanByProjectile(x, y, b.r, CORRUPT_TITAN_RAYGUN_DAMAGE)) {
        particles.spawnSparks(x, y, 8);
        alive = false;
      }
      // stop if very far off-screen
      if (x < cameraX - 200 || x > cameraX + w + 200) alive = false;
    }
    if (alive) { b.x = x; b.y = y; next.push(b); }
  }
  blasts = next;
}

function drawBlasts(ctx) {
  if (!blasts.length) return;
  ctx.save();
  for (const b of blasts) {
    // Do not fade out visually; always render at full alpha
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#66e3ff';
    ctx.beginPath();
    ctx.arc(Math.round(b.x), Math.round(b.y), b.r, 0, Math.PI * 2);
    ctx.fill();
    // small trailing streak (also full alpha)
    ctx.fillRect(Math.round(b.x - Math.sign(b.vx) * b.r * 2), Math.round(b.y - 1), Math.max(1, b.r * 2), 2);
  }
  ctx.restore();
  ctx.globalAlpha = 1;
}

// --- Sky background: gradient + sun + subtle noise ---
let skyNoisePattern = null;
function ensureSkyNoisePattern() {
  if (skyNoisePattern) return;
  const S = 144;
  const cv = document.createElement('canvas');
  cv.width = S; cv.height = S;
  const c = cv.getContext('2d');
  const img = c.createImageData(S, S);
  for (let i = 0; i < img.data.length; i += 4) {
    const v = (Math.random() * 35) | 0; // 0..35
    img.data[i + 0] = 10 + v; // bluish noise
    img.data[i + 1] = 16 + v;
    img.data[i + 2] = 28 + v;
    img.data[i + 3] = 12; // lower alpha per texel
  }
  c.putImageData(img, 0, 0);
  skyNoisePattern = c.createPattern(cv, 'repeat');
}
/**
 * Draws the sky background with gradient, sun, clouds, and atmospheric effects
 * @param {CanvasRenderingContext2D} ctx - The canvas rendering context
 * @param {number} w - Canvas width
 * @param {number} h - Canvas height
 */
function drawSkyBackground(ctx, w, h) {
  const groundTopScreen = (h - world.groundH) + cameraY;
  const grad = ctx.createLinearGradient(0, 0, 0, groundTopScreen);
  grad.addColorStop(0.0, '#5aa7e6');
  grad.addColorStop(0.5, '#4b8fd0');
  grad.addColorStop(1.0, '#2f5e9a');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
  // DISABLED: Darken sky progressively with altitude
  const darkK = 0; // FORCED TO ZERO - no darkening
  if (darkK > 0) {
    ctx.save();
    ctx.globalAlpha = 0.55 * darkK;
    ctx.fillStyle = '#0a1020';
    ctx.fillRect(0, 0, w, Math.max(0, groundTopScreen));
    ctx.restore();
  }
  // Subtle sky noise to reduce banding
  ensureSkyNoisePattern();
  if (skyNoisePattern) {
    ctx.save();
    ctx.globalAlpha = 0.012; // softer noise to hide rings
    ctx.fillStyle = skyNoisePattern;
    ctx.fillRect(0, 0, w, Math.max(0, groundTopScreen));
    ctx.restore();
  }
  // Horizon haze band near the ground line
  const hazeH = Math.max(40, Math.round(Math.min(140, groundTopScreen * 0.35)));
  const g2 = ctx.createLinearGradient(0, groundTopScreen - hazeH, 0, groundTopScreen);
  g2.addColorStop(0.0, 'rgba(255,255,255,0.00)');
  g2.addColorStop(1.0, 'rgba(255,255,255,0.10)');
  ctx.fillStyle = g2;
  ctx.fillRect(0, groundTopScreen - hazeH, w, hazeH);
  // Very light vignette (even smoother to avoid visible rings)
  const cx = w * 0.5, cy = (groundTopScreen) * 0.45;
  const vr = Math.hypot(w, groundTopScreen) * 1.05;
  const vg = ctx.createRadialGradient(cx, cy, vr * 0.05, cx, cy, vr);
  vg.addColorStop(0.0, 'rgba(0,0,0,0.00)');
  vg.addColorStop(0.6, 'rgba(0,0,0,0.008)');
  vg.addColorStop(1.0, 'rgba(0,0,0,0.04)');
  ctx.fillStyle = vg;
  ctx.fillRect(0, 0, w, Math.max(0, groundTopScreen));

  // Stars appear as it gets darker
  drawStars(ctx, w, Math.max(0, groundTopScreen), darkK);
  // Moon fades in once it's dark enough
  drawMoon(ctx, w, Math.max(0, groundTopScreen), darkK);
}

// --- Ground extras: strata + surface cap ---
function drawGroundStrata(ctx, w, h, groundTopY) {
  ctx.save();
  ctx.globalAlpha = 0.06;
  ctx.fillStyle = '#000';
  const band = Math.max(6, Math.round(TILE_SIZE));
  const endY = groundTopY + Math.min(h + 300, world.groundH + GROUND_EXTRA);
  for (let y = groundTopY + band * 2; y < endY; y += band) {
    // Slightly vary alpha with depth
    const t = Math.min(1, (y - groundTopY) / (world.groundH + 1));
    ctx.globalAlpha = 0.04 + 0.06 * t;
    ctx.fillRect(cameraX, y, w, 2);
  }
  ctx.restore();
}
function drawGroundCap(ctx, w, groundTopY) {
  const capH = Math.max(2, Math.round(TILE_SIZE * 0.5));
  const g = ctx.createLinearGradient(0, groundTopY - capH, 0, groundTopY + capH);
  g.addColorStop(0.0, 'rgba(240,220,160,0.25)');
  g.addColorStop(0.5, 'rgba(140,100,60,0.25)');
  g.addColorStop(1.0, 'rgba(0,0,0,0.0)');
  ctx.fillStyle = g;
  // Only draw the cap above tiles that actually exist at the surface to avoid
  // leaving a rim over freshly-dug holes.
  const startX = Math.floor(cameraX / TILE_SIZE) * TILE_SIZE;
  const endX = Math.ceil((cameraX + w) / TILE_SIZE) * TILE_SIZE;
  for (let px = startX; px < endX; px += TILE_SIZE) {
    const txGlobal = Math.floor(px / TILE_SIZE);
    const cx = Math.floor(txGlobal / CHUNK_W_TILES);
    const tx = txGlobal - cx * CHUNK_W_TILES;
    const solid0 = getTileSolid(cx, 0, tx, 0);
    const solid1 = getTileSolid(cx, 0, tx, 1);
    const solid2 = getTileSolid(cx, 0, tx, 2);
    // Only draw cap if surface and 2 layers below are solid (avoid drawing over holes)
    if (solid0 && solid1 && solid2) ctx.fillRect(px, groundTopY - capH, TILE_SIZE, capH);
  }
}

// --- Cloud shadows projected on ground ---
function drawCloudShadows(ctx, w, h, groundH) {
  ensureCloudChunksInView(cameraX, w);
  const skyH = Math.max(0, h - groundH);
  const groundTopY = h - groundH;
  for (let li = 0; li < cloudLayers.length; li++) {
    const layer = cloudLayers[li];
    const map = cloudsByLayer[li];
    const camL = cameraX * layer.speed + cloudDrift * layer.speed * CLOUD_IDLE_FACTOR;
    const marginTop = 16;
    const marginBottom = 24;
    const usableH = Math.max(0, skyH - marginTop - marginBottom);
    for (const [, arr] of map) {
      for (const c of arr) {
        const baseY = marginTop + cloudCamY + c.yNorm * usableH;
        const period = Math.max(1, usableH || 1);
        const startPage = Math.floor((-baseY - 120) / period) - 1;
        const endPage = Math.ceil((h + 120 - baseY) / period) + 1;
        for (let k = startPage; k <= endPage; k++) {
          let yk = baseY + k * period;
          if (yk < -140 || yk > h + 140) continue;
          const prng = seededPRNG(((c.seed ^ ((k * 73856093) >>> 0)) >>> 0));
          const jx = (prng() - 0.5) * 2 * CLOUD_PAGE_JITTER_X;
          const jy = (prng() - 0.5) * 2 * CLOUD_PAGE_JITTER_Y;
          const sVar = 1 + (prng() - 0.5) * 2 * CLOUD_PAGE_SCALE_VAR;
          const img = cloudImgs[(c.img + Math.floor(prng() * cloudImgs.length)) % cloudImgs.length];
          if (!img || !img.naturalWidth) continue;
          const dw = img.naturalWidth * c.scale * CLOUD_IMG_BASE_SCALE * sVar;
          const dh = img.naturalHeight * c.scale * CLOUD_IMG_BASE_SCALE * sVar;
          const cloudScreenX = (c.x + jx) - camL; // screen-space x in world units
          // Only cast shadow if cloud is above the ground horizon
          if (yk + jy + dh / 2 > groundTopY - 2) continue;
          // Shadow ellipse on ground in world space (we're already translated to world)
          const cx = cloudScreenX;
          const cy = groundTopY - 2;
          const rw = Math.max(12, dw * 0.9);
          const rh = Math.max(3, dw * 0.18);
          ctx.save();
          ctx.globalAlpha = 0.10 * layer.alpha;
          ctx.fillStyle = '#000';
          ctx.beginPath();
          // Draw ellipse
          ctx.ellipse(Math.round(cx), Math.round(cy), Math.round(rw / 2), Math.round(rh / 2), 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
      }
    }
  }
}

function drawSwordSlashArc(ctx) {
  if (!swordSlashArc || !swordSwingActive) return;

  const t = Math.max(0, Math.min(1, swordSwingT));
  const ease = 1 - Math.pow(1 - t, 2); // easeOutQuad

  // Arc parameters
  const centerX = player.x + player.w / 2;
  const centerY = player.y + player.h * 0.5;
  const radius = 42; // Arc radius
  const thickness = 4;

  // Angle sweep based on facing direction
  const facing = swordSlashArc.facing;
  let startAngle, endAngle;

  // Use same angle progression for both sides, adjust arc direction to mirror
  const baseAngle = (-50 * Math.PI / 180) + (ease * 120 * Math.PI / 180); // -50deg -> +70deg
  const arcWidth = (20 * Math.PI / 180);

  if (facing === 1) {
    // Right: normal arc
    startAngle = baseAngle;
    endAngle = startAngle + arcWidth;
  } else {
    // Left: mirror the arc by flipping the angle around the vertical axis
    const mirroredAngle = Math.PI - baseAngle;
    startAngle = mirroredAngle - arcWidth;
    endAngle = mirroredAngle;
  }

  // Fade out as animation progresses
  const alpha = Math.max(0, 1 - t * 1.2);

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = thickness;
  ctx.lineCap = 'round';

  // Draw the arc (always clockwise)
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, startAngle, endAngle, false);
  ctx.stroke();

  // Add a glow effect
  ctx.globalAlpha = alpha * 0.5;
  ctx.strokeStyle = '#e0f2fe';
  ctx.lineWidth = thickness + 2;
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, startAngle, endAngle, false);
  ctx.stroke();

  ctx.restore();
}

function drawHeldSword(ctx) {
  if (!swordReady) return;
  const targetW = 32;
  const aspect = swordImg.naturalWidth && swordImg.naturalHeight ? (swordImg.naturalWidth / swordImg.naturalHeight) : 1;
  const dw = targetW;
  const dh = Math.max(1, Math.round(targetW / aspect));
  const inward = Math.round(dw * 0.25);
  const offsetX = player.facing === 1 ? (player.w - inward - 2) : (-dw + inward + 2 - 4); // Nudge left by 4px when facing left
  const offsetY = Math.round(player.h * 0.12);
  const px = Math.round(player.x + offsetX);
  const py = Math.round(player.y + offsetY);

  // swing angle: same arc for both sides (flip handles mirroring)
  let angle = 0;
  if (swordSwingActive) {
    const t = Math.max(0, Math.min(1, swordSwingT));
    const ease = 1 - Math.pow(1 - t, 2); // easeOutQuad
    // Use same angle progression for both directions; the sprite flip handles the rest
    const angDeg = -50 + 120 * ease;  // -50deg -> +70deg (outward slash)
    angle = (angDeg * Math.PI) / 180;
  }

  // pivot at handle near lower-left of the sprite (adjusted per facing)
  const handleOX = dw * 0.15;
  const handleOY = dh * 0.85;

  ctx.save();
  // Invert holding state: flip when facing LEFT (previously flipped on right)
  const flip = (player.facing === -1);
  // translate to handle pivot in world space (pivot x mirrors when flipped)
  const pivotOX = flip ? (dw - handleOX) : handleOX;
  // When facing left, push the sword a bit further outward (left) for better alignment
  const leftAdjust = flip ? -Math.round(dw * 0.45) : 0;
  const cx = px + pivotOX + leftAdjust;
  const cy = py + handleOY;
  ctx.translate(cx, cy);
  if (flip) ctx.scale(-1, 1);
  // rotate around handle for swing (angle already mirrored by facing)
  ctx.rotate(angle);
  // draw so that handle lies at pivot
  ctx.drawImage(swordImg, -pivotOX, -handleOY, dw, dh);
  ctx.restore();
}

function drawDoNotPress(ctx, w, h, groundH) {
  if (!SHOW_DNP_BUTTON || !dnpReady) return;
  const targetW = 80; // Much bigger button
  const aspect = dnpImg.naturalWidth && dnpImg.naturalHeight ? (dnpImg.naturalWidth / dnpImg.naturalHeight) : 1;
  const dw = targetW;
  const dh = Math.max(1, Math.round(targetW / aspect));
  const groundTop = h - groundH;
  const x = Math.round(dnpBtn.x - dw / 2);
  const dnpYOffset = 23; // EDIT DONOTPRESS Y POSITION: Change this value to move button up (negative) or down (positive)
  const y = Math.round(groundTop - dh + dnpYOffset);
  if (x + dw < cameraX - 80 || x > cameraX + w + 80) return;
  ctx.drawImage(dnpImg, x, y, dw, dh);
}

function getGoblinRect(worldH) {
  const groundTop = worldH - world.groundH;
  const gw = goblin.w || GOBLIN_TARGET_H;
  const gh = goblin.h || GOBLIN_TARGET_H;
  const offset = goblin.yOffset || 0;
  return {
    x: goblin.x - gw / 2,
    y: groundTop - gh + offset,
    w: gw,
    h: gh
  };
}
function getGoblinWifeRect(worldH) {
  const groundTop = worldH - world.groundH;
  const aspect = (goblinWifeImg.naturalWidth && goblinWifeImg.naturalHeight) ? (goblinWifeImg.naturalWidth / goblinWifeImg.naturalHeight) : 1;
  const dw = Math.round(goblinWife.h * aspect);
  const dh = goblinWife.h;
  return { x: goblinWife.x - dw / 2, y: groundTop - dh, w: dw, h: dh };
}
function getGoblinSonRect(worldH) {
  const groundTop = worldH - world.groundH;
  const aspect = (goblinSonImg.naturalWidth && goblinSonImg.naturalHeight) ? (goblinSonImg.naturalWidth / goblinSonImg.naturalHeight) : 1;
  const dw = Math.round(goblinSon.h * aspect);
  const dh = goblinSon.h;
  return { x: goblinSon.x - dw / 2, y: groundTop - dh, w: dw, h: dh };
}
function drawGoblin(ctx, w, h, groundH) {
  if (!goblinReady || !goblin.spawned) return;
  const rect = getGoblinRect(h);
  const drawX = Math.round(rect.x);
  const drawY = Math.round(rect.y);
  ctx.save();
  const flip = (goblin.facing !== -1);
  if (flip) {
    ctx.translate(drawX + rect.w, drawY);
    ctx.scale(-1, 1);
    ctx.drawImage(goblinImg, 0, 0, rect.w, rect.h);
  } else {
    ctx.drawImage(goblinImg, drawX, drawY, rect.w, rect.h);
  }
  ctx.restore();
}

// Goblin family functions (wife and son)
function spawnGoblinFamily() {
  if (goblinFamilySpawned) return;
  goblinFamilySpawned = true;

  // Spawn wife and son at the cave entrance
  const caveX = cave.x;
  // EDIT GOBLIN WIFE SPAWN HERE - Adjust initial spawn and target positions
  goblinWife.x = caveX + 30; // Start inside cave - change +30 to adjust starting position
  goblinWife.targetX = caveX - 150; // Walk out to the left - change -150 to adjust final position
  goblinWife.spawned = true;

  // EDIT GOBLIN SON SPAWN HERE - Adjust initial spawn and target positions
  goblinSon.x = caveX + 50; // Start a bit behind wife - change +50 to adjust starting position
  goblinSon.targetX = caveX - 100; // Walk out to the left - change -100 to adjust final position
  goblinSon.spawned = true;

  // Make Bwak (the goblin) run from far left to join his family
  goblin.runFromLeft = true;
  goblin.runStartX = caveX - 800; // Start far to the left
  goblin.runTargetX = caveX - 120; // Target position near his family
  goblin.x = goblin.runStartX;
  goblin.runSpeed = 180; // Fast running speed

  // Spawn a toast message
  addWorldToast("The cave entrance opens!", caveX, world.height() - world.groundH - 100);
}

function updateGoblinFamily(dt, w, h) {
  if (!goblinFamilySpawned) return;

  const groundTop = h - world.groundH;

  // Update Bwak (goblin) running to join family
  if (goblin.runFromLeft) {
    if (Math.abs(goblin.x - goblin.runTargetX) > 2) {
      const dir = goblin.x < goblin.runTargetX ? 1 : -1;
      goblin.x += dir * goblin.runSpeed * dt;
      goblin.facing = dir;
    } else {
      goblin.x = goblin.runTargetX;
      goblin.runFromLeft = false;
      goblin.facing = 1; // Face right when stopped
      // When goblin reaches target, spawn toolbox if not already spawned
      if (!goblinFamilyJetbackSpawned) {
        goblinFamilyJetbackSpawned = true;
        // Spawn toolbox to the left of the family
        toolbox.x = goblin.x - 90;
        toolbox.picked = false; // Make it collectible
        toolbox.spawned = true; // Make it visible
        toolbox.spawnTime = 0; // Reset float animation
      }
    }
  }

  // Update goblin wife
  if (goblinWife.spawned && goblinWifeReady) {
    if (Math.abs(goblinWife.x - goblinWife.targetX) > 2) {
      const dir = goblinWife.x < goblinWife.targetX ? 1 : -1;
      goblinWife.x += dir * goblinWife.walkSpeed * dt;
    } else {
      goblinWife.x = goblinWife.targetX;
    }
  }

  // Update goblin son
  if (goblinSon.spawned && goblinSonReady) {
    if (Math.abs(goblinSon.x - goblinSon.targetX) > 2) {
      const dir = goblinSon.x < goblinSon.targetX ? 1 : -1;
      goblinSon.x += dir * goblinSon.walkSpeed * dt;
    } else {
      goblinSon.x = goblinSon.targetX;
    }
  }
}

function drawGoblinFamily(ctx, w, h) {
  if (!goblinFamilySpawned) return;
  const groundTop = h - world.groundH;

  // Draw goblin wife
  if (goblinWife.spawned && goblinWifeReady) {
    const aspect = (goblinWifeImg.naturalWidth && goblinWifeImg.naturalHeight) ? (goblinWifeImg.naturalWidth / goblinWifeImg.naturalHeight) : 1;
    // EDIT GOBLIN WIFE HERE - Adjust wife scale and position
    const dw = Math.round(goblinWife.h * aspect); // goblinWife.h controls size (default set at spawn)
    const dh = goblinWife.h;
    const drawX = Math.round(goblinWife.x - dw / 2); // goblinWife.x controls horizontal position
    const wifeYOffset = 0; // EDIT WIFE Y POSITION: Change this value to move wife up (negative) or down (positive)
    const drawY = Math.round(groundTop - dh + wifeYOffset);

    ctx.save();
    ctx.drawImage(goblinWifeImg, drawX, drawY, dw, dh);
    ctx.restore();
  }

  // Draw goblin son
  if (goblinSon.spawned && goblinSonReady) {
    const aspect = (goblinSonImg.naturalWidth && goblinSonImg.naturalHeight) ? (goblinSonImg.naturalWidth / goblinSonImg.naturalHeight) : 1;
    // EDIT GOBLIN SON HERE - Adjust son scale and position
    const dw = Math.round(goblinSon.h * aspect); // goblinSon.h controls size (default set at spawn)
    const dh = goblinSon.h;
    const drawX = Math.round(goblinSon.x - dw / 2); // goblinSon.x controls horizontal position
    const sonYOffset = 0; // EDIT SON Y POSITION: Change this value to move son up (negative) or down (positive)
    const drawY = Math.round(groundTop - dh + sonYOffset);

    ctx.save();
    ctx.drawImage(goblinSonImg, drawX, drawY, dw, dh);
    ctx.restore();
  }
}

function drawShrineWithHelicohat(ctx, w, h, groundH) {
  if (!forestShrineReady || forestShrine.burning) return;
  const aspect = (forestShrineImg.naturalWidth && forestShrineImg.naturalHeight) ? (forestShrineImg.naturalWidth / forestShrineImg.naturalHeight) : 1;
  const dh = forestShrine.h;
  const dw = Math.round(dh * aspect);
  const groundTop = h - groundH;
  const drawX = Math.round(forestShrine.x - dw / 2);
  const drawY = Math.round(groundTop - dh + forestShrine.yOffset);

  ctx.save();

  // Draw base shrine image
  if (forestShrine.glowProgress < 1) {
    ctx.globalAlpha = 1 - forestShrine.glowProgress;
    ctx.drawImage(forestShrineImg, drawX, drawY, dw, dh);
  }

  // Draw glow version blended on top
  if (forestShrine.glowProgress > 0 && forestShrineGlowReady) {
    ctx.globalAlpha = forestShrine.glowProgress;
    ctx.drawImage(forestShrineGlowImg, drawX, drawY, dw, dh);
  }

  ctx.restore();

  // Draw static cloud above shrine
  if (shrineCloud.spawned && cloudImgs && cloudImgs.length > 0) {
    const cloudImg = cloudImgs[SHRINE_CLOUD_IMG_INDEX]; // Use cloud2.png
    if (cloudImg && cloudImg.naturalWidth) {
      // Ensure metrics are up to date (especially after image load)
      refreshShrineCloudMetrics();
      const cloudW = shrineCloud.w;
      const cloudH = shrineCloud.h;
      const cloudX = Math.round(shrineCloud.x - cloudW / 2);
      const cloudY = Math.round(shrineCloud.y);
      ctx.globalAlpha = 0.9;
      ctx.drawImage(cloudImg, cloudX, cloudY, cloudW, cloudH);
      ctx.globalAlpha = 1;
    }
  }
}

function drawWipNpc(ctx, w, h, groundH) {
  if (!wipNpcReady || wipNpc.burning) return;
  const aspect = (wipNpcImg.naturalWidth && wipNpcImg.naturalHeight) ? (wipNpcImg.naturalWidth / wipNpcImg.naturalHeight) : 1;
  const dh = wipNpc.h;
  const dw = Math.round(dh * aspect);
  const groundTop = h - groundH;
  const drawX = Math.round(wipNpc.x - dw / 2);
  const drawY = Math.round(groundTop - dh + wipNpc.yOffset);

  ctx.save();
  const flip = (wipNpc.facing !== -1);
  if (flip) {
    ctx.translate(drawX + dw, drawY);
    ctx.scale(-1, 1);
    ctx.drawImage(wipNpcImg, 0, 0, dw, dh);
  } else {
    ctx.drawImage(wipNpcImg, drawX, drawY, dw, dh);
  }
  ctx.restore();
}

function drawUndergroundNpcs(ctx) {
  if (!undergroundNpcReady || !undergroundNpcs.length) return;
  for (const npc of undergroundNpcs) {
    const aspect = (undergroundNpcImg.naturalWidth && undergroundNpcImg.naturalHeight) ? (undergroundNpcImg.naturalWidth / undergroundNpcImg.naturalHeight) : 1;
    const dh = npc.h;
    const dw = Math.round(dh * aspect);
    const drawX = Math.round(npc.x - dw / 2);
    const drawY = Math.round(npc.y);

    ctx.save();
    const flip = (npc.facing !== -1);
    if (flip) {
      ctx.translate(drawX + dw, drawY);
      ctx.scale(-1, 1);
      ctx.drawImage(undergroundNpcImg, 0, 0, dw, dh);
    } else {
      ctx.drawImage(undergroundNpcImg, drawX, drawY, dw, dh);
    }
    ctx.restore();
  }
}

function drawWipBanner(ctx, w, h, groundH) {
  if (!wipBannerImg || !wipBannerImg.naturalWidth || wipNpc.burning) return;

  const groundTop = h - groundH;
  const aspect = wipBannerImg.naturalWidth / wipBannerImg.naturalHeight;
  const targetH = 160; // Bigger banner
  const targetW = targetH * aspect;

  // EDIT WIP BANNER POSITION HERE - Adjust banner position relative to WIP NPC
  const bannerX = wipNpc.x + 40; // Horizontal offset from NPC (negative = left, positive = right)
  const bannerYOffset = 10; // EDIT BANNER Y POSITION: Change this value to move banner up (negative) or down (positive)
  const bannerY = groundTop - targetH + bannerYOffset;

  ctx.save();
  ctx.drawImage(wipBannerImg, bannerX - targetW / 2, bannerY, targetW, targetH);
  ctx.restore();
}

function drawForestShrinePrompt(ctx) {
  if (!forestShrine.overlap || forestShrine.activated) return;
  const msg = 'Interact (E)';
  ctx.save();
  ctx.font = '14px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  const textW = Math.ceil(ctx.measureText(msg).width);
  const padX = 10;
  const padY = 6;
  const boxW = textW + padX * 2;
  const boxH = 24 + padY * 2;
  const screenX = Math.round((forestShrine.x - cameraX) * VIEW_SCALE);
  const screenY = Math.round((forestShrine.x ? world.height() - world.groundH - forestShrine.h - 20 : 0) * VIEW_SCALE);
  const x = Math.round(screenX - boxW / 2);
  const y = Math.max(8, screenY - boxH);
  ctx.fillStyle = 'rgba(0,0,0,0.6)';
  ctx.strokeStyle = 'rgba(255,255,255,0.55)';
  ctx.lineWidth = 1;
  roundRectPath(ctx, x, y, boxW, boxH, 8);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#e7f6ff';
  ctx.fillText(msg, x + padX, y + padY + 18);
  ctx.restore();
}

function drawUndergroundNpcPrompt(ctx) {
  if (!undergroundNpcReady || !undergroundNpcs.length || dialogueState.active) return;
  // Find the first NPC that has overlap
  const npc = undergroundNpcs.find(n => n.overlap);
  if (!npc) return;

  const msg = 'Talk (E)';
  ctx.save();
  ctx.font = '14px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  const textW = Math.ceil(ctx.measureText(msg).width);
  const padX = 10;
  const padY = 6;
  const boxW = textW + padX * 2;
  const boxH = 36;
  const screenX = Math.round((npc.x - cameraX) * VIEW_SCALE);
  const screenY = Math.round((npc.y - 20) * VIEW_SCALE);
  const x = Math.round(screenX - boxW / 2);
  const y = Math.round(screenY - boxH - 8);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
  ctx.strokeStyle = 'rgba(255,255,255,0.55)';
  ctx.lineWidth = 1;
  roundRectPath(ctx, x, y, boxW, boxH, 8);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#e7f6ff';
  ctx.fillText(msg, x + padX, y + padY + 18);
  ctx.restore();
}

function drawWipNpcPrompt(ctx) {
  if (!wipNpc.overlap || dialogueState.active) return;
  const msg = 'Talk (E)';
  ctx.save();
  ctx.font = '14px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  const textW = Math.ceil(ctx.measureText(msg).width);
  const padX = 10;
  const padY = 6;
  const boxW = textW + padX * 2;
  const boxH = 24 + padY * 2;
  const screenX = Math.round((wipNpc.x - cameraX) * VIEW_SCALE);
  const screenY = Math.round((wipNpc.x ? world.height() - world.groundH - wipNpc.h - 20 : 0) * VIEW_SCALE);
  const x = Math.round(screenX - boxW / 2);
  const y = Math.max(8, screenY - boxH);
  ctx.fillStyle = 'rgba(0,0,0,0.6)';
  ctx.strokeStyle = 'rgba(255,255,255,0.55)';
  ctx.lineWidth = 1;
  roundRectPath(ctx, x, y, boxW, boxH, 8);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#e7f6ff';
  ctx.fillText(msg, x + padX, y + padY + 18);
  ctx.restore();
}

function getArchPartInfo(img, bounds) {
  if (!img || !img.naturalWidth || !img.naturalHeight) return null;
  const dh = ARCH_TARGET_H;
  const aspect = img.naturalWidth / img.naturalHeight;
  const dw = Math.max(12, Math.round(dh * aspect));
  const scaleY = dh / img.naturalHeight;
  const bottomOffset = bounds && img.naturalHeight ? Math.round((bounds.bottom || 0) * scaleY) : 0;
  return { img, dw, dh, bottomOffset };
}

function drawArchHalf(ctx, w, h, side) {
  const info = side === 'left' ? getArchPartInfo(archLeftImg, archLeftBounds) : getArchPartInfo(archRightImg, archRightBounds);
  const ready = side === 'left' ? archLeftReady : archRightReady;
  if (!ready || !info) return;
  const groundTop = h - world.groundH;
  const xBase = Math.round(arch.x);
  const xAdjust = side === 'left' ? ARCH_LEFT_X_OFFSET : ARCH_RIGHT_X_OFFSET;
  const yAdjust = side === 'left' ? ARCH_LEFT_Y_OFFSET : ARCH_RIGHT_Y_OFFSET;
  const drawX = side === 'left'
    ? Math.round(xBase - info.dw + xAdjust)
    : Math.round(xBase + xAdjust);
  let drawY = Math.round(groundTop - info.dh + info.bottomOffset + ARCH_GROUND_OFFSET + yAdjust);
  if (drawX + info.dw < cameraX - 200 || drawX > cameraX + w + 200) return;

  ctx.save();
  if (side === 'left') {
    const groundFoot = groundTop;
    const clipTop = drawY;
    const clipHeight = Math.max(0, groundFoot - clipTop);
    ctx.beginPath();
    ctx.rect(drawX - 6, clipTop, info.dw + 12, clipHeight);
    ctx.clip();
  }
  ctx.drawImage(info.img, drawX, drawY, info.dw, info.dh);
  ctx.restore();
}

function drawArchBack(ctx, w, h) {
  drawArchHalf(ctx, w, h, 'left');
}

function drawArchFront(ctx, w, h) {
  drawArchHalf(ctx, w, h, 'right');
}

function getShrineDrawInfo() {
  const img = shrine.activated ? shrineFreedImg : shrineCorruptImg;
  const ready = shrine.activated ? shrineFreedReady : shrineCorruptReady;
  const bounds = shrine.activated ? shrineFreedBounds : shrineCorruptBounds;
  if (!ready || !img || !img.naturalWidth || !img.naturalHeight) return null;
  const dh = SHRINE_TARGET_H;
  const aspect = img.naturalWidth / img.naturalHeight;
  const dw = Math.max(40, Math.round(dh * aspect));
  const scaleY = dh / img.naturalHeight;
  const bottomOffset = bounds && img.naturalHeight ? Math.round((bounds.bottom || 0) * scaleY) : 0;
  return { img, dw, dh, bottomOffset };
}

function drawShrineBloomTrees(ctx, w, h) {
  if (!shrineBloomTrees.length) return;
  const groundTop = h - world.groundH;
  for (const tree of shrineBloomTrees) {
    const img = treeImgs[tree.imgIndex | 0];
    if (!img || !img.naturalHeight) continue;
    const baseH = tree.targetH || 150;
    const aspect = img.naturalWidth && img.naturalHeight ? (img.naturalWidth / img.naturalHeight) : 1;
    const baseW = Math.max(20, Math.round(baseH * aspect));
    const age = tree.age || 0;
    const delay = tree.delay || 0;
    const fadeDur = tree.fadeDuration || 1.0;
    if (age <= delay) continue;
    const progress = Math.max(0, Math.min(1, (age - delay) / Math.max(0.001, fadeDur)));
    const scaleStart = typeof tree.startScale === 'number' ? tree.startScale : 0.8;
    const scaleEnd = typeof tree.endScale === 'number' ? tree.endScale : 1.0;
    const scale = scaleStart + (scaleEnd - scaleStart) * progress;
    const dh = Math.max(12, Math.round(baseH * scale));
    const dw = Math.max(12, Math.round(baseW * scale));
    const alpha = Math.max(0, Math.min(1, progress));
    const left = Math.round(tree.x - dw / 2);
    let y = Math.round(groundTop - dh);
    const bounds = treeImgBounds[tree.imgIndex | 0];
    if (bounds && img.naturalHeight) {
      const scaleY = dh / img.naturalHeight;
      y += Math.round((bounds.bottom || 0) * scaleY);
    }
    ctx.save();
    ctx.globalAlpha = (tree.opacity || 1) * alpha;
    ctx.drawImage(img, left, y, dw, dh);
    ctx.restore();
  }
}

function drawForestShrine(ctx, w, h) {
  const info = getShrineDrawInfo();
  if (!info) return;
  const groundTop = h - world.groundH;
  const x = Math.round(shrine.x - info.dw / 2);
  const y = Math.round(groundTop - info.dh + info.bottomOffset + SHRINE_GROUND_OFFSET);
  const shrineVisible = !(x + info.dw < cameraX - 200 || x > cameraX + w + 200);

  if (shrineVisible) {
    ctx.save();
    if (!shrine.activated) {
      ctx.shadowColor = 'rgba(40, 255, 170, 0.45)';
      ctx.shadowBlur = 22;
      if (shrine.damageFlash > 0) {
        const flash = Math.max(0, Math.min(1, shrine.damageFlash / 0.25));
        ctx.globalAlpha = 1 - flash * 0.35;
      }
    }
    ctx.drawImage(info.img, x, y, info.dw, info.dh);
    if (!shrine.activated && shrine.damageFlash > 0) {
      const flash = Math.max(0, Math.min(1, shrine.damageFlash / 0.25));
      ctx.globalAlpha = flash * 0.6;
      ctx.globalCompositeOperation = 'lighter';
      ctx.drawImage(info.img, x, y, info.dw, info.dh);
      ctx.globalCompositeOperation = 'source-over';
    }
    ctx.restore();
  }

  drawShrineBloomTrees(ctx, w, h);
}

function getBarrierVinesDrawInfo() {
  if (!barrierVinesReady || !barrierVinesImg || !barrierVinesImg.naturalWidth || !barrierVinesImg.naturalHeight) {
    return null;
  }
  const dh = BARRIER_VINES_TARGET_H;
  const aspect = barrierVinesImg.naturalWidth / barrierVinesImg.naturalHeight;
  const dw = Math.max(60, Math.round(dh * aspect));
  const scaleY = dh / barrierVinesImg.naturalHeight;
  const bottomOffset = barrierVinesBounds && barrierVinesImg.naturalHeight
    ? Math.round((barrierVinesBounds.bottom || 0) * scaleY)
    : 0;
  return { img: barrierVinesImg, dw, dh, bottomOffset };
}

function drawBarrierVines(ctx, w, h) {
  if (barrierVines.removed || barrierVines.fade <= 0) return;
  const info = getBarrierVinesDrawInfo();
  syncBarrierVinesPlacement(info);
  const groundTop = h - world.groundH;
  const drawW = info ? info.dw : Math.round(BARRIER_VINES_TARGET_H * 0.55);
  const drawH = info ? info.dh : Math.round(BARRIER_VINES_TARGET_H * 0.95);
  const bottomOffset = info ? info.bottomOffset : 0;
  const x = Math.round(barrierVines.x - drawW / 2);
  const y = Math.round(groundTop - drawH + bottomOffset + BARRIER_VINES_GROUND_OFFSET);
  const vinesVisible = !(x + drawW < cameraX - 200 || x > cameraX + w + 200);
  if (!vinesVisible) return;
  ctx.save();
  const alpha = Math.max(0, Math.min(1, barrierVines.fade));
  ctx.globalAlpha = alpha;
  if (info && info.img) {
    ctx.shadowColor = 'rgba(62, 120, 78, 0.55)';
    ctx.shadowBlur = 28;
    ctx.drawImage(info.img, x, y, drawW, drawH);
  } else {
    ctx.fillStyle = '#24431f';
    ctx.fillRect(x, y, drawW, drawH);
  }
  ctx.restore();
}

function syncBarrierVinesPlacement(vinesInfo) {
  if (!barrierVines || barrierVines.removed) return;
  const vinesW = vinesInfo ? vinesInfo.dw : Math.round(BARRIER_VINES_TARGET_H * 0.55);
  const shrineInfo = getShrineDrawInfo();
  const shrineW = shrineInfo ? shrineInfo.dw : Math.round(SHRINE_TARGET_H * 0.68);
  const targetX = shrine.x + shrineW / 2 + BARRIER_VINES_GAP + vinesW / 2;
  if (Number.isFinite(targetX)) {
    barrierVines.x = targetX;
  }
}

function barrierVinesBlockRange() {
  const info = getBarrierVinesDrawInfo();
  syncBarrierVinesPlacement(info);
  const fallbackW = Math.round(BARRIER_VINES_TARGET_H * 0.55);
  const blockW = info ? Math.max(BARRIER_VINES_BLOCK_WIDTH, info.dw * 0.6) : Math.max(BARRIER_VINES_BLOCK_WIDTH, fallbackW * 0.6);
  const half = blockW * 0.5;
  return {
    left: barrierVines.x - half,
    right: barrierVines.x + half
  };
}

function updateBarrierVines(dt) {
  if (barrierVines.removed) return;
  syncBarrierVinesPlacement(getBarrierVinesDrawInfo());
  if (shrine.activated && !barrierVines.fading) {
    barrierVines.fading = true;
  }
  if (barrierVines.fading) {
    barrierVines.fade = Math.max(0, barrierVines.fade - BARRIER_VINES_FADE_SPEED * dt);
    if (barrierVines.fade <= 0) {
      barrierVines.removed = true;
      barrierVines.fading = false;
    }
  } else {
    barrierVines.fade = Math.min(1, barrierVines.fade);
  }
}

function getShrineRect(h) {
  const info = getShrineDrawInfo();
  if (!info) return null;
  const groundTop = h - world.groundH;
  const x = shrine.x - info.dw / 2;
  let y = groundTop - info.dh + info.bottomOffset + SHRINE_GROUND_OFFSET;
  return { x, y, w: info.dw, h: info.dh };
}

function applyShrineDamage(amount, h) {
  if (shrine.activated || shrine.maxHp <= 0) return false;
  shrine.hp = Math.max(0, shrine.hp - amount);
  shrine.damageFlash = 0.25;
  if (shrine.hp <= 0) {
    cleanseShrine(h);
  }
  return true;
}

function cleanseShrine(h) {
  if (shrine.activated) return;
  shrine.activated = true;
  shrine.hp = 0;
  shrine.damageFlash = 0;
  verdantGrassActive = true;
  spawnShrineBloomTrees();
  if (!barrierVines.fading && !barrierVines.removed) {
    barrierVines.fading = true;
  }
  if (typeof h === 'number') {
    const rect = getShrineRect(h);
    if (rect) {
      const cx = rect.x + rect.w / 2;
      const cy = rect.y + rect.h / 2;
      const green = ['#b7ffcc', '#84f59a', '#56d86d', '#a9f0c0'];
      for (let k = 0; k < 4; k++) particles.spawnSparks(cx, cy, 12, green);
    }
  }
  try {
    if (shrineSfx) {
      shrineSfx.currentTime = 0;
      const p = shrineSfx.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    }
  } catch {}
}

function spawnShrineBloomTrees() {
  const baseX = shrine.x;
  const offsets = [220, 360, 520];
  const configs = [
    { imgIndex: ORANGE_TREE_INDEX, targetH: 150, delay: 0.2 },
    { imgIndex: OAK_TREE_INDEX, targetH: 220, delay: 0 },
    { imgIndex: TREE_IMG_SRC.indexOf('img_assets/tree2.png'), targetH: 170, delay: 0.35 }
  ];
  shrineBloomTrees = offsets.map((dx, i) => {
    const cfg = configs[i] || configs[configs.length - 1];
    const startScale = 0.65;
    const endScale = 1.0;
    return {
      x: baseX + dx,
      imgIndex: cfg.imgIndex >= 0 ? cfg.imgIndex : 0,
      targetH: cfg.targetH,
      delay: cfg.delay || 0,
      fadeDuration: 0.9,
      startScale,
      endScale,
      age: 0,
      opacity: 1,
      converted: false
    };
  });
}

function updateShrineBloomTrees(dt) {
  if (!shrineBloomTrees.length) return;
  for (const tree of shrineBloomTrees) {
    tree.age = (tree.age || 0) + dt;
    if (!tree.converted) {
      const delay = tree.delay || 0;
      const fadeDur = tree.fadeDuration || 0.9;
      if (tree.age >= delay + fadeDur) {
        convertShrineBloomTree(tree);
        tree.converted = true;
      }
    }
  }
  shrineBloomTrees = shrineBloomTrees.filter(tree => !tree.converted);
}

function convertShrineBloomTree(tree) {
  if (!tree) return;
  const imgIndex = tree.imgIndex >= 0 ? tree.imgIndex : 0;
  const kind = (function resolveKind() {
    if (imgIndex === ORANGE_TREE_INDEX) return 'orange';
    if (imgIndex === OAK_TREE_INDEX || imgIndex === OAK_TREE_FADE_INDEX) return 'oak';
    if (imgIndex === CORRUPTED_TREE_INDEX) return 'corrupted';
    return 'standard';
  })();
  const baseHeight = (kind === 'oak') ? 160 : (kind === 'orange' ? 124 : 110);
  const targetH = Math.max(10, tree.targetH || (baseHeight * (tree.endScale || 1)));
  const scale = Math.max(0.5, targetH / baseHeight);
  const maxHp = (kind === 'oak') ? 3 : 1;
  const chunkIdx = Math.floor(tree.x / chunkSize);
  let arr = treesByChunk.get(chunkIdx);
  if (!arr) {
    arr = [];
    treesByChunk.set(chunkIdx, arr);
  }
  // Replace any placeholder trees that may occupy the same spot
  for (let i = arr.length - 1; i >= 0; i--) {
    if (Math.abs((arr[i]?.x || 0) - tree.x) < 4) {
      arr.splice(i, 1);
    }
  }
  arr.push({
    x: tree.x,
    scale,
    img: imgIndex,
    kind,
    hp: maxHp,
    maxHp,
    felled: false,
    fallT: 0,
    fallDir: 1,
    fromShrine: true
  });
}

function hitShrineWithAxe(h, cx, cy, r) {
  if (shrine.activated) return false;
  const rect = getShrineRect(h);
  if (!rect) return false;
  const nearestX = Math.max(rect.x, Math.min(cx, rect.x + rect.w));
  const nearestY = Math.max(rect.y, Math.min(cy, rect.y + rect.h));
  const dx = cx - nearestX;
  const dy = cy - nearestY;
  if (dx * dx + dy * dy > r * r) return false;
  applyShrineDamage(1, h);
  return true;
}

// Unified interact prompt text builder
function buildInteractPrompt(actionText) {
  const raw = (actionText || '').replace(/^to\s+/i, '').trim();
  if (!raw) return '';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function drawCavePrompt(ctx, msg) {
  const text = buildInteractPrompt(msg);
  if (!text) return;
  ctx.save();
  const isTiny = text.length <= 3;
  ctx.font = isTiny ? '12px system-ui, -apple-system, Segoe UI, sans-serif' : '14px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  const paddingX = isTiny ? 6 : 10;
  const paddingY = isTiny ? 4 : 6;
  const textW = Math.ceil(ctx.measureText(text).width);
  const boxW = textW + paddingX * 2;
  const boxH = (isTiny ? 18 : 24) + paddingY * 2;
  const anchorX = (cavePromptAnchor && typeof cavePromptAnchor.x === 'number')
    ? cavePromptAnchor.x
    : (player.x + player.w / 2);
  const anchorY = (cavePromptAnchor && typeof cavePromptAnchor.y === 'number')
    ? cavePromptAnchor.y
    : (player.y - (isTiny ? 32 : 20));
  const px = Math.round((anchorX - cameraX) * VIEW_SCALE);
  const py = Math.round((anchorY - cameraY) * VIEW_SCALE);
  const x = Math.round(px - boxW / 2);
  const y = Math.max(8, py - boxH);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
  ctx.strokeStyle = 'rgba(255,255,255,0.55)';
  ctx.lineWidth = isTiny ? 0.75 : 1;
  roundRectPath(ctx, x, y, boxW, boxH, 8);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#e7f6ff';
  ctx.fillText(text, x + paddingX, y + paddingY + (isTiny ? 13 : 18));
  ctx.restore();
}

function drawPressPrompt(ctx) {
  const msg = buildInteractPrompt('Press - E (really?)');
  ctx.save();
  ctx.font = '14px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  const paddingX = 10;
  const paddingY = 6;
  const textW = Math.ceil(ctx.measureText(msg).width);
  const boxW = textW + paddingX * 2;
  const boxH = 24 + paddingY * 2;
  const px = Math.round(((player.x - cameraX) + player.w / 2) * VIEW_SCALE);
  const py = Math.round(((player.y + cameraY) - 20) * VIEW_SCALE);
  const x = Math.round(px - boxW / 2);
  const y = Math.max(8, py - boxH);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
  ctx.strokeStyle = 'rgba(255,255,255,0.5)';
  ctx.lineWidth = 1;
  roundRectPath(ctx, x, y, boxW, boxH, 8);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#fff';
  ctx.fillText(msg, x + paddingX, y + paddingY + 18);
  ctx.restore();
}

function drawGoblinPrompt(ctx) {
  if (!goblin.spawned || !goblin.overlap || dialogueState.active) return;
  const msg = 'Talk (E)';
  ctx.save();
  ctx.font = '14px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  const padX = 12;
  const padY = 6;
  const textW = Math.ceil(ctx.measureText(msg).width);
  const boxW = textW + padX * 2;
  const boxH = 24 + padY * 2;
  const rect = getGoblinRect(world.height());
  const px = Math.round(((rect.x + rect.w / 2) - cameraX) * VIEW_SCALE);
  const py = Math.round(((rect.y + cameraY) - 16) * VIEW_SCALE);
  const x = Math.round(px - boxW / 2);
  const y = Math.max(8, py - boxH);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
  ctx.strokeStyle = 'rgba(160, 210, 255, 0.55)';
  ctx.lineWidth = 1;
  roundRectPath(ctx, x, y, boxW, boxH, 8);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#e7f6ff';
  ctx.fillText(msg, x + padX, y + padY + 18);
  ctx.restore();
}

function drawRockTitanPrompt(ctx) {
  if (!rockTitan || !rockTitan.overlap || dialogueState.active || !rockTitan._rect) return;
  const msg = 'Talk (E)';
  ctx.save();
  ctx.font = '14px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  const padX = 12;
  const padY = 6;
  const textW = Math.ceil(ctx.measureText(msg).width);
  const boxW = textW + padX * 2;
  const boxH = 24 + padY * 2;
  const rect = rockTitan._rect;
  const px = Math.round(((rect.x + rect.w / 2) - cameraX) * VIEW_SCALE);
  const py = Math.round(((rect.y + cameraY) - 16) * VIEW_SCALE);
  const x = Math.round(px - boxW / 2);
  const y = Math.max(8, py - boxH);
  ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
  ctx.strokeStyle = 'rgba(200, 220, 255, 0.45)';
  ctx.lineWidth = 1;
  roundRectPath(ctx, x, y, boxW, boxH, 8);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#f2f7ff';
  ctx.fillText(msg, x + padX, y + padY + 18);
  ctx.restore();
}

function drawJetbackItem(ctx, w, h, groundH) {
  if (!jetOffReady || !jetback.spawned) return;
  const targetW = 56; // tuned for jetback sprite aspect
  const aspect = jetImgOff.naturalWidth && jetImgOff.naturalHeight ? (jetImgOff.naturalWidth / jetImgOff.naturalHeight) : 1;
  const dw = targetW;
  const dh = Math.max(1, Math.round(targetW / aspect));
  const groundTop = h - groundH;
  const fade = Math.max(0, Math.min(1, (jetback.spawnTime || 0) / 0.75));
  if (fade <= 0.01) return;
  const bob = Math.sin((jetback.floatPhase || 0) * 2.4) * 3 - 3;
  const x = Math.round(jetback.x - dw / 2);
  // Anchor a bit lower so it sits deeper into the ground for weight
  const y = Math.round(groundTop - dh + 12 + bob);
  if (x + dw < cameraX - 80 || x > cameraX + w + 80) return;
  ctx.save();
  ctx.globalAlpha = fade;
  ctx.shadowColor = 'rgba(150,200,255,0.35)';
  ctx.shadowBlur = 12 * fade;
  ctx.drawImage(jetImgOff, x, y, dw, dh);
  ctx.restore();
}

function drawHeldJetbackBehind(ctx) {
  // Draw backpack attached to the player's back; use on/off sprite depending on active state
  if (!(jetback.equipped && (jetOnReady || jetOffReady))) return;
  const img = jetback.active && jetOnReady ? jetImgOn : jetImgOff;
  // Retune size/position for new jetback art
  const targetH = Math.round(player.h * 0.95); // closer to body height
  const aspect = img.naturalWidth && img.naturalHeight ? (img.naturalWidth / img.naturalHeight) : 1;
  const dh = targetH;
  const dw = Math.max(1, Math.round(targetH * aspect));
  // Anchor to the player's back: when facing right, back is left side; facing left, back is right side
  const backAnchorX = (player.facing === 1)
    ? (player.x + player.w * 0.26)
    : (player.x + player.w * 0.74);
  const backAnchorY = player.y + player.h * 0.48; // mid-back
  const bob = jetback.active ? Math.sin(performance.now() / 120) * 1.2 : 0;
  ctx.save();
  ctx.translate(Math.round(backAnchorX), Math.round(backAnchorY + bob));
  // Flip when facing right so the backpack's orientation matches body facing
  if (player.facing === 1) ctx.scale(-1, 1);
  // Local offset: push slightly toward the back and align vertically around the middle
  const backShift = Math.round(dw * 0.22);
  ctx.drawImage(img, -dw / 2 + backShift, -Math.round(dh * 0.50), dw, dh);
  ctx.restore();
}

function drawPickupPrompt(ctx, itemLabel) {
  const msg = buildInteractPrompt(`to pick up the ${itemLabel}`);
  ctx.save();
  ctx.font = '14px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  const paddingX = 10;
  const paddingY = 6;
  const textW = Math.ceil(ctx.measureText(msg).width);
  const boxW = textW + paddingX * 2;
  const boxH = 24 + paddingY * 2;
  const px = Math.round(((player.x - cameraX) + player.w / 2) * VIEW_SCALE);
  const py = Math.round(((player.y + cameraY) - 20) * VIEW_SCALE);
  const x = Math.round(px - boxW / 2);
  const y = Math.max(8, py - boxH);
  // box
  ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
  ctx.strokeStyle = 'rgba(255,255,255,0.5)';
  ctx.lineWidth = 1;
  roundRectPath(ctx, x, y, boxW, boxH, 8);
  ctx.fill();
  ctx.stroke();
  // text
  ctx.fillStyle = '#fff';
  ctx.fillText(msg, x + paddingX, y + paddingY + 18);
  ctx.restore();
}

function roundRectPath(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
}

// --- Pickup toast: green up arrow + "axe" ---
let pickupToasts = [];
function addPickupToast(label) {
  const sx = Math.round(((player.x - cameraX) + player.w / 2) * VIEW_SCALE);
  const sy = Math.round(((player.y + cameraY) - 16) * VIEW_SCALE);
  pickupToasts.push({ x: sx, y: sy, vy: -26, life: 0, dur: 1.2, alpha: 1, label });
}
function updatePickupToasts(dt) {
  for (const p of pickupToasts) {
    p.life += dt;
    p.y += p.vy * dt;
    const t = Math.min(1, p.life / p.dur);
    p.alpha = 1 - t;
  }
  pickupToasts = pickupToasts.filter(p => p.life < p.dur);
}
function drawPickupToasts(ctx) {
  if (!pickupToasts.length) return;
  ctx.save();
  ctx.font = 'bold 14px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  for (const p of pickupToasts) {
    ctx.globalAlpha = p.alpha;
    const isSpend = (p.label === 'log_spend');
    const arrow = isSpend ? '↓' : '↑';
    // Check for log_x## format (e.g., "log_x15")
    const logMatch = p.label.match(/^log_x(\d+)$/);
    const useLogIcon = ((p.label === 'log' || p.label === 'log_spend' || logMatch) && logReady);
    const useSilkIcon = (p.label === 'silk' && silkReady);
    const aw = Math.ceil(ctx.measureText(arrow).width);
    const gap = 6;
    let total = aw + gap;
    let dw = 0, dh = 0, img = null;
    let iconHeight = 14;
    let extraText = null; // for showing count like "x15"
    if (useLogIcon) {
      img = logImg;
      if (logMatch) {
        extraText = `×${logMatch[1]}`;
      }
    }
    if (useSilkIcon) { img = silkImg; }
    const useFeatherIcon = (p.label === 'feather' && featherReady);
    if (useFeatherIcon) { img = featherImg; iconHeight = 18; }
    if (img && img.naturalWidth) {
      dh = iconHeight; // icon height to match text
      const aspect = img.naturalWidth / img.naturalHeight;
      dw = Math.max(1, Math.round(dh * aspect));
      total += dw;
      if (extraText) {
        total += gap + Math.ceil(ctx.measureText(extraText).width);
      }
    } else {
      // Fallback to text label
      const label = p.label || 'item';
      const lw = Math.ceil(ctx.measureText(label).width);
      total += lw;
    }
    const x0 = Math.round(p.x - total / 2);
    const y0 = Math.round(p.y);
    // subtle shadow
    ctx.shadowColor = 'rgba(0,0,0,0.35)';
    ctx.shadowBlur = 2;
    ctx.shadowOffsetY = 1;
    // arrow color: green for pickup, red for spend
    ctx.fillStyle = isSpend ? '#ef4444' : '#34d399';
    ctx.fillText(arrow, x0, y0);
    let cursorX = x0 + aw + gap;
    if (img && img.naturalWidth) {
      // draw icon aligned to baseline
      ctx.drawImage(img, cursorX, Math.round(y0 - dh + 2), dw, dh);
      cursorX += dw;
      // draw extra text if present (e.g., "×15")
      if (extraText) {
        cursorX += gap;
        ctx.fillStyle = '#ffffff';
        ctx.fillText(extraText, cursorX, y0);
      }
    } else {
      // text fallback
      const label = p.label || 'item';
      ctx.fillStyle = '#ffffff';
      ctx.fillText(label, cursorX, y0);
    }
  }
  ctx.restore();
}

function updateDialogue(dt) {
  // Always decrement cooldown, even when dialogue is not active
  if (dialogueState.cooldown > 0) dialogueState.cooldown = Math.max(0, dialogueState.cooldown - dt);

  if (!dialogueState.active) return;
  const pages = dialogueState.pages;
  const idx = Math.max(0, Math.min(dialogueState.pageIndex, pages.length - 1));
  const page = pages[idx] || '';
  const len = page.length;
  const fastHeld = input.dialogueHeld && dialogueState.visibleChars < len;
  const speed = fastHeld ? DIALOGUE_FAST_SPEED : DIALOGUE_TYPE_SPEED;
  dialogueState.charProgress = Math.min(len, dialogueState.charProgress + speed * dt);
  dialogueState.visibleChars = Math.min(len, Math.floor(dialogueState.charProgress));

  const pressed = input.consumeDialogue() || (!input.hasKeyboard && input.consumeInteract());
  if (pressed) {
    if (dialogueState.visibleChars < len) {
      dialogueState.charProgress = len;
      dialogueState.visibleChars = len;
    } else if (dialogueState.pageIndex < pages.length - 1) {
      if (page && page.trim().length) dialogueState.history.push(page.trim());
      stopDialogueBlip();
      dialogueState.pageIndex += 1;
      dialogueState.charProgress = 0;
      dialogueState.visibleChars = 0;
    } else {
      if (page && page.trim().length) dialogueState.history.push(page.trim());
      endDialogue();
    }
  }

  if (!dialogueState.active) {
    stopDialogueBlip();
    return;
  }

  if (dialogueState.visibleChars < len) {
    startDialogueBlip();
  } else {
    stopDialogueBlip();
  }
}

// === Buildable Planks (placed using a builder item) ===
// Planks are axis-aligned solid rectangles you can place to build bridges/steps.
// They collide like tiles and persist until pruned far off-screen.
let planks = []; // { x, y, w, h, vy? }
const PLANK_THICK = 12;            // 1 tile thick
const PLANK_LEN_WALK = 12 * 4;     // 4 tiles long (default)
const PLANK_LEN_RUN  = 12 * 6;     // 6 tiles long when holding run

// Placeholder builder item (we'll add the art later)
const builder = { picked: false, equipped: false }; // Unlocked by picking up toolbox from goblin family
let builderNudgeTiles = 0;
let builderNudgeTimer = 0;

function rectOverlapsPlanks(rx, ry, rw, rh) {
  for (const p of planks) {
    if (!(rx + rw <= p.x || p.x + p.w <= rx || ry + rh <= p.y || p.y + p.h <= ry)) return true;
  }
  return false;
}

function rectOverlapsWorldSolid(w, h, rx, ry, rw, rh) {
  return rectOverlapsSolid(w, h, rx, ry, rw, rh) || rectOverlapsPlanks(rx, ry, rw, rh);
}

// Gently move the player upward into newly dug air if space allows
function nudgePlayerUpIntoClear(w, h, maxPix = TILE_SIZE) {
  let moved = 0;
  while (moved < maxPix) {
    if (rectOverlapsWorldSolid(w, h, player.x + 1, player.y - 1, Math.max(1, player.w - 2), player.h)) break;
    player.y -= 1; moved += 1;
  }
  if (moved > 0) {
    player.onGround = false;
    if (player.vy > 0) player.vy = 0; // do not fight upward motion
  }
}

function dropRectToSupport(w, h, rect) {
  // Move rect down until it would collide, then step back up 1px
  const MAX_FALL = 12 * 20; // 20 tiles
  let dy = 0;
  while (dy < MAX_FALL && !rectOverlapsWorldSolid(w, h, rect.x, rect.y + dy, rect.w, rect.h)) {
    dy += 1;
  }
  if (dy > 0) dy -= 1;
  return { x: rect.x, y: rect.y + dy, w: rect.w, h: rect.h };
}

function tryStackAbovePlank(w, h, rect) {
  // Try to place the rect immediately above an overlapping plank below
  // without intersecting anything. Returns a new rect or null.
  let best = null;
  for (const p of planks) {
    // Horizontal overlap check
    if ((rect.x + rect.w) <= p.x || (p.x + p.w) <= rect.x) continue;
    const candidateY = p.y - rect.h;
    const test = { x: rect.x, y: candidateY, w: rect.w, h: rect.h };
    // Do not overlap the player
    const overlapsPlayer = !(player.x + player.w <= test.x || test.x + test.w <= player.x || player.y + player.h <= test.y || test.y + test.h <= player.y);
    if (overlapsPlayer) continue;
    if (!rectOverlapsWorldSolid(w, h, test.x, test.y, test.w, test.h)) {
      // Pick the highest valid candidate (smallest y)
      if (!best || test.y < best.y) best = test;
    }
  }
  return best;
}

function tryPlacePlank(w, h) {
  if (choppedCount <= 0) return false;

  // Use the same logic as the ghost preview for consistency
  const preview = computePlankPreviewRect(w, h);
  if (!preview || !preview.ok) return false;

  const finalRect = preview.rect;

  // Place and consume one log
  planks.push({ x: finalRect.x, y: finalRect.y, w: finalRect.w, h: finalRect.h, vy: 0 });
  choppedCount = Math.max(0, choppedCount - 1);

  // Reset builder ghost nudge after a placement to avoid drifting too far
  builderNudgeTiles = 0;

  // Toast: spending a log (red down arrow + log icon)
  addPickupToast('log_spend');
  return true;
}

function drawPlanks(ctx) {
  if (!planks.length) return;
  ctx.save();
  for (const p of planks) {
    // wood fill with subtle shading
    const grad = ctx.createLinearGradient(p.x, p.y, p.x, p.y + p.h);
    grad.addColorStop(0, '#7b5a3a');
    grad.addColorStop(1, '#5d4029');
    ctx.fillStyle = grad;
    ctx.fillRect(Math.round(p.x), Math.round(p.y), p.w, p.h);
    // edge lines
    ctx.strokeStyle = 'rgba(0,0,0,0.25)';
    ctx.lineWidth = 1;
    ctx.strokeRect(Math.round(p.x) + 0.5, Math.round(p.y) + 0.5, p.w - 1, p.h - 1);
  }
  ctx.restore();
}

function computePlankPreviewRect(w, h) {
  if (!builder.equipped || choppedCount <= 0) return null;
  const horizontal = !input.rotateHeld;
  const len = input.runHeld ? PLANK_LEN_RUN : PLANK_LEN_WALK;
  const wPlank = horizontal ? len : PLANK_THICK;
  const hPlank = horizontal ? PLANK_THICK : len;

  let x0, y0;

  // Use mouse position if available (desktop), otherwise use player position (keyboard/mobile)
  if (input.mousePresent && input.hasKeyboard) {
    // Convert canvas mouse coordinates to world coordinates
    // Canvas transform is: ctx.translate(-cameraX, cameraY)
    // So to convert back: worldX = mouseX/scale + cameraX, worldY = mouseY/scale - cameraY
    const worldX = cameraX + input.mouseX / VIEW_SCALE;
    const worldY = input.mouseY / VIEW_SCALE - cameraY;

    // Snap to tile grid and center the plank
    x0 = Math.round((worldX - wPlank / 2) / TILE_SIZE) * TILE_SIZE;
    y0 = Math.round((worldY - hPlank / 2) / TILE_SIZE) * TILE_SIZE;
  } else {
    // Fallback to player-relative placement (keyboard without mouse, or mobile)
    const dir = player.facing >= 0 ? 1 : -1;
    x0 = Math.round((player.x + (dir > 0 ? player.w + 4 : -wPlank - 4)) / TILE_SIZE) * TILE_SIZE;
    x0 += builderNudgeTiles * TILE_SIZE;
    const baseY = player.onGround
      ? (player.y + player.h - hPlank - 2)
      : (player.y + player.h * 0.35 - hPlank * 0.5);
    y0 = Math.round(baseY / TILE_SIZE) * TILE_SIZE;
  }

  let rect = { x: x0, y: y0, w: wPlank, h: hPlank };

  // Check if placement is valid
  const overlapsPlayer = !(player.x + player.w <= rect.x || rect.x + rect.w <= player.x || player.y + player.h <= rect.y || rect.y + rect.h <= player.y);
  let ok = choppedCount > 0 && !rectOverlapsWorldSolid(w, h, rect.x, rect.y, rect.w, rect.h) && !overlapsPlayer;
  let visRect = rect;

  // If overlapping, try stacking above existing planks
  if (!ok && rectOverlapsPlanks(rect.x, rect.y, rect.w, rect.h)) {
    const stacked = tryStackAbovePlank(w, h, rect);
    if (stacked && !rectOverlapsWorldSolid(w, h, stacked.x, stacked.y, stacked.w, stacked.h)) {
      visRect = stacked;
      ok = true;
    }
  }

  return { rect: visRect, ok };
}

// Plank physics: unsupported planks fall until touching solid ground or other planks
function updatePlanks(dt, w, h) {
  if (!planks.length) return;
  const GRAV = 2400; // px/s^2
  const MAX_FALL = 1400; // terminal velocity
  const groundTopY = h - world.groundH;

  // Helper: is there support directly below this rect (within 1-2px)
  const hasSupportBelow = (r) => {
    // Support from world tiles/terrain
    if (rectOverlapsWorldSolid(w, h, r.x, r.y + r.h + 1, r.w, 2)) return true;
    // Or from other planks touching edges
    for (const q of planks) {
      if (q === r) continue;
      // horizontally overlaps at least 1px and our bottom is at or within 1px above q.top
      const horiz = !(r.x + r.w <= q.x || q.x + q.w <= r.x);
      const touching = (r.y + r.h) <= q.y && (r.y + r.h + 2) >= q.y;
      if (horiz && touching) return true;
    }
    // Also consider the hard ground line as support (above-ground surface)
    if (r.y + r.h >= groundTopY - 1 && r.y + r.h <= groundTopY + 1) return true;
    return false;
  };

  for (const p of planks) {
    // Accelerate when unsupported
    const supported = hasSupportBelow(p);
    if (!supported) {
      p.vy = Math.min(MAX_FALL, (p.vy || 0) + GRAV * dt);
      // Predict drop distance this frame, but clamp to safe steps
      let dy = Math.max(0, Math.round((p.vy || 0) * dt));
      if (dy <= 0) dy = 1;
      // Move down until collision
      let moved = 0;
      while (moved < dy) {
        const step = Math.min(2, dy - moved);
        // If moving would overlap any solid (including other planks), stop and stick just above
        if (rectOverlapsWorldSolid(w, h, p.x, p.y + step, p.w, p.h)) {
          // Snap down one pixel at a time until touching or blocked
          while (!rectOverlapsWorldSolid(w, h, p.x, p.y + 1, p.w, p.h)) {
            p.y += 1;
          }
          p.vy = 0;
          break;
        }
        p.y += step;
        moved += step;
        // If we found support after moving, stop
        if (hasSupportBelow(p)) { p.vy = 0; break; }
      }
    } else {
      // Supported: cancel vertical velocity
      p.vy = 0;
    }
  }
}

function drawPlankGhost(ctx, w, h) {
  const prev = computePlankPreviewRect(w, h);
  if (!prev) return;
  const { rect, ok } = prev;

  // More vibrant colors with better visibility
  const color = ok ? 'rgba(100,220,140,0.45)' : 'rgba(255,100,100,0.45)';
  const border = ok ? 'rgba(100,220,140,0.95)' : 'rgba(255,100,100,0.95)';

  ctx.save();

  // Draw fill
  ctx.fillStyle = color;
  ctx.fillRect(Math.round(rect.x), Math.round(rect.y), rect.w, rect.h);

  // Draw border with slightly thicker line for visibility
  ctx.strokeStyle = border;
  ctx.lineWidth = 2;
  ctx.strokeRect(Math.round(rect.x)+1, Math.round(rect.y)+1, rect.w-2, rect.h-2);

  // Add subtle wood grain pattern for valid placements
  if (ok) {
    ctx.strokeStyle = 'rgba(255,255,255,0.15)';
    ctx.lineWidth = 1;
    const numLines = rect.h > rect.w ? 3 : 5;
    if (rect.h > rect.w) {
      // Vertical plank - horizontal grain
      for (let i = 1; i < numLines; i++) {
        const y = rect.y + (rect.h / numLines) * i;
        ctx.beginPath();
        ctx.moveTo(rect.x + 2, y);
        ctx.lineTo(rect.x + rect.w - 2, y);
        ctx.stroke();
      }
    } else {
      // Horizontal plank - vertical grain
      for (let i = 1; i < numLines; i++) {
        const x = rect.x + (rect.w / numLines) * i;
        ctx.beginPath();
        ctx.moveTo(x, rect.y + 2);
        ctx.lineTo(x, rect.y + rect.h - 2);
        ctx.stroke();
      }
    }
  }

  ctx.restore();
}

function tryRemovePlankAtCircle(cx, cy, r) {
  // Find first plank intersecting the circle and remove it; refund one log
  const circleIntersectsRect = (rx, ry, rw, rh) => {
    const nearestX = Math.max(rx, Math.min(cx, rx + rw));
    const nearestY = Math.max(ry, Math.min(cy, ry + rh));
    const dx = cx - nearestX, dy = cy - nearestY;
    return (dx * dx + dy * dy <= r * r);
  };
  for (let i = 0; i < planks.length; i++) {
    const p = planks[i];
    if (circleIntersectsRect(p.x, p.y, p.w, p.h)) {
      planks.splice(i, 1);
      choppedCount += 1;
      addPickupToast('log');
      particles.spawnSparks(cx, cy, 6);
      return true;
    }
  }
  return false;
}

function drawChopCounter(ctx) {
  if (!logReady || choppedCount <= 0) return;
  const pad = 10;
  const x = 12, y = 12;
  const targetH = 22;
  const aspect = logImg.naturalWidth && logImg.naturalHeight ? (logImg.naturalWidth / logImg.naturalHeight) : 1;
  const dh = targetH;
  const dw = Math.max(1, Math.round(targetH * aspect));
  // optional subtle bg
  ctx.save();
  ctx.globalAlpha = 0.85;
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.beginPath();
  ctx.roundRect?.(x - 6, y - 6, dw + 60, dh + 12, 8);
  if (!ctx.roundRect) { ctx.fillRect(x - 6, y - 6, dw + 60, dh + 12); } else { ctx.fill(); }
  ctx.globalAlpha = 1;
  ctx.drawImage(logImg, x, y, dw, dh);
  ctx.fillStyle = '#fff';
  ctx.font = '16px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  ctx.fillText(`x ${choppedCount}`, x + dw + pad, y + dh - 4);
  ctx.restore();
}

function drawSilkCounter(ctx) {
  if (silkCount <= 0) return;
  const pad = 10;
  // Place to the right of the logs counter if visible, otherwise at default x
  const baseX = 12 + (choppedCount > 0 && logReady ? 90 : 0);
  const x = baseX, y = 12;
  const targetH = 22;
  const aspect = silkImg.naturalWidth && silkImg.naturalHeight ? (silkImg.naturalWidth / silkImg.naturalHeight) : 1;
  const dh = targetH;
  const dw = Math.max(1, Math.round(targetH * aspect));
  ctx.save();
  ctx.globalAlpha = 0.85;
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.beginPath();
  ctx.roundRect?.(x - 6, y - 6, dw + 60, dh + 12, 8);
  if (!ctx.roundRect) { ctx.fillRect(x - 6, y - 6, dw + 60, dh + 12); } else { ctx.fill(); }
  ctx.globalAlpha = 1;
  if (silkReady) ctx.drawImage(silkImg, x, y, dw, dh);
  ctx.fillStyle = '#fff';
  ctx.font = '16px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  ctx.fillText(`x ${silkCount}`, x + dw + pad, y + dh - 4);
  ctx.restore();
}

/**
 * Main game update loop - handles physics, AI, collisions, and game state
 * @param {number} dt - Delta time in seconds since last update
 */
function update(dt) {
  const w = world.width();
  const h = world.height();
  if (gameOverActive) {
    if (consumeDialogueInput() || consumeWorldInteract()) {
      respawnFromApocalypse();
    }
  }
  if (gameOverPending) {
    gameOverBurnT = Math.max(0, gameOverBurnT - dt);
    if (gameOverBurnT <= 0) {
      gameOverPending = false;
      gameOverActive = true;
    }
  }
  // Always track altitude (persists atmosphere after eject, drives cloud/platform fade)
  updateAltitude(dt);

  if (insideCave) {
    if (deepForestDebugTimer > 0) deepForestDebugTimer = Math.max(0, deepForestDebugTimer - dt);
    updateDialogue(dt);
    updateCaveInterior(dt, w, h);
    return;
  }

  if (insideMoon) {
    updateDialogue(dt);
    updateMoon(dt, w, h);
    return;
  }

  // Spaceship cinematic / flight mode — freeze normal world update
  if (spaceship.mode === 'entering' || spaceship.mode === 'occupied') {
    updateThrustParticles(dt);
    updateSpaceshipMode(dt, w, h);
    return;
  }
  if (spaceship.mode === 'transitioning') {
    updateMoonTransition(dt);
    return;
  }
  // Reaching here means we're on the surface (not in cave/moon/spaceship). Remember this
  // position as the save resume point (skip during the apocalypse catastrophe sequence).
  if (!apocalypseActive) {
    lastSurfaceX = player.x;
    lastSurfaceY = player.y;
  }
  if (deepForestDebugTimer > 0) deepForestDebugTimer = Math.max(0, deepForestDebugTimer - dt);
  // Intro Wisp timing
  if (wispIntro && wispIntro.active) {
    wispIntro.t += dt;
  }
  if (!groveLegend.seen) {
    const playerCenter = player.x + player.w * 0.5;
    if (playerCenter > arch.x + 20) {
      groveLegend.seen = true;
      groveLegend.active = true;
      groveLegend.t = 0;
    }
  }
  if (groveLegend.active) {
    groveLegend.t += dt;
    if (groveLegend.t >= groveLegend.duration) {
      groveLegend.active = false;
    }
  }
  if (dialogueState && dialogueState.cooldown > 0) {
    dialogueState.cooldown = Math.max(0, dialogueState.cooldown - dt);
  }
  // Update inventory hint timer
  if (inventoryHintTimer > 0) {
    inventoryHintTimer = Math.max(0, inventoryHintTimer - dt);
    // Update UI when timer expires to hide hint
    if (inventoryHintTimer === 0) {
      updateInventoryUI();
    }
  }
  // updateShooting(dt, w, h); // Now handled by skyRenderer
  if (controlsOverlayVisible && (input.leftHeld || input.rightHeld || input.jumpHeld || input.interactHeld)) {
    hideControlsOverlay();
  }
  if (axe.spawned && !axe.picked) advanceFloatingItem(axe, dt);
  if (hat.spawned && !hat.picked) advanceFloatingItem(hat, dt);
  if (SPAWN_PICKAXE && !pickaxe.picked) advanceFloatingItem(pickaxe, dt);
  if (SPAWN_SWORD && !sword.picked) advanceFloatingItem(sword, dt);
  if (SPAWN_RAYGUN && !raygun.picked) advanceFloatingItem(raygun, dt);
  if (toolbox.spawned && !toolbox.picked) advanceFloatingItem(toolbox, dt);
  if (jetback.spawned && !jetback.picked) advanceFloatingItem(jetback, dt);
  // Tick the spaceship line phase, particles, and empty-shell drift
  spaceship.linePhase = (spaceship.linePhase + dt * 1.6) % 1;
  updateThrustParticles(dt);
  if (spaceship.emptyActive) {
    spaceship.emptyVY -= 250 * dt; // keep accelerating upward
    spaceship.emptyY  += spaceship.emptyVY * dt;
    spaceship.emptyTimer += dt;
    if (spaceship.emptyTimer > 3.5) spaceship.emptyActive = false;
  }
  if (shrine.damageFlash > 0) {
    shrine.damageFlash = Math.max(0, shrine.damageFlash - dt);
  }
  updateShrineBloomTrees(dt);
  updateBarrierVines(dt);
  if (goblin.spawned && goblinReady) {
    goblin.spawnTime = Math.min(goblin.spawnTime + dt, 4);
  }
  updateGoblinFamily(dt, w, h);
  if (verdantGrassActive && grassReveal < 20000) {
    grassReveal = Math.min(20000, grassReveal + GRASS_GROW_SPEED * dt);
  }
  updateCorruptTitanTests(dt, w, h);
  updateCorruptTitanGuard(dt, w, h);
  // Provide player screen rect to Input for relative-touch controls
  const psx_now = player.x - cameraX;
  const psy_now = player.y + cameraY;
  if (typeof input.setPlayerScreenRect === 'function') {
    // Input expects CSS pixels; convert world units by VIEW_SCALE
    input.setPlayerScreenRect(Math.round(psx_now * VIEW_SCALE), Math.round(psy_now * VIEW_SCALE), Math.round(player.w * VIEW_SCALE), Math.round(player.h * VIEW_SCALE));
  }
  // Capture the starting X once
  if (!firstUpdateRan) { firstUpdateRan = true; update._startPlayerX = player.x; }
  standingOnPlatform = false;
  currentPlatformCarry = null;
  const prevY = player.y;
  const prevBottom = player.y + player.h;
  const prevVy = player.vy;
  // Remember previous-frame position/velocity so enemy handling can detect stomps
  player._prevUpdateY = prevY;
  player._prevUpdateBottom = prevBottom;
  player._prevUpdateVy = prevVy;

  if (!controlGuideState.move) {
    const movedDist = Math.abs(player.x - (update._startPlayerX || player.x));
    if (movedDist >= 48) {
      completeControlGuide('move');
    } else if (controlGuideActive === null || controlGuideActive === 'move') {
      showControlGuide('move');
    }
  }
  // Builder grip-snapped lateral nudging: while holding Shift, use left/right to nudge ghost by tiles
  if (builder.equipped && input.runHeld) {
    const dir = (input.rightHeld ? 1 : 0) - (input.leftHeld ? 1 : 0);
    // Repeat rate and initial delay
    const REPEAT = 0.12; // seconds per step while held
    if (dir !== 0) {
      if (builderNudgeTimer <= 0) {
        builderNudgeTiles = Math.max(-12, Math.min(12, builderNudgeTiles + dir));
        builderNudgeTimer = REPEAT;
      } else {
        builderNudgeTimer -= dt;
      }
    } else {
      // No directional intent while gripping: let timer elapse so next press is immediate
      builderNudgeTimer = 0;
    }
  } else {
    // Not gripping: reset timer so next grip starts immediately
    builderNudgeTimer = 0;
  }
  // If placing a plank with builder, suppress jump for this frame so Up acts as a modifier only
  if (builder.equipped && (input.interactQueued)) {
    input.consumeJump();
  }
  // Compute local ground deformation under the player
  // Tile-based depth (phase 1): sample at both feet for stability
  const leftX = player.x + player.w * 0.2;
  const rightX = player.x + player.w * 0.8;
  // Tile-based depth calculation (legacy columnar system removed)
  const dL = tileDepthAtX(w, h, leftX);
  const dR = tileDepthAtX(w, h, rightX);
  const holeDepth = Math.max(dL, dR);

  // Determine player's current depth from ground line
  const playerDepth = (player.y + player.h) - (h - world.groundH);

  // Physics now uses tile collision directly (no smoothing needed)
  const groundHEff = world.groundH;
  // Jetback: boost speed when moving horizontally above surface
  const JETBACK_SPEED_MUL = 1.5;
  const movingHoriz = input.leftHeld || input.rightHeld;
  const aboveSurface = playerDepth <= 0;
  jetback.active = !!(jetback.equipped && movingHoriz && aboveSurface);
  let speedMul = (gameOverPending || gameOverActive) ? 0 : (jetback.active ? JETBACK_SPEED_MUL : 1);
  if (playerStickyTime > 0) speedMul *= 0.25;
  // Track ground state before update to detect jump edges
  const wasOnGround = player.onGround;

  // Sword swing on interact with dash (BEFORE player.update so dash velocity takes priority)
  if (sword.equipped && consumeWorldInteract()) {
    swordSwingActive = true; swordSwingT = 0; swordSwingHit = false;
    swordDashActive = true;
    // Apply instant dash velocity in facing direction
    player.vx = player.facing * SWORD_DASH_SPEED;
    // Create slash arc visual
    swordSlashArc = { startT: performance.now ? performance.now() : Date.now(), facing: player.facing };
    // Visual effect: spawn initial sparks at player position
    particles.spawnSparks(player.x + player.w / 2, player.y + player.h / 2, 6, ['#7dd3fc', '#38bdf8', '#0ea5e9']);
  }

  // When burning (turning to ash), freeze player completely - no movement or physics
  if (!gameOverPending && !gameOverActive) {
    // During sword dash, temporarily suppress horizontal input to prevent walk acceleration from overriding dash
    const savedLeftHeld = input.leftHeld;
    const savedRightHeld = input.rightHeld;
    if (swordDashActive) {
      input.leftHeld = false;
      input.rightHeld = false;
    }

    // No surface clamp; rely purely on collision resolution
    player.update(dt, input, { width: w, height: h, groundH: groundHEff, doubleJump: hat.equipped, speedMul, clampGround: false });

    // Restore input state
    if (swordDashActive) {
      input.leftHeld = savedLeftHeld;
      input.rightHeld = savedRightHeld;
    }

    // Re-apply dash velocity after player.update to maintain dash momentum
    if (swordDashActive) {
      player.vx = player.facing * SWORD_DASH_SPEED;
    }
  } else {
    // Freeze player in place when dead/burning
    player.vx = 0;
    player.vy = 0;
  }
  // Prevent escaping the world on the far left (world bound)
  if (player.x < 0) { player.x = 0; if (player.vx < 0) player.vx = 0; }
  if (!shrine.activated && !barrierVines.removed && barrierVines.fade > 0.05) {
    const { left } = barrierVinesBlockRange();
    const maxRight = left;
    if (player.x + player.w > maxRight) {
      player.x = maxRight - player.w;
      if (player.vx > 0) player.vx = 0;
    }
  }
  if (corruptTitanGuardReady && corruptTitanGuard && !corruptTitanGuard.defeated) {
    const bodyRect = getCorruptTitanBodyRect(corruptTitanGuard);
    if (bodyRect) {
      const blockEdge = bodyRect.x + CORRUPT_TITAN_BODY_BLOCK_INSET;
      if (player.x + player.w > blockEdge) {
        player.x = blockEdge - player.w;
        if (player.vx > 0) player.vx = 0;
      }
    }
  }
  const groundTop = h - world.groundH;
  const playerFeet = player.y + player.h;
  // Arm cloud bounce only after touching the actual ground line (not platforms) once
  const playerDepthNowGround = playerFeet - groundTop;
  const emptyDepthHere = tileDepthAtX(w, h, player.x + player.w * 0.5);
  const nearGroundLine = (playerDepthNowGround > -6 && playerDepthNowGround < 12);
  if (player.onGround && nearGroundLine) skyRenderer.armCloudBounce(true);
  const cloudBounceArmed = skyRenderer.isCloudBounceArmed();

  // Side-scrolling with hysteresis + easing:
  // - On ground: Scroll right at ~50% screen, left at ~33%
  // - In air (near clouds): Much wider deadzone for free exploration
  // - Landing anchor: When landing beyond normal threshold, camera sticks until you move back
  // - Smooth transition based on height above ground

  // Calculate height factor (0 = on ground, 1 = high in air near clouds)
  const playerHeightNow = groundTop - (player.y + player.h);
  const cloudHeight = h * 0.4; // Height where clouds typically are
  const heightFactor = Math.max(0, Math.min(1, playerHeightNow / cloudHeight));

  // Ground thresholds (tight camera)
  const groundRightThreshold = 0.50;
  const groundLeftThreshold = 0.33;

  // Air thresholds (very loose camera - nearly full screen freedom)
  const airRightThreshold = 0.85; // Need to be at 85% of screen to scroll right
  const airLeftThreshold = 0.10;  // Need to be at 10% of screen to scroll left

  // Only allow air thresholds when high enough (above double-jump height, around sun level)
  const sunHeightThreshold = 0.65; // Must be above this heightFactor to get air thresholds
  const isAboveSunHeight = heightFactor >= sunHeightThreshold;

  // Detect landing transition (from air to ground) using onGround state
  const justLanded = !previousOnGround && player.onGround;
  const justTookOff = previousOnGround && !player.onGround;
  const isHighInAir = isAboveSunHeight; // Only count as "high" when above sun height

  const screenX = player.x - cameraX;
  const screenRatio = screenX / w;

  // Track if player has been high in air (to prevent interpolation during descent)
  if (isHighInAir) {
    wasHighInAir = true;
  }

  if (justLanded) {
    // Only set landing anchor if we were high in the air (to prevent snap-back from high jumps)
    // Short jumps don't need anchor since player stays within normal thresholds
    if (wasHighInAir) {
      landingAnchorRatio = screenRatio;
    }
    wasHighInAir = false; // Reset flag after landing
    jumpOffThresholds = null; // Clear jump-off thresholds
  }

  // Determine thresholds based on ground/air state and anchor
  let rightThresholdRatio;
  let leftThresholdRatio;

  if (!player.onGround) {
    // IN AIR - only use jump-off threshold system when high enough to matter
    if (isAboveSunHeight && justTookOff && jumpOffThresholds === null) {
      // Store the thresholds we had at jump-off (including anchor if present)
      const hadAnchor = landingAnchorRatio !== null;
      if (hadAnchor) {
        const anchorOnRight = landingAnchorRatio > groundRightThreshold;
        const anchorOnLeft = landingAnchorRatio < groundLeftThreshold;
        if (anchorOnRight) {
          jumpOffThresholds = { right: landingAnchorRatio, left: groundLeftThreshold };
        } else if (anchorOnLeft) {
          jumpOffThresholds = { right: groundRightThreshold, left: landingAnchorRatio };
        } else {
          jumpOffThresholds = { right: groundRightThreshold, left: groundLeftThreshold };
        }
      } else {
        jumpOffThresholds = { right: groundRightThreshold, left: groundLeftThreshold };
      }
    }

    // Use jump-off thresholds and interpolate to air thresholds only when high enough
    if (wasHighInAir) {
      // Keep full air thresholds while descending from height
      rightThresholdRatio = airRightThreshold;
      leftThresholdRatio = airLeftThreshold;
    } else if (jumpOffThresholds && isAboveSunHeight) {
      // Interpolate to air thresholds when above sun height
      const adjustedFactor = (heightFactor - sunHeightThreshold) / (1 - sunHeightThreshold);
      rightThresholdRatio = jumpOffThresholds.right + (airRightThreshold - jumpOffThresholds.right) * adjustedFactor;
      leftThresholdRatio = jumpOffThresholds.left + (airLeftThreshold - jumpOffThresholds.left) * adjustedFactor;
    } else {
      // Below sun height or no jumpOffThresholds - use ground thresholds
      rightThresholdRatio = groundRightThreshold;
      leftThresholdRatio = groundLeftThreshold;
    }
  } else if (landingAnchorRatio !== null) {
    // ON GROUND with landing anchor - preserve player's landing position
    // Supports both right and left landings

    // Determine if anchor is on right side (beyond 50%) or left side (below 33%)
    const anchorOnRight = landingAnchorRatio > groundRightThreshold;
    const anchorOnLeft = landingAnchorRatio < groundLeftThreshold;

    if (anchorOnRight) {
      // Landed on right side - anchor acts as right threshold, ratchets down
      rightThresholdRatio = landingAnchorRatio;
      leftThresholdRatio = groundLeftThreshold;

      // Ratchet down as player moves left
      if (screenRatio < landingAnchorRatio) {
        landingAnchorRatio = screenRatio;
      }

      // Clear when player crosses back into normal zone
      if (screenRatio <= groundRightThreshold) {
        landingAnchorRatio = null;
      }
    } else if (anchorOnLeft) {
      // Landed on left side - anchor acts as left threshold, ratchets up
      rightThresholdRatio = groundRightThreshold;
      leftThresholdRatio = landingAnchorRatio;

      // Ratchet up as player moves right
      if (screenRatio > landingAnchorRatio) {
        landingAnchorRatio = screenRatio;
      }

      // Clear when player crosses back into normal zone
      if (screenRatio >= groundLeftThreshold) {
        landingAnchorRatio = null;
      }
    } else {
      // Anchor is in normal zone (between 33% and 50%) - use normal thresholds
      rightThresholdRatio = groundRightThreshold;
      leftThresholdRatio = groundLeftThreshold;
      landingAnchorRatio = null; // Clear anchor
    }
  } else {
    // ON GROUND, no anchor - normal ground thresholds
    rightThresholdRatio = groundRightThreshold;
    leftThresholdRatio = groundLeftThreshold;
    wasHighInAir = false; // Reset flag when back on ground
  }

  const rightThreshold = w * rightThresholdRatio;
  const leftThreshold = w * leftThresholdRatio;
  let targetCameraX = cameraX;
  if (screenX > rightThreshold) {
    targetCameraX = Math.max(0, player.x - rightThreshold);
  } else if (screenX < leftThreshold) {
    targetCameraX = Math.max(0, player.x - leftThreshold);
  }
  const k = 8; // higher = snappier camera
  const t = 1 - Math.exp(-k * dt);
  cameraX += (targetCameraX - cameraX) * t;

  // Store state for next frame's landing detection
  previousHeightFactor = heightFactor;
  previousOnGround = player.onGround;

  // Prevent player from moving out of the screen to the left (only above surface).
  // When underground, do NOT clamp to the camera edge to avoid horizontal teleports
  // if the camera is ahead of the player.
  const isUndergroundNow = (playerFeet > groundTop) || (emptyDepthHere > TILE_SIZE * 0.5);
  const wasUnderground = playerIsUnderground;
  playerIsUnderground = isUndergroundNow;

  // Show ascend guide when player is significantly underground (a few digs deep)
  const undergroundDepth = Math.max(0, playerFeet - groundTop);
  const ASCEND_GUIDE_DEPTH = TILE_SIZE * 3; // Show after digging ~3 blocks deep
  const deepEnoughUnderground = undergroundDepth > ASCEND_GUIDE_DEPTH;

  if (!controlGuideState.ascend && deepEnoughUnderground && !wasUnderground) {
    showControlGuide('ascend');
  } else if (controlGuideActive === 'ascend' && !deepEnoughUnderground) {
    hideControlGuide('ascend');
  }

  if (!isUndergroundNow && player.x < cameraX) {
    player.x = cameraX; if (player.vx < 0) player.vx = 0;
  }

  // Prevent player from going past right edge of screen (hard cap at 98%)
  const maxScreenRatio = 0.98;
  const maxRightX = cameraX + w * maxScreenRatio - player.w;
  if (player.x > maxRightX) {
    player.x = maxRightX;
    if (player.vx > 0) player.vx = 0;
  }

  updateRockTitan(dt, w, h);
  updateDeepForestElements(dt, w, h);
  if (enterableCave.x === 0 && rockTitan && typeof rockTitan.x === 'number') {
    enterableCave.x = rockTitan.x + ENTERABLE_CAVE_OFFSET;
  }
  // Update deep forest audio with gradual transition
  if (!insideCave) {
    updateDeepForestAudioTransition(player.x + player.w * 0.5);
  }

  // Update ambiance state based on location with gradual underground transition
  if (!insideCave) {
    const undergroundDepth = Math.max(0, playerFeet - groundTop);
    const playerCenterX = player.x + player.w * 0.5;
    const dfStart = deepForestStartX();

    // Only play forest ambiance when in/near deep forest
    const distanceToDeepForest = Number.isFinite(dfStart) ? (dfStart - playerCenterX) : Infinity;
    const isNearDeepForest = distanceToDeepForest < DEEP_FOREST_AUDIO_TRANSITION_DISTANCE;

    // Maintain forest ambiance state when near deep forest and not deep underground
    if (undergroundDepth < UNDERGROUND_TRANSITION_START_DEPTH + UNDERGROUND_TRANSITION_DISTANCE) {
      if (isNearDeepForest && currentAmbianceState !== 'forest') {
        setAmbianceState('forest');
      } else if (!isNearDeepForest && currentAmbianceState === 'forest') {
        setAmbianceState('none');
      }
    }

    // Apply gradual audio transition based on depth
    updateUndergroundAudioTransition(undergroundDepth);
  }

  updatePlantTraps(dt, w, h);
  if (playerStickyTime > 0) playerStickyTime = Math.max(0, playerStickyTime - dt);
  if (playerHitFlash > 0) playerHitFlash = Math.max(0, playerHitFlash - dt);

  // Update sky renderer (shooting stars, lightning, clouds)
  const playerHeightAboveGround = Math.max(0, groundTop - playerFeet);
  skyRenderer.update(dt, w, h, playerHeightAboveGround);

  // Sync cloud values for compatibility
  cloudDrift = skyRenderer.getCloudDrift();
  cloudCamY = skyRenderer.getCloudCamY();

  // HelicoHat spin: on second jump, queue exactly one full rotation (2π)
  if (hat.equipped && player.justDidAirJump) {
    hatSpinRemain += Math.PI * 2;
  }
  // Axe: also spin on the second (air) jump, mirroring hat behavior
  if (axe.equipped && player.justDidAirJump) {
    axeSpinRemain += Math.PI * 2;
  }

  // Dismiss touch controls hint after user-driven interaction (not spawn physics)
  if (touchHintActive && touchHintEl) {
    // Count rising edges of inputs as discrete actions
    if (input.leftHeld && !hintPrevLeft) hintActionCount++;
    if (input.rightHeld && !hintPrevRight) hintActionCount++;
    if (input.jumpHeld && !hintPrevJump) hintActionCount++;
    if (input.interactHeld && !hintPrevInteract) hintActionCount++;
    hintPrevLeft = input.leftHeld; hintPrevRight = input.rightHeld; hintPrevJump = input.jumpHeld; hintPrevInteract = input.interactHeld;

    const moved = Math.abs(player.x - (update._startPlayerX || player.x)) > 280; // require substantial movement
    const anyInput = input.leftHeld || input.rightHeld || input.jumpHeld || input.interactHeld;
    const elapsed = ((typeof performance !== 'undefined' ? performance.now() : Date.now()) - hintStartTS) / 1000;
    const enoughActions = hintActionCount >= 3;
    const timeUp = elapsed >= HINT_FORCE_HIDE_SEC;
    if ((elapsed >= HINT_MIN_SHOW_SEC && (enoughActions || moved)) || timeUp || anyInput && elapsed >= HINT_MIN_SHOW_SEC && userGestureSeen && (moved || hintActionCount >= 2)) {
      // Keep it non-blocking: simply remove the element
      try { touchHintEl.remove(); } catch {}
      touchHintActive = false;
    }
  }
  // Integrate spin to consume queued rotation at a constant speed
  const HAT_SPIN_SPEED = 12; // radians per second
  if (hat.equipped && hatSpinRemain > 0) {
    const add = Math.min(hatSpinRemain, HAT_SPIN_SPEED * dt);
    hatSpin += add;
    hatSpinRemain -= add;
    // keep angle bounded
    if (hatSpin > Math.PI * 4) hatSpin -= Math.PI * 2;
  }

  // Axe fun spin: small chance to spin when jumping (leaving ground upward)
  if (axe.equipped && wasOnGround && !player.onGround && player.vy < -50) {
    if (Math.random() < 0.40) axeSpinRemain += Math.PI * 2; // 40% chance to spin on jump
  }
  if (axeSpinRemain > 0) {
    const add = Math.min(axeSpinRemain, AXE_SPIN_SPEED * dt);
    // Invert spin direction (counter the previous orientation)
    axeSpin -= add;
    axeSpinRemain -= add;
    if (axeSpin < -Math.PI * 4) axeSpin += Math.PI * 2;
  }

  // Vertical camera with hysteresis and cloud-flight cap:
  // keeps the player centered when flying toward the sky, eases back to ground view,
  // and biases toward the action while still respecting ground visibility.
  const topEnter = h * 0.18; // engage when above this (closer to top)
  const topExit  = h * 0.32; // disengage when comfortably below
  const bottomEnter = h * 0.82; // engage when below this (closer to bottom)
  const bottomExit  = h * 0.68; // disengage when comfortably above
  const ky = 6; // easing factor
  const ty = 1 - Math.exp(-ky * dt);
  let screenY = player.y + cameraY;
  if (cloudBounceArmed) {
    if (!player.onGround && !verticalFollowActive && screenY < topEnter) verticalFollowActive = true;
    if (verticalFollowActive && (player.onGround || screenY > topExit)) verticalFollowActive = false;
    const screenYBottom = screenY + player.h;
    if (!player.onGround && !bottomFollowActive && screenYBottom > bottomEnter) bottomFollowActive = true;
    if (bottomFollowActive && (player.onGround || screenYBottom < bottomExit)) bottomFollowActive = false;
  } else {
    verticalFollowActive = false;
    bottomFollowActive = false;
  }

  const topThreshold = h * 0.18; // target line to hold when active (player top)
  const bottomThreshold = h * 0.82; // target line to hold when active (player bottom)
  let targetCamY = 0;
  if (verticalFollowActive && !player.onGround) {
    targetCamY = Math.max(0, topThreshold - player.y);
  } else if (bottomFollowActive) {
    // Move world up (cameraY negative) to hold player's bottom near bottomThreshold
    targetCamY = bottomThreshold - (player.y + player.h);
  } else {
    // Raise ground position slightly so player sits higher when idle
    targetCamY = -h * 0.08;
  }
  // Apply camera smoothing
  cameraY += (targetCamY - cameraY) * ty;

  // When underground, bias the camera upward so we see more of the tunnel below
  // Reduced bias to keep character more centered vertically
  const depthBelowSurface = Math.max(0, playerDepthNowGround);
  const desiredUndergroundBias = depthBelowSurface > 0
    ? -Math.min(depthBelowSurface * 0.05, h * 0.08)
    : 0;
  const biasEase = 1 - Math.exp(-(depthBelowSurface > 0 ? 4 : 8) * dt);
  undergroundCameraBias += (desiredUndergroundBias - undergroundCameraBias) * biasEase;
  if (depthBelowSurface <= 0.5) {
    undergroundCameraBias *= Math.pow(0.2, dt); // quickly settle when resurfacing
  }
  cameraY += undergroundCameraBias;

  // Ensure player never leaves the screen vertically
  screenY = player.y + cameraY;
  if (screenY < 0) cameraY = -player.y; // keep player visible at top (screenY = 0)
  // When underground, allow player to sit at 60% from top instead of bottom edge
  // This gives visibility of what's below while digging down
  const maxVerticalRatio = isUndergroundNow ? 0.60 : 1.0;
  const maxScreenY = h * maxVerticalRatio - player.h;
  if (screenY > maxScreenY) cameraY = maxScreenY - player.y;

  // Hard cap: player cannot go above 20% from top; camera matches player speed beyond this line
  const capLine = h * 0.20;
  screenY = player.y + cameraY;
  if (cloudBounceArmed && screenY < capLine) {
    cameraY = capLine - player.y;
  }

  // Sync clouds vertical camera with world camera so ascending reveals higher clouds
  skyRenderer.setCloudCamY(cameraY);
  cloudCamY = cameraY; // compatibility

  // ITEM PICKUPS: Check these FIRST before NPC dialogue interactions
  // This ensures players can pick up items even when near NPCs

  // Axe overlap + pickup (S)
  axeOverlap = false;
  if (axe.spawned && !axe.picked && axeReady) {
    const groundTop = h - world.groundH;
    const targetW = 42;
    const aspect = axeImg.naturalWidth && axeImg.naturalHeight ? (axeImg.naturalWidth / axeImg.naturalHeight) : 1;
    const dw = targetW;
    const dh = Math.max(1, Math.round(targetW / aspect));
    const axeRect = { x: axe.x - dw / 2, y: groundTop - dh, w: dw, h: dh };
    const plyRect = { x: player.x, y: player.y, w: player.w, h: player.h };
    const overlap = !(axeRect.x + axeRect.w < plyRect.x || plyRect.x + plyRect.w < axeRect.x || axeRect.y + axeRect.h < plyRect.y || plyRect.y + plyRect.h < axeRect.y);
    if (overlap) {
      axeOverlap = true;
      if (consumeWorldInteract()) {
        axe.picked = true;
        axe.spawned = false;
        equipWeapon('axe');
        addPickupToast('axe');
        updateInventoryUI();
      }
    }
  }

  // Hat overlap + pickup (same logic)
  hatOverlap = false;
  if (hat.spawned && !hat.picked && hatReady) {
    const groundTop = h - world.groundH;
    const targetW = 36;
    const aspect = hatImg.naturalWidth && hatImg.naturalHeight ? (hatImg.naturalWidth / hatImg.naturalHeight) : 1;
    const dw = targetW;
    const dh = Math.max(1, Math.round(targetW / aspect));
    const hatRect = { x: hat.x - dw / 2, y: groundTop - dh, w: dw, h: dh };
    const plyRect = { x: player.x, y: player.y, w: player.w, h: player.h };
    const overlap = !(hatRect.x + hatRect.w < plyRect.x || plyRect.x + plyRect.w < hatRect.x || hatRect.y + hatRect.h < plyRect.y || plyRect.y + plyRect.h < hatRect.y);
    if (overlap) {
      hatOverlap = true;
      if (consumeWorldInteract()) {
        hat.picked = true;
        hat.equipped = true;
        addPickupToast('helicohat');
        updateInventoryUI();
      }
    }
  }

  // Pickaxe collectible
  pickaxeOverlap = false;
  if (SPAWN_PICKAXE && !pickaxe.picked && pickaxeReady) {
    const groundTop = h - world.groundH;
    const targetW = 42;
    const aspect = pickaxeImg.naturalWidth && pickaxeImg.naturalHeight ? (pickaxeImg.naturalWidth / pickaxeImg.naturalHeight) : 1;
    const dw = targetW;
    const dh = Math.max(1, Math.round(targetW / aspect));
    const pxRect = { x: pickaxe.x - dw / 2, y: groundTop - dh, w: dw, h: dh };
    const plyRect = { x: player.x, y: player.y, w: player.w, h: player.h };
    const overlap = !(pxRect.x + pxRect.w < plyRect.x || plyRect.x + plyRect.w < pxRect.x || pxRect.y + pxRect.h < plyRect.y || plyRect.y + plyRect.h < pxRect.y);
    if (overlap) {
      pickaxeOverlap = true;
      if (consumeWorldInteract()) {
        pickaxe.picked = true;
        equipWeapon('pickaxe'); // auto-equip on pickup so players can dig immediately
        addPickupToast('pickaxe');
      }
    }
  }

  // Sword collectible
  swordOverlap = false;
  if (SPAWN_SWORD && !sword.picked && swordReady) {
    const groundTop = h - world.groundH;
    const targetW = 40;
    const aspect = swordImg.naturalWidth && swordImg.naturalHeight ? (swordImg.naturalWidth / swordImg.naturalHeight) : 1;
    const dw = targetW;
    const dh = Math.max(1, Math.round(targetW / aspect));
    const swRect = { x: sword.x - dw / 2, y: groundTop - dh, w: dw, h: dh };
    const plyRect = { x: player.x, y: player.y, w: player.w, h: player.h };
    const overlap = !(swRect.x + swRect.w < plyRect.x || plyRect.x + plyRect.w < swRect.x || swRect.y + swRect.h < plyRect.y || plyRect.y + plyRect.h < swRect.y);
    if (overlap) {
      swordOverlap = true;
      if (consumeWorldInteract()) {
        sword.picked = true;
        equipWeapon('sword');
        addPickupToast('sword');
        updateInventoryUI();
      }
    }
  }

  // Raygun collectible
  raygunOverlap = false;
  if (SPAWN_RAYGUN && !raygun.picked && raygunReady) {
    const groundTop = h - world.groundH;
    const targetW = 42;
    const aspect = raygunImg.naturalWidth && raygunImg.naturalHeight ? (raygunImg.naturalWidth / raygunImg.naturalHeight) : 1;
    const dw = targetW;
    const dh = Math.max(1, Math.round(targetW / aspect));
    const rgRect = { x: raygun.x - dw / 2, y: groundTop - dh, w: dw, h: dh };
    const plyRect = { x: player.x, y: player.y, w: player.w, h: player.h };
    const overlap = !(rgRect.x + rgRect.w < plyRect.x || plyRect.x + plyRect.w < rgRect.x || rgRect.y + rgRect.h < plyRect.y || plyRect.y + plyRect.h < rgRect.y);
    if (overlap) {
      raygunOverlap = true;
      if (consumeWorldInteract()) {
        raygun.picked = true;
        equipWeapon('raygun');
        addPickupToast('raygun');
        updateInventoryUI();
      }
    }
  }

  // Toolbox collectible (builder tool)
  toolboxOverlap = false;
  if (toolbox.spawned && !toolbox.picked && toolboxReady) {
    const groundTop = h - world.groundH;
    const targetW = 46;
    const aspect = toolboxImg.naturalWidth && toolboxImg.naturalHeight ? (toolboxImg.naturalWidth / toolboxImg.naturalHeight) : 1;
    const dw = targetW;
    const dh = Math.max(1, Math.round(targetW / aspect));
    const tbRect = { x: toolbox.x - dw / 2, y: groundTop - dh, w: dw, h: dh };
    const plyRect = { x: player.x, y: player.y, w: player.w, h: player.h };
    const overlap = !(tbRect.x + tbRect.w < plyRect.x || plyRect.x + plyRect.w < tbRect.x || tbRect.y + tbRect.h < plyRect.y || plyRect.y + plyRect.h < tbRect.y);
    if (overlap) {
      toolboxOverlap = true;
      if (consumeWorldInteract()) {
        toolbox.picked = true;
        builder.picked = true;
        // Do not auto-equip; let the player choose from inventory
        addPickupToast('toolbox');
        updateInventoryUI();
      }
    }
  }

  // Jetback collectible (rewarded by the goblin family)
  jetbackOverlap = false;
  if (jetback.spawned && !jetback.picked && jetOffReady) {
    const groundTop = h - world.groundH;
    const targetW = 42;
    const aspect = jetImgOff.naturalWidth && jetImgOff.naturalHeight ? (jetImgOff.naturalWidth / jetImgOff.naturalHeight) : 1;
    const dw = targetW;
    const dh = Math.max(1, Math.round(targetW / aspect));
    const jbRect = { x: jetback.x - dw / 2, y: groundTop - dh, w: dw, h: dh };
    const plyRect = { x: player.x, y: player.y, w: player.w, h: player.h };
    const overlap = !(jbRect.x + jbRect.w < plyRect.x || plyRect.x + plyRect.w < jbRect.x || jbRect.y + jbRect.h < plyRect.y || plyRect.y + plyRect.h < jbRect.y);
    if (overlap) {
      jetbackOverlap = true;
      if (consumeWorldInteract()) {
        jetback.picked = true;
        jetback.equipped = true;
        jetback.active = false;
        addPickupToast('jetback');
      }
    }
  }

  // Do Not Press button overlap (no action yet, just prompt)
  dnpOverlap = false;
  if (SHOW_DNP_BUTTON && dnpReady) {
    const groundTop = h - world.groundH;
    const targetW = 80; // Match the new bigger button size
    const aspect = dnpImg.naturalWidth && dnpImg.naturalHeight ? (dnpImg.naturalWidth / dnpImg.naturalHeight) : 1;
    const dw = targetW;
    const dh = Math.max(1, Math.round(targetW / aspect));
    const dnpRect = { x: dnpBtn.x - dw / 2, y: groundTop - dh, w: dw, h: dh };
    const plyRect = { x: player.x, y: player.y, w: player.w, h: player.h };
    const overlap = !(dnpRect.x + dnpRect.w < plyRect.x || plyRect.x + plyRect.w < dnpRect.x || dnpRect.y + dnpRect.h < plyRect.y || plyRect.y + plyRect.h < dnpRect.y);
    if (overlap) {
      dnpOverlap = true;
      if (consumeDialogueInput() && !apocalypseActive) {
        try {
          if (buttonPressSfx) {
            buttonPressSfx.currentTime = 0;
            buttonPressSfx.play?.();
          }
        } catch {}
        triggerApocalypse();
      }
    }
  }

  // Spaceship proximity check (E to enter)
  spaceshipOverlap = false;
  if (!insideCave && !apocalypseActive && spaceship.mode === 'idle') {
    const groundTop = h - world.groundH;
    const sz = spaceshipRenderSize(spaceshipOffImg.naturalWidth > 0 ? spaceshipOffImg : spaceshipEquipImg);
    const shipCX   = spaceship.x + sz.w / 2;
    const playerCX = player.x + player.w / 2;
    const playerFeet = player.y + player.h;
    if (Math.abs(playerCX - shipCX) < sz.w * 0.45 && playerFeet >= groundTop - 6 && playerFeet <= groundTop + 40) {
      spaceshipOverlap = true;
      if (consumeDialogueInput()) {
        enterSpaceship();
      }
    }
  }

  // Cave entrance interact (E/dialogue) - disabled during apocalypse
  caveEnterOverlap = false;
  if (!insideCave && !apocalypseActive) {
    const groundTop = h - world.groundH;
    const playerFeet = player.y + player.h;
    const nearEntrance = Math.abs((player.x + player.w * 0.5) - enterableCave.x) < (enterableCave.width || 220) * 0.6;
    if (nearEntrance && playerFeet >= groundTop - 6 && playerFeet <= groundTop + 40) {
      caveEnterOverlap = true;
      if (consumeDialogueInput()) {
        enterSurfaceCave();
      }
    }
  }

  // Deer riding: detect stomp from above to mount
  for (const d of deers) {
    if (d.type !== 'deer1' || d.rider) continue;
    const deerImg = (d.state === 'running' && deerRunFramesReady >= DEER_RUN_FRAME_COUNT) ? deerRunFrames[d.frame] : deerIdleImg;
    const deerH = getDeerRenderHeight(d); // Match render height per state
    const deerAspect = (deerImg && deerImg.naturalWidth && deerImg.naturalHeight) ? (deerImg.naturalWidth / deerImg.naturalHeight) : 1.5;
    const deerW = deerH * deerAspect;
    const rect = { x: d.x - deerW / 2, y: (h - world.groundH) - deerH + 6 + getDeerYOffset(d), w: deerW, h: deerH };
    const verticalHit = player.vy > 0 && (player.y + player.h) <= rect.y + deerH * 0.5;
    const overlap = !(rect.x + rect.w < player.x || player.x + player.w < rect.x || rect.y + rect.h < player.y || player.y + player.h < rect.y);
    if (overlap && verticalHit) {
      d.rider = player;
      d.state = 'running';
      d.facing = (player.x > d.x) ? -1 : 1;
      player.vy = 0;
      player.onGround = true;
      break;
    }
  }

  // NPC DIALOGUE INTERACTIONS: Check AFTER item pickups
  // This ensures item pickups have priority over dialogue

  goblin.overlap = false;
  if (goblin.spawned && goblinReady) {
    const playerCenterX = player.x + player.w * 0.5;
    // Don't update facing if goblin is running
    if (!goblin.runFromLeft) {
      goblin.facing = (playerCenterX < goblin.x) ? -1 : 1;
    }
    const rect = getGoblinRect(h);
    const gx = rect.x + rect.w * 0.5;
    const gy = rect.y + rect.h * 0.6;
    const playerCenterY = player.y + player.h * 0.55;
    if (!dialogueState.active && goblin.spawnTime > 0.25) {
      const dist = Math.hypot(playerCenterX - gx, playerCenterY - gy);
      if (dist <= goblin.talkRadius) {
        goblin.overlap = true;
        if (consumeDialogueInput()) {
          // If family has spawned (titan defeated), show thanks dialogue
          if (goblinFamilySpawned) {
            startDialogue('goblin_family_thanks', goblinFamilyThanksDialogue);
          } else {
            startDialogue('goblin_bwak', goblinDialoguePages);
          }
        }
      }
    }
  }

  rockTitan.overlap = false;
  rockTitan._rect = null;
  if (rockTitanBaseReady) {
    const infoForRect = rockTitanFrameInfo(rockTitan.attentive) || rockTitanFrameInfo(false);
    if (infoForRect) {
      const groundTopY = h - world.groundH;
      const rect = {
        x: rockTitan.x - infoForRect.width / 2,
        y: groundTopY - infoForRect.height + infoForRect.bottomOffset + ROCK_TITAN_SINK,
        w: infoForRect.width,
        h: infoForRect.height
      };
      rockTitan._rect = rect;
      const titanCenterX = rect.x + rect.w * 0.5;
      const titanCenterY = rect.y + rect.h * 0.45;
      const playerCenterX = player.x + player.w * 0.5;
      const playerCenterY = player.y + player.h * 0.55;
      const dist = Math.hypot(playerCenterX - titanCenterX, playerCenterY - titanCenterY);
      if (!dialogueState.active && dist <= ROCK_TITAN_TALK_RADIUS) {
        rockTitan.overlap = true;
        if (consumeDialogueInput()) {
          startDialogue('rock_titan', rockTitanDialoguePages);
        }
      }
    }
  }

  // Forest Shrine (spawns helicohat)
  forestShrine.overlap = false;
  if (forestShrineReady && forestShrine.h > 0) {
    const playerCenterX = player.x + player.w * 0.5;
    const playerCenterY = player.y + player.h * 0.55;
    const dist = Math.hypot(playerCenterX - forestShrine.x, playerCenterY - (h - world.groundH - forestShrine.h * 0.5));
    if (!dialogueState.active && dist <= forestShrine.interactRadius) {
      forestShrine.overlap = true;
      if (!forestShrine.activated && consumeDialogueInput()) {
        // Activate shrine
        forestShrine.activated = true;

        // Play shrine sound (similar to first shrine)
        audioManager.playSfx('audio/sfx/shrine.wav', 0.35);

        // Spawn static cloud above shrine
        if (!shrineCloud.spawned) {
          shrineCloud.spawned = true;
          refreshShrineCloudMetrics();
          shrineCloud.x = forestShrine.x;
          const groundTop = h - world.groundH;
          shrineCloud.y = groundTop - forestShrine.h - SHRINE_CLOUD_GAP - shrineCloud.h; // Above shrine with gap
        }

        // Spawn helicohat after activation
        if (!hat.spawned) {
          hat.spawned = true;
          hat.spawnTime = Date.now();
        }
      }
    }

    // Update glow transition
    if (forestShrine.activated && forestShrine.glowProgress < 1) {
      forestShrine.glowProgress = Math.min(1, forestShrine.glowProgress + dt * 0.8); // Smooth transition
    }
  }

  // WIP NPC (end of game marker)
  wipNpc.overlap = false;
  if (wipNpcReady) {
    const playerCenterX = player.x + player.w * 0.5;
    const playerCenterY = player.y + player.h * 0.55;
    wipNpc.facing = -1; // Always face left
    const dist = Math.hypot(playerCenterX - wipNpc.x, playerCenterY - (h - world.groundH - wipNpc.h * 0.5));
    if (!dialogueState.active && dist <= wipNpc.talkRadius) {
      wipNpc.overlap = true;
      if (consumeDialogueInput()) {
        startDialogue('wip_npc', apocalypseActive ? wipNpcApocalypseDialogue : wipNpcDialogue);
      }
    }
  }

  // Underground NPCs (helpers in caves)
  if (undergroundNpcReady && undergroundNpcs.length) {
    const playerCenterX = player.x + player.w * 0.5;
    const playerCenterY = player.y + player.h * 0.55;
    for (const npc of undergroundNpcs) {
      npc.overlap = false;
      npc.facing = (playerCenterX < npc.x) ? -1 : 1;
      const dist = Math.hypot(playerCenterX - npc.x, playerCenterY - (npc.y + npc.h * 0.5));
      if (!dialogueState.active && dist <= npc.talkRadius) {
        npc.overlap = true;
        if (consumeDialogueInput()) {
          startDialogue('underground_npc', undergroundNpcDialogue);
        }
        break; // Only interact with one NPC at a time
      }
    }
  }

  if (dialogueState.active) {
    const playerCenterX = player.x + player.w * 0.5;
    const playerCenterY = player.y + player.h * 0.5;
    let keepDialogue = true;
    if (dialogueState.npcId === 'goblin_bwak' || dialogueState.npcId === 'goblin_family_thanks') {
      const rect = getGoblinRect(h);
      const gx = rect.x + rect.w * 0.5;
      const gy = rect.y + rect.h * 0.5;
      const dist = Math.hypot(playerCenterX - gx, playerCenterY - gy);
      if (dist > goblin.talkRadius * 1.5) keepDialogue = false;
    } else if (dialogueState.npcId === 'rock_titan' && rockTitan._rect) {
      const rx = rockTitan._rect.x + rockTitan._rect.w * 0.5;
      const ry = rockTitan._rect.y + rockTitan._rect.h * 0.5;
      const dist = Math.hypot(playerCenterX - rx, playerCenterY - ry);
      if (dist > ROCK_TITAN_TALK_RADIUS * 1.5) keepDialogue = false;
    } else if (dialogueState.npcId === 'underground_npc') {
      const npc = undergroundNpcs.find(n => n.overlap);
      if (npc) {
        const dist = Math.hypot(playerCenterX - npc.x, playerCenterY - (npc.y + npc.h * 0.5));
        if (dist > npc.talkRadius * 1.5) keepDialogue = false;
      }
    }
    if (!keepDialogue) endDialogue();
  }

  const interactableNearby = (
    (axeOverlap && !axe.picked) ||
    (hatOverlap && !hat.picked) ||
    (pickaxeOverlap && !pickaxe.picked) ||
    (toolboxOverlap && !toolbox.picked) ||
    (jetbackOverlap && !jetback.picked) ||
    (raygunOverlap && !raygun.picked) ||
    (swordOverlap && !sword.picked) ||
    (goblin.overlap && !dialogueState.active) ||
    (rockTitan.overlap && !dialogueState.active) ||
    dnpOverlap
  );

  if (!controlGuideState.interact) {
    if (interactableNearby) {
      showControlGuide('interact');
    } else if (controlGuideActive === 'interact') {
      hideControlGuide('interact');
    }
  }

  // Chest interaction: open and fade out
  for (const ch of skyChests) {
    if (!ch || ch.fade <= 0) continue;
    const plyRect = { x: player.x, y: player.y, w: player.w, h: player.h };
    const overlap = !(ch.x + ch.w < plyRect.x || plyRect.x + plyRect.w < ch.x || ch.y + ch.h < plyRect.y || plyRect.y + plyRect.h < ch.y);
    if (overlap && !ch.open && consumeWorldInteract()) {
      ch.open = true;
      // Award sky loot
      if (ch.contents && ch.contents.kind === 'diamonds') {
        const amt = Math.max(1, Math.round(ch.contents.amount || 1));
        diamondCount += amt;
        // Show toast for diamonds
        for (let i = 0; i < amt; i++) {
          addPickupToast('diamond');
        }
      } else if (ch.contents && ch.contents.kind === 'wood') {
        const amt = Math.max(1, Math.round(ch.contents.amount || 10));
        choppedCount += amt;
        // Show toast for wood
        addPickupToast(`log_x${amt}`);
      }
      // green-ish sparkle
      const cx = ch.x + ch.w / 2, cy = ch.y + ch.h / 2;
      const green = ['#b7ffcc', '#84f59a', '#56d86d', '#a9f0c0'];
      for (let k = 0; k < 3; k++) particles.spawnSparks(cx, cy, 10, green);
    }
    if (ch.open) {
      ch.fade -= dt * 1.6;
    }
  }
  // Remove fully faded
  skyChests = skyChests.filter(ch => ch.fade > 0);

  // Underground chest interaction
  for (const ch of undergroundChests) {
    if (!ch || ch.fade <= 0) continue;
    const plyRect = { x: player.x, y: player.y, w: player.w, h: player.h };
    const overlap = !(ch.x + ch.w < plyRect.x || plyRect.x + plyRect.w < ch.x || ch.y + ch.h < plyRect.y || plyRect.y + plyRect.h < ch.y);
    if (overlap && !ch.open && consumeWorldInteract()) {
      ch.open = true;
      // Award loot based on contents
      if (ch.contents === 'diamond') {
        diamondCount++;
        // blue-ish sparkle for diamonds
        const cx = ch.x + ch.w / 2, cy = ch.y + ch.h / 2;
        const blue = ['#a0d8ff', '#5eb3ff', '#2e8fff', '#b0e0ff'];
        for (let k = 0; k < 4; k++) particles.spawnSparks(cx, cy, 12, blue);
      } else {
        // green-ish sparkle for other loot
        const cx = ch.x + ch.w / 2, cy = ch.y + ch.h / 2;
        const green = ['#b7ffcc', '#84f59a', '#56d86d', '#a9f0c0'];
        for (let k = 0; k < 3; k++) particles.spawnSparks(cx, cy, 10, green);
      }
    }
    if (ch.open) {
      ch.fade -= dt * 1.6;
    }
  }
  // Remove fully faded
  undergroundChests = undergroundChests.filter(ch => ch.fade > 0);

  // Verdant Forest shrine overlap (prompt handled separately)
  shrine.overlap = false;
  const shrineReadyNow = shrine.activated ? shrineFreedReady : shrineCorruptReady;
  if (shrineReadyNow) {
    const rect = getShrineRect(h);
    if (rect) {
      const plyRect = { x: player.x, y: player.y, w: player.w, h: player.h };
      const overlap = !(rect.x + rect.w < plyRect.x || plyRect.x + plyRect.w < rect.x || rect.y + rect.h < plyRect.y || plyRect.y + plyRect.h < rect.y);
      if (overlap) shrine.overlap = true;
    }
  }

  // Tree chopping when equipped: press interact near a tree
  if (axe.equipped && consumeWorldInteract()) {
    tryChopNearestTree(h);
    // Also hit spiders in front when swinging axe (one-hit kill, no silk)
    const r = Math.max(18, Math.round(player.w * 0.85));
    const cx = player.x + (player.facing === 1 ? player.w * 1.0 : -player.w * 0.2);
    const cy = player.y + player.h * 0.55;
    hitSpidersAtCircle(cx, cy, r, 'axe');
    hitPlantsAtCircle(cx, cy, r);
    hitBirdsAtCircle(cx, cy, r);
    if (hitCorruptTitanAtCircle(cx, cy, r, CORRUPT_TITAN_AXE_DAMAGE)) {
      particles.spawnSparks(cx, cy, 12, ['#d8b6ff', '#b58dff']);
    }
    // Remove planks in front with axe, refund one log
    tryRemovePlankAtCircle(cx, cy, r);
    hitShrineWithAxe(h, cx, cy, r);
  }
  // Raygun fire: tap or hold to shoot forward
  if (raygun.equipped) {
    // Immediate shot on press
    if (consumeWorldInteract() && raygunCooldown <= 0) {
      spawnBlast();
      raygunCooldown = RAYGUN_COOLDOWN;
    }
    // Hold-to-fire at fixed cadence
    if (input.interactHeld && raygunCooldown <= 0) {
      spawnBlast();
      raygunCooldown = RAYGUN_COOLDOWN;
    }
  }
  // Builder: place a plank with S key or mouse click. Hold X for vertical, hold Shift for longer.
  if (builder.equipped && (consumeWorldInteract() || input.consumeMouseClick())) {
    tryPlacePlank(w, h);
  }
  let pickaxeTapDigBoost = 0;
  if (pickaxe.equipped && consumeWorldInteract()) {
    pickaxeTapDigBoost = 0.35; // treat single tap as a head start on the dig channel
  }
  // Hold-to-chop: while holding interact and moving over trees, auto-chop at a steady cadence
  axeChopCooldown = Math.max(0, axeChopCooldown - dt);
  if (axe.equipped && input.interactHeld && axeChopCooldown <= 0) {
    // Require some horizontal intent to avoid accidental chops when idle
    const movingHoriz = input.leftHeld || input.rightHeld || Math.abs(player.vx) > 10;
    if (movingHoriz) {
      tryChopNearestTree(h);
      // Periodic axe swing also damages spiders
      const r = Math.max(18, Math.round(player.w * 0.85));
      const cx = player.x + (player.facing === 1 ? player.w * 1.0 : -player.w * 0.2);
      const cy = player.y + player.h * 0.55;
      hitSpidersAtCircle(cx, cy, r, 'axe');
      hitPlantsAtCircle(cx, cy, r);
      hitBirdsAtCircle(cx, cy, r);
      if (hitCorruptTitanAtCircle(cx, cy, r, CORRUPT_TITAN_AXE_DAMAGE)) {
        particles.spawnSparks(cx, cy, 12, ['#d8b6ff', '#b58dff']);
      }
      tryRemovePlankAtCircle(cx, cy, r);
      hitShrineWithAxe(h, cx, cy, r);
      axeChopCooldown = AXE_CHOP_HOLD_RATE;
    }
  }

  updateDialogue(dt);
  diggingSystem.update(dt);
  const moveDir = input.rightHeld ? 1 : (input.leftHeld ? -1 : 0);
  const isUnderground = isUndergroundNow;
  const canStartDownDig = playerDepthNowGround >= -diggingSystem.getBlockSize() * 0.4;
  const levitatePress = input.consumeLevitate();

  // Digging loop: feed player input into the block-based DiggingSystem so taps, holds,
  // and directional presses carve chunky tunnels (downward, sideways, upward) exactly
  // like the former drill but using the new channel timers.

  if (pickaxe.equipped) {
    const carveBlock = (cx, cy, size) => digBlockRegion(w, h, cx, cy, size);

    // Horizontal dig: only when moving into a wall
    if (moveDir !== 0) {
      const dir = moveDir;
      const isBlocked = (d) => rectOverlapsWorldSolid(
        w, h,
        player.x + (d > 0 ? player.w : -2),
        player.y + 2,
        3,
        player.h - 4
      );
      if (isUnderground) {
        diggingSystem.digHorizontal(dt, player, dir, isBlocked, carveBlock, isUnderground, false);
      } else {
        diggingSystem.resetChannel('horizontal');
      }
    } else {
      diggingSystem.resetChannel('horizontal');
    }

    const wantsDownDig = input.downHeld || input.interactHeld || pickaxeTapDigBoost > 0;
    if (wantsDownDig && (isUnderground || canStartDownDig)) {
      const downDt = dt + pickaxeTapDigBoost;
      diggingSystem.digDown(downDt, player, carveBlock);
    } else {
      diggingSystem.resetChannel('down');
    }

    if ((isUnderground || canStartDownDig) && input.jumpHeld) {
      const bandH = Math.max(4, Math.floor(TILE_SIZE));
      const hasCeiling = rectOverlapsSolid(
        w, h,
        player.x + 1,
        player.y - bandH - 1,
        Math.max(1, player.w - 2),
        bandH
      );
      const carvedUp = diggingSystem.digUp(dt, player, hasCeiling, carveBlock);
      if (carvedUp) {
        nudgePlayerUpIntoClear(w, h, Math.floor(diggingSystem.getBlockSize() * 0.25));
      }
    } else {
      diggingSystem.resetChannel('up');
    }
  } else {
    diggingSystem.resetAll();
  }

  if (pickaxe.equipped) {
    pickaxeDigTimer += dt;
    const diggingDown = diggingSystem.isChannelActive('down');
    const diggingHoriz = diggingSystem.isChannelActive('horizontal');
    const diggingUp = diggingSystem.isChannelActive('up');
    if (diggingDown) {
      pickaxeDigMode = 'down';
    } else if (diggingHoriz) {
      pickaxeDigMode = 'horizontal';
    } else if (diggingUp) {
      pickaxeDigMode = 'up';
    } else {
      pickaxeDigMode = 'idle';
    }
    const digActive = pickaxeDigMode !== 'idle';
    if (digActive) pickaxeDigAnim = Math.min(1, pickaxeDigAnim + dt * 6);
    else pickaxeDigAnim = Math.max(0, pickaxeDigAnim - dt * 4);
  } else {
    pickaxeDigAnim = Math.max(0, pickaxeDigAnim - dt * 8);
    pickaxeDigMode = 'idle';
    pickaxeDigTimer = 0;
  }

  // Levitate system (underground escape with C key)
  const aboveGround = playerFeet <= groundTop;

  if (levitatePress && isUnderground && levitateState === 'idle') {
    // Start levitating
    levitateState = 'ascending';
    levitateTimer = 0;
    // Mark ascend guide as complete when player uses it
    completeControlGuide('ascend');
  }

  if (levitateState !== 'idle') {
    if (levitateState === 'ascending') {
      // Ascending underground - apply upward velocity
      if (!input.levitateHeld || aboveGround) {
        // Cancel if released or reached surface
        if (aboveGround) {
          levitateState = 'surfacing';
          levitateSurfaceY = player.y;
          levitateArcStartX = player.x;
          levitateTimer = 0;
        } else {
          levitateState = 'idle';
        }
      } else {
        // Check for ceiling collision before applying velocity
        const ceilingCheck = rectOverlapsWorldSolid(w, h, player.x, player.y - 8, player.w, 4);
        if (ceilingCheck) {
          // Hit ceiling - stop ascending
          player.vy = 0;
          levitateState = 'idle';
        } else {
          // Apply upward velocity
          const LEVITATE_SPEED = -350; // upward velocity
          player.vy = LEVITATE_SPEED;
          player.onGround = false;
        }

        // Spawn wind particles under player
        levitateTimer += dt;
        if (levitateTimer >= 0.05) { // Every 50ms
          levitateTimer = 0;
          const particleCount = 3;
          for (let i = 0; i < particleCount; i++) {
            const px = player.x + player.w * 0.5 + (Math.random() - 0.5) * player.w;
            const py = player.y + player.h + 2;
            levitateWindParticles.push({
              x: px,
              y: py,
              vy: 40 + Math.random() * 20,
              life: 0,
              dur: 0.4 + Math.random() * 0.2,
              alpha: 0.6 + Math.random() * 0.4
            });
          }
        }
      }
    } else if (levitateState === 'surfacing') {
      // Just reached surface - arc forward and up
      levitateTimer += dt;
      const ARC_DURATION = 0.6;
      const ARC_FORWARD = 120; // pixels forward
      const ARC_HEIGHT = 60; // pixels above surface

      if (levitateTimer >= ARC_DURATION) {
        levitateState = 'idle';
        player.onGround = false;
      } else {
        // Parabolic arc
        const t = levitateTimer / ARC_DURATION;
        const arcProgress = Math.sin(t * Math.PI); // 0 to 1 to 0 (smooth arc)
        player.x = levitateArcStartX + ARC_FORWARD * t;
        player.y = levitateSurfaceY - ARC_HEIGHT * arcProgress;
        player.vx = ARC_FORWARD / ARC_DURATION;
        player.vy = 0;
      }
    }
  } else {
    // Not levitating - clear particles gradually
    levitateTimer = 0;
  }

  // Update wind particles
  for (const p of levitateWindParticles) {
    p.life += dt;
    p.y += p.vy * dt;
    const t = p.life / p.dur;
    p.alpha = (1 - t) * (0.6 + Math.random() * 0.4);
  }
  levitateWindParticles = levitateWindParticles.filter(p => p.life < p.dur);

  // Update felled trees animation
  updateTrees(dt, w);
  updateTreePieces(dt, w, h);
  updatePickupToasts(dt);
  updateBirds(dt, w, h);
  particles.updateSparks(dt);
  particles.updateDashTrails(dt);
  updateBlasts(dt, w, h);
  updateApocalypse(dt, w, h);
  // updateStorm(dt, w, h); // Now handled by skyRenderer (lightning)
  updateComets(dt, w, h);
  updateDebris(dt, w, h);
  updateEnemies(dt, w, h);
  updateFeathers(dt, w, h);
  updatePlanks(dt, w, h);
  realizeCaveSpawns(dt, w, h);
  // advance sword swing animation
  if (swordSwingActive) {
    swordSwingT += dt / SWORD_SWING_DUR;
    // Spawn dash trail particles while dashing
    if (swordDashActive) {
      particles.spawnDashTrail(player.x + player.w / 2, player.y + player.h / 2);
    }
    // Deactivate dash after SWORD_DASH_DUR
    if (swordDashActive && swordSwingT >= (SWORD_DASH_DUR / SWORD_SWING_DUR)) {
      swordDashActive = false;
    }
    // trigger hit window once mid-swing
    if (!swordSwingHit && swordSwingT >= 0.28 && swordSwingT <= 0.65) {
      if (swordHitTrees(w, h)) swordSwingHit = true;
    }
    if (swordSwingT >= 1) { swordSwingActive = false; swordSwingT = 0; swordSwingHit = false; swordDashActive = false; swordSlashArc = null; }
  }

  // Cloud collision bounce
  // Determine if we stand on a player plank (narrow probe under feet)
  standingOnPlank = rectOverlapsPlanks(player.x + 1, player.y + player.h + 1, Math.max(1, player.w - 2), 2);
  checkCloudBounce(w, h);

  // Axe convenience: bumping your head into a plank while the axe is equipped breaks it
  if (axe.equipped) {
    // Probe a band above the player's head
    const rx = player.x + 2;
    const rw = Math.max(1, player.w - 4);
    const bandH = Math.min(TILE_SIZE, Math.max(8, Math.floor(player.h * 0.35)));
    const ry = player.y - bandH - 1;
    // Find first plank intersecting this band
    for (let i = 0; i < planks.length; i++) {
      const p = planks[i];
      const overlap = !(rx + rw <= p.x || p.x + p.w <= rx || ry + bandH <= p.y || p.y + p.h <= ry);
      if (overlap) {
        planks.splice(i, 1);
        choppedCount += 1;
        addPickupToast('log');
        particles.spawnSparks(player.x + player.w / 2, player.y, 8);
        break;
      }
    }
  }

  // Prune dug data outside of view to keep memory bounded
  pruneTileChunks(cameraX, w);
  prunePlanks(cameraX, w);

  // Resolve tile collisions (sides + ceiling)
  ensureTileChunksInView(cameraX, w, h);
  resolvePlayerTileCollisions(w, h);

  // Sky platforms: ensure chunks in view and resolve collisions
  ensurePlatformCellsInView(cameraX, w, h);
  resolvePlatformCollisions(w, h, prevBottom);

  // If standing on a platform, move that platform forward and carry the player with it
  if (standingOnPlatform && currentPlatformCarry) {
    const dx = PLATFORM_MOVE_SPEED * dt;
    currentPlatformCarry.x += dx;
    player.x += dx;
    // Move any chests attached to this platform
    for (const ch of skyChests) {
      if (ch.platform === currentPlatformCarry) {
        ch.x += dx;
      }
    }
  }

}

function prunePlanks(camX, viewW) {
  const margin = 1200;
  const left = camX - margin;
  const right = camX + viewW + margin;
  planks = planks.filter(p => (p.x + p.w) > left && p.x < right);
}

function realizeCaveSpawns(dt, w, h) {
  if (!caveSpawns.length) return;
  const groundTopY = h - world.groundH;
  const margin = 1800;
  const left = cameraX - margin;
  const right = cameraX + w + margin;
  for (const s of caveSpawns) {
    if (s.realized) continue;
    const worldX = (s.cx * CHUNK_W_TILES + s.tx) * TILE_SIZE + TILE_SIZE / 2;
    if (worldX < left || worldX > right) continue;
    if (s.type === 'diamond') {
      if (diamondReady) {
        // Size diamond sprite
        const targetH = 18;
        const aspect = diamondImg.naturalWidth && diamondImg.naturalHeight ? (diamondImg.naturalWidth / diamondImg.naturalHeight) : 1;
        const dh = targetH;
        const dw = Math.max(8, Math.round(targetH * aspect));
        const worldY = groundTopY + (s.cy * CHUNK_H_TILES + s.ty + 1) * TILE_SIZE - dh;
        diamonds.push({ x: Math.round(worldX - dw / 2), y: Math.round(worldY), w: dw, h: dh, picked: false });
        s.realized = true;
      }
    // Disabled: Spider spawning removed due to teleportation issues
    // } else if (s.type === 'spider' && spiderReady) {
    //   // Spawn a cave spider sitting on this floor
    //   const targetH = 34;
    //   const aspect = spiderImg.naturalWidth && spiderImg.naturalHeight ? (spiderImg.naturalWidth / spiderImg.naturalHeight) : 1.6;
    //   const dh = targetH;
    //   const dw = Math.max(16, Math.round(targetH * aspect));
    //   const y = groundTopY + (s.cy * CHUNK_H_TILES + s.ty + 1) * TILE_SIZE - dh;
    //   const x = Math.round(worldX - dw / 2);
    //   const speed = 50 + Math.random() * 35;
    //   const dir = Math.random() < 0.5 ? -1 : 1;
    //   enemies.push({ kind: 'spider', x, y, w: dw, h: dh, speed, baseSpeed: speed, dir, stickyTimer: 0 });
    //   s.realized = true;
    } else if (s.type === 'ruins' && ruinsOverrunReady) {
      // Spawn underground ruins in chamber - find floor below spawn position
      const targetH = 100 + Math.random() * 50; // 100-150px (smaller than surface ruins)

      // Search downward from spawn position to find floor
      let floorTy = s.ty;
      const ch = getChunk(s.cx, s.cy);
      for (let searchTy = s.ty; searchTy < CHUNK_H_TILES; searchTy++) {
        const idx = searchTy * CHUNK_W_TILES + s.tx;
        const isSolid = ch.solid[idx] !== 0;
        if (isSolid) {
          floorTy = searchTy;
          break;
        }
      }

      const worldY = groundTopY + (s.cy * CHUNK_H_TILES + floorTy) * TILE_SIZE;
      undergroundRuins.push({ x: worldX, y: worldY, targetH, inCave: true });
      s.realized = true;
    } else if (s.type === 'chest' && chestClosedReady) {
      // Spawn underground chest in chamber - find floor below spawn position
      const img = chestClosedImg;
      const targetW = 56;
      const aspect = (img.naturalWidth && img.naturalHeight) ? (img.naturalWidth / img.naturalHeight) : 1;
      const dw = targetW;
      const dh = Math.max(12, Math.round(targetW / Math.max(0.1, aspect)));

      // Search downward from spawn position to find floor
      let floorTy = s.ty;
      const ch = getChunk(s.cx, s.cy);
      for (let searchTy = s.ty; searchTy < CHUNK_H_TILES; searchTy++) {
        const idx = searchTy * CHUNK_W_TILES + s.tx;
        const isSolid = ch.solid[idx] !== 0;
        if (isSolid) {
          floorTy = searchTy;
          break;
        }
      }

      const worldY = groundTopY + (s.cy * CHUNK_H_TILES + floorTy) * TILE_SIZE - dh;
      const x = Math.round(worldX - dw / 2);
      const y = Math.round(worldY);
      // Underground chests can contain diamonds or other loot
      const contents = Math.random() < 0.7 ? 'diamond' : 'coins';
      undergroundChests.push({ x, y, w: dw, h: dh, open: false, fade: 1, contents });
      s.realized = true;
    } else if (s.type === 'underground_npc' && undergroundNpcReady) {
      // Spawn underground NPC helper - find floor below spawn position
      const targetH = 64;
      const aspect = (undergroundNpcImg.naturalWidth && undergroundNpcImg.naturalHeight) ? (undergroundNpcImg.naturalWidth / undergroundNpcImg.naturalHeight) : 1;
      const dh = targetH;
      const dw = Math.max(16, Math.round(targetH * aspect));

      // Search downward from spawn position to find floor
      let floorTy = s.ty;
      const ch = getChunk(s.cx, s.cy);
      for (let searchTy = s.ty; searchTy < CHUNK_H_TILES; searchTy++) {
        const idx = searchTy * CHUNK_W_TILES + s.tx;
        const isSolid = ch.solid[idx] !== 0;
        if (isSolid) {
          floorTy = searchTy;
          break;
        }
      }

      const worldY = groundTopY + (s.cy * CHUNK_H_TILES + floorTy) * TILE_SIZE - dh;
      const x = Math.round(worldX - dw / 2);
      const y = Math.round(worldY);
      const yOffset = undergroundNpcBounds ? Math.round(undergroundNpcBounds.bottom * (dh / Math.max(1, undergroundNpcImg.naturalHeight || dh))) : 0;
      undergroundNpcs.push({ x: worldX, y, w: dw, h: dh, facing: -1, overlap: false, talkRadius: 110, yOffset });
      s.realized = true;
    }
  }
  // Optionally prune far-realized spawns to keep array small
  caveSpawns = caveSpawns.filter(s => !s.realized || ( (s.cx * CHUNK_W_TILES * TILE_SIZE) >= (cameraX - 6000) ));

  // Update dev tools with current game state
  // Calculate useful contextual information for dev tools
  const groundTopWorld = h - world.groundH;
  const playerFeet = player.y + player.h;
  const depthBelowGround = Math.max(0, playerFeet - groundTopWorld);
  const altitudeAboveGround = Math.max(0, groundTopWorld - playerFeet);
  const playerCenterX = player.x + player.w * 0.5;

  // Determine biome/location
  let location = 'Plains';
  if (insideCave) {
    location = 'Cave';
  } else if (depthBelowGround > 0) {
    location = `Underground (${Math.round(depthBelowGround)}px deep)`;
  } else if (altitudeAboveGround > h * 0.5) {
    location = `Sky (${Math.round(altitudeAboveGround)}px high)`;
  } else if (playerCenterX > deepForestStartX()) {
    location = 'Deep Forest';
  }

  devTools.update({
    deltaTime: dt,
    player: {
      x: player.x,
      y: player.y,
      vx: player.vx,
      vy: player.vy
    },
    camera: {
      x: cameraX,
      y: cameraY
    },
    state: `${player.onGround ? 'Grounded' : 'Airborne'} | ${location}`
  });
}

/**
 * Main rendering function - draws all game entities, UI, and effects
 * @param {CanvasRenderingContext2D} ctx - The canvas 2D rendering context
 */
function draw(ctx) {
  const w = world.width();
  const h = world.height();
  const dpr = canvas.__dpr || window.devicePixelRatio || 1;
  const screenW = canvas.width / dpr;
  const screenH = canvas.height / dpr;
  // Debug position box
  drawPlayerPosDebug(ctx, screenW);
  if (insideCave) {
    ctx.save();
    ctx.scale(VIEW_SCALE, VIEW_SCALE);
    ctx.clearRect(0, 0, w, h);
    drawCaveInteriorScene(ctx, w, h);
    ctx.restore();

    // Minimal HUD/prompts while inside the cave
    if (cavePromptMessage && !dialogueState.active) drawCavePrompt(ctx, cavePromptMessage);
    drawPickupToasts(ctx);
    drawChopCounter(ctx);
    drawSilkCounter(ctx);
  drawDiamondCounter(ctx);
  drawFeatherCounter(ctx);
  drawDialogue(ctx);
  drawAudioUnlockToast(ctx, screenW, screenH);
  if (gameOverActive) drawGameOverOverlay(ctx, screenW, screenH);
  if (deepForestDebugTimer > 0 && deepForestDebugMsg) drawDeepForestDebug(ctx, screenW, screenH);
  return;
}

  if (insideMoon) {
    ctx.save();
    ctx.scale(VIEW_SCALE, VIEW_SCALE);
    ctx.clearRect(0, 0, w, h);
    drawMoonScene(ctx, w, h);
    ctx.restore();
    drawPickupToasts(ctx);
    drawDialogue(ctx);
    drawAudioUnlockToast(ctx, screenW, screenH);
    if (gameOverActive) drawGameOverOverlay(ctx, screenW, screenH);
    return;
  }
  // Scale all world visuals (including background and clouds)
  ctx.save();
  ctx.scale(VIEW_SCALE, VIEW_SCALE);
  if (apocalypseActive) ctx.translate(apocShakeX * (1 / VIEW_SCALE), apocShakeY * (1 / VIEW_SCALE));
  // Clear and paint the sky background (gradient + sun + subtle noise), then mountains
  ctx.clearRect(0, 0, w, h);
  skyRenderer.drawSkyBackground(ctx, w, h, world.groundH, cameraY, player.y);
  skyRenderer.drawShootingStars(ctx, w, h, world.groundH, cameraY);
  // DISABLED: Lightning effects removed
  // skyRenderer.drawLightning(ctx, w, h, world.groundH, cameraY);
  drawComets(ctx, w, h);
  drawAuroras(ctx, w, h);
  drawDebris(ctx, w, h);

  // Background trees sit behind clouds but respond to parallax
  // Background trees disabled - using denser normal trees instead
  // ctx.save();
  // ctx.translate(-cameraX, cameraY);
  // drawBackgroundTrees(ctx, w, h, world.groundH);
  // ctx.restore();

  // parallax clouds behind mid/foreground content
  skyRenderer.drawClouds(ctx, w, h, world.groundH, cameraX, cameraY);
  drawApocalypseSkyTint(ctx, w, h);
  drawSpaceshipAtmosphere(ctx, w, h);
  drawSpaceMoon(ctx, w, h);

  // trees and player in world space (scroll with camera)
  ctx.save();
  ctx.translate(-cameraX, cameraY);
  // ground shifts with camera; draw across the current screen interval in world coords
  const groundTopY = h - world.groundH;
  // Dirt gradient fill for ground body (use cached gradient)
  const groundGrad = getGroundGradient(ctx, h, world.groundH);
  ctx.fillStyle = groundGrad;
  // Draw a tall ground column so ground continues as we scroll down
  ctx.fillRect(cameraX, groundTopY, w, world.groundH + GROUND_EXTRA);
  // Subtle dirt texture overlay
  ensureDirtPattern();
  if (dirtPattern) {
    ctx.save();
    ctx.globalAlpha = 0.12; // slightly stronger texture for depth
    ctx.fillStyle = dirtPattern;
    ctx.fillRect(cameraX, groundTopY, w, world.groundH + GROUND_EXTRA);
    ctx.restore();
  }
  // (Removed horizontal strata lines for a cleaner, darker ground)
  drawGroundCap(ctx, w, groundTopY);
  // After activation, paint a thin verdant grass layer along the surface (grows from shrine)
  // Only draw where surface tiles (ty=0) are solid to avoid drawing over holes
  if (verdantGrassActive) {
    ctx.save();
    const grassH = 6;
    const grad = ctx.createLinearGradient(0, groundTopY - grassH, 0, groundTopY + 1);
    grad.addColorStop(0, '#3e8a34');
    grad.addColorStop(1, '#245c22');
    ctx.fillStyle = grad;
    const left = Math.max(cameraX, shrine.x - grassReveal);
    const right = Math.min(cameraX + w, shrine.x + grassReveal);
    const startX = Math.floor(left / TILE_SIZE) * TILE_SIZE;
    const endX = Math.ceil(right / TILE_SIZE) * TILE_SIZE;
    for (let px = startX; px < endX; px += TILE_SIZE) {
      const txGlobal = Math.floor(px / TILE_SIZE);
      const cx = Math.floor(txGlobal / CHUNK_W_TILES);
      const tx = txGlobal - cx * CHUNK_W_TILES;
      const solid0 = getTileSolid(cx, 0, tx, 0);
      const solid1 = getTileSolid(cx, 0, tx, 1);
      const solid2 = getTileSolid(cx, 0, tx, 2);
      // Only draw grass if surface and 2 layers below are solid (avoid drawing over holes)
      if (solid0 && solid1 && solid2 && px >= left && px < right) {
        ctx.fillRect(px, groundTopY - grassH, TILE_SIZE, grassH);
      }
    }
    ctx.restore();
  }
  // Carve ground using tile empties
  drawTileCarves(ctx, w, h);
  drawCorruptTitanTests(ctx, w, h);
  // Cloud shadows disabled for a cleaner look
  drawFoliage(ctx, w, h);
  drawCave(ctx, w, h);
  drawCorruptTitanGuard(ctx, w, h);
  drawRockTitan(ctx, w, h);
  drawDeepForestElements(ctx, w, h);
  drawPlantTraps(ctx, w, h);
  // Draw player-built planks above ground
  drawPlanks(ctx);
  // birds fly in the sky behind trees
  drawBirds(ctx);
  // draw platforms in world space (sky) (also draws sky chests)
  // Fade platforms out: start at 15000px, gone by 18000px (Stratosphere entry)
  const _platformFade = 1 - Math.min(1, Math.max(0, (spaceshipAltitude - 15000) / 3000));
  if (_platformFade > 0) {
    ctx.save();
    ctx.globalAlpha = _platformFade;
    drawPlatforms(ctx);
    ctx.restore();
  }
  // Midground trees disabled - using denser normal trees instead
  // drawMidgroundTrees(ctx, w, h, world.groundH);
  drawTrees(ctx, w, h, world.groundH);
  drawDeepForestAnimals(ctx, w, h);
  if (!axe.picked) drawAxe(ctx, w, h, world.groundH);
  if (hat.spawned && !hat.picked) drawHat(ctx, w, h, world.groundH);
  if (SPAWN_PICKAXE && !pickaxe.picked) drawPickaxe(ctx, w, h, world.groundH);
  // fun prop (not pickable)
  drawDoNotPress(ctx, w, h, world.groundH);
  if (SPAWN_SWORD && !sword.picked) drawSword(ctx, w, h, world.groundH);
  if (SPAWN_RAYGUN && !raygun.picked) drawRaygun(ctx, w, h, world.groundH);
  // Toolbox (builder)
  if (toolbox.spawned && !toolbox.picked) drawToolbox(ctx, w, h, world.groundH);
  if (jetback.spawned && !jetback.picked) drawJetbackItem(ctx, w, h, world.groundH);
  drawSpaceshipIdle(ctx, w, h);
  drawSpaceshipEmptyShell(ctx);
  drawGoblinFamily(ctx, w, h); // Draw family first (behind)
  drawGoblin(ctx, w, h, world.groundH); // Draw main goblin second (in front)
  drawShrineWithHelicohat(ctx, w, h, world.groundH);
  drawWipBanner(ctx, w, h, world.groundH);
  drawWipNpc(ctx, w, h, world.groundH);
  drawUndergroundNpcs(ctx);
  drawArchBack(ctx, w, h);
  // Verdant Forest shrine (original one)
  drawForestShrine(ctx, w, h);
  drawEnterableCave(ctx, w, h);
  drawEnemies(ctx);
  drawTreePieces(ctx, w, h);
  particles.drawSparks(ctx);
  particles.drawDashTrails(ctx);
  drawLevitateWindParticles(ctx);
  drawBlasts(ctx);
  // Atmospheric depth: soft darkness and dust motes as you go deeper
  // (Draw after world restore in screen space)
  if (spaceship.mode !== 'occupied') drawFeathers(ctx);
  if (spaceship.mode !== 'occupied') drawDiamonds(ctx, w, h);
  // Show plank ghost preview when builder is active
  if (builder.equipped && choppedCount > 0) drawPlankGhost(ctx, w, h);

  // Draw player or ash pile if burning/dead
  if (gameOverPending || gameOverActive) {
    // Player is dead - draw ash pile instead
    const ashProgress = gameOverBurnT > 0 ? (1 - (gameOverBurnT / GAME_OVER_BURN_DUR)) : 1; // Full opacity when game over
    const groundY = player.y + player.h;
    const ashW = 16;
    const ashH = 8;
    const ashX = player.x + player.w / 2 - ashW / 2;
    const ashY = groundY - ashH; // Settled on ground

    // Draw ash pile (simple dark gray mound)
    ctx.save();
    ctx.globalAlpha = Math.min(1, ashProgress * 2); // Fade in, then stay visible
    ctx.fillStyle = '#3a3a3a';
    ctx.beginPath();
    ctx.ellipse(ashX + ashW / 2, ashY + ashH, ashW / 2, ashH / 2, 0, 0, Math.PI * 2);
    ctx.fill();
    // Darker center
    ctx.fillStyle = '#2a2a2a';
    ctx.beginPath();
    ctx.ellipse(ashX + ashW / 2, ashY + ashH - 2, ashW / 3, ashH / 3, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  } else if (spaceship.mode === 'occupied') {
    drawSpaceshipEquipped(ctx);
  } else if (spaceship.mode === 'entering' || spaceship.mode === 'transitioning') {
    // drawn in screen-space after bar overlay, or hidden during cinematic
  } else {
    player.draw(ctx);
  }
  if (playerHitFlash > 0 && spaceship.mode !== 'transitioning') {
    const sprites = Character.sprites;
    const flashImg = sprites.sideFlash;
    const baseImg = sprites.side;
    const ready = flashImg && flashImg.complete && flashImg.naturalWidth && baseImg && baseImg.complete && baseImg.naturalWidth;
    if (ready) {
      const scale = 1.8;

      const baseAspect = baseImg.naturalWidth / baseImg.naturalHeight;
      const baseDh = Math.round(player.h * scale);
      const baseDw = Math.round(baseDh * baseAspect);
      const baseBounds = Character.getOpaqueBounds(baseImg);
      const baseBottomSrc = baseBounds ? (baseBounds.top + baseBounds.height) : baseImg.naturalHeight;
      const baseScaleY = baseDh / baseImg.naturalHeight;
      const baseDrawY = Math.round(player.h - baseBottomSrc * baseScaleY);
      const baseDrawX = Math.round((player.w - baseDw) / 2);

      const flashAspect = flashImg.naturalWidth / flashImg.naturalHeight;
      const flashDh = Math.round(player.h * scale);
      const flashDw = Math.round(flashDh * flashAspect);
      const flashBounds = Character.getOpaqueBounds(flashImg);
      const flashBottomSrc = flashBounds ? (flashBounds.top + flashBounds.height) : flashImg.naturalHeight;
      const flashScaleY = flashDh / flashImg.naturalHeight;
      const flashDrawYInitial = Math.round(player.h - flashBottomSrc * flashScaleY);
      const flashDrawXInitial = Math.round((player.w - flashDw) / 2);

      const baseCenter = baseDrawX + baseDw / 2;
      const baseBottom = baseDrawY + baseDh;
      const flashCenter = flashDrawXInitial + flashDw / 2;
      const flashBottom = flashDrawYInitial + flashDh;

      const drawXLocal = flashDrawXInitial + Math.round(baseCenter - flashCenter);
      const drawYLocal = flashDrawYInitial + Math.round(baseBottom - flashBottom);

      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, playerHitFlash / PLAYER_HIT_FLASH_DUR));
      if (player.facing === -1) {
        const tx = Math.round(player.x + drawXLocal + flashDw);
        const ty = Math.round(player.y + drawYLocal);
        ctx.translate(tx, ty);
        ctx.scale(-1, 1);
        ctx.drawImage(flashImg, 0, 0, flashDw, flashDh);
      } else {
        const drawX = Math.round(player.x + drawXLocal);
        const drawY = Math.round(player.y + drawYLocal);
        ctx.drawImage(flashImg, drawX, drawY, flashDw, flashDh);
      }
      ctx.restore();
    }
    // Ensure the simple feet overlays remain visible during the flash effect
    player.drawFeet(ctx);
  }
  // Draw jetback on top of the player so it's visible (still positioned on the back side)
  if (jetback.equipped) drawHeldJetbackBehind(ctx);
  if (axe.equipped) drawHeldAxe(ctx);
  if (hat.equipped) drawHeldHat(ctx);
  if (pickaxe.equipped) drawHeldPickaxe(ctx);
  if (sword.equipped) {
    drawSwordSlashArc(ctx); // Draw slash arc first (behind sword)
    drawHeldSword(ctx);
  }
  if (raygun.equipped) drawHeldRaygun(ctx);
  if (builder.equipped) drawHeldBuilder(ctx);
  drawArchFront(ctx, w, h);
  drawBarrierVines(ctx, w, h);
  // Titans and apocalypse effects should be the foremost world layer
  drawApocalypseWorld(ctx, w, h);
  ctx.restore();
  // Draw entering bar then ship here: world translate removed, VIEW_SCALE still active — matches idle ship position exactly
  if (spaceship.mode === 'entering') {
    const scaledW = Math.round(screenW / VIEW_SCALE);
    const scaledH = Math.round(screenH / VIEW_SCALE);
    drawSpaceshipBar(ctx, scaledW, scaledH);
    const sx = Math.round(spaceship.flightX - cameraX);
    const sy = Math.round(spaceship.flightY + cameraY);
    const fadeIn = Math.min(1, spaceship.barT / 0.30);
    ctx.save();
    if (spaceshipOffReady && fadeIn < 1) {
      const sz = spaceshipRenderSize(spaceshipOffImg, 240);
      ctx.globalAlpha = 1 - fadeIn;
      ctx.drawImage(spaceshipOffImg, sx, sy, sz.w, sz.h);
    }
    if (spaceshipEquipReady && fadeIn > 0) {
      const sz = spaceshipRenderSize(spaceshipEquipImg, 240);
      ctx.globalAlpha = fadeIn;
      ctx.drawImage(spaceshipEquipImg, sx, sy, sz.w, sz.h);
    }
    ctx.restore();
  }
  ctx.restore(); // end world-scale block

  // Removed top-left help text for a cleaner view

  // HUD text stays screen-space (unscaled)

  // Depth atmosphere (screen-space overlay)
  drawUndergroundAtmosphere(ctx, screenW, screenH);
  drawSpaceshipSpaceEffects(ctx, screenW, screenH);
  drawMoonTransitionOverlay(ctx, screenW, screenH);
  // DISABLED: Forest fog overlay removed
  // drawForestFogOverlay(ctx, screenW, screenH);
  if (deepForestDebugTimer > 0 && deepForestDebugMsg) drawDeepForestDebug(ctx, screenW, screenH);
  if (axeOverlap && !axe.picked) drawPickupPrompt(ctx, 'axe');
  if (hat.spawned && hatOverlap && !hat.picked) drawPickupPrompt(ctx, 'helicohat');
  if (pickaxeOverlap && !pickaxe.picked) drawPickupPrompt(ctx, 'pickaxe');
  if (SPAWN_SWORD && swordOverlap && !sword.picked) drawPickupPrompt(ctx, 'sword');
  if (SPAWN_RAYGUN && raygunOverlap && !raygun.picked) drawPickupPrompt(ctx, 'raygun');
  if (toolbox.spawned && toolboxOverlap && !toolbox.picked) drawPickupPrompt(ctx, 'toolbox');
  if (jetbackOverlap && jetback.spawned && !jetback.picked) drawPickupPrompt(ctx, 'jetback');
  drawGoblinPrompt(ctx);
  drawRockTitanPrompt(ctx);
  drawForestShrinePrompt(ctx);
  drawWipNpcPrompt(ctx);
  drawUndergroundNpcPrompt(ctx);
  if (!insideCave && caveEnterOverlap) drawCavePrompt(ctx, 'Enter Cave (E)');
  if (spaceshipOverlap && spaceship.mode === 'idle') drawCavePrompt(ctx, 'Enter Spaceship (E)');
  if (dnpOverlap) drawPressPrompt(ctx);
  drawPickupToasts(ctx);
  drawChopCounter(ctx);
  drawSilkCounter(ctx);
  drawDiamondCounter(ctx);
  drawFeatherCounter(ctx);
  if (typeof SKY_DEBUG !== 'undefined' && SKY_DEBUG) drawSkyDebug(ctx);
  // Intro Wisp overlay (screen-space)
  drawGroveLegend(ctx);
  drawWispIntro(ctx);
  drawDialogue(ctx);
  drawAudioUnlockToast(ctx, screenW, screenH);
  if (gameOverActive) drawGameOverOverlay(ctx, screenW, screenH);
}

// --- Debug: show altitude, darkness, and level state ---
let SKY_DEBUG = false; // overlay disabled by default
function drawSkyDebug(ctx) {
  const w = world.width();
  const h = world.height();
  const groundTopWorld = h - world.groundH;
  const playerCenterY = player.y + player.h * 0.5;
  const playerCenterX = player.x + player.w * 0.5;
  const altitudePx = Math.max(0, groundTopWorld - playerCenterY);
  const screens = altitudePx / Math.max(1, h);
  const darkK = skyDarkness(h);
  const level = darkK >= 0.7 ? 'Level 3' : (darkK >= 0.35 ? 'Level 2' : 'Level 1');
  const pad = 8;
  const x = 8, y = 8;
  const barW = 160, barH = 6;
  ctx.save();
  ctx.globalAlpha = 0.85;
  ctx.fillStyle = 'rgba(0,0,0,0.45)';
  ctx.beginPath();
  ctx.roundRect?.(x - pad, y - pad, 220, 74, 8);
  if (!ctx.roundRect) ctx.fillRect(x - pad, y - pad, 220, 74); else ctx.fill();
  ctx.globalAlpha = 1;
  ctx.fillStyle = '#fff';
  ctx.font = '12px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  ctx.fillText(`Altitude: ${altitudePx.toFixed(0)}px (${screens.toFixed(2)} screens)`, x, y + 12);
  ctx.fillText(`Darkness: ${(darkK*100).toFixed(0)}%  ${level}`, x, y + 26);
  ctx.fillText(`Scale: ${SKY_DARKNESS_SCALE} screens → full`, x, y + 40);
  ctx.fillText(`Pos: x=${playerCenterX.toFixed(1)} y=${playerCenterY.toFixed(1)}  camX=${cameraX.toFixed(1)} camY=${cameraY.toFixed(1)}`, x, y + 54);
  ctx.fillStyle = 'rgba(255,255,255,0.2)';
  ctx.fillRect(x, y + 52, barW, barH);
  ctx.fillStyle = '#7ad65d';
  ctx.fillRect(x, y + 52, Math.max(0, Math.min(barW, Math.round(barW * darkK))), barH);
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.fillRect(x + Math.round(barW * 0.35) - 1, y + 50, 2, barH + 4);
  ctx.fillRect(x + Math.round(barW * 0.70) - 1, y + 50, 2, barH + 4);
  ctx.restore();
}

function drawWispIntro(ctx) {
  if (!wispIntro || !wispIntro.active) return;
  const screenW = Math.round(world.width() * VIEW_SCALE);
  const screenH = Math.round(world.height() * VIEW_SCALE);
  const t = wispIntro.t || 0;
  // Parametric swirl along X from right toward the center
  const speed = 120; // px/s baseline
  const startX = screenW + 120;
  const centerX = screenW * 0.5;
  const baseX = startX - speed * t;
  const x = baseX + 48 * Math.sin(t * 1.2);
  const baseY = screenH * 0.45;
  const y = baseY + 60 * Math.sin(t * 1.6) + 24 * Math.sin(t * 3.1 + 0.7);
  // Fade in/out (entering from the right, vanishing near center)
  const travel = startX - baseX;
  const fadeIn = Math.max(0, Math.min(1, travel / 220));
  const fadeOut = Math.max(0, Math.min(1, (x - centerX) / 180));
  const a = Math.max(0, Math.min(1, Math.min(fadeIn, fadeOut)));

  // End once the wisp fades out near the center (after it has entered)
  if (travel > 20 && a <= 0.01) { wispIntro.active = false; return; }
  if (!wispReady) return;

  ctx.save();
  // Draw the wisp sprite with a gentle glow
  ctx.globalAlpha = a;
  const frames = getReadyWispFrames();
  const frame = frames.length ? frames[Math.floor(t * 8) % frames.length] : null;
  const baseW = 36;
  const aspect = frame ? (frame.width || frame.naturalWidth || baseW) / Math.max(1, (frame.height || frame.naturalHeight || baseW)) : 1;
  const dw = baseW;
  const dh = Math.round(dw / Math.max(0.1, aspect));
  const glowScale = 1.2 + 0.1 * Math.sin(t * 4.5);
  if (frame) {
    ctx.globalCompositeOperation = 'lighter';
    ctx.globalAlpha = a * 0.45;
    ctx.drawImage(frame, Math.round(x - (dw * glowScale) / 2), Math.round(y - (dh * glowScale) / 2), Math.round(dw * glowScale), Math.round(dh * glowScale));
    ctx.globalAlpha = a;
    ctx.drawImage(frame, Math.round(x - dw / 2), Math.round(y - dh / 2), dw, dh);
    ctx.globalCompositeOperation = 'source-over';
  }

  // Title text
  const title = wispIntro.title || 'and so your quest begins..';
  const lore = wispIntro.lore || '';
  ctx.globalAlpha = Math.min(1, a * 1.25);
  ctx.font = '24px Merriweather, Georgia, serif';
  ctx.fillStyle = 'rgba(255,255,255,0.95)';
  const tw = Math.ceil(ctx.measureText(title).width);
  const tx = Math.round((screenW - tw) / 2);
  const ty = Math.round(screenH * 0.18);
  // Soft panel behind text
  const padX = 14, padY = 8;
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.fillRect(tx - padX, ty - 26, tw + padX * 2, 36);
  ctx.fillStyle = 'rgba(255,255,255,0.98)';
  ctx.fillText(title, tx, ty);

  if (lore) {
    ctx.globalAlpha = Math.min(1, a);
    ctx.font = '14px Merriweather, Georgia, serif';
    ctx.fillStyle = 'rgba(255,255,255,0.88)';
    const lw = Math.ceil(ctx.measureText(lore).width);
    const lx = Math.round((screenW - lw) / 2);
    ctx.fillText(lore, lx, ty + 24);
  }
  ctx.restore();
}

function drawGroveLegend(ctx) {
  if (!groveLegend.active) return;
  const screenW = Math.round(world.width() * VIEW_SCALE);
  const screenH = Math.round(world.height() * VIEW_SCALE);
  const fadeIn = Math.min(1, groveLegend.t / 0.8);
  const fadeOut = Math.min(1, Math.max(0, (groveLegend.duration - groveLegend.t) / 1.2));
  const alpha = Math.max(0, Math.min(1, fadeIn * fadeOut));
  if (alpha <= 0) return;
  const text = 'The Sundered Grove';
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.font = '30px Merriweather, Georgia, serif';
  ctx.fillStyle = 'rgba(230, 255, 238, 0.95)';
  const textW = Math.ceil(ctx.measureText(text).width);
  const x = Math.round((screenW - textW) / 2);
  const y = Math.round(screenH * 0.18);
  ctx.fillText(text, x, y);
  ctx.restore();
}

/**
 * Wraps text to fit within maxWidth, breaking on word boundaries
 * @param {CanvasRenderingContext2D} ctx - Context with font already set
 * @param {string} text - Text to wrap
 * @param {number} maxWidth - Maximum width in pixels
 * @returns {string[]} Array of wrapped lines
 */
function wrapText(ctx, text, maxWidth) {
  const words = text.split(' ');
  const lines = [];
  let currentLine = '';

  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const testLine = currentLine ? currentLine + ' ' + word : word;
    const metrics = ctx.measureText(testLine);

    if (metrics.width > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines.length ? lines : [''];
}

/**
 * Draws the dialogue UI when a conversation is active
 * @param {CanvasRenderingContext2D} ctx - The canvas rendering context
 */
function drawDialogue(ctx) {
  if (!dialogueState.active) return;
  const screenW = Math.round(world.width() * VIEW_SCALE);
  const screenH = Math.round(world.height() * VIEW_SCALE);
  const pages = dialogueState.pages;
  const page = pages[dialogueState.pageIndex] || '';

  const margin = 18;
  const padX = 20;
  const minWidth = 320;
  const maxWidth = Math.min(600, screenW - margin * 2);
  const maxTextWidth = maxWidth - padX * 2 - 10; // Reserve padding and safety margin

  // Set font before wrapping
  ctx.save();
  ctx.font = '16px Merriweather, Georgia, serif';

  // Wrap text to fit within dialogue box
  const paragraphs = page ? page.split('\n') : [''];
  const wrappedLines = [];
  for (const para of paragraphs) {
    if (!para.trim()) {
      wrappedLines.push('');
    } else {
      const lines = wrapText(ctx, para, maxTextWidth);
      wrappedLines.push(...lines);
    }
  }

  const layoutLines = wrappedLines;
  const lineCount = Math.max(1, layoutLines.length);
  let remainingChars = dialogueState.visibleChars;
  const displayLines = [];
  for (let i = 0; i < lineCount; i += 1) {
    const fullLine = layoutLines[i] || '';
    let take = 0;
    if (remainingChars > 0) {
      take = Math.min(fullLine.length, remainingChars);
      remainingChars -= take;
    }
    displayLines.push(fullLine.substring(0, take));
    if (i < lineCount - 1 && remainingChars > 0) {
      remainingChars -= 1; // account for space/newline between wrapped lines
      if (remainingChars < 0) remainingChars = 0;
    }
  }
  const total = Math.max(1, pages.length);
  const typing = dialogueState.visibleChars < page.length;
  const hasKeyboard = !!input.hasKeyboard;
  const prompt = typing ? (hasKeyboard ? 'E to skip' : 'Hold to skip') : (dialogueState.pageIndex < total - 1 ? (hasKeyboard ? 'E to continue' : 'Tap to continue') : (hasKeyboard ? 'E to finish' : 'Tap to finish'));
  const historyText = (dialogueState.historyVisible && dialogueState.history.length) ? dialogueState.history.join('\n\n') : '';
  const rawHistoryLines = historyText ? historyText.split('\n') : [];
  const historyLines = rawHistoryLines.map(line => line.trimEnd());
  const historyLineH = 18;
  const historyGap = historyLines.length ? 8 : 0;
  const historyHeight = historyLines.length ? historyLines.length * historyLineH + historyGap : 0;

  const padY = 12;
  const headerHeight = 0;
  const headerGap = 8;
  const lineH = 24;
  const promptGap = 8;
  const promptLineH = 16;
  const footerLines = 1; // Only the E to continue/finish prompt
  const footerHeight = footerLines * promptLineH;

  ctx.textBaseline = 'top';

  // Use maxWidth for box (text is already wrapped to fit)
  const boxW = Math.round(maxWidth);
  const textHeight = Math.max(lineH, lineCount * lineH);
  const boxH = Math.round(padY * 2 + headerHeight + headerGap + historyHeight + textHeight + promptGap + footerHeight);

  // Position dialogue box above the NPC speaker
  let speakerX = Math.round(screenW / 2);
  let speakerY = Math.round(screenH * 0.6);

  if (dialogueState.npcId === 'goblin_bwak' || dialogueState.npcId === 'goblin_family_thanks') {
    // Position above goblin (using same coordinate system as drawGoblinPrompt)
    const rect = getGoblinRect(world.height());
    speakerX = Math.round(((rect.x + rect.w / 2) - cameraX) * VIEW_SCALE);
    speakerY = Math.round(((rect.y + cameraY)) * VIEW_SCALE);
  } else if (dialogueState.npcId === 'rock_titan' && rockTitan._rect) {
    // Position above rock titan (using same coordinate system as drawRockTitanPrompt)
    const rect = rockTitan._rect;
    speakerX = Math.round(((rect.x + rect.w / 2) - cameraX) * VIEW_SCALE);
    speakerY = Math.round(((rect.y + cameraY)) * VIEW_SCALE);
  } else if (dialogueState.npcId === 'cave_knight' && caveKnight._rect) {
    // Position above knight in cave (cave uses cameraX=0, cameraY=0)
    const rect = caveKnight._rect;
    speakerX = Math.round((rect.x + rect.w / 2) * VIEW_SCALE);
    speakerY = Math.round(rect.y * VIEW_SCALE);
  } else if (dialogueState.npcId === 'wip_npc') {
    // Position above WIP NPC
    speakerX = Math.round((wipNpc.x - cameraX) * VIEW_SCALE);
    speakerY = Math.round((world.height() - world.groundH - wipNpc.h - 20) * VIEW_SCALE);
  } else if (dialogueState.npcId === 'underground_npc') {
    // Position above underground NPC
    const npc = undergroundNpcs.find(n => n.overlap);
    if (npc) {
      speakerX = Math.round((npc.x - cameraX) * VIEW_SCALE);
      speakerY = Math.round((npc.y - 20) * VIEW_SCALE);
    }
  }

  speakerX = Math.max(margin, Math.min(screenW - margin, speakerX));
  speakerY = Math.max(margin, Math.min(screenH - margin, speakerY));

  const x = Math.round(Math.max(margin, Math.min(screenW - margin - boxW, speakerX - boxW / 2)));
  const desiredAbove = speakerY - (boxH + 32);
  let y;
  if (desiredAbove >= margin) {
    y = Math.round(desiredAbove);
    if (y + boxH > speakerY - 18) {
      const adjust = speakerY - (boxH + 18);
      if (adjust >= margin) y = Math.round(adjust);
    }
  } else {
    y = Math.round(Math.min(screenH - margin - boxH, speakerY + 32));
  }
  y = Math.max(margin, Math.min(screenH - margin - boxH, y));
  const bubbleTop = y;
  const bubbleBottom = y + boxH;
  let tailPointsUp = false;
  if (speakerY <= bubbleTop) {
    tailPointsUp = true;
  } else if (speakerY >= bubbleBottom) {
    tailPointsUp = false;
  } else {
    tailPointsUp = (speakerY - bubbleTop) < (bubbleBottom - speakerY);
  }

  const tailHalfW = 18;
  const tailBaseX = Math.round(Math.max(x + 24, Math.min(x + boxW - 24, speakerX)));
  const tailTipX = speakerX;
  let tailBaseY = tailPointsUp ? y : (y + boxH);
  let tailTipY = speakerY;
  const tailGap = 12;
  if (tailPointsUp) {
    tailTipY = Math.min(tailBaseY - tailGap, tailTipY);
  } else {
    tailTipY = Math.max(tailBaseY + tailGap, tailTipY);
  }
  tailTipY = Math.max(margin, Math.min(screenH - margin, tailTipY));

  const fillStyle = 'rgba(0, 0, 0, 0.58)';
  const strokeStyle = 'rgba(255, 255, 255, 0.55)';
  ctx.fillStyle = fillStyle;
  ctx.strokeStyle = strokeStyle;
  ctx.lineWidth = 1;
  roundRectPath(ctx, x, y, boxW, boxH, 14);
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  if (tailPointsUp) {
    ctx.moveTo(tailBaseX - tailHalfW, tailBaseY);
    ctx.lineTo(tailTipX, tailTipY);
    ctx.lineTo(tailBaseX + tailHalfW, tailBaseY);
  } else {
    ctx.moveTo(tailBaseX - tailHalfW, tailBaseY);
    ctx.lineTo(tailTipX, tailTipY);
    ctx.lineTo(tailBaseX + tailHalfW, tailBaseY);
  }
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  const headerY = y + padY;
  let textY = headerY + headerHeight + headerGap;
  if (historyLines.length) {
    ctx.font = '13px Merriweather, Georgia, serif';
    ctx.fillStyle = 'rgba(210, 230, 255, 0.65)';
    for (const line of historyLines) {
      if (!line.trim()) {
        textY += historyLineH * 0.6;
      } else {
        ctx.fillText(line, x + padX, textY);
        textY += historyLineH;
      }
    }
    textY += historyGap;
  }

  ctx.font = '16px Merriweather, Georgia, serif';
  ctx.fillStyle = '#ffffff';
  for (let i = 0; i < lineCount; i += 1) {
    const line = displayLines[i] || '';
    if (line) ctx.fillText(line, x + padX, textY);
    textY += lineH;
  }

  ctx.font = '12px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  const footerY = y + boxH - padY - promptLineH;
  const promptW = Math.ceil(ctx.measureText(prompt).width);
  ctx.fillStyle = 'rgba(220, 240, 255, 0.9)';
  ctx.fillText(prompt, x + boxW - padX - promptW, footerY);

  ctx.restore();
}

// --- Cursor auto-hide system ---
let cursorHideTimeout = null;
const CURSOR_HIDE_DELAY = 2000; // 2 seconds

function showCursor() {
  document.body.classList.remove('cursor-hidden');
  clearTimeout(cursorHideTimeout);

  // Don't hide cursor if inventory is visible
  if (showInventory) return;

  cursorHideTimeout = setTimeout(() => {
    document.body.classList.add('cursor-hidden');
  }, CURSOR_HIDE_DELAY);
}

function updateCursorVisibility() {
  // Always show cursor when inventory is visible
  if (showInventory) {
    document.body.classList.remove('cursor-hidden');
    clearTimeout(cursorHideTimeout);
  }
}

// Show cursor on any mouse movement
window.addEventListener('mousemove', showCursor, { passive: true });

// --- Inventory UI and equip logic ---
let showInventory = false;
let selectedWeapon = null; // 'axe' | 'pickaxe' | 'sword' | 'raygun' | 'builder' | null
let inventoryHintTimer = 0; // Timer for temporary hint display
let inventoryHintShown = false; // Track if hint was shown after 2nd item

function equipWeapon(id) {
  if (id === 'axe') {
    axe.equipped = true; pickaxe.equipped = false; sword.equipped = false; raygun.equipped = false; builder.equipped = false; selectedWeapon = 'axe';
  } else if (id === 'pickaxe') {
    pickaxe.equipped = true; axe.equipped = false; sword.equipped = false; raygun.equipped = false; builder.equipped = false; selectedWeapon = 'pickaxe';
  } else if (id === 'sword') {
    sword.equipped = true; axe.equipped = false; pickaxe.equipped = false; raygun.equipped = false; builder.equipped = false; selectedWeapon = 'sword';
  } else if (id === 'raygun') {
    raygun.equipped = true; axe.equipped = false; pickaxe.equipped = false; sword.equipped = false; builder.equipped = false; selectedWeapon = 'raygun';
  } else if (id === 'builder') {
    builder.equipped = true; axe.equipped = false; pickaxe.equipped = false; sword.equipped = false; raygun.equipped = false; selectedWeapon = 'builder';
  } else {
    axe.equipped = false; pickaxe.equipped = false; sword.equipped = false; raygun.equipped = false; builder.equipped = false; selectedWeapon = null;
  }
  updateInventoryUI();
  if (!controlGuideState.item) {
    if (selectedWeapon) showControlGuide('item');
    else if (controlGuideActive === 'item') hideControlGuide('item');
  }
}

function updateInventoryUI() {
  const inv = document.getElementById('inventory');
  const invHint = document.getElementById('inventory-hint');
  if (!inv) return;
  showInventory = axe.picked || pickaxe.picked || sword.picked || raygun.picked || builder.picked;
  inv.classList.toggle('visible', !!showInventory);
  inv.setAttribute('aria-hidden', showInventory ? 'false' : 'true');
  updateCursorVisibility();

  // Count picked items
  const pickedCount = (axe.picked ? 1 : 0) + (pickaxe.picked ? 1 : 0) + (sword.picked ? 1 : 0) +
                      (raygun.picked ? 1 : 0) + (builder.picked ? 1 : 0);

  // Show hint temporarily when picking up 2nd item
  if (pickedCount >= 2 && !inventoryHintShown) {
    inventoryHintShown = true;
    inventoryHintTimer = 5; // Show for 5 seconds
  }

  // Update hint visibility based on timer
  if (invHint) {
    const showHint = inventoryHintTimer > 0;
    invHint.classList.toggle('visible', showHint);
    invHint.setAttribute('aria-hidden', showHint ? 'false' : 'true');
  }

  const btnA = document.getElementById('inv-axe');
  const btnP = document.getElementById('inv-pickaxe');
  const btnS = document.getElementById('inv-sword');
  const btnR = document.getElementById('inv-raygun');
  const btnB = document.getElementById('inv-builder');

  // Show/hide parent divs instead of buttons directly
  if (btnA) {
    const parent = btnA.parentElement;
    if (parent) parent.style.display = axe.picked ? '' : 'none';
    btnA.setAttribute('aria-pressed', selectedWeapon === 'axe' ? 'true' : 'false');
  }
  if (btnP) {
    const parent = btnP.parentElement;
    if (parent) parent.style.display = pickaxe.picked ? '' : 'none';
    btnP.setAttribute('aria-pressed', selectedWeapon === 'pickaxe' ? 'true' : 'false');
  }
  if (btnS) {
    const parent = btnS.parentElement;
    if (parent) parent.style.display = sword.picked ? '' : 'none';
    btnS.setAttribute('aria-pressed', selectedWeapon === 'sword' ? 'true' : 'false');
  }
  if (btnR) {
    const parent = btnR.parentElement;
    if (parent) parent.style.display = raygun.picked ? '' : 'none';
    btnR.setAttribute('aria-pressed', selectedWeapon === 'raygun' ? 'true' : 'false');
  }
  if (btnB) {
    const parent = btnB.parentElement;
    if (parent) parent.style.display = builder.picked ? '' : 'none';
    btnB.setAttribute('aria-pressed', selectedWeapon === 'builder' ? 'true' : 'false');
  }
}

// Get list of available items in order
function getAvailableItems() {
  const items = [];
  if (axe.picked) items.push('axe');
  if (pickaxe.picked) items.push('pickaxe');
  if (sword.picked) items.push('sword');
  if (raygun.picked) items.push('raygun');
  if (builder.picked) items.push('builder');
  return items;
}

// Cycle to next/previous item
function cycleItem(direction = 1) {
  const items = getAvailableItems();
  if (items.length === 0) return;

  const currentIndex = selectedWeapon ? items.indexOf(selectedWeapon) : -1;
  let nextIndex = currentIndex + direction;

  // Wrap around
  if (nextIndex >= items.length) nextIndex = 0;
  if (nextIndex < 0) nextIndex = items.length - 1;

  equipWeapon(items[nextIndex]);
}

// Wire inventory buttons and keyboard shortcuts
const btnInvAxe = document.getElementById('inv-axe');
const btnInvPick = document.getElementById('inv-pickaxe');
const btnInvSword = document.getElementById('inv-sword');
const btnInvRay = document.getElementById('inv-raygun');
if (btnInvAxe) btnInvAxe.addEventListener('click', ()=> equipWeapon('axe'));
if (btnInvPick) btnInvPick.addEventListener('click', ()=> equipWeapon('pickaxe'));
if (btnInvSword) btnInvSword.addEventListener('click', ()=> equipWeapon('sword'));
if (btnInvRay) btnInvRay.addEventListener('click', ()=> equipWeapon('raygun'));
const btnInvBuilder = document.getElementById('inv-builder');
if (btnInvBuilder) btnInvBuilder.addEventListener('click', ()=> equipWeapon('builder'));

// Mouse wheel to cycle items
window.addEventListener('wheel', (e) => {
  const items = getAvailableItems();
  if (items.length === 0) return;

  // Only cycle if we have items
  if (e.deltaY > 0) {
    cycleItem(1); // scroll down = next item
  } else if (e.deltaY < 0) {
    cycleItem(-1); // scroll up = previous item
  }
}, { passive: true });

// Initial cursor setup - start timer to hide cursor
showCursor();

window.addEventListener('keydown', (e)=>{
  // Normalize key (handle composed accents)
  let k = (e.key || '').toString();
  if (k.length === 1) k = k.normalize('NFC');
  k = k.toLowerCase();

  // Number keys for direct selection (supports both QWERTY and AZERTY)
  if ((k === '1' || k === '&') && axe.picked) equipWeapon('axe');
  if ((k === '2' || k === 'é') && pickaxe.picked) equipWeapon('pickaxe');
  if ((k === '3' || k === '"') && sword.picked) equipWeapon('sword');
  if ((k === '4' || k === "'") && raygun.picked) equipWeapon('raygun');
  if ((k === '5' || k === '(') && builder.picked) equipWeapon('builder');

  // Tab to cycle forward, Shift+Tab to cycle backward
  if (e.key === 'Tab') {
    const items = getAvailableItems();
    if (items.length > 0) {
      e.preventDefault();
      cycleItem(e.shiftKey ? -1 : 1);
    }
  }
});

function checkCloudBounce(w, h) {
  const shrineCloudActive = shrineCloud.spawned;
  const bounceArmed = skyRenderer.isCloudBounceArmed();
  if (!bounceArmed && !shrineCloudActive) return;
  // Never bounce while underground, even if camera moved
  const groundTopWorld = h - world.groundH;
  const playerFeetWorld = player.y + player.h;
  if (playerFeetWorld > groundTopWorld) return;

  // Check shrine cloud collision first (if spawned)
  if (shrineCloudActive) {
    const playerRect = { x: player.x, y: player.y, w: player.w, h: player.h };
    const cloudRect = {
      x: shrineCloud.x + (shrineCloud.hitOffsetX || -shrineCloud.w / 2),
      y: shrineCloud.y + (shrineCloud.hitOffsetY || 0),
      w: shrineCloud.hitW || shrineCloud.w,
      h: shrineCloud.hitH || shrineCloud.h
    };
    if (rectsOverlap(playerRect, cloudRect)) {
      player.vy = -900; // strong boost upwards
      player.onGround = false;
      if (typeof player.airJumpUsed !== 'undefined') player.airJumpUsed = false;
      skyRenderer.triggerCloudBounce();
      return;
    }
  }

  if (player.onGround || standingOnPlatform || standingOnPlank) return;

  const collision = skyRenderer.checkCloudCollision(player, cameraX, cameraY, world.groundH, w, h);
  if (!collision || player.vy <= 0) return;

  // bounce way up
  player.vy = -900; // strong boost upwards
  player.onGround = false;
  // Reset double-jump availability upon cloud bounce
  if (typeof player.airJumpUsed !== 'undefined') player.airJumpUsed = false;
  skyRenderer.triggerCloudBounce();
}

function rectsOverlap(a, b) {
  return !(a.x + a.w < b.x || b.x + b.w < a.x || a.y + a.h < b.y || b.y + b.h < a.y);
}

function updateTrees(dt, w) {
  // Falling animation removed; prune any felled trees to free memory
  ensureChunksInView(cameraX, w);
  for (const [key, arr] of treesByChunk) {
    for (const t of arr) {
      if (t && typeof t.hitFlash === 'number' && t.hitFlash > 0) {
        t.hitFlash = Math.max(0, t.hitFlash - dt);
      }
    }
    const kept = arr.filter(t => !t.felled);
    if (kept.length !== arr.length) treesByChunk.set(key, kept);
  }
}

function tryChopNearestTree(h) {
  let nearest = null;
  let nd = Infinity;
  ensureChunksInView(cameraX, world.width());
  const groundTop = h - world.groundH;
  const px = player.x + player.w / 2;
  for (const [, arr] of treesByChunk) {
    for (const t of arr) {
      if (t.felled) continue;
      const d = Math.abs(t.x - px);
      if (d < nd) { nd = d; nearest = t; }
    }
  }
  // Use a threshold based on the drawn sprite width for intuitive reach
  const reach = nearest ? Math.max(18, Math.min(40, (nearest._dw || 40) * 0.35)) : 22;
  if (nearest && nd < reach) {
    if (typeof nearest.maxHp !== 'number' || nearest.maxHp <= 0) nearest.maxHp = 1;
    if (typeof nearest.hp !== 'number' || nearest.hp > nearest.maxHp) nearest.hp = nearest.maxHp;
    nearest.hp = Math.max(0, nearest.hp - 1);
    particles.spawnSparks(nearest.x, groundTop - 6, 6, ['#f1d27f', '#d59c63']);
    if (nearest.hp > 0) {
      nearest.hitFlash = 0.18;
      return;
    }
    nearest.felled = true;
    nearest.fallDir = player.facing >= 0 ? 1 : -1;
    nearest.fallT = 0;
    applyTreeDamage(nearest);
    spawnTreePieces(nearest, h);
    removeTreeFromChunks(nearest);
  }
}

function removeTreeFromChunks(tree) {
  const idx = Math.floor(tree.x / chunkSize);
  const arr = treesByChunk.get(idx);
  if (!arr) return;
  const i = arr.indexOf(tree);
  if (i >= 0) arr.splice(i, 1);
}

// --- Simple spark FX for sword impacts ---
// --- Impact sparks + sword dash trail particles: extracted to systems/ParticleSystem.js
// (spawn/update/draw are methods on the `particles` instance; state lives in the module) ---

// --- Enemy interactions (silk drops) ---
let silkCount = 0; // collected silk threads (from spiders via sword only)

function killEnemy(e, killKind) {
  // Effects and rewards
  particles.spawnSparks(e.x + e.w / 2, e.y + e.h / 2, 8);
  if (e.kind === 'spider' && killKind === 'sword') {
    silkCount += 1;
    addPickupToast('silk');
  }
  e.dead = true;
}

function getEnemyHitbox(e) {
  if (!e) return { x: 0, y: 0, w: 0, h: 0 };
  if (e.kind === 'spider') {
    const insetX = Math.round(e.w * 0.30);
    const insetY = Math.round(e.h * 0.25);
    const w = Math.max(4, e.w - insetX * 2);
    const h = Math.max(4, e.h - insetY);
    return { x: e.x + insetX, y: e.y + insetY, w, h };
  }
  if (e.kind === 'tree_monster') {
    const insetX = Math.round(e.w * 0.22);
    const insetY = Math.round(e.h * 0.18);
    const w = Math.max(6, e.w - insetX * 2);
    const h = Math.max(10, e.h - insetY);
    return { x: e.x + insetX, y: e.y + insetY, w, h };
  }
  return { x: e.x, y: e.y, w: e.w, h: e.h };
}

// Apply damage to an enemy; spiders die instantly
function damageEnemy(e, amount, kind) {
  if (!e || e.dead) return false;
  if (e.kind === 'spider') {
    killEnemy(e, kind);
    return true;
  }
  // Old titans removed
  if (e.kind === 'tree_monster') {
    if (typeof e.maxHp !== 'number') e.maxHp = 3;
    if (typeof e.hp !== 'number') e.hp = e.maxHp;
    const dmg = Math.max(0, amount || 1);
    e.hp = Math.max(0, (e.hp || 0) - dmg);
    particles.spawnSparks(e.x + e.w / 2, e.y + e.h * 0.5, 6);
    e.hitFlash = 0.25;
    if (e.hp <= 0) {
      killEnemy(e, kind);
    }
    return true;
  }
  return false;
}

function hitSpidersAtCircle(cx, cy, r, killKind) {
  let hit = false;
  for (const e of enemies) {
    if ((e.kind !== 'spider' && e.kind !== 'titan' && e.kind !== 'tree_monster') || e.dead) continue;
    const hb = getEnemyHitbox(e);
    const nearestX = Math.max(hb.x, Math.min(cx, hb.x + hb.w));
    const nearestY = Math.max(hb.y, Math.min(cy, hb.y + hb.h));
    const dx = cx - nearestX, dy = cy - nearestY;
    if (dx * dx + dy * dy <= r * r) {
      // Melee damage: sword hits harder than axe
      const dmg = killKind === 'sword' ? 2 : 1;
      damageEnemy(e, dmg, killKind);
      hit = true;
      // one-hit kill; continue to allow multi-kill if stacked
    }
  }
  return hit;
}

function damageCorruptTitan(amount) {
  if (!corruptTitanGuardReady || !corruptTitanGuard || corruptTitanGuard.defeated) return false;
  const titan = corruptTitanGuard;
  titan.hp = Math.max(0, (typeof titan.hp === 'number' ? titan.hp : CORRUPT_TITAN_MAX_HP) - Math.max(0, amount || 0));
  titan.hitFlash = 0.25;
  titan.attackCooldown = Math.max(titan.attackCooldown || 0, 0.35);
  const bodyRect = titan.lastBodyRect || getCorruptTitanBodyRect(titan);
  if (bodyRect) {
    particles.spawnSparks(bodyRect.x + bodyRect.w * 0.5, bodyRect.y + bodyRect.h * 0.35, 12, ['#d8b6ff', '#b58dff', '#8e62ff']);
  }
  if (titan.hp <= 0) {
    titan.defeated = true;
    titan.state = 'defeated';
    titan.attackType = null;
    titan.attackHitboxRect = null;
    titan.vineTrigger = 0;
    titan.slamTrigger = 0;
    titan.glowKind = null;
    titan.glowTimer = 0;
    titan.afterimages = [];
    titan.activeAttackMeta = null;
    titan.activeAttackDamageSource = null;
    titan.activeAttackKnock = null;
    titan.frameIndex = CORRUPT_TITAN_FINAL_FRAME_INDEX;
    titan.nextIndex = CORRUPT_TITAN_FINAL_FRAME_INDEX;
    titan.defeatedTimer = 0;
    titan.defeatedAlpha = 1.0; // Start fade from fully visible
    titan.defeatedFadeDuration = 2.5; // Fade out over 2.5 seconds

    // Trigger goblin family to come out and spawn toolbox
    spawnGoblinFamily();
  }
  return true;
}

function hitCorruptTitanAtCircle(cx, cy, r, damage) {
  if (!corruptTitanGuardReady || !corruptTitanGuard || corruptTitanGuard.defeated) return false;
  const bodyRect = corruptTitanGuard.lastBodyRect || getCorruptTitanBodyRect(corruptTitanGuard);
  if (!bodyRect) return false;
  const nearestX = Math.max(bodyRect.x, Math.min(cx, bodyRect.x + bodyRect.w));
  const nearestY = Math.max(bodyRect.y, Math.min(cy, bodyRect.y + bodyRect.h));
  const dx = cx - nearestX;
  const dy = cy - nearestY;
  if (dx * dx + dy * dy > r * r) return false;
  return damageCorruptTitan(damage || CORRUPT_TITAN_AXE_DAMAGE);
}

function hitCorruptTitanByProjectile(cx, cy, r, damage) {
  return hitCorruptTitanAtCircle(cx, cy, r, damage || CORRUPT_TITAN_RAYGUN_DAMAGE);
}

function flashTreesInRect(rect) {
  if (!rect) return;
  const groundTop = world.height() - world.groundH;
  for (const [, arr] of treesByChunk) {
    for (const t of arr) {
      if (!t || t.felled) continue;
      const dw = t._dw || 32;
      const dh = t._dh || 96;
      const treeRect = { x: t.x - dw / 2, y: groundTop - dh, w: dw, h: dh };
      if (!rectsOverlap(rect, treeRect)) continue;
      t.hitFlash = Math.max(t.hitFlash || 0, 0.3);
    }
  }
}

function blastSpidersAtRect(rect) {
  if (!rect) return;
  const cx = rect.x + rect.w * 0.5;
  const cy = rect.y + rect.h * 0.5;
  const radius = Math.hypot(rect.w, rect.h) * 0.55;
  hitSpidersAtCircle(cx, cy, radius, 'sword');
}

function hitPlantsAtCircle(cx, cy, r) {
  const groundTopY = world.height() - world.groundH;
  let hit = false;
  for (const trap of plantTraps) {
    if (trap.dead) continue;
    const info = plantTrapFrameInfo(trap.frame);
    const marginX = info.width * 0.20;
    const marginY = info.height * 0.25;
    const hx = trap.x - info.width / 2 + marginX;
    const hy = groundTopY - info.height + info.bottomOffset + marginY;
    const hw = info.width - marginX * 2;
    const hh = info.height - marginY * 2;
    const nearestX = Math.max(hx, Math.min(cx, hx + hw));
    const nearestY = Math.max(hy, Math.min(cy, hy + hh));
    const dx = cx - nearestX, dy = cy - nearestY;
    if (dx * dx + dy * dy <= r * r) {
      trap.dead = true;
      particles.spawnSparks(trap.x, hy + hh * 0.5, 10, ['#3b8f58', '#5cb877', '#a8d5a2', '#ffffff']);
      hit = true;
    }
  }
  return hit;
}

const SPIDER_STOMP_VERTICAL_TOLERANCE = 18;
const SPIDER_STOMP_MIN_DESCENT_SPEED = 40;
const SPIDER_STOMP_BOUNCE_VELOCITY = 650;

function tryStompSpider(e, hb) {
  if (!player || !e || e.dead) return false;
  const prevBottom = (typeof player._prevUpdateBottom === 'number') ? player._prevUpdateBottom : null;
  if (prevBottom == null) return false;
  const prevVy = (typeof player._prevUpdateVy === 'number') ? player._prevUpdateVy : null;
  const currentBottom = player.y + player.h;
  const wasAbove = prevBottom <= hb.y + SPIDER_STOMP_VERTICAL_TOLERANCE;
  const movedDownward = currentBottom > prevBottom;
  const vyNow = player.vy;
  const descending = (vyNow > SPIDER_STOMP_MIN_DESCENT_SPEED) || (prevVy != null && prevVy > SPIDER_STOMP_MIN_DESCENT_SPEED);
  if (!(wasAbove && movedDownward && descending)) return false;

  if (!damageEnemy(e, 1, 'stomp')) return false;

  player.vy = -SPIDER_STOMP_BOUNCE_VELOCITY;
  player.onGround = false;
  if (typeof player.airJumpUsed !== 'undefined') player.airJumpUsed = false;
  if (typeof player.coyoteTimer === 'number') player.coyoteTimer = 0;
  if (typeof player.jumpBufferTimer === 'number') player.jumpBufferTimer = 0;
  playerStickyTime = 0;
  return true;
}

function spawnEnemy(w, h) {
  const groundTop = h - world.groundH;
  const fromX = cameraX + w + 80; // spawn just off the right edge
  const roll = Math.random();
  if (!apocalypseActive && roll < 0.7 && spiderReady) {
    // Spider: small, faster
    const targetH = 38;
    const aspect = spiderImg.naturalWidth && spiderImg.naturalHeight ? (spiderImg.naturalWidth / spiderImg.naturalHeight) : 1.6;
    const dh = targetH;
    const dw = Math.max(8, Math.round(targetH * aspect));
    const y = groundTop - dh;
    const speed = 70 + Math.random() * 40;
    enemies.push({ kind: 'spider', x: fromX, y, w: dw, h: dh, speed, baseSpeed: speed, dir: -1, stickyTimer: 0 });
  }
  // Old titan enemies removed - only apocalypse titans exist now
}

function updateEnemies(dt, w, h) {
  // Spawn cadence
  enemySpawnTimer += dt;
  if (enemySpawnTimer >= enemyNextSpawn) {
    if (!apocalypseActive) spawnEnemy(w, h);
    enemySpawnTimer = 0;
    enemyNextSpawn = 3 + Math.random() * 4;
  }
  // Move and cull
  const leftCull = cameraX - 120;
  for (const e of enemies) {
    if (typeof e.dir !== 'number' || !e.dir) e.dir = -1;
    if (typeof e.baseSpeed !== 'number') e.baseSpeed = e.speed || 0;
    if (typeof e.stickyTimer === 'number' && e.stickyTimer > 0) {
      e.stickyTimer = Math.max(0, e.stickyTimer - dt);
    }
    if (typeof e.attackCooldown === 'number' && e.attackCooldown > 0) {
      e.attackCooldown = Math.max(0, e.attackCooldown - dt);
    }
    if (typeof e.hitFlash === 'number' && e.hitFlash > 0) {
      e.hitFlash = Math.max(0, e.hitFlash - dt);
    }
    if (e.kind === 'tree_monster') {
      if (typeof e.walkPhase !== 'number') e.walkPhase = 0;
      e.walkPhase += dt * 4.6;
      const playerCenter = player.x + player.w / 2;
      const enemyCenter = e.x + e.w / 2;
      const desiredDir = Math.sign(playerCenter - enemyCenter) || e.dir || 1;
      e.dir = desiredDir;
      if (typeof e.spawnBob === 'number' && e.spawnBob > 0) {
        e.spawnBob = Math.max(0, e.spawnBob - dt);
      }
    }
    const slowFactor = (e.kind === 'spider') && e.stickyTimer > 0 ? 0.18 : 1;
    let moveSpeed = (e.baseSpeed || e.speed || 0) * slowFactor;
    if (e.kind === 'tree_monster') {
      const playerCenter = player.x + player.w / 2;
      const enemyCenter = e.x + e.w / 2;
      const dist = Math.abs(playerCenter - enemyCenter);
      const boost = dist > 220 ? 1.25 : (dist > 120 ? 1.1 : 1);
      moveSpeed *= boost;
    }
    const dx = e.dir * moveSpeed * dt;
    // Predict next rect
    const nx = e.x + dx;
    const blocked = rectOverlapsWorldSolid(w, h, nx, e.y, e.w, e.h);
    // Also reverse if we would overlap any plank on next step
    let plankBlock = false;
    if (!blocked) {
      for (const p of planks) {
        if (nx + e.w <= p.x || p.x + p.w <= nx || e.y + e.h <= p.y || p.y + p.h <= e.y) continue;
        plankBlock = true;
        break;
      }
    }

    // Hole detection: check if there's a gap ahead (no ground support)
    const isGrounded = rectOverlapsWorldSolid(w, h, e.x + 1, e.y + e.h + 1, Math.max(1, e.w - 2), 2);
    const holeAhead = !blocked && !plankBlock && isGrounded && !rectOverlapsWorldSolid(
      w, h,
      nx + e.w * 0.5,
      e.y + e.h + 1,
      Math.max(2, e.w * 0.3),
      TILE_SIZE * 2
    );

    if (blocked || plankBlock || holeAhead) {
      // Reverse direction on impact with wall OR when detecting a hole
      // NPCs turn around at holes instead of trying to jump
      e.dir *= -1;
    } else {
      e.x = nx;
    }
    // Vertical physics: gravity and jumping
    if (typeof e.vy !== 'number') e.vy = 0;
    const GRAVITY = 800;
    e.vy += GRAVITY * dt;
    const dy = e.vy * dt;

    // Try to move vertically
    const targetY = e.y + dy;
    const verticalBlocked = rectOverlapsWorldSolid(w, h, e.x, targetY, e.w, e.h);
    if (!verticalBlocked) {
      e.y = targetY;
    } else {
      // Hit ground or ceiling, stop vertical movement
      e.vy = 0;
    }

    // Settle onto ground if close
    const MAX_DROP = Math.floor(TILE_SIZE * 0.5);
    let drop = 0;
    while (!rectOverlapsWorldSolid(w, h, e.x + 1, e.y + e.h + 1, Math.max(1, e.w - 2), 2) && drop < MAX_DROP && e.vy >= 0) {
      if (rectOverlapsWorldSolid(w, h, e.x, e.y + 1, e.w, e.h)) break;
      e.y += 1; drop += 1;
    }
    if (drop > 0 && e.vy >= 0) e.vy = 0; // Reset vertical velocity when grounded
    // If embedded due to carving, nudge up out of solids a little
    let guard = 0;
    while (rectOverlapsWorldSolid(w, h, e.x, e.y, e.w, e.h) && guard++ < TILE_SIZE) {
      e.y -= 1;
    }
    if (e.kind === 'spider') {
      const hb = getEnemyHitbox(e);
      const overlap = !(player.x + player.w <= hb.x || hb.x + hb.w <= player.x || player.y + player.h <= hb.y || hb.y + hb.h <= player.y);
      if (overlap) {
        if (tryStompSpider(e, hb)) {
          continue;
        }
        if (typeof e.attackCooldown !== 'number' || e.attackCooldown <= 0) {
          const center = player.x + player.w * 0.5;
          const dir = center < hb.x + hb.w / 2 ? -1 : 1;
          playerHitFlash = PLAYER_HIT_FLASH_DUR;
          player.vx = dir * 280;
          player.vy = -420;
          player.onGround = false;
          if (typeof player.airJumpUsed !== 'undefined') player.airJumpUsed = false;
          e.attackCooldown = 0.6;
        }
      }
    } else if (e.kind === 'tree_monster') {
      const hb = getEnemyHitbox(e);
      const overlap = !(player.x + player.w <= hb.x || hb.x + hb.w <= player.x || player.y + player.h <= hb.y || hb.y + hb.h <= player.y);
      if (overlap && (typeof e.attackCooldown !== 'number' || e.attackCooldown <= 0)) {
        const center = player.x + player.w * 0.5;
        const dir = center < hb.x + hb.w / 2 ? -1 : 1;
        const KNOCK_X = 280;
        const KNOCK_Y = 420;
        playerHitFlash = PLAYER_HIT_FLASH_DUR;
        player.vx = dir * KNOCK_X;
        player.vy = -KNOCK_Y;
        player.onGround = false;
        if (typeof player.airJumpUsed !== 'undefined') player.airJumpUsed = false;
        e.attackCooldown = 0.9;
      }
    }
  }
  enemies = enemies.filter(e => !e.dead && (e.x + e.w > leftCull));
}

function drawEnemies(ctx) {
  if (!enemies.length) return;
  const ENEMY_GROUND_SINK = 2; // px: sink slightly into ground for contact
  for (const e of enemies) {
    if (e.kind === 'spider' && spiderReady) {
      const b = spiderBounds || { bottom: 0 };
      const scaleY = spiderImg.naturalHeight ? (e.h / spiderImg.naturalHeight) : 1;
      const yAdj = Math.round((b.bottom || 0) * scaleY) + ENEMY_GROUND_SINK;
      const bob = Math.sin((performance.now?.() || Date.now()) * 0.006 + e.x * 0.02) * 1.5;
      ctx.save();
      ctx.translate(0, bob);
      ctx.drawImage(spiderImg, Math.round(e.x), Math.round(e.y + yAdj), e.w, e.h);
      ctx.restore();
    } else if (e.kind === 'tree_monster') {
      const b = treeMonsterBounds || { bottom: 0 };
      const scaleY = treeMonsterImg.naturalHeight ? (e.h / treeMonsterImg.naturalHeight) : 1;
      const yAdj = Math.round((b.bottom || 0) * scaleY) + ENEMY_GROUND_SINK;
      const phase = e.walkPhase || 0;
      const bob = Math.sin(phase * 2.2) * 3;
      const sway = Math.sin(phase * 1.4) * 2;
      ctx.save();
      const drawX = Math.round(e.x + sway);
      const drawY = Math.round(e.y + yAdj + bob - (e.spawnBob ? Math.sin((1 - Math.min(1, e.spawnBob / 0.4)) * Math.PI) * 6 : 0));
      if (treeMonsterReady) {
        if (e.dir === 1) {
          ctx.translate(drawX + e.w, drawY);
          ctx.scale(-1, 1);
          if (e.hitFlash) {
            ctx.globalAlpha = Math.max(0.3, 1 - e.hitFlash * 2);
          }
          ctx.drawImage(treeMonsterImg, 0, 0, e.w, e.h);
        } else {
          if (e.hitFlash) {
            ctx.globalAlpha = Math.max(0.3, 1 - e.hitFlash * 2);
          }
          ctx.drawImage(treeMonsterImg, drawX, drawY, e.w, e.h);
        }
      } else {
        ctx.fillStyle = '#5c3a1d';
        ctx.fillRect(drawX, drawY, e.w, e.h);
      }
      ctx.restore();
    }
  }
}

function ensureCorruptTitanTestsInitialized() {
  if (!ENABLE_CORRUPT_TITAN_TESTS || corruptTitanTestsReady) return;
  if (!corruptTitanFramesLoaded) return;
  const frameCount = corruptTitanFrames.length;
  if (!frameCount) return;
  const baseX = CORRUPT_TITAN_WORLD_X;
  corruptTitanTests = CORRUPT_TITAN_TEST_MODES.map((entry, idx) => ({
    mode: entry.mode,
    label: entry.label,
    x: baseX + idx * CORRUPT_TITAN_TEST_SPACING,
    frameIndex: 0,
    nextIndex: frameCount > 1 ? 1 : 0,
    timer: 0,
    frameDuration: getCorruptTitanFrameDuration(0),
    blend: 0,
    afterimages: [],
    initPhase: 0
  }));
  corruptTitanTestsReady = true;
}

function getCorruptTitanFrameDuration(frameIndex) {
  const count = corruptTitanFrames.length || corruptTitanFramePaths.length;
  if (!count) return CORRUPT_TITAN_FRAME_DUR;
  if (frameIndex >= count - 1) return Infinity;
  if (frameIndex === count - 2) return CORRUPT_TITAN_IDLE_HOLD;
  return CORRUPT_TITAN_FRAME_DUR;
}

function updateCorruptTitanTests(dt, w, h) {
  if (!ENABLE_CORRUPT_TITAN_TESTS) return;
  ensureCorruptTitanTestsInitialized();
  if (!corruptTitanTestsReady) return;
  const frameCount = corruptTitanFrames.length;
  if (!frameCount) return;
  for (const titan of corruptTitanTests) {
    if (!titan) continue;
    titan.timer += dt;
    let duration = titan.frameDuration || getCorruptTitanFrameDuration(titan.frameIndex);
    let advanced = false;
    while (frameCount && titan.timer >= duration && duration > 0) {
      titan.timer -= duration;
      if (titan.mode === 'afterimage') {
        titan.afterimages.push({
          frameIndex: titan.frameIndex,
          age: 0,
          life: CORRUPT_TITAN_AFTERIMAGE_LIFE,
          alpha: CORRUPT_TITAN_AFTERIMAGE_ALPHA
        });
      }
      titan.frameIndex = titan.nextIndex % frameCount;
      titan.nextIndex = (titan.frameIndex + 1) % frameCount;
      duration = titan.frameDuration = getCorruptTitanFrameDuration(titan.frameIndex);
      advanced = true;
      if (!frameCount || duration <= 0) break;
    }
    if (advanced && titan.mode === 'afterimage') {
      // limit trail length
      if (titan.afterimages.length > 12) titan.afterimages.splice(0, titan.afterimages.length - 12);
    }
    titan.frameDuration = duration;
    const blendWindow = Math.min(CORRUPT_TITAN_CROSSFADE_WINDOW, Math.max(0.12, duration * 0.75));
    const fadeStart = Math.max(0, duration - blendWindow);
    titan.blend = duration <= 0 ? 0 : Math.max(0, Math.min(1, (titan.timer - fadeStart) / blendWindow));
    if (titan.mode === 'afterimage' && titan.afterimages.length) {
      for (const trail of titan.afterimages) {
        trail.age += dt;
        const t = Math.max(0, Math.min(1, trail.age / trail.life));
        trail.alpha = CORRUPT_TITAN_AFTERIMAGE_ALPHA * (1 - t);
      }
      titan.afterimages = titan.afterimages.filter(trail => trail.alpha > 0.02);
    }
  }
}


function resolveCorruptTitanMeta(frameIndex) {
  if (corruptTitanFrameMeta[frameIndex]) return corruptTitanFrameMeta[frameIndex];
  const img = corruptTitanFrames[frameIndex] || null;
  if (!img || !img.naturalHeight) return null;
  const bounds = corruptTitanFrameBounds[frameIndex] || { bottom: 0, top: 0 };
  const baseline = corruptTitanFootBaseline || 0;
  const footOffset = (typeof bounds.bottomCenter === 'number' ? (bounds.bottomCenter - img.naturalWidth / 2) : 0) - baseline;
  const meta = {
    img,
    bounds,
    footOffsetPx: footOffset,
    groundOffset: (frameIndex === CORRUPT_TITAN_IDLE_FRAME_INDEX ? CORRUPT_TITAN_LAST_FRAME_GROUND_OFFSET : 0)
  };
  corruptTitanFrameMeta[frameIndex] = meta;
  return meta;
}

function getCorruptTitanLayout(meta, x, groundTop, targetH, scaleMul = 1, yOffset = 0) {
  if (!meta || !meta.img || !meta.img.naturalHeight) return null;
  const img = meta.img;
  const bounds = meta.bounds || { bottom: 0, top: 0 };
  const dh = targetH * scaleMul;
  const scale = dh / img.naturalHeight;
  const dw = img.naturalWidth * scale;
  const bottom = (bounds.bottom || 0) * scale;
  const footShift = (meta.footOffsetPx || 0) * scale;
  const drawX = Math.round(x - dw / 2 - footShift);
  const drawY = Math.round(groundTop - dh + bottom + yOffset + (meta.groundOffset || 0));
  return { x: drawX, y: drawY, w: dw, h: dh, scale };
}

function renderCorruptTitanMetaFrame(ctx, meta, x, groundTop, targetH, opts = {}) {
  const layout = getCorruptTitanLayout(meta, x, groundTop, targetH, typeof opts.scaleMul === 'number' ? opts.scaleMul : 1, typeof opts.yOffset === 'number' ? opts.yOffset : 0);
  if (!layout) return null;
  const blur = typeof opts.blur === 'number' ? Math.max(0, opts.blur) : 0;
  const alpha = typeof opts.alpha === 'number' ? opts.alpha : 1;
  ctx.save();
  if (blur > 0) ctx.filter = `blur(${blur}px)`;
  ctx.globalAlpha *= alpha;
  ctx.drawImage(meta.img, layout.x, layout.y, layout.w, layout.h);
  ctx.restore();
  return layout;
}

function renderCorruptTitanFrame(ctx, frameIndex, x, groundTop, targetH, opts = {}) {
  const meta = resolveCorruptTitanMeta(frameIndex);
  if (!meta) return null;
  return renderCorruptTitanMetaFrame(ctx, meta, x, groundTop, targetH, opts);
}

function renderCorruptTitanAttackFrame(ctx, x, groundTop, targetH, opts = {}) {
  if (!corruptTitanAttackMeta) return null;
  return renderCorruptTitanMetaFrame(ctx, corruptTitanAttackMeta, x, groundTop, targetH, opts);
}

function getCorruptTitanBodyRect(titan) {
  if (!titan) return null;
  const frameIndex = Math.min(CORRUPT_TITAN_IDLE_FRAME_INDEX, Math.max(0, titan.frameIndex || CORRUPT_TITAN_IDLE_FRAME_INDEX));
  const meta = resolveCorruptTitanMeta(frameIndex);
  const h = world.height();
  const groundTop = h - world.groundH;
  return getCorruptTitanLayout(meta, titan.x, groundTop, CORRUPT_TITAN_GUARD_TARGET_H, 1, 0);
}

function getCorruptTitanVineTriggerRect(bodyRect) {
  if (!bodyRect) return null;
  const width = CORRUPT_TITAN_VINE_TRIGGER_RECT.width;
  const front = bodyRect.x; // player approaches from the left, so "front" is the left edge
  const x = front - CORRUPT_TITAN_VINE_TRIGGER_RECT.forwardOffset - width;
  const h = CORRUPT_TITAN_VINE_TRIGGER_RECT.height;
  const y = (bodyRect.y + bodyRect.h) - h + CORRUPT_TITAN_VINE_TRIGGER_RECT.groundPadding;
  return { x, y, w: width, h };
}

function getCorruptTitanSlamTriggerRect(bodyRect) { return null; }

function randomCorruptTitanAttackDelay() {
  return CORRUPT_TITAN_ATTACK_MIN_DELAY + Math.random() * (CORRUPT_TITAN_ATTACK_MAX_DELAY - CORRUPT_TITAN_ATTACK_MIN_DELAY);
}

function updateCorruptTitanDefeated(titan, dt) {
  // Titan already disappeared, just clean up
  corruptTitanGuard = null;
  corruptTitanGuardReady = false;
  return;
}

function updateCorruptTitanAfterimages(titan, dt) {
  if (!titan.afterimages || !titan.afterimages.length) return;
  for (const trail of titan.afterimages) {
    trail.age += dt;
    const t = Math.max(0, Math.min(1, trail.age / trail.life));
    trail.alpha = CORRUPT_TITAN_AFTERIMAGE_ALPHA * (1 - t);
  }
  titan.afterimages = titan.afterimages.filter(trail => trail.alpha > 0.02);
}

function ensureCorruptTitanGuardInitialized() {
  if (debugBypassCorruptTitan) return;
  if (corruptTitanGuardReady) return;
  if (!corruptTitanFramesLoaded) return;
  prepareCorruptTitanMeta();
  const frameCount = corruptTitanFrames.length;
  if (!frameCount) return;
  corruptTitanGuard = {
    mode: CORRUPT_TITAN_GUARD_MODE,
    x: CORRUPT_TITAN_WORLD_X,
    frameIndex: CORRUPT_TITAN_INTRO_FRAME_INDEX,
    nextIndex: Math.min(frameCount - 1, CORRUPT_TITAN_INTRO_FRAME_INDEX + 1),
    timer: 0,
    frameDuration: getCorruptTitanFrameDuration(CORRUPT_TITAN_INTRO_FRAME_INDEX),
    blend: 0,
    afterimages: [],
    started: false,
    state: 'intro',
    idleTimer: 0,
    nextAttack: randomCorruptTitanAttackDelay(),
    attackTimer: 0,
    attackHitApplied: false,
    attackHitboxRect: null,
    attackType: null,
    attackCooldown: 0,
    activeAttackMeta: null,
    activeAttackDamageSource: null,
    activeAttackKnock: null,
    activeAttackWindup: 0,
    activeAttackDuration: 0,
    activeAttackDamageStart: 0,
    activeAttackDamageEnd: 0,
    activeAttackTargetH: CORRUPT_TITAN_GUARD_TARGET_H,
    vineTrigger: 0,
    slamTrigger: 0,
    glowKind: null,
    glowTimer: 0,
    hitFlash: 0,
    hp: CORRUPT_TITAN_MAX_HP,
    defeated: false,
    lastBodyRect: null,
    defeatedTimer: 0,
    defeatedAlpha: 1
  };
  corruptTitanGuardReady = true;
}

function updateCorruptTitanGuardIntro(titan, dt, frameCount) {
  titan.timer += dt;
  let duration = titan.frameDuration || getCorruptTitanFrameDuration(titan.frameIndex);
  let advanced = false;
  while (frameCount && duration > 0 && titan.timer >= duration) {
    titan.timer -= duration;
    if (titan.mode === 'afterimage') {
      titan.afterimages.push({
        frameIndex: titan.frameIndex,
        age: 0,
        life: CORRUPT_TITAN_AFTERIMAGE_LIFE,
        alpha: CORRUPT_TITAN_AFTERIMAGE_ALPHA
      });
      if (titan.afterimages.length > CORRUPT_TITAN_GUARD_AFTERIMAGE_CAP) {
        titan.afterimages.splice(0, titan.afterimages.length - CORRUPT_TITAN_GUARD_AFTERIMAGE_CAP);
      }
    }
    titan.frameIndex = Math.min(frameCount - 1, titan.nextIndex % frameCount);
    titan.nextIndex = Math.min(frameCount - 1, titan.frameIndex + 1);
    duration = titan.frameDuration = getCorruptTitanFrameDuration(titan.frameIndex);
    advanced = true;
    if (titan.frameIndex >= CORRUPT_TITAN_IDLE_FRAME_INDEX) {
      titan.state = 'idle';
      titan.timer = 0;
      titan.frameDuration = Infinity;
      titan.frameIndex = CORRUPT_TITAN_IDLE_FRAME_INDEX;
      titan.nextIndex = CORRUPT_TITAN_IDLE_FRAME_INDEX;
      titan.blend = 0;
      titan.idleTimer = 0;
      titan.nextAttack = randomCorruptTitanAttackDelay();
      break;
    }
    if (!isFinite(duration) || duration <= 0) break;
  }
  titan.frameDuration = duration;
  if (titan.state === 'intro') {
    const blendWindow = Math.min(CORRUPT_TITAN_CROSSFADE_WINDOW, Math.max(0.12, duration * 0.75));
    const fadeStart = Math.max(0, duration - blendWindow);
    titan.blend = duration <= 0 || !isFinite(duration)
      ? 0
      : Math.max(0, Math.min(1, (titan.timer - fadeStart) / blendWindow));
  } else {
    titan.blend = 0;
  }
  if (advanced || titan.afterimages.length) updateCorruptTitanAfterimages(titan, dt);
}

function applyCorruptTitanAttackDamage(titan) {
  const meta = titan.activeAttackMeta || corruptTitanAttackMeta;
  const damageSource = titan.activeAttackDamageSource || CORRUPT_TITAN_VINE_DAMAGE_SOURCE;
  const knock = titan.activeAttackKnock || CORRUPT_TITAN_VINE_KNOCK;
  if (!meta || !damageSource) return false;
  const h = world.height();
  const groundTop = h - world.groundH;
  const layout = getCorruptTitanLayout(meta, titan.x, groundTop, titan.activeAttackTargetH || CORRUPT_TITAN_GUARD_TARGET_H, 1, 0);
  if (!layout) return false;
  const scale = layout.scale;
  const rect = {
    x: layout.x + damageSource.minX * scale,
    y: layout.y + damageSource.minY * scale,
    w: (damageSource.maxX - damageSource.minX) * scale,
    h: (damageSource.maxY - damageSource.minY) * scale
  };
  titan.attackHitboxRect = rect;
  blastSpidersAtRect(rect);
  flashTreesInRect(rect);
  const playerRect = { x: player.x, y: player.y, w: player.w, h: player.h };
  const overlap = !(playerRect.x + playerRect.w <= rect.x || rect.x + rect.w <= playerRect.x || playerRect.y + playerRect.h <= rect.y || rect.y + rect.h <= playerRect.y);
  if (!overlap) return false;
  const center = player.x + player.w * 0.5;
  const dir = center < titan.x ? -1 : 1;
  playerHitFlash = PLAYER_HIT_FLASH_DUR;
  const knockX = knock && typeof knock.x === 'number' ? knock.x : 0;
  const knockY = knock && typeof knock.y === 'number' ? knock.y : 0;
  player.vx = dir * knockX;
  player.vy = -Math.abs(knockY);
  player.onGround = false;
  if (typeof player.airJumpUsed !== 'undefined') player.airJumpUsed = false;
  return true;
}

function updateCorruptTitanWindup(titan, dt) {
  if (!titan.activeAttackMeta) {
    titan.state = 'idle';
    titan.glowKind = null;
    titan.glowTimer = 0;
    return;
  }
  titan.attackTimer += dt;
  titan.glowTimer = Math.max(0, (titan.activeAttackWindup || 0) - titan.attackTimer);
  titan.attackHitboxRect = null;
  updateCorruptTitanAfterimages(titan, dt);
  if (titan.attackTimer >= (titan.activeAttackWindup || 0)) {
    titan.state = titan.attackType === 'slam' ? 'slamAttack' : 'vineAttack';
    titan.attackTimer = 0;
    titan.attackHitApplied = false;
  }
}

function updateCorruptTitanActive(titan, dt) {
  if (!titan.activeAttackMeta) {
    titan.state = 'recover';
    titan.attackCooldown = Math.max(titan.attackCooldown || 0, CORRUPT_TITAN_ATTACK_COOLDOWN);
    titan.attackTimer = 0;
    titan.attackHitApplied = false;
    titan.attackHitboxRect = null;
    return;
  }
  titan.attackHitboxRect = null;
  titan.attackTimer += dt;
  const damageStart = titan.activeAttackDamageStart || 0;
  const damageEnd = titan.activeAttackDamageEnd || 0;
  const duration = titan.activeAttackDuration || 0.5;
  const inDamageWindow = titan.attackTimer >= damageStart && titan.attackTimer <= damageEnd;
  if (inDamageWindow && !titan.attackHitApplied) {
    titan.attackHitApplied = applyCorruptTitanAttackDamage(titan);
  }
  if (titan.attackTimer >= duration) {
    titan.attackTimer = 0;
    titan.attackHitApplied = false;
    titan.attackHitboxRect = null;
    titan.attackCooldown = CORRUPT_TITAN_ATTACK_COOLDOWN;
    titan.state = 'recover';
    titan.glowKind = null;
    titan.glowTimer = 0;
  }
  updateCorruptTitanAfterimages(titan, dt);
}

function updateCorruptTitanRecover(titan, dt) {
  titan.attackHitboxRect = null;
  titan.glowTimer = Math.max(0, titan.glowTimer - dt);
  titan.vineTrigger = 0;
  titan.slamTrigger = 0;
  titan.glowKind = null;
  if ((titan.attackCooldown || 0) <= 0) {
    titan.state = 'idle';
    titan.idleTimer = 0;
    titan.attackType = null;
    titan.activeAttackMeta = null;
    titan.activeAttackDamageSource = null;
    titan.activeAttackKnock = null;
  }
  updateCorruptTitanAfterimages(titan, dt);
}

function startCorruptTitanWindup(titan, type) {
  let meta = type === 'slam' ? corruptTitanAttackSelfMeta : corruptTitanAttackMeta;
  if (!meta || !meta.img) {
    const fallbackFrame = resolveCorruptTitanMeta(CORRUPT_TITAN_FINAL_FRAME_INDEX);
    meta = fallbackFrame;
  }
  if (!meta || !meta.img) return;
  titan.attackType = type;
  titan.attackTimer = 0;
  titan.attackHitApplied = false;
  titan.attackHitboxRect = null;
  titan.vineTrigger = 0;
  titan.frameIndex = CORRUPT_TITAN_IDLE_FRAME_INDEX;
  titan.nextIndex = CORRUPT_TITAN_IDLE_FRAME_INDEX;
  titan.idleTimer = 0;
  titan.activeAttackMeta = meta;
  titan.activeAttackDamageSource = (type === 'slam') ? CORRUPT_TITAN_SLAM_DAMAGE_SOURCE : CORRUPT_TITAN_VINE_DAMAGE_SOURCE;
  titan.activeAttackKnock = (type === 'slam') ? CORRUPT_TITAN_SLAM_KNOCK : CORRUPT_TITAN_VINE_KNOCK;
  titan.activeAttackWindup = (type === 'slam') ? CORRUPT_TITAN_SLAM_WINDUP : CORRUPT_TITAN_VINE_WINDUP;
  titan.activeAttackDuration = (type === 'slam') ? CORRUPT_TITAN_SLAM_ACTIVE : CORRUPT_TITAN_VINE_ACTIVE;
  const dmgWindow = (type === 'slam') ? CORRUPT_TITAN_SLAM_DAMAGE_WINDOW : CORRUPT_TITAN_VINE_DAMAGE_WINDOW;
  titan.activeAttackDamageStart = dmgWindow.start;
  titan.activeAttackDamageEnd = dmgWindow.end;
  titan.activeAttackTargetH = CORRUPT_TITAN_GUARD_TARGET_H;
  titan.attackCooldown = 0;
  titan.state = type === 'slam' ? 'slamWindup' : 'vineWindup';
  titan.glowKind = type;
  titan.glowTimer = titan.activeAttackWindup;
}

function updateCorruptTitanIdle(titan, dt, w, h) {
  titan.frameIndex = CORRUPT_TITAN_IDLE_FRAME_INDEX;
  titan.nextIndex = CORRUPT_TITAN_IDLE_FRAME_INDEX;
  titan.attackHitboxRect = null;
  titan.glowTimer = Math.max(0, titan.glowTimer - dt);
  const bodyRect = getCorruptTitanBodyRect(titan);
  if (bodyRect) titan.lastBodyRect = bodyRect;
  const playerRect = { x: player.x, y: player.y, w: player.w, h: player.h };

  if ((titan.attackCooldown || 0) > 0) {
    titan.vineTrigger = 0;
    titan.slamTrigger = 0;
    titan.glowKind = null;
    updateCorruptTitanAfterimages(titan, dt);
    return;
  }

  let startedWindup = false;
  if (bodyRect) {
    const vineRect = getCorruptTitanVineTriggerRect(bodyRect);
    const inVine = vineRect && rectsOverlap(vineRect, playerRect);

    if (inVine) {
      titan.vineTrigger += dt;
      titan.glowKind = 'vine';
      titan.glowTimer = Math.min(CORRUPT_TITAN_VINE_WINDUP, titan.glowTimer + dt);
      if (titan.vineTrigger >= CORRUPT_TITAN_VINE_TRIGGER_DELAY) {
        startCorruptTitanWindup(titan, 'vine');
        startedWindup = true;
      }
    } else {
      titan.vineTrigger = 0;
      titan.glowKind = null;
    }
  }

  if (startedWindup) {
    updateCorruptTitanAfterimages(titan, dt);
    return;
  }

  titan.activeAttackMeta = null;
  titan.activeAttackDamageSource = null;
  titan.activeAttackKnock = null;
  updateCorruptTitanAfterimages(titan, dt);
}

function updateCorruptTitanGuard(dt, w, h) {
  if (debugBypassCorruptTitan) return;
  if (ENABLE_CORRUPT_TITAN_TESTS) return; // dev tests handle their own anims
  ensureCorruptTitanGuardInitialized();
  if (!corruptTitanGuardReady || !corruptTitanGuard) return;
  const titan = corruptTitanGuard;
  const frameCount = corruptTitanFrames.length;
  if (!frameCount) return;
  const playerCenter = player.x + player.w * 0.5;
  if (!titan.started) {
    if (playerCenter >= titan.x - CORRUPT_TITAN_TRIGGER_DISTANCE) {
      titan.started = true;
      titan.state = 'intro';
      titan.timer = 0;
      titan.frameDuration = getCorruptTitanFrameDuration(titan.frameIndex);
      titan.nextIndex = Math.min(frameCount - 1, titan.frameIndex + 1);
    } else {
      return;
    }
  }

  titan.hitFlash = Math.max(0, (titan.hitFlash || 0) - dt);
  if (titan.attackCooldown > 0) titan.attackCooldown = Math.max(0, titan.attackCooldown - dt);

  const bodyRect = getCorruptTitanBodyRect(titan);
  if (bodyRect) titan.lastBodyRect = bodyRect;

  if (titan.defeated) {
    titan.state = 'defeated';
    titan.attackHitboxRect = null;
    titan.glowKind = null;
    titan.glowTimer = Math.max(0, titan.glowTimer - dt);
    updateCorruptTitanAfterimages(titan, dt);

    // Fade out animation
    if (titan.defeatedAlpha > 0) {
      titan.defeatedTimer += dt;
      const fadeDuration = titan.defeatedFadeDuration || 2.5;
      titan.defeatedAlpha = Math.max(0, 1 - (titan.defeatedTimer / fadeDuration));

      // Spawn dissipating particles during fade
      if (titan.defeatedAlpha > 0 && Math.random() < 0.3) {
        const bodyRect = titan.lastBodyRect || titan.lastDrawRect;
        if (bodyRect) {
          const px = bodyRect.x + Math.random() * bodyRect.w;
          const py = bodyRect.y + Math.random() * bodyRect.h * 0.6;
          particles.spawnSparks(px, py, 1, ['#d8b6ff', '#b58dff', '#8e62ff', '#6842d0']);
        }
      }
    }
    return;
  }

  switch (titan.state) {
    case 'intro':
      updateCorruptTitanGuardIntro(titan, dt, frameCount);
      break;
    case 'idle':
      updateCorruptTitanIdle(titan, dt, w, h);
      break;
    case 'vineWindup':
    case 'slamWindup':
      updateCorruptTitanWindup(titan, dt);
      break;
    case 'vineAttack':
    case 'slamAttack':
      updateCorruptTitanActive(titan, dt);
      break;
    case 'recover':
      updateCorruptTitanRecover(titan, dt);
      break;
    case 'defeated':
      updateCorruptTitanDefeated(titan, dt);
      break;
    default:
      titan.state = 'idle';
      titan.idleTimer = 0;
      break;
  }
}

function drawCorruptTitanGuard(ctx, w, h) {
  if (ENABLE_CORRUPT_TITAN_TESTS) return; // avoid doubling up when tests active
  ensureCorruptTitanGuardInitialized();
  if (!corruptTitanGuardReady || !corruptTitanGuard) return;
  const frameCount = corruptTitanFrames.length;
  if (!frameCount) return;
  const groundTop = h - world.groundH;
  const viewLeft = cameraX - 200;
  const viewRight = cameraX + w + 200;
  const titan = corruptTitanGuard;
  if (titan.x < viewLeft || titan.x > viewRight) return;

  if (titan.defeated && (titan.defeatedAlpha || 0) <= 0) return;

  const baseAlpha = titan.defeated ? (titan.defeatedAlpha ?? 0) : 1;

  if (titan.mode === 'afterimage' && titan.afterimages && titan.afterimages.length) {
    for (const trail of titan.afterimages) {
      if (trail.alpha <= 0) continue;
      renderCorruptTitanFrame(ctx, trail.frameIndex % frameCount, titan.x, groundTop, CORRUPT_TITAN_GUARD_TARGET_H, {
        alpha: trail.alpha * baseAlpha,
        yOffset: -3,
        scaleMul: 1.02
      });
    }
  }

  let meta;
  let layout = null;
  const attackState = titan.state === 'vineAttack' || titan.state === 'slamAttack';
  if (attackState && titan.activeAttackMeta) {
    meta = titan.activeAttackMeta;
    layout = renderCorruptTitanMetaFrame(ctx, meta, titan.x, groundTop, titan.activeAttackTargetH || CORRUPT_TITAN_GUARD_TARGET_H, { alpha: baseAlpha });
  } else {
    const frameIndex = Math.max(0, Math.min(frameCount - 1, typeof titan.frameIndex === 'number' ? titan.frameIndex : CORRUPT_TITAN_IDLE_FRAME_INDEX));
    meta = resolveCorruptTitanMeta(frameIndex);
    const scaleMul = (frameIndex === CORRUPT_TITAN_FINAL_FRAME_INDEX) ? CORRUPT_TITAN_FINAL_SCALE : 1;
    layout = renderCorruptTitanMetaFrame(ctx, meta, titan.x, groundTop, CORRUPT_TITAN_GUARD_TARGET_H, { alpha: baseAlpha, scaleMul });
  }
  if (layout) {
    titan.lastDrawRect = layout;
    if (!attackState || titan.defeated) titan.lastBodyRect = layout;
  }

  if (layout && !titan.defeated && typeof titan.hp === 'number') {
    const pct = Math.max(0, Math.min(1, titan.hp / CORRUPT_TITAN_MAX_HP));
    const barW = 160;
    const barH = 8;
    const barX = Math.round(layout.x + (layout.w - barW) / 2);
    const barY = Math.round(layout.y - 24);
    ctx.save();
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.fillRect(barX - 2, barY - 2, barW + 4, barH + 4);
    ctx.fillStyle = 'rgba(60, 20, 110, 0.85)';
    ctx.fillRect(barX, barY, barW, barH);
    ctx.fillStyle = '#b997ff';
    ctx.fillRect(barX, barY, Math.round(barW * pct), barH);
    ctx.restore();
  }

  const glowDuration = titan.glowKind === 'slam' ? CORRUPT_TITAN_SLAM_WINDUP : CORRUPT_TITAN_VINE_WINDUP;
  if (meta && layout && titan.glowKind && titan.glowTimer > 0 && glowDuration > 0) {
    const glowAlpha = Math.max(0, Math.min(1, titan.glowTimer / glowDuration));
    ctx.save();
    ctx.globalAlpha = 0.35 * glowAlpha * baseAlpha;
    ctx.globalCompositeOperation = 'lighter';
    ctx.shadowColor = CORRUPT_TITAN_GLOW_COLOR;
    ctx.shadowBlur = 24;
    ctx.drawImage(meta.img, layout.x, layout.y, layout.w, layout.h);
    ctx.restore();
  }

  if (meta && layout && titan.hitFlash > 0) {
    const flashAlpha = Math.max(0, Math.min(1, titan.hitFlash / 0.25));
    ctx.save();
    ctx.globalAlpha = 0.25 * flashAlpha * baseAlpha;
    ctx.globalCompositeOperation = 'lighter';
    ctx.shadowColor = 'rgba(255,105,105,0.85)';
    ctx.shadowBlur = 18;
    ctx.drawImage(meta.img, layout.x, layout.y, layout.w, layout.h);
    ctx.restore();
  }
}

function drawCorruptTitanTests(ctx, w, h) {
  if (!ENABLE_CORRUPT_TITAN_TESTS) return;
  if (!corruptTitanTestsReady) {
    ensureCorruptTitanTestsInitialized();
    if (!corruptTitanTestsReady) return;
  }
  const groundTop = h - world.groundH;
  const viewLeft = cameraX - 200;
  const viewRight = cameraX + w + 200;
  for (const titan of corruptTitanTests) {
    if (!titan || titan.x < viewLeft || titan.x > viewRight) continue;
    const baseX = titan.x;
    const currentFrame = titan.frameIndex % corruptTitanFrames.length;
    const nextFrame = titan.nextIndex % corruptTitanFrames.length;
    if (titan.mode === 'afterimage' && titan.afterimages && titan.afterimages.length) {
      for (const trail of titan.afterimages) {
        if (trail.alpha <= 0) continue;
        renderCorruptTitanFrame(ctx, trail.frameIndex % corruptTitanFrames.length, baseX, groundTop, CORRUPT_TITAN_TEST_TARGET_H, {
          alpha: trail.alpha,
          yOffset: -2,
          scaleMul: 1.02
        });
      }
    }
    let rect = renderCorruptTitanFrame(ctx, currentFrame, baseX, groundTop, CORRUPT_TITAN_TEST_TARGET_H, { alpha: 1 });
    if (titan.mode === 'crossfade' && titan.blend > 0 && corruptTitanFrames.length > 1) {
      const blendAlpha = Math.min(0.75, titan.blend);
      renderCorruptTitanFrame(ctx, nextFrame, baseX, groundTop, CORRUPT_TITAN_TEST_TARGET_H, { alpha: blendAlpha });
    }
    if (titan.mode === 'blur' && titan.blend > 0 && corruptTitanFrames.length > 1) {
      const blurAlpha = Math.min(0.8, titan.blend * 0.9);
      renderCorruptTitanFrame(ctx, nextFrame, baseX, groundTop, CORRUPT_TITAN_TEST_TARGET_H, {
        alpha: blurAlpha,
        blur: CORRUPT_TITAN_BLUR_PX,
        scaleMul: 1.01,
        yOffset: -1
      });
    }
    if (rect && titan.label) {
      ctx.save();
      ctx.globalAlpha = 0.85;
      ctx.font = '14px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
      ctx.fillStyle = 'rgba(12, 16, 26, 0.6)';
      const textX = Math.round(baseX - ctx.measureText(titan.label).width / 2);
      const textY = Math.round(rect.y - 18);
      ctx.fillText(titan.label, textX, textY);
      ctx.restore();
    }
  }
}

function drawLevitateWindParticles(ctx) {
  if (!levitateWindParticles.length) return;
  ctx.save();
  for (const p of levitateWindParticles) {
    ctx.globalAlpha = p.alpha;
    ctx.fillStyle = '#b0d0ff'; // Light blue wind color
    const size = 2 + Math.random();
    ctx.fillRect(Math.round(p.x), Math.round(p.y), size, size);
  }
  ctx.restore();
  ctx.globalAlpha = 1;
}

function updateFeathers(dt, w, h) {
  ensureFeathersInView(cameraX, w, h);
  ensureDeepForestElements(cameraX, w, h);
  if (!feathers.length) return;
  const playerRect = { x: player.x, y: player.y, w: player.w, h: player.h };
  for (const f of feathers) {
    if (f.collected || f.removed) continue;
    f.phase += dt * f.floatSpeed;
    f.glowPhase += dt * f.glowSpeed;
    const sway = Math.sin(f.phase) * f.swayAmp;
    const bob = Math.sin(f.phase * 1.85 + f.phaseOffset) * f.bobAmp;
    f.x = f.baseX + sway;
    f.y = f.baseY + bob;
    const glowPulse = Math.sin(f.glowPhase) * 0.28;
    f.glowStrength = Math.max(0.18, 0.38 + glowPulse);

    const fx = f.x - f.w / 2;
    const fy = f.y - f.h / 2;
    const overlap = !(playerRect.x + playerRect.w <= fx || fx + f.w <= playerRect.x ||
      playerRect.y + playerRect.h <= fy || fy + f.h <= playerRect.y);
    if (overlap) {
      f.collected = true;
      featherCount += 1;
      addPickupToast('feather');
      particles.spawnSparks(f.x, f.y, 10, ['#dffbfd', '#9fd5ff', '#c4f0ff']);
      const chunkArr = featherChunks.get(f.chunk);
      if (chunkArr) {
        const idx = chunkArr.indexOf(f);
        if (idx >= 0) chunkArr.splice(idx, 1);
      }
    }
  }
  feathers = feathers.filter(f => !f.collected && !f.removed);
}

function drawFeathers(ctx) {
  if (!featherReady || !feathers.length) return;
  ctx.save();
  for (const f of feathers) {
    if (f.collected || f.removed) continue;
    const centerX = f.x;
    const centerY = f.y;
    const radius = Math.max(f.w, f.h) * 0.85;
    if (!Number.isFinite(centerX) || !Number.isFinite(centerY) || !Number.isFinite(radius) || radius <= 0) continue;
    const innerR = Math.max(4, radius * 0.35);
    const gradient = ctx.createRadialGradient(centerX, centerY, innerR, centerX, centerY, radius);
    const glowAlpha = Math.min(0.7, Math.max(0.25, f.glowStrength));
    gradient.addColorStop(0, `rgba(148, 220, 255, ${0.55 * glowAlpha})`);
    gradient.addColorStop(1, 'rgba(148, 220, 255, 0)');
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    ctx.save();
    const spriteAlpha = 0.88 + Math.sin(f.glowPhase * 1.05) * 0.08;
    ctx.globalAlpha = Math.max(0.6, Math.min(1, spriteAlpha));
    const drawX = Math.round(centerX - f.w / 2);
    const drawY = Math.round(centerY - f.h / 2);
    ctx.drawImage(featherImg, drawX, drawY, f.w, f.h);
    ctx.restore();
  }
  ctx.restore();
}

// --- Diamonds: draw and pickup ---
function drawDiamonds(ctx, w, h) {
  if (!diamondReady || !diamonds.length) return;
  for (let i = 0; i < diamonds.length; i++) {
    const d = diamonds[i];
    // Overlap with player to pick up
    const ply = { x: player.x, y: player.y, w: player.w, h: player.h };
    if (!(d.x + d.w < ply.x || ply.x + ply.w < d.x || d.y + d.h < ply.y || ply.y + ply.h < d.y)) {
      diamondCount += 1;
      addPickupToast('diamond');
      diamonds.splice(i, 1); i--; continue;
    }
    ctx.drawImage(diamondImg, Math.round(d.x), Math.round(d.y), d.w, d.h);
  }
}

function drawDiamondCounter(ctx) {
  if (diamondCount <= 0) return;
  // Draw at top-left after silk counter; reuse similar style
  const pad = 10;
  const baseX = 12 + (choppedCount > 0 && logReady ? 90 : 0) + (silkCount > 0 ? 90 : 0);
  const x = baseX, y = 12;
  const targetH = 22;
  const aspect = diamondImg.naturalWidth && diamondImg.naturalHeight ? (diamondImg.naturalWidth / diamondImg.naturalHeight) : 1;
  const dh = targetH;
  const dw = Math.max(1, Math.round(targetH * aspect));
  ctx.save();
  ctx.globalAlpha = 0.85;
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.beginPath();
  ctx.roundRect?.(x - 6, y - 6, dw + 60, dh + 12, 8);
  if (!ctx.roundRect) { ctx.fillRect(x - 6, y - 6, dw + 60, dh + 12); } else { ctx.fill(); }
  ctx.globalAlpha = 1;
  ctx.drawImage(diamondImg, x, y, dw, dh);
  ctx.fillStyle = '#fff';
  ctx.font = '16px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  ctx.fillText(`x ${diamondCount}`, x + dw + pad, y + dh - 4);
  ctx.restore();
}

function drawFeatherCounter(ctx) {
  if (!featherReady || featherCount <= 0) return;
  const pad = 10;
  const baseX = 12
    + (choppedCount > 0 && logReady ? 90 : 0)
    + (silkCount > 0 ? 90 : 0)
    + (diamondCount > 0 ? 90 : 0);
  const x = baseX;
  const y = 12;
  const targetH = 22;
  const aspect = featherImg.naturalWidth && featherImg.naturalHeight
    ? (featherImg.naturalWidth / featherImg.naturalHeight)
    : 0.55;
  const dh = targetH;
  const dw = Math.max(1, Math.round(targetH * aspect));
  ctx.save();
  ctx.globalAlpha = 0.85;
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.beginPath();
  ctx.roundRect?.(x - 6, y - 6, dw + 60, dh + 12, 8);
  if (!ctx.roundRect) { ctx.fillRect(x - 6, y - 6, dw + 60, dh + 12); } else { ctx.fill(); }
  ctx.globalAlpha = 1;
  ctx.drawImage(featherImg, x, y, dw, dh);
  ctx.fillStyle = '#fff';
  ctx.font = '16px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
  ctx.fillText(`x ${featherCount}`, x + dw + pad, y + dh - 4);
  ctx.restore();
}

// --- Depth-based atmosphere: darkness + motes ---
let motes = [];
function drawUndergroundAtmosphere(ctx, screenW, screenH) {
  const groundTopWorld = world.height() - world.groundH;
  const depth = (player.y + player.h) - groundTopWorld;
  const START_DEPTH = 900;  // don't show ambience until well underground
  const FULL_DEPTH = 2600;  // fully ramped ambience depth

  // Don't apply underground atmosphere when above ground (negative depth)
  if (depth <= 0) {
    const dt = 1 / 60;
    for (const m of motes) m.life += dt;
    motes = motes.filter(m => m.life < m.dur * 0.6);
    return;
  }

  if (depth <= START_DEPTH) {
    // Let existing motes expire quietly without drawing
    const dt = 1 / 60;
    for (const m of motes) m.life += dt;
    motes = motes.filter(m => m.life < m.dur * 0.6);
    return;
  }
  const t = Math.max(0, Math.min(1, (depth - START_DEPTH) / (FULL_DEPTH - START_DEPTH)));

  // Ambient fog tint that deepens with depth
  const fogTopY = Math.min(screenH, Math.max(0, (groundTopWorld + cameraY) * VIEW_SCALE));
  const fogGradient = ctx.createLinearGradient(0, fogTopY, 0, screenH);
  fogGradient.addColorStop(0, `rgba(28, 34, 58, ${0.36 * t * 0.35})`);
  fogGradient.addColorStop(1, `rgba(6, 10, 18, ${0.8 * t})`);
  ctx.save();
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = fogGradient;
  ctx.fillRect(0, fogTopY, screenW, Math.max(0, screenH - fogTopY + 200));
  ctx.restore();

  // Darkness overlay radial around player
  const cx = (player.x + player.w / 2 - cameraX) * VIEW_SCALE;
  const cy = (player.y + player.h / 2 + cameraY) * VIEW_SCALE;
  const inner = Math.max(140, 240 - 120 * t);
  const outer = Math.max(inner + 80, 420 + 380 * t);
  const g = ctx.createRadialGradient(cx, cy, inner, cx, cy, outer);
  g.addColorStop(0, 'rgba(0,0,0,0.00)');
  g.addColorStop(1, `rgba(0,0,0,${0.6 * t})`);
  ctx.save();
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, screenW, screenH);
  ctx.restore();

  // Dust motes near player; spawn rate scales with depth
  const spawnRate = t > 0 ? 10 * (0.3 + 1.2 * t) : 0;
  if (!drawUndergroundAtmosphere._acc) drawUndergroundAtmosphere._acc = 0;
  drawUndergroundAtmosphere._acc += spawnRate * (1/60);
  while (drawUndergroundAtmosphere._acc >= 1) {
    drawUndergroundAtmosphere._acc -= 1;
    const mx = player.x + (Math.random() - 0.5) * 160;
    const my = player.y + player.h/2 + (Math.random() - 0.5) * 120;
    motes.push({ x: mx, y: my, vx: (Math.random()-0.5)*12, vy: -10 - Math.random()*15, a: 0.7 + Math.random()*0.3, r: 1 + Math.random()*2, life: 0, dur: 2.5 + Math.random()*2.5 });
  }
  // Sim/update + draw
  const dt = 1/60; // assume small frame; visuals only
  ctx.save();
  ctx.scale(VIEW_SCALE, VIEW_SCALE);
  ctx.translate(-cameraX, cameraY);
  for (const m of motes) {
    m.life += dt; if (m.life > m.dur) continue;
    m.x += m.vx * dt; m.y += m.vy * dt;
    const alpha = Math.max(0, (1 - m.life / m.dur)) * m.a * (0.4 + 0.6 * t);
    ctx.globalAlpha = alpha;
    ctx.fillStyle = '#e6d9b8';
    ctx.beginPath(); ctx.arc(m.x, m.y, m.r, 0, Math.PI*2); ctx.fill();
  }
  ctx.restore(); ctx.globalAlpha = 1;
  motes = motes.filter(m => m.life < m.dur);
}

function swordHitTrees(h, screenH) {
  // Build a small hit circle in front of player mid-body
  const r = Math.max(10, player.w * 0.4);
  const cx = player.x + (player.facing === 1 ? player.w * 0.9 : -player.w * 0.4);
  const cy = player.y + player.h * 0.55;
  // Loop visible trees and check distance to trunk center line
  ensureChunksInView(cameraX, world.width());
  const groundTop = screenH ? (screenH - world.groundH) : (world.height() - world.groundH);
  let hit = false;
  for (const [, arr] of treesByChunk) {
    for (const t of arr) {
      if (t.felled) continue;
      // Approximate trunk hitbox using lower portion of sprite width
      const dw = t._dw || 28;
      const dh = t._dh || 80;
      const trunkW = Math.max(8, Math.round(dw * 0.28));
      const trunkH = Math.max(14, Math.round(dh * 0.45));
      const tx = t.x;
      const ty = groundTop - trunkH;
      // simple circle-rect overlap
      const rx = tx - trunkW / 2, ry = ty, rw = trunkW, rh = trunkH;
      const nearestX = Math.max(rx, Math.min(cx, rx + rw));
      const nearestY = Math.max(ry, Math.min(cy, ry + rh));
      const dx = cx - nearestX, dy = cy - nearestY;
      if (dx * dx + dy * dy <= r * r) {
        particles.spawnSparks(nearestX, nearestY, 6);
        hit = true;
      }
    }
  }
  // Also hit nearby spiders with the sword (one-hit kill + silk drop)
  const titanHit = hitCorruptTitanAtCircle(cx, cy, r, CORRUPT_TITAN_SWORD_DAMAGE);
  if (titanHit) particles.spawnSparks(cx, cy, 10, ['#d8b6ff', '#b58dff']);
  hit = hitSpidersAtCircle(cx, cy, r, 'sword') || titanHit || hit;
  hit = hitPlantsAtCircle(cx, cy, r) || hit;
  hit = hitBirdsAtCircle(cx, cy, r) || hit;
  return hit;
}

function applyTreeDamage(tree) {
  // With image-based trees we don't track partial damage visuals; keep stub
  tree.damage = null;
}

function spawnOrangeTreePieces(tree, groundTop) {
  const s = tree.scale || 1;
  const baseX = tree.x;
  const baseY = groundTop - Math.max(16, (tree._dh || 80) * 0.5);
  const debris = 3;
  for (let i = 0; i < debris; i++) {
    const chunkIndex = i % treeChunkImgs.length;
    const img = treeChunkImgs[chunkIndex];
    const targetH = Math.round(12 + Math.random() * 6);
    const aspect = (img && img.naturalHeight) ? (img.naturalWidth / img.naturalHeight) : 1;
    const dw = Math.max(6, Math.round(targetH * aspect));
    const dh = targetH;
    const ox = (Math.random() - 0.5) * Math.max(14, (tree._dw || 60) * 0.35);
    const oy = -Math.random() * Math.max(6, (tree._dh || 80) * 0.2);
    treePieces.push({
      x: baseX + ox,
      y: baseY + oy,
      w: dw,
      h: dh,
      vx: (Math.random() - 0.5) * 90 * s,
      vy: - (60 + Math.random() * 80) * s,
      rot: Math.random() * Math.PI,
      vrot: (Math.random() - 0.5) * 2,
      alpha: 1,
      img,
      collectible: false,
      collected: false
    });
  }

  const logAspect = (logImg && logImg.naturalHeight) ? (logImg.naturalWidth / logImg.naturalHeight) : 2.4;
  const logWidth = Math.round(26 * s);
  const logHeight = Math.max(12, Math.round(logWidth / logAspect));
  treePieces.push({
    x: baseX - logWidth / 2,
    y: baseY - Math.max(4, logHeight * 0.15),
    w: logWidth,
    h: logHeight,
    vx: (Math.random() - 0.5) * 60,
    vy: -180,
    rot: (Math.random() - 0.5) * 0.4,
    vrot: (Math.random() - 0.5) * 1.5,
    alpha: 1,
    img: logImg,
    collectible: true,
    collected: false
  });
}

function spawnOakTreePieces(tree, groundTop) {
  const s = tree.scale || 1.1;
  const baseX = tree.x;
  const baseY = groundTop - Math.max(20, (tree._dh || 80) * 0.55);
  const chunkCount = 8;
  for (let i = 0; i < chunkCount; i++) {
    const chunkIndex = i % treeChunkImgs.length;
    const img = treeChunkImgs[chunkIndex];
    if (!img || !img.naturalHeight) continue;
    const targetH = Math.round(18 + Math.random() * 12);
    const aspect = img.naturalWidth / img.naturalHeight;
    const dw = Math.max(8, Math.round(targetH * aspect));
    const dh = targetH;
    const ox = (Math.random() - 0.5) * Math.max(36, (tree._dw || 72) * 0.5);
    const oy = -Math.random() * Math.max(16, (tree._dh || 80) * 0.35);
    const dir = (player.facing >= 0 ? 1 : -1);
    const vx = (Math.random() - 0.2) * 150 * s * dir;
    const vy = - (120 + Math.random() * 160) * s;
    treePieces.push({
      x: baseX + ox,
      y: baseY + oy,
      w: dw,
      h: dh,
      vx,
      vy,
      rot: Math.random() * Math.PI,
      vrot: (Math.random() - 0.5) * 4,
      alpha: 1,
      img,
      collectible: false,
      collected: false
    });
  }
  const logAspect = (logImg && logImg.naturalHeight) ? (logImg.naturalWidth / logImg.naturalHeight) : 2.4;
  const logCount = 6;
  for (let i = 0; i < logCount; i++) {
    const logWidth = Math.round(28 * s);
    const logHeight = Math.max(14, Math.round(logWidth / logAspect));
    const spread = (i - (logCount - 1) / 2) * (logWidth + 12);
    const spawnX = baseX + spread;
    treePieces.push({
      x: spawnX - logWidth / 2,
      y: baseY - Math.max(6, logHeight * 0.2),
      w: logWidth,
      h: logHeight,
      vx: (Math.random() - 0.5) * 45,
      vy: -140 - Math.random() * 40,
      rot: (Math.random() - 0.5) * 0.4,
      vrot: (Math.random() - 0.5) * 1.4,
      alpha: 1,
      img: logImg,
      collectible: true,
      collected: false
    });
  }
}

function spawnTreeMonsterFromTree(tree, groundTop) {
  const baseSpeed = 60 + Math.random() * 24;
  const targetH = 56;
  const aspect = (treeMonsterImg && treeMonsterImg.naturalHeight) ? (treeMonsterImg.naturalWidth / treeMonsterImg.naturalHeight) : 0.7;
  const dh = targetH;
  const dw = Math.max(32, Math.round(dh * aspect));
  const x = Math.round(tree.x - dw / 2);
  const y = Math.round(groundTop - dh);
  const dir = (player.x + player.w / 2) >= tree.x ? 1 : -1;
  const hp = 4;
  enemies.push({
    kind: 'tree_monster',
    x,
    y,
    w: dw,
    h: dh,
    speed: baseSpeed,
    baseSpeed,
    dir: dir || 1,
    hp,
    maxHp: hp,
    walkPhase: Math.random() * Math.PI * 2,
    spawnBob: 0.4,
    attackCooldown: 0
  });
  particles.spawnSparks(tree.x, groundTop - dh * 0.5, 10);
}

function spawnTreePieces(tree, h) {
  const groundTop = h - world.groundH;
  if (tree.kind === 'orange') {
    spawnOrangeTreePieces(tree, groundTop);
    spawnTreeMonsterFromTree(tree, groundTop);
    return;
  }
  if (tree.kind === 'oak') {
    spawnOakTreePieces(tree, groundTop);
    return;
  }
  const s = tree.scale || 1;
  const baseX = tree.x;
  // Spawn around lower half of the tree
  const baseY = groundTop - Math.max(16, (tree._dh || 80) * 0.5);
  const count = 6; // use provided 6 chunk sprites
  for (let i = 0; i < count; i++) {
    const chunkIndex = i % treeChunkImgs.length;
    const img = treeChunkImgs[chunkIndex];
    if (!img || !img.naturalHeight) continue;
    // Keep chunks small
    const targetH = Math.round(15 + Math.random() * 10); // 10..20 px
    const aspect = img.naturalWidth / img.naturalHeight;
    const dw = Math.max(6, Math.round(targetH * aspect));
    const dh = targetH;
    const ox = (Math.random() - 0.5) * Math.max(20, (tree._dw || 60) * 0.6);
    const oy = -Math.random() * Math.max(10, (tree._dh || 80) * 0.3);
    const dir = (player.facing >= 0 ? 1 : -1);
    const vx = (Math.random() - 0.1) * 120 * s * dir;
    const vy = - (80 + Math.random() * 120) * s;
    const rot = Math.random() * Math.PI;
    const vrot = (Math.random() - 0.5) * 3;
    treePieces.push({
      x: baseX + ox,
      y: baseY + oy,
      w: dw,
      h: dh,
      vx,
      vy,
      rot,
      vrot,
      alpha: 1,
      img,
      collectible: (chunkIndex < 3),
      collected: false
    });
  }
}

function updateTreePieces(dt, w, h) {
  const groundTop = h - world.groundH;
  const GRAV = 1500;
  const FRICTION = 600;
  const BOUNCE = 0.25;
  const DAMP = 0.98;
  const FADE = 0.15; // per second
  const playerRect = { x: player.x, y: player.y, w: player.w, h: player.h };
  // integrate
  for (const p of treePieces) {
    p.vy += GRAV * dt;
    p.x += p.vx * dt;
    p.y += p.vy * dt;
    p.rot += p.vrot * dt;
    // ground collision
    if (p.y + p.h > groundTop) {
      p.y = groundTop - p.h;
      p.vy = -p.vy * BOUNCE;
      p.vx *= 1 - Math.min(1, FRICTION * dt / 1000);
      p.vrot *= DAMP;
    }
    if (p.collectible && !p.collected) {
      const overlap = !(playerRect.x + playerRect.w < p.x || playerRect.x > p.x + p.w || playerRect.y + playerRect.h < p.y || playerRect.y > p.y + p.h);
      if (overlap) {
        p.collected = true;
        p.collectible = false;
        p.alpha = 0;
        choppedCount += 1;
        addPickupToast('log');
        particles.spawnSparks(p.x + p.w / 2, p.y + p.h / 2, 6);
        continue;
      }
    }
    // fade
    const fadeRate = p.collectible ? (FADE * 0.35) : FADE;
    p.alpha = Math.max(0, p.alpha - fadeRate * dt);
  }
  // cull faded or far pieces
  const left = cameraX - 200;
  const right = cameraX + w + 200;
  treePieces = treePieces.filter(p => p.alpha > 0 && p.x + p.w > left && p.x < right);
}

function drawTreePieces(ctx, w, h) {
  const groundTop = h - world.groundH;
  ctx.save();
  for (const p of treePieces) {
    ctx.globalAlpha = p.alpha;
    ctx.save();
    ctx.translate(Math.round(p.x + p.w / 2), Math.round(p.y + p.h / 2));
    ctx.rotate(p.rot);
    if (p.img && p.img.naturalWidth) {
      ctx.drawImage(p.img, -Math.round(p.w / 2), -Math.round(p.h / 2), p.w, p.h);
    } else {
      ctx.fillStyle = '#4f7d4f';
      ctx.fillRect(-Math.round(p.w / 2), -Math.round(p.h / 2), p.w, p.h);
    }
    ctx.restore();
  }
  ctx.restore();
  ctx.globalAlpha = 1;
}

// Engine start will be invoked after all systems are defined (see end of file)

function getControlRows(layoutName) {
  const layout = (layoutName || 'WASD').toUpperCase();
  let moveKeys = 'A / D';
  let jumpKey = 'W';
  let interactKeys = 'S / Arrow Down';
  const dialogueKey = 'E';
  if (layout === 'ZQSD') {
    moveKeys = 'Q / D';
    jumpKey = 'Z';
  }
  const rows = [
    { label: 'Move', value: `${moveKeys} or Arrow Keys` },
    { label: 'Jump', value: `${jumpKey} or Space / Arrow Up` },
    { label: 'Interact', value: interactKeys },
    { label: 'Dialogue', value: dialogueKey },
    { label: 'Run', value: 'Shift' }
  ];
  if (builder && builder.picked) rows.push({ label: 'Builder Rotate', value: 'X' });
  if (isTouchEnv) rows.push({ label: 'Touch', value: 'Tap second finger to interact' });
  return rows;
}

function renderControlsOverlay() {
  if (!controlsListEl) return;
  const rows = getControlRows(input.layout);
  controlsListEl.innerHTML = rows.map(row => (
    `<div class="controls-row"><span class="label">${row.label}</span><span class="value">${row.value}</span></div>`
  )).join('');
  if (controlsTitleEl) controlsTitleEl.textContent = `Controls — ${input.layout}`;
}

function handleOptionsPointerDown(e) {
  if (!optionsMenuOpen) return;
  if (!optionsPanel || !optionsBtn) return;
  if (optionsPanel.contains(e.target) || optionsBtn.contains(e.target)) return;
  closeOptionsMenu(false);
}

function handleOptionsKeydown(e) {
  if (!optionsMenuOpen) return;
  const key = (e && e.key) ? e.key.toLowerCase() : '';
  if (key === 'escape') {
    e.preventDefault();
    e.stopPropagation();
    closeOptionsMenu(true);
  }
}

function openOptionsMenu() {
  if (!optionsBtn || !optionsPanel) return;
  if (optionsMenuOpen) return;
  optionsMenuOpen = true;
  optionsPanel.classList.add('open');
  optionsPanel.setAttribute('aria-hidden', 'false');
  optionsBtn.setAttribute('aria-expanded', 'true');
  document.addEventListener('pointerdown', handleOptionsPointerDown, true);
  window.addEventListener('keydown', handleOptionsKeydown, true);
}

function closeOptionsMenu(restoreFocus = false) {
  if (!optionsBtn || !optionsPanel) return;
  if (!optionsMenuOpen) return;
  optionsMenuOpen = false;
  optionsPanel.classList.remove('open');
  optionsPanel.setAttribute('aria-hidden', 'true');
  optionsBtn.setAttribute('aria-expanded', 'false');
  document.removeEventListener('pointerdown', handleOptionsPointerDown, true);
  window.removeEventListener('keydown', handleOptionsKeydown, true);
  if (restoreFocus && typeof optionsBtn.focus === 'function') {
    try { optionsBtn.focus(); } catch {}
  }
}

function toggleOptionsMenu() {
  if (optionsMenuOpen) closeOptionsMenu(false);
  else openOptionsMenu();
}

function showControlsOverlay() {
  if (!controlsOverlay) return;
  closeOptionsMenu(false);
  renderControlsOverlay();
  controlsOverlay.classList.add('visible');
  controlsOverlay.setAttribute('aria-hidden', 'false');
  if (controlsBtn) controlsBtn.setAttribute('aria-pressed', 'true');
  controlsOverlayVisible = true;
}

function hideControlsOverlay() {
  if (!controlsOverlay) return;
  controlsOverlay.classList.remove('visible');
  controlsOverlay.setAttribute('aria-hidden', 'true');
  if (controlsBtn) controlsBtn.setAttribute('aria-pressed', 'false');
  controlsOverlayVisible = false;
}

if (controlsBtn && controlsOverlay) {
  controlsBtn.addEventListener('click', () => {
    if (controlsOverlayVisible) hideControlsOverlay();
    else showControlsOverlay();
  });
  controlsOverlay.addEventListener('click', (e) => {
    if (e.target === controlsOverlay) hideControlsOverlay();
  });
}

window.addEventListener('keydown', (e) => {
  if (controlsOverlayVisible && e.key && e.key.toLowerCase() === 'escape') hideControlsOverlay();
});

// Options menu toggle + volume sliders
if (optionsBtn) {
  optionsBtn.addEventListener('click', (e) => {
    e.preventDefault();
    toggleOptionsMenu();
  });
}

if (optionsPanel) {
  optionsPanel.addEventListener('focusout', (e) => {
    if (!optionsMenuOpen) return;
    const next = e.relatedTarget || document.activeElement;
    if (next && (optionsPanel.contains(next) || (optionsBtn && optionsBtn.contains(next)))) return;
    closeOptionsMenu(false);
  });
}

function attachVolumeSlider(el, prop) {
  if (!el || !(prop in volumeSettings)) return;
  const syncAttributes = () => {
    const pct = Math.round(volumeSettings[prop] * 100);
    el.value = String(pct);
    el.setAttribute('aria-valuetext', `${pct}%`);
  };
  syncAttributes();
  el.addEventListener('input', () => {
    const raw = parseFloat(el.value);
    if (Number.isFinite(raw)) {
      const value = clamp01(raw / 100);
      // Use AudioManager methods for proper volume management
      if (prop === 'master') audioManager.setMasterVolume(value);
      else if (prop === 'music') audioManager.setMusicVolume(value);
      else if (prop === 'sfx') audioManager.setSfxVolume(value);
      syncAttributes();
    }
  });
  // No need for separate 'change' handler - setters handle persistence
}

attachVolumeSlider(masterVolumeSlider, 'master');
attachVolumeSlider(musicVolumeSlider, 'music');
attachVolumeSlider(sfxVolumeSlider, 'sfx');

// Wire up keyboard layout toggle UI (only layout buttons inside .kbd-toggle)
const layoutButtons = Array.from(document.querySelectorAll('.kbd-toggle .kbd-btn'));
const storedLayout = (typeof localStorage !== 'undefined' && localStorage.getItem('kbLayout')) || 'ZQSD';
setLayoutUI(storedLayout);

layoutButtons.forEach(btn => {
  btn.addEventListener('click', (e) => {
    const layout = btn.dataset.layout || 'ZQSD';
    setLayoutUI(layout);
  });
});

if (layoutToolbarBtn) {
  layoutToolbarBtn.addEventListener('click', () => {
    const next = (input.layout === 'ZQSD') ? 'WASD' : 'ZQSD';
    setLayoutUI(next);
  });
}

function setLayoutUI(layout) {
  input.setLayout(layout);
  try { localStorage.setItem('kbLayout', input.layout); } catch {}
  layoutButtons.forEach(b => {
    const active = (b.dataset.layout || '') === input.layout;
    b.setAttribute('aria-pressed', active ? 'true' : 'false');
  });
  syncLayoutToolbarButton();
  refreshActiveGuideText();
  renderControlsOverlay();
}

function syncLayoutToolbarButton() {
  if (!layoutToolbarBtn) return;
  layoutToolbarBtn.textContent = `Layout: ${input.layout}`;
  layoutToolbarBtn.setAttribute('aria-pressed', 'true');
  layoutToolbarBtn.setAttribute('aria-label', `Keyboard layout ${input.layout}. Click to switch.`);
}

// Mute button wiring (music only)
const muteBtn = document.getElementById('mute-btn');
// musicMuted declared at top with audioManager
try { musicMuted = localStorage.getItem('musicMuted') === '1'; } catch {}
audioManager.setMusicMuted(musicMuted); // Sync initial mute state
const muteButtons = [muteBtn, muteToolbarBtn].filter(Boolean);
if (muteButtons.length) {
  applyMuteState();
  syncMuteUI();
  muteButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      musicMuted = !musicMuted;
      audioManager.setMusicMuted(musicMuted);
      applyMuteState();
      syncMuteUI();
      try { localStorage.setItem('musicMuted', musicMuted ? '1' : '0'); } catch {}
    });
  });
}

function applyMuteState() {
  audioManager.setMusicMuted(musicMuted);
}
function syncMuteUI() {
  if (!muteButtons.length) return;
  const label = musicMuted ? '🔇' : '🎵';
  muteButtons.forEach(btn => {
    btn.setAttribute('aria-pressed', musicMuted ? 'true' : 'false');
    btn.textContent = label;
    btn.setAttribute('aria-label', musicMuted ? 'Music muted. Click to unmute.' : 'Music on. Click to mute.');
  });
}

// Mute all button wiring (music + SFX + ambiance)
const muteAllBtn = document.getElementById('mute-all-btn');
const muteAllToolbarBtn = document.getElementById('mute-all-toolbar-btn');
let allMuted = false;
try { allMuted = localStorage.getItem('allMuted') === '1'; } catch {}
audioManager.setAllMuted(allMuted); // Sync initial mute state
const muteAllButtons = [muteAllBtn, muteAllToolbarBtn].filter(Boolean);
if (muteAllButtons.length) {
  applyAllMuteState();
  syncAllMuteUI();
  muteAllButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      allMuted = !allMuted;
      audioManager.setAllMuted(allMuted);
      applyAllMuteState();
      syncAllMuteUI();
      try { localStorage.setItem('allMuted', allMuted ? '1' : '0'); } catch {}
    });
  });
}

function applyAllMuteState() {
  audioManager.setAllMuted(allMuted);
}
function syncAllMuteUI() {
  if (!muteAllButtons.length) return;
  const label = allMuted ? '🔇' : '🔊';
  muteAllButtons.forEach(btn => {
    btn.setAttribute('aria-pressed', allMuted ? 'true' : 'false');
    btn.textContent = label;
    btn.setAttribute('aria-label', allMuted ? 'All sounds muted. Click to unmute.' : 'All sounds on. Click to mute.');
  });
}

// Next song button wiring
const nextSongBtn = document.getElementById('next-song-btn');
if (nextSongBtn) {
  nextSongBtn.addEventListener('click', () => {
    audioManager.playNextTrack();
  });
}

// Scale button wiring
const scaleBtn = document.getElementById('scale-btn');
if (scaleBtn) {
  function syncScaleUI() {
    const pct = Math.round(VIEW_SCALE * 100);
    scaleBtn.textContent = `Scale ${pct}%`;
  }
  function setViewScale(s) {
    // Snap to nearest supported step and persist
    let best = SCALE_STEPS[0];
    let bestDiff = Math.abs(s - best);
    for (const v of SCALE_STEPS) {
      const d = Math.abs(s - v);
      if (d < bestDiff) { best = v; bestDiff = d; }
    }
    VIEW_SCALE = best;
    try { localStorage.setItem('viewScale', String(VIEW_SCALE)); } catch {}
    syncScaleUI();
  }
  function cycleScale(dir = 1) {
    const i = SCALE_STEPS.findIndex(v => Math.abs(v - VIEW_SCALE) < 1e-6);
    const ni = (i < 0 ? 0 : (i + dir + SCALE_STEPS.length) % SCALE_STEPS.length);
    setViewScale(SCALE_STEPS[ni]);
  }
  // Initialize label and bind click
  syncScaleUI();
  scaleBtn.addEventListener('click', () => cycleScale(1));
}

// Quality (DPR cap) button wiring
const qualityBtn = document.getElementById('quality-btn');
let qualityMode = (() => {
  try { return localStorage.getItem('qualityMode') || 'balanced'; } catch { return 'balanced'; }
})();
function applyQuality(mode) {
  qualityMode = (mode === 'native') ? 'native' : 'balanced';
  try { localStorage.setItem('qualityMode', qualityMode); } catch {}
  const cap = (qualityMode === 'native') ? 10 : 1.5; // effectively uncapped vs balanced
  setMaxDprCap(cap);
  fitCanvasToDisplaySize(canvas);
  syncQualityUI();
}
function syncQualityUI() {
  if (!qualityBtn) return;
  const label = (qualityMode === 'native') ? 'Quality: Native' : 'Quality: Balanced';
  qualityBtn.textContent = label;
}
applyQuality(qualityMode);
if (qualityBtn) {
  syncQualityUI();
  qualityBtn.addEventListener('click', () => {
    applyQuality(qualityMode === 'balanced' ? 'native' : 'balanced');
  });
}

// --- Save System (autosave + export/import) ---
const exportBtn = document.getElementById('export-btn');
const importBtn = document.getElementById('import-btn');
const importFileInput = document.getElementById('import-file');
const saveInfoEl = document.getElementById('save-info');
// Gate: don't autosave until the player has chosen Continue / New Game, so a background
// autosave of the default spawn can't clobber the save before the launch prompt is answered.
let gameStarted = false;

/**
 * Captures the current game state for saving
 * @returns {Object} Serialized game state object with player, camera, and world data
 */
function captureGameState() {
  const state = new GameState();

  // Resume position: the last place the player stood on the surface.
  state.player = { x: lastSurfaceX, y: lastSurfaceY };

  // Tools/inventory ownership + equip state + current selection.
  state.inventory = {
    axe: axe.picked,
    pickaxe: pickaxe.picked,
    sword: sword.picked,
    raygun: raygun.picked,
    builder: builder.picked,
    hat: hat.picked,
    jetback: jetback.picked,
    toolbox: toolbox.picked,
    hatEquipped: hat.equipped,
    jetbackEquipped: jetback.equipped,
    jetbackActive: jetback.active,
    selectedWeapon,
  };

  // Collected currencies.
  state.currency = {
    chopped: choppedCount,
    diamond: diamondCount,
    feather: featherCount,
    silk: silkCount,
  };

  // Progression flags that alter the world (so it doesn't reset on resume).
  state.flags = {
    shrineActivated: shrine.activated,
    barrierVinesRemoved: barrierVines.removed,
  };

  return state.serialize();
}

/**
 * Applies a loaded game state to restore the game
 * @param {Object} loadedState - The serialized game state to apply
 * @returns {boolean} True if state was successfully applied, false otherwise
 */
function applyGameState(loadedState) {
  if (!loadedState) return false;

  const state = GameState.deserialize(loadedState);
  if (!state.player) return false; // incompatible/empty save — caller starts fresh

  // Always resume on the surface (dug terrain isn't persisted). Leave any special mode.
  insideCave = false;
  insideMoon = false;
  moonLanding = null;
  moonTransition = null;
  apocalypseActive = false;
  spaceship.mode = 'idle';

  // Place the player on the ground at the saved surface X; snap Y to the current ground so
  // it works regardless of viewport height.
  const h = world.height();
  const groundTop = h - world.groundH;
  player.x = state.player.x;
  player.y = groundTop - player.h;
  player.vx = 0;
  player.vy = 0;
  player.onGround = true;
  cameraX = Math.max(0, player.x - world.width() * 0.33);
  cameraY = 0;
  skyRenderer.setCloudCamY(0);
  lastSurfaceX = player.x;
  lastSurfaceY = player.y;

  // Inventory / tools ownership
  const inv = state.inventory || {};
  axe.picked = !!inv.axe;
  pickaxe.picked = !!inv.pickaxe;
  sword.picked = !!inv.sword;
  raygun.picked = !!inv.raygun;
  builder.picked = !!inv.builder;
  hat.picked = !!inv.hat;
  jetback.picked = !!inv.jetback;
  if (inv.toolbox !== undefined) toolbox.picked = !!inv.toolbox;
  hat.equipped = !!inv.hatEquipped;
  jetback.equipped = !!inv.jetbackEquipped;
  jetback.active = !!inv.jetbackActive;
  // Mark owned world-collectibles as already spawned so they don't pop back in.
  if (axe.picked) axe.spawned = true;
  if (hat.picked) hat.spawned = true;
  if (jetback.picked) jetback.spawned = true;
  // Restore the equipped weapon (or clear all if nothing was selected).
  if (inv.selectedWeapon) {
    equipWeapon(inv.selectedWeapon);
  } else {
    axe.equipped = pickaxe.equipped = sword.equipped = raygun.equipped = builder.equipped = false;
    selectedWeapon = null;
  }

  // Currencies
  const cur = state.currency || {};
  choppedCount = cur.chopped || 0;
  diamondCount = cur.diamond || 0;
  featherCount = cur.feather || 0;
  silkCount = cur.silk || 0;

  // Progression flags
  const fl = state.flags || {};
  shrine.activated = !!fl.shrineActivated;
  barrierVines.removed = !!fl.barrierVinesRemoved;
  if (barrierVines.removed) {
    barrierVines.fade = 0;
    barrierVines.fading = false;
  }

  // Resuming a saved game: skip the one-time intro (wisp swirl + "your quest begins" title),
  // and treat the grove legend as already seen if we're resuming past the archway.
  if (wispIntro) wispIntro.active = false;
  if (player.x + player.w * 0.5 > arch.x + 20) groveLegend.seen = true;

  updateInventoryUI();
  console.log('Game state restored');
  return true;
}

/**
 * Updates the save info display with the latest save metadata
 */
function updateSaveInfo() {
  if (!saveInfoEl) return;

  const metadata = saveManager.getSaveMetadata(0);
  if (metadata && metadata.exists) {
    const date = new Date(metadata.timestamp);
    const timeStr = date.toLocaleTimeString();
    const dateStr = date.toLocaleDateString();
    saveInfoEl.textContent = `Last saved: ${dateStr} ${timeStr}`;
  } else {
    saveInfoEl.textContent = 'No save data found';
  }
}

// Briefly show a status message under the Save-File options, then restore the info line.
function flashSaveInfo(text, color) {
  if (!saveInfoEl) return;
  saveInfoEl.textContent = text;
  saveInfoEl.style.color = color || '';
  setTimeout(() => { updateSaveInfo(); saveInfoEl.style.color = ''; }, 2200);
}

// --- Autosave ---
function doAutosave(reason = '') {
  if (!gameStarted) return false;
  try {
    const ok = saveManager.quickSave(captureGameState());
    if (ok) updateSaveInfo();
    return ok;
  } catch (err) {
    console.warn('Autosave failed:', err);
    return false;
  }
}
// Periodic safety-net save, plus a save whenever the player leaves/hides the tab.
setInterval(() => doAutosave('interval'), 30000);
window.addEventListener('pagehide', () => doAutosave('pagehide'));

// --- Export / Import a save file ---
if (exportBtn) {
  exportBtn.addEventListener('click', () => {
    if (gameStarted) saveManager.quickSave(captureGameState()); // capture latest first
    const json = saveManager.exportSave(0);
    if (!json) { flashSaveInfo('Nothing to export yet', '#f87171'); return; }
    try {
      const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }));
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const a = document.createElement('a');
      a.href = url;
      a.download = `bloop-save-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      flashSaveInfo('✓ Save exported', '#4ade80');
    } catch (err) {
      console.error('Export failed:', err);
      flashSaveInfo('✗ Export failed', '#f87171');
    }
  });
}

if (importBtn && importFileInput) {
  importBtn.addEventListener('click', () => importFileInput.click());
  importFileInput.addEventListener('change', () => {
    const file = importFileInput.files && importFileInput.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result || '');
        if (!saveManager.importSave(0, text)) throw new Error('invalid save file');
        const data = saveManager.quickLoad();
        if (data && applyGameState(data)) {
          gameStarted = true;
          updateSaveInfo();
          flashSaveInfo('✓ Save imported', '#4ade80');
        } else {
          flashSaveInfo('✗ Incompatible save', '#f87171');
        }
      } catch (err) {
        console.error('Import failed:', err);
        flashSaveInfo('✗ Import failed', '#f87171');
      }
      importFileInput.value = ''; // allow re-importing the same file
    };
    reader.readAsText(file);
  });
}

// --- Launch prompt: Continue where you left off, or start a New Game ---
const continueOverlay = document.getElementById('continue-overlay');
const continueBtn = document.getElementById('continue-btn');
const newGameBtn = document.getElementById('newgame-btn');
const continueInfoEl = document.getElementById('continue-info');

// Begin play: enable autosave and nudge audio (called after the launch choice).
function startGameplay() {
  gameStarted = true;
  try { tryKickMusic(); } catch {}
}

function hideContinueOverlay() {
  if (continueOverlay) continueOverlay.hidden = true;
  if (engine) engine.resume();
}

// Called once assets are loaded and the engine has started. If a save exists (and we're not
// doing a debug jump), freeze the game behind a Continue / New Game modal.
function maybeShowContinuePrompt() {
  const params = new URLSearchParams(location.search);
  const debugJump = params.has('moon') || params.has('transition');
  const meta = saveManager.getSaveMetadata(0);
  if (debugJump || !meta || !meta.exists || !continueOverlay) {
    startGameplay(); // fresh run or debug jump — no prompt
    return;
  }
  if (engine) engine.pause();
  // A save exists: the Continue buttons double as the start gesture, so drop the mobile
  // tap-to-start overlay to avoid a second tap.
  const tapEl = document.getElementById('tap-overlay');
  if (tapEl) tapEl.style.display = 'none';
  if (continueInfoEl) {
    const d = new Date(meta.timestamp);
    continueInfoEl.textContent = `Last played ${d.toLocaleDateString()} ${d.toLocaleTimeString()}`;
  }
  continueOverlay.hidden = false;
  // Focus the primary action so Enter/Space resumes (matches the on-screen hint).
  if (continueBtn) continueBtn.focus();
}

if (continueBtn) {
  continueBtn.addEventListener('click', () => {
    const data = saveManager.quickLoad();
    if (data) applyGameState(data);
    hideContinueOverlay();
    startGameplay();
  });
}
if (newGameBtn) {
  newGameBtn.addEventListener('click', () => {
    // Start fresh from the default spawn, and immediately overwrite the slot with this fresh
    // state so a quick reload resumes the new game (not the old save).
    hideContinueOverlay();
    startGameplay();
    doAutosave('newgame');
  });
}

// Initialize save info on startup
updateSaveInfo();

// --- Background Music (using AudioManager) ---
// Compatibility wrappers for existing code
function startBackgroundMusic() {
  audioManager.startBackgroundMusic();
}

function pauseBackgroundMusic() {
  audioManager.pauseBackgroundMusic();
}

function resumeBackgroundMusic() {
  audioManager.resumeBackgroundMusic();
}

function softPauseMusic() {
  const music = audioManager.musicAudio;
  if (!music) { pauseBackgroundMusic(); return; }
  audioManager.musicWasPlaying = !music.paused && !musicMuted && !music.muted;
  audioManager.fadeAudio(music, 0.0, 140, AUDIO_KIND_MUSIC);
  setTimeout(() => {
    try { music.pause(); } catch {}
  }, 150);
}

function softResumeMusic() {
  if (musicMuted || titanMusicPlaying || apocalypseActive) return;
  const music = audioManager.musicAudio;
  if (!music) { resumeBackgroundMusic(); return; }
  audioManager.musicWasPlaying = true;
  setAudioBaseVolume(music, AUDIO.MUSIC_BASE_VOLUME, AUDIO_KIND_MUSIC);
  resumeBackgroundMusic();
}

// Music starts when gameplay begins (startGameplay), not on load — so the Continue/New Game
// launch menu stays silent. See kickMusicOnGesture below for the autoplay-unlock gesture.
// Start forest ambiance at game start
setAmbianceState('forest');
window.addEventListener('keydown', (e)=>{
  if (!dialogueState || !dialogueState.active) return;
  if ((e.key || '').toLowerCase() === 'h') {
    e.preventDefault();
    dialogueState.historyVisible = !dialogueState.historyVisible;
  }
  if ((e.key || '').toLowerCase() === 'escape') {
    e.preventDefault();
    endDialogue();
  }
});
// Track real user gesture for hint dismissal and opportunistically start music/ambiance
function tryKickMusic() {
  userGestureSeen = true;
  try {
    if (!audioManager.musicAudio) {
      startBackgroundMusic();
    } else if (!musicMuted && audioManager.musicAudio.paused) {
      // If muted, respect the setting; otherwise ensure playback starts
      const p = audioManager.musicAudio.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => { /* ignore; gesture unlock may still be required */ });
      }
    }

    // Also try to start ambiance if it needs a gesture
    if (ambianceNeedsGesture) {
      ambianceNeedsGesture = false;
      // DISABLED: Forest birds ambiance
      // const forestBirdsAudio = audioManager.namedAudio.get('forestBirds')?.audio;
      // if (forestBirdsAudio && forestBirdsAudio.paused) {
      //   const p = forestBirdsAudio.play();
      //   if (p && typeof p.catch === 'function') {
      //     p.catch(() => { ambianceNeedsGesture = true; });
      //   }
      //   if (p && typeof p.then === 'function') {
      //     p.then(() => {
      //       audioManager.fadeAudio(forestBirdsAudio, VOLUME_BASES.birds, 400, AUDIO_KIND_SFX);
      //     });
      //   }
      // }
      const undergroundAmbianceAudio = audioManager.namedAudio.get('undergroundAmbiance')?.audio;
      if (undergroundAmbianceAudio && undergroundAmbianceAudio.paused) {
        const p = undergroundAmbianceAudio.play();
        if (p && typeof p.then === 'function') {
          // Single chain: avoid an unhandled rejection when ambiance autoplay is blocked.
          p.then(() => {
            audioManager.fadeAudio(undergroundAmbianceAudio, 0.30, 400, AUDIO_KIND_SFX);
          }).catch(() => { ambianceNeedsGesture = true; });
        }
      }
    }
  } catch {}
}
// Start music on the first user gesture — but NOT while the Continue/New Game menu is open
// (gameStarted is false there). Stays armed until a gesture actually kicks music off.
function kickMusicOnGesture() {
  if (!gameStarted) return; // keep the launch menu silent
  tryKickMusic();
  window.removeEventListener('pointerdown', kickMusicOnGesture);
  window.removeEventListener('keydown', kickMusicOnGesture);
}
window.addEventListener('pointerdown', kickMusicOnGesture);
window.addEventListener('keydown', kickMusicOnGesture);

// Mobile: show a tap overlay to unlock audio and focus the canvas
const tapOverlay = document.getElementById('tap-overlay');
if (tapOverlay) {
  const isTouchEnv = window.matchMedia && window.matchMedia('(hover:none) and (pointer:coarse)').matches;
  if (!isTouchEnv) {
    // Ensure hidden on non-touch environments
    tapOverlay.style.display = 'none';
  } else {
    const dismiss = () => {
      // Guard if already removed
      if (!tapOverlay.parentNode) return;
      tapOverlay.remove();
      try { canvas.focus(); } catch {}
      // Kick music start on gesture; if blocked, gesture unlock will reattach
      try { startBackgroundMusic(); } catch {}
    };
    tapOverlay.addEventListener('pointerdown', (e)=>{ e.preventDefault(); dismiss(); });
    tapOverlay.addEventListener('keydown', (e)=>{
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); dismiss(); }
    });
  }
}

// Finally, start the game loop now that everything is wired
// Asset Loading System
const assetLoader = new AssetLoader();
const loadingScreen = document.getElementById('loading-screen');
const loadingBar = document.getElementById('loading-bar');
const loadingText = document.getElementById('loading-text');
const loadingStatus = document.getElementById('loading-status');
let engine = null; // Will be initialized after assets load

// Queue critical assets for preloading
// (Most assets are still loaded on-demand for faster initial load)
assetLoader.queueImages({
  // Character sprites
  'char-front': 'img_assets/character/char.png',
  'char-side': 'img_assets/character/char_profile.png',
  'char-side-flash': 'img_assets/character/char_profile_flash_red.png',

  // Key items
  'axe': 'img_assets/axe.png',
  'pickaxe': 'img_assets/pickaxe.png',
  'sword': 'img_assets/sword.png',
  'raygun': 'img_assets/raygun.png',
  'toolbox': 'img_assets/toolbox.png',

  // UI elements
  'moon': 'img_assets/moon.png',
  'wisp': 'img_assets/wisp.png',
});

// Progress callback
assetLoader.onProgress = (progress, loaded, total) => {
  const percent = Math.round(progress * 100);
  if (loadingBar) loadingBar.style.width = `${percent}%`;
  if (loadingText) loadingText.textContent = `${percent}%`;
  if (loadingStatus) {
    loadingStatus.textContent = `Loading assets... (${loaded}/${total})`;
  }
};

// Completion callback
assetLoader.onComplete = async (failed) => {
  if (loadingStatus) {
    if (failed && failed.length > 0) {
      loadingStatus.textContent = `Warning: ${failed.length} asset(s) failed to load`;
      console.warn('Failed assets:', failed);
    } else {
      loadingStatus.textContent = 'Loading audio...';
    }
  }

  // Preload critical audio files
  const criticalAudio = [
    // DISABLED: forestBirds and caveRumble ambiance
    // { name: 'forestBirds', src: 'ambiance_sfx/forestbirds-319791.mp3', options: { loop: true, baseVolume: VOLUME_BASES.birds, kind: AUDIO_KIND_SFX } },
    { name: 'dialogueBlip', src: 'sfx_assets/dialogue_blip.ogg', options: { loop: true, baseVolume: VOLUME_BASES.dialogueBlip, kind: AUDIO_KIND_SFX } },
    // { name: 'caveRumble', src: 'ambiance_sfx/mystic-forest-ambient-23812.mp3', options: { loop: true, baseVolume: VOLUME_BASES.caveRumble, kind: AUDIO_KIND_SFX } }
  ];

  try {
    await audioManager.preloadAudio(criticalAudio);
    if (loadingStatus) loadingStatus.textContent = 'Ready!';
  } catch (err) {
    console.warn('Audio preload failed:', err);
    if (loadingStatus) loadingStatus.textContent = 'Ready! (some audio may load later)';
  }

  // Hide loading screen with fade
  setTimeout(() => {
    if (loadingScreen) {
      loadingScreen.classList.add('hidden');
      // Remove from DOM after transition
      setTimeout(() => {
        if (loadingScreen && loadingScreen.parentNode) {
          loadingScreen.remove();
        }
      }, 500);
    }

    // Create error-wrapped game loop functions
    const safeUpdate = (dt) => {
      try {
        update(dt);
      } catch (error) {
        errorHandler.handleError({
          message: error.message,
          stack: error.stack,
          type: 'Update Loop Error'
        });
      }
    };

    const safeDraw = (ctx) => {
      try {
        draw(ctx);
      } catch (error) {
        errorHandler.handleError({
          message: error.message,
          stack: error.stack,
          type: 'Render Loop Error'
        });
      }
    };

    // Start the game engine with error-wrapped functions
    applyDebugSpawn();
    engine = new Engine(canvas, safeUpdate, safeDraw);
    engine.start();

    // Lock canvas size to prevent world coordinate issues on resize
    lockCanvasSize();

    // Offer Continue / New Game if a save exists (freezes the game behind a modal until chosen).
    maybeShowContinuePrompt();
  }, 300);
};

// Error callback
assetLoader.onError = (error, key, path) => {
  console.error(`Asset load error [${key}]:`, path, error);
};

// Start loading
if (loadingStatus) loadingStatus.textContent = 'Loading assets...';
assetLoader.load().catch(err => {
  console.error('Asset loading failed:', err);
  if (loadingStatus) loadingStatus.textContent = 'Error loading assets';
});
// Pause game when tab is hidden; pause music while hidden (fade)
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    if (engine) engine.pause();
    if (!apocalypseActive && !titanMusicPlaying) softPauseMusic();
  } else {
    if (engine) engine.resume();
    if (!apocalypseActive && !titanMusicPlaying && audioManager.musicWasPlaying && audioManager.musicAudio && audioManager.musicAudio.paused) {
      softResumeMusic();
    }
  }
});

// --- Clouds (parallax, varying shapes/sizes) ---
const cloudChunkSize = 600;
const cloudLayers = [
  { speed: 0.2, alpha: 0.35, density: 1/950, size: [0.7, 1.3] },
  { speed: 0.35, alpha: 0.5, density: 1/750, size: [0.65, 1.1] },
  { speed: 0.55, alpha: 0.65, density: 1/650, size: [0.6, 1.0] },
];
const cloudsByLayer = cloudLayers.map(() => new Map()); // Map<chunkIndex, Cloud[]>

// Load cloud images (smallest to biggest)
const cloudImgSrcs = [
  'img_assets/cloud1.png',
  'img_assets/cloud2.png',
  'img_assets/cloud3.png',
  'img_assets/cloud4.png',
];
const cloudImgs = cloudImgSrcs.map(src => { const im = new Image(); im.src = src; return im; });
// Global scale applied to all cloud images to keep them subtle
const CLOUD_IMG_BASE_SCALE = 0.13;
// Vertical tiling randomization to avoid repetitive patterns when going up
const CLOUD_PAGE_JITTER_X = 12; // px
const CLOUD_PAGE_JITTER_Y = 12; // px
const CLOUD_PAGE_SCALE_VAR = 0.15; // +/- 15%
// Precomputed opaque bounds for each cloud image (to tighten hitboxes)
const cloudImgBounds = new Array(cloudImgSrcs.length).fill(null);

function computeCloudBoundsForIndex(i) {
  const img = cloudImgs[i];
  if (!img || !img.naturalWidth) return;
  const w = img.naturalWidth; const h = img.naturalHeight;
  const off = document.createElement('canvas');
  off.width = w; off.height = h;
  const octx = off.getContext('2d');
  octx.clearRect(0, 0, w, h);
  octx.drawImage(img, 0, 0);
  const data = octx.getImageData(0, 0, w, h).data;
  let minX = w, minY = h, maxX = -1, maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const a = data[(y * w + x) * 4 + 3];
      if (a > 8) { // treat low alpha as transparent
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) { // fully transparent fallback
    cloudImgBounds[i] = { minX: w / 2, maxX: w / 2, minY: h / 2, maxY: h / 2 };
  } else {
    cloudImgBounds[i] = { minX, maxX, minY, maxY };
  }
  if (i === SHRINE_CLOUD_IMG_INDEX) refreshShrineCloudMetrics();
}

// Kick off bounds computation when images load (or immediately if already loaded)
cloudImgs.forEach((img, i) => {
  if (img.complete && img.naturalWidth) computeCloudBoundsForIndex(i);
  else img.addEventListener('load', () => computeCloudBoundsForIndex(i), { once: true });
});
// Initialize shrine cloud metrics once cloud assets are available
refreshShrineCloudMetrics();

function ensureCloudChunksInView(camX, w) {
  const margin = 160;
  for (let li = 0; li < cloudLayers.length; li++) {
    const layer = cloudLayers[li];
    const camL = camX * layer.speed + cloudDrift * layer.speed * CLOUD_IDLE_FACTOR; // effective camera incl. idle drift
    const start = Math.floor((camL - margin) / cloudChunkSize);
    const end = Math.floor((camL + w + margin) / cloudChunkSize);
    const map = cloudsByLayer[li];
    for (let i = start; i <= end; i++) {
      if (!map.has(i)) map.set(i, generateCloudChunk(li, i));
    }
    // prune
    for (const key of Array.from(map.keys())) {
      if (key < start - 3 || key > end + 3) map.delete(key);
    }
  }
}

function generateCloudChunk(layerIndex, chunkIndex) {
  const layer = cloudLayers[layerIndex];
  const seed = 0xC1A0D5 ^ (layerIndex * 0x9E3779B9) ^ (chunkIndex | 0);
  const rng = seededPRNG(seed >>> 0);
  const startX = chunkIndex * cloudChunkSize;
  const endX = startX + cloudChunkSize;
  const baseCount = Math.max(1, Math.round(cloudChunkSize * layer.density));
  const list = [];
  for (let i = 0; i < baseCount * 2.5; i++) {
    const x = Math.round(randRange(rng, startX + 24, endX - 24));
    const yNorm = randRange(rng, 0.05, 0.80);
    // More even distribution with boost to mid-high altitudes (0.05-0.45 range)
    const altitudeFactor = yNorm < 0.45
      ? 1.0 - Math.pow(yNorm / 0.45, 0.8) * 0.3  // mid-high: 70-100% chance
      : 1.0 - Math.pow(Math.max(0, yNorm - 0.45) / 0.35, 1.6) * 0.7; // lower: decreasing chance
    if (rng() > altitudeFactor) continue;
    const scale = randRange(rng, layer.size[0], layer.size[1]);
    const img = Math.floor(rng() * cloudImgs.length) % cloudImgs.length;
    const cSeed = (rng() * 0xffffffff) >>> 0;
    list.push({ x, yNorm, scale, seed: cSeed, img });
    if (list.length >= baseCount) break;
  }
  list.sort((a, b) => a.x - b.x);
  return list;
}

function drawClouds(ctx, w, h, groundH) {
  ensureCloudChunksInView(cameraX, w);
  const skyH = Math.max(0, h - groundH);
  const darkK = skyDarkness(h);
  // Smoothly interpolate cloud size/alpha across thresholds
  // 0..0.35: 1.00 -> 1.00, 0.35..0.70: 1.00 -> 0.85, 0.70..1.0: 0.85 -> 0.70
  let sizeMul = 1.0;
  let alphaMul = 1.0;
  if (darkK > 0.35) {
    const t1 = Math.min(1, (darkK - 0.35) / 0.35);
    sizeMul = lerp(1.0, 0.85, Math.min(t1, 1));
    alphaMul = lerp(1.0, 0.90, Math.min(t1, 1));
    if (darkK > 0.70) {
      const t2 = Math.min(1, (darkK - 0.70) / 0.30);
      sizeMul = lerp(0.85, 0.70, t2);
      alphaMul = lerp(0.90, 0.80, t2);
    }
  }
  for (let li = 0; li < cloudLayers.length; li++) {
    const layer = cloudLayers[li];
    const map = cloudsByLayer[li];
    ctx.save();
    ctx.translate(-(cameraX * layer.speed + cloudDrift * layer.speed * CLOUD_IDLE_FACTOR), 0);
    ctx.globalAlpha = layer.alpha * alphaMul;
    for (const [, arr] of map) {
      for (const c of arr) {
        const camL = cameraX * layer.speed + cloudDrift * layer.speed * CLOUD_IDLE_FACTOR;
        if (c.x < camL - 200 || c.x > camL + w + 200) continue; // cull
        const marginTop = 16;
        const marginBottom = 24;
        const usableH = Math.max(0, skyH - marginTop - marginBottom);
        const baseY = marginTop + cloudCamY + c.yNorm * usableH;
        const period = Math.max(1, usableH || 1);
        // Draw vertical tilings to fill view as we scroll up
        const startPage = Math.floor((-baseY - 120) / period) - 1;
        const endPage = Math.ceil((h + 120 - baseY) / period) + 1;
        for (let k = startPage; k <= endPage; k++) {
          let yk = baseY + k * period;
          if (yk < -140 || yk > h + 140) continue;
          // Per-page deterministic jitter and subtle scale variation
          const prng = seededPRNG(((c.seed ^ ((k * 73856093) >>> 0)) >>> 0));
          const jx = (prng() - 0.5) * 2 * CLOUD_PAGE_JITTER_X;
          const jy = (prng() - 0.5) * 2 * CLOUD_PAGE_JITTER_Y;
          const sVar = 1 + (prng() - 0.5) * 2 * CLOUD_PAGE_SCALE_VAR; // 1 +/- var
          const imgIdx = (c.img + Math.floor(prng() * cloudImgs.length)) % cloudImgs.length;
          const img = cloudImgs[imgIdx | 0];
          if (!img || !img.naturalWidth) continue;
          const dw = img.naturalWidth * c.scale * CLOUD_IMG_BASE_SCALE * sVar * sizeMul;
          const dh = img.naturalHeight * c.scale * CLOUD_IMG_BASE_SCALE * sVar * sizeMul;
          // Prevent clouds from appearing under/below the ground line (account for vertical camera)
          const groundTop = (h - groundH) + cameraY;
          if (yk + jy + dh / 2 > groundTop - 2) continue;
          ctx.drawImage(img, (c.x + jx) - dw / 2, (yk + jy) - dh / 2, dw, dh);
        }
      }
    }
    ctx.restore();
  }
  ctx.globalAlpha = 1;
}

function drawEllipse(ctx, cx, cy, rx, ry) {
  ctx.save();
  ctx.translate(cx, cy);
  ctx.scale(rx / ry, 1);
  ctx.beginPath();
  ctx.arc(0, 0, ry, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// Dirt texture pattern (subtle noise) for ground overlay
let dirtPattern = null;
function ensureDirtPattern() {
  if (dirtPattern) return;
  const size = 64;
  const cv = document.createElement('canvas');
  cv.width = size; cv.height = size;
  const c = cv.getContext('2d');
  c.clearRect(0, 0, size, size);
  const rng = seededPRNG(0xD17BACC);
  // background transparent; draw semi-transparent brown specks and faint layers
  for (let i = 0; i < 260; i++) {
    const x = Math.floor(rng() * size);
    const y = Math.floor(rng() * size);
    const a = 0.10 + rng() * 0.20;
    const dark = rng() < 0.7;
    const col = dark ? '28,18,10' : '100,75,50'; // darker soil or darker crumb
    c.fillStyle = `rgba(${col},${a})`;
    c.fillRect(x, y, 1, 1);
  }
  // subtle horizontal streaks to suggest strata
  for (let i = 0; i < 10; i++) {
    const y = Math.floor(rng() * size);
    const len = size;
    const alpha = 0.05 + rng() * 0.05;
    c.fillStyle = `rgba(63, 44, 28, ${alpha})`;
    c.fillRect(0, y, len, 1);
  }
  dirtPattern = c.createPattern(cv, 'repeat');
}

// Ground gradient cache to avoid rebuilding every frame
let groundGradCache = { h: 0, groundH: 0, grad: null };
function getGroundGradient(ctx, h, groundH) {
  if (!groundGradCache.grad || groundGradCache.h !== h || groundGradCache.groundH !== groundH) {
    const groundTopY = h - groundH;
    const grad = ctx.createLinearGradient(0, groundTopY, 0, groundTopY + groundH + GROUND_EXTRA);
    // Transition ground from deep verdant surface into mossy earth layers
    grad.addColorStop(0.00, '#4b5c14'); // muted highlight at the surface
    grad.addColorStop(0.02, '#293c0e'); // rich shadowed grass
    grad.addColorStop(0.08, '#293c0e'); // damp soil crust
    grad.addColorStop(0.40, '#18290a'); // mossy upper earth
    grad.addColorStop(0.62, '#2f3813'); // mid earth transition
    grad.addColorStop(1.00, '#202e0d'); // deep soil base
    groundGradCache = { h, groundH, grad };
  }
  return groundGradCache.grad;
}

// Compute opaque bounds (min/max extents where alpha > threshold) for a single image
function computeImageOpaqueBounds(img) {
  if (!img || !img.naturalWidth || !img.naturalHeight) return null;
  const w = img.naturalWidth, h = img.naturalHeight;
  const off = document.createElement('canvas');
  off.width = w; off.height = h;
  const c = off.getContext('2d');
  c.clearRect(0, 0, w, h);
  c.drawImage(img, 0, 0);
  const data = c.getImageData(0, 0, w, h).data;
  let minX = w, minY = h, maxX = -1, maxY = -1;
  const bottomStart = Math.max(0, Math.floor(h * 0.75));
  let bottomSum = 0;
  let bottomCount = 0;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const a = data[(y * w + x) * 4 + 3];
      if (a > 8) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
        if (y >= bottomStart) {
          bottomSum += x;
          bottomCount += 1;
        }
      }
    }
  }
  if (maxX < 0) return { left: 0, right: 0, top: 0, bottom: 0, bottomCenter: w / 2, width: w, height: h };
  const bottomCenter = bottomCount > 0 ? (bottomSum / bottomCount) : (w / 2);
  return {
    left: minX,
    right: w - 1 - maxX,
    top: minY,
    bottom: h - 1 - maxY,
    bottomCenter,
    width: w,
    height: h
  };
}

// === Tile Terrain (Phase 1) ===
// Simple chunked solid grid under the ground; digging clears tiles; rendering carves sky-colored rects.
// TILE_SIZE defined at top of file
const CHUNK_W_TILES = 48;         // tiles per chunk (width)
const CHUNK_H_TILES = 48;         // tiles per chunk (height)
const CHUNK_W = CHUNK_W_TILES * TILE_SIZE;
const CHUNK_H = CHUNK_H_TILES * TILE_SIZE;
// Map key: `${cx},${cy}` where cx,cy are chunk indices in world space (x from 0, y from groundTop downward)
const tileChunks = new Map(); // value: { solid: Uint8Array, hp: Uint8Array, canvas: HTMLCanvasElement, dirty: boolean }

function chunkKey(cx, cy) { return cx + ',' + cy; }
function getChunk(cx, cy) {
  const k = chunkKey(cx, cy);
  let c = tileChunks.get(k);
  if (!c) {
    // New chunk: fill solid by default
    c = { solid: new Uint8Array(CHUNK_W_TILES * CHUNK_H_TILES), hp: new Uint8Array(CHUNK_W_TILES * CHUNK_H_TILES), canvas: null, dirty: true };
    c.solid.fill(1);
    // Initialize hitpoints with depth-based hardness and slight randomness
    const rng = seededPRNG(((cx * 73856093) ^ (cy * 19349663) ^ 0xD1CE) >>> 0);
    for (let ty = 0; ty < CHUNK_H_TILES; ty++) {
      const globalTy = cy * CHUNK_H_TILES + ty; // 0 at ground surface, increases downward
      const depthPx = globalTy * TILE_SIZE;
      let base = 2 + Math.floor(depthPx / 180); // harder as you go deeper
      if (base > 6) base = 6;
      for (let tx = 0; tx < CHUNK_W_TILES; tx++) {
        const idx = ty * CHUNK_W_TILES + tx;
        let hp0 = base;
        if (rng() < 0.20) hp0 += 1; // occasional tougher pockets
        c.hp[idx] = hp0;
      }
    }
    // Random caves: pre-carve empty spaces in underground chunks
    generateCavesForChunk(cx, cy, c);
    tileChunks.set(k, c);
  }
  return c;
}
function setTileSolid(cx, cy, tx, ty, v) {
  if (ty < 0 || ty >= CHUNK_H_TILES || tx < 0 || tx >= CHUNK_W_TILES) return;
  const ch = getChunk(cx, cy);
  const idx = ty * CHUNK_W_TILES + tx;
  const nv = v ? 1 : 0;
  if (ch.solid[idx] !== nv) { ch.solid[idx] = nv; ch.dirty = true; }
}

// Carve helper: ellipse in local chunk coordinates
function carveEllipseInChunk(ch, cx, cy, rx, ry) {
  const rx2 = rx * rx, ry2 = ry * ry;
  const x0 = Math.max(0, Math.floor(cx - rx));
  const x1 = Math.min(CHUNK_W_TILES - 1, Math.ceil(cx + rx));
  const y0 = Math.max(0, Math.floor(cy - ry));
  const y1 = Math.min(CHUNK_H_TILES - 1, Math.ceil(cy + ry));
  for (let ty = y0; ty <= y1; ty++) {
    for (let tx = x0; tx <= x1; tx++) {
      const dx = (tx + 0.5) - cx;
      const dy = (ty + 0.5) - cy;
      if ((dx * dx) / rx2 + (dy * dy) / ry2 <= 1) {
        ch.solid[ty * CHUNK_W_TILES + tx] = 0;
      }
    }
  }
  ch.dirty = true;
}

// Drunk-walk tunnel generation: creates snaking horizontal tunnels with branches
function carveDrunkWalkTunnel(ch, startX, startY, length, thickness, branchChance, rng) {
  let x = startX;
  let y = startY;
  const radius = Math.floor(thickness / 2);

  for (let step = 0; step < length; step++) {
    // Carve circle at current position
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist <= radius) {
          const tx = Math.floor(x + dx);
          const ty = Math.floor(y + dy);
          if (tx >= 0 && tx < CHUNK_W_TILES && ty >= 0 && ty < CHUNK_H_TILES) {
            ch.solid[ty * CHUNK_W_TILES + tx] = 0;
          }
        }
      }
    }

    // Random walk: mostly horizontal with vertical wobble
    x += (rng() < 0.7) ? 1 : (rng() < 0.5 ? -1 : 0);
    y += (rng() < 0.15) ? 1 : (rng() < 0.15 ? -1 : 0);

    // Keep within chunk bounds
    x = Math.max(radius, Math.min(CHUNK_W_TILES - radius - 1, x));
    y = Math.max(radius, Math.min(CHUNK_H_TILES - radius - 1, y));

    // Branching: spawn a side tunnel
    if (rng() < branchChance && step > 8 && step < length - 8) {
      const branchLength = 12 + Math.floor(rng() * 16); // 12-28 tiles
      const branchDir = (rng() < 0.5) ? 1 : -1; // up or down
      let bx = x;
      let by = y;
      for (let bs = 0; bs < branchLength; bs++) {
        for (let dy = -radius; dy <= radius; dy++) {
          for (let dx = -radius; dx <= radius; dx++) {
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist <= radius) {
              const tx = Math.floor(bx + dx);
              const ty = Math.floor(by + dy);
              if (tx >= 0 && tx < CHUNK_W_TILES && ty >= 0 && ty < CHUNK_H_TILES) {
                ch.solid[ty * CHUNK_W_TILES + tx] = 0;
              }
            }
          }
        }
        bx += (rng() < 0.6) ? 1 : (rng() < 0.5 ? -1 : 0);
        by += branchDir * (rng() < 0.6 ? 1 : 0);
        bx = Math.max(radius, Math.min(CHUNK_W_TILES - radius - 1, bx));
        by = Math.max(radius, Math.min(CHUNK_H_TILES - radius - 1, by));
      }
    }
  }
  ch.dirty = true;
}

// Vertical shaft: creates vertical connections within current chunk
function carveVerticalShaft(ch, localX, height, width, rng) {
  // Clamp height to current chunk bounds only (no multi-chunk to avoid recursion)
  const maxHeight = Math.min(height, CHUNK_H_TILES);

  for (let ty = 0; ty < maxHeight; ty++) {
    for (let dx = -Math.floor(width/2); dx <= Math.floor(width/2); dx++) {
      const tx = localX + dx;
      if (tx >= 0 && tx < CHUNK_W_TILES && ty >= 0 && ty < CHUNK_H_TILES) {
        ch.solid[ty * CHUNK_W_TILES + tx] = 0;
      }
    }
    // Add occasional ledges for climbing (20% chance per row)
    if (rng() < 0.20) {
      const ledgeSide = (rng() < 0.5) ? -1 : 1;
      const ledgeTx = localX + ledgeSide * Math.ceil(width/2) + ledgeSide;
      if (ledgeTx >= 0 && ledgeTx < CHUNK_W_TILES && ty >= 0 && ty < CHUNK_H_TILES) {
        ch.solid[ty * CHUNK_W_TILES + ledgeTx] = 1; // keep solid for ledge
      }
    }
  }
  ch.dirty = true;
}

// Add stepped formations: creates stair-like edges for natural climbing
function addSteppedFormations(ch, stepChance, rng) {
  for (let ty = 1; ty < CHUNK_H_TILES - 1; ty++) {
    for (let tx = 1; tx < CHUNK_W_TILES - 1; tx++) {
      const idx = ty * CHUNK_W_TILES + tx;
      if (ch.solid[idx] !== 0) continue; // only empty tiles

      // Check if this is a floor edge (empty above, solid below)
      const solidBelow = ch.solid[(ty + 1) * CHUNK_W_TILES + tx] !== 0;
      const emptyAbove = ch.solid[(ty - 1) * CHUNK_W_TILES + tx] === 0;
      const solidLeft = ch.solid[ty * CHUNK_W_TILES + (tx - 1)] !== 0;
      const solidRight = ch.solid[ty * CHUNK_W_TILES + (tx + 1)] !== 0;

      if (solidBelow && emptyAbove && (solidLeft || solidRight) && rng() < stepChance) {
        // Create a 1-2 tile step
        const stepHeight = 1 + Math.floor(rng() * 2);
        for (let s = 0; s < stepHeight && ty - s >= 0; s++) {
          const stepIdx = (ty - s) * CHUNK_W_TILES + tx;
          if (ch.solid[stepIdx] === 0) {
            ch.solid[stepIdx] = 1; // make solid to create step
          }
        }
      }
    }
  }
  ch.dirty = true;
}

function generateCavesForChunk(cx, cy, ch) {
  // Skip only the surface chunk to avoid surface interference
  if (cy === 0) return;
  const rng = seededPRNG((0xC0FFEE ^ (cx * 1103515245) ^ (cy * 12345)) >>> 0);

  // Depth-based parameters
  const depthFactor = Math.min(1, cy / 6);
  const isDeep = cy >= 11;        // 660px+ deep (155 tiles)
  const isVeryDeep = cy >= 21;    // 1260px+ deep (295 tiles)

  // Phase 1: Generate horizontal tunnel network
  const tunnelCount = isVeryDeep ? (2 + Math.floor(rng() * 2)) :
                      isDeep ? (1 + Math.floor(rng() * 2)) :
                      (rng() < 0.7 ? 1 : 2);

  for (let i = 0; i < tunnelCount; i++) {
    const startX = 8 + Math.floor(rng() * (CHUNK_W_TILES - 16));
    const startY = 8 + Math.floor(rng() * (CHUNK_H_TILES - 16));
    const length = 24 + Math.floor(rng() * 36); // longer tunnels
    const thickness = 7 + Math.floor(rng() * 5); // 7-12 tiles (84-144px) - comfortable width
    const branchChance = 0.08 + (depthFactor * 0.08); // less branching to avoid clutter
    carveDrunkWalkTunnel(ch, startX, startY, length, thickness, branchChance, rng);
  }

  // Phase 2: Add vertical shafts (medium+ depth)
  if (cy >= 6 && rng() < 0.25 + depthFactor * 0.15) {
    const shaftX = 10 + Math.floor(rng() * (CHUNK_W_TILES - 20));
    const shaftHeight = 12 + Math.floor(rng() * 20);
    const shaftWidth = 6 + Math.floor(rng() * 5); // 6-11 tiles (72-132px)
    carveVerticalShaft(ch, shaftX, shaftHeight, shaftWidth, rng);
  }

  // Phase 3: Place chambers
  const chamberCount = isDeep ? (1 + Math.floor(rng() * 2)) :
                       (cy >= 4 && rng() < 0.5) ? 1 : 0;

  const chambers = [];
  for (let i = 0; i < chamberCount; i++) {
    const radius = isDeep ? (20 + Math.floor(rng() * 16)) : // 20-36 tiles deep (240-432px)
                   (12 + Math.floor(rng() * 8)); // 12-20 tiles shallow (144-240px)
    const centerX = radius + 6 + Math.floor(rng() * Math.max(1, CHUNK_W_TILES - 2*radius - 12));
    const centerY = radius + 6 + Math.floor(rng() * Math.max(1, CHUNK_H_TILES - 2*radius - 12));

    chambers.push({ centerX, centerY, radius });
    carveEllipseInChunk(ch, centerX, centerY, radius, radius * 0.85);
  }

  // Phase 4: Add stepped formations at deep levels
  if (isDeep) {
    addSteppedFormations(ch, 0.15, rng);
  }

  // Phase 5: Schedule spawns (collectibles + deep structures)
  // Regular diamond/spider spawns
  const spawnChance = 0.28 + 0.15 * depthFactor;
  if (rng() < spawnChance) {
    for (let tries = 0; tries < 12; tries++) {
      const tx = 2 + Math.floor(rng() * Math.max(1, CHUNK_W_TILES - 4));
      const ty = 2 + Math.floor(rng() * Math.max(1, CHUNK_H_TILES - 4));
      const idx = ty * CHUNK_W_TILES + tx;
      const emptyHere = ch.solid[idx] === 0;
      const solidBelow = (ty + 1 < CHUNK_H_TILES) ? (ch.solid[idx + CHUNK_W_TILES] !== 0) : true;
      const emptyAbove = (ty > 0) ? (ch.solid[idx - CHUNK_W_TILES] === 0) : true;
      if (emptyHere && solidBelow && emptyAbove) {
        const type = (rng() < 0.60) ? 'diamond' : (rng() < 0.50 ? 'spider' : 'none');
        if (type !== 'none') caveSpawns.push({ cx, cy, tx, ty, type, realized: false });
        break;
      }
    }
  }

  // Deep structure spawns (ruins/chests in large chambers)
  if (isDeep && chambers.length > 0) {
    const largestChamber = chambers.reduce((max, c) =>
      c.radius > max.radius ? c : max, chambers[0]);

    if (largestChamber.radius >= 24) { // require large chambers (24+ tiles = 288px+)
      const structRoll = rng();
      let structType = null;

      if (structRoll < 0.15) structType = 'ruins';        // 15% chance
      // Chests disabled
      // else if (structRoll < 0.35) structType = 'chest';   // 20% chance
      else if (structRoll < 0.20) structType = 'underground_npc'; // 5% chance (adjusted range)

      if (structType) {
        caveSpawns.push({
          cx, cy,
          tx: Math.floor(largestChamber.centerX),
          ty: Math.floor(largestChamber.centerY),
          type: structType,
          realized: false
        });
      }
    }
  }
}
function getTileSolid(cx, cy, tx, ty) {
  if (ty < 0 || ty >= CHUNK_H_TILES || tx < 0 || tx >= CHUNK_W_TILES) return 0;
  const k = chunkKey(cx, cy);
  const ch = tileChunks.get(k);
  if (!ch) return 1; // default solid
  return ch.solid[ty * CHUNK_W_TILES + tx] ? 1 : 0;
}

function damageTile(cx, cy, tx, ty, dmg) {
  if (ty < 0 || ty >= CHUNK_H_TILES || tx < 0 || tx >= CHUNK_W_TILES) return;
  const ch = getChunk(cx, cy);
  const idx = ty * CHUNK_W_TILES + tx;
  if (!ch.solid[idx]) return; // already empty
  let hp = ch.hp[idx] || 1;
  hp -= dmg|0;
  if (hp <= 0) {
    ch.solid[idx] = 0;
    ch.hp[idx] = 0;
    ch.dirty = true;
  } else {
    ch.hp[idx] = hp;
    // optional: could mark dirty to show damage overlay later
  }
}

function ensureChunkCanvas(ch) {
  if (!ch.canvas) {
    const cv = document.createElement('canvas');
    cv.width = CHUNK_W; cv.height = CHUNK_H;
    ch.canvas = cv;
    ch.dirty = true;
  }
  return ch.canvas;
}

function rebuildChunkCanvas(cx, cy, ch, w, h) {
  const cv = ensureChunkCanvas(ch);
  if (!ch.dirty) return cv;
  const cctx = cv.getContext('2d');
  cctx.clearRect(0, 0, cv.width, cv.height);
  // Draw empty tiles only (sky color)
  cctx.fillStyle = '#121935';
  for (let ty = 0; ty < CHUNK_H_TILES; ty++) {
    let runStart = -1;
    for (let tx = 0; tx <= CHUNK_W_TILES; tx++) {
      const empty = (tx < CHUNK_W_TILES) ? (ch.solid[ty * CHUNK_W_TILES + tx] === 0) : false;
      if (empty && runStart < 0) runStart = tx;
      if ((!empty || tx === CHUNK_W_TILES) && runStart >= 0) {
        const rx = runStart * TILE_SIZE;
        const rw = (tx - runStart) * TILE_SIZE;
        const ry = ty * TILE_SIZE;
        cctx.fillRect(rx, ry, rw, TILE_SIZE);
        runStart = -1;
      }
    }
  }
  // Edge pass: draw an inner rim along solid borders for cleaner edges (simple autotile look)
  const RIM = Math.max(1, Math.round(TILE_SIZE * 0.16));
  const RIM_DARK = '#120c08';       // deeper ambient occlusion on edges
  const RIM_LIGHT = '#846348';      // slightly brighter top highlight
  for (let ty = 0; ty < CHUNK_H_TILES; ty++) {
    for (let tx = 0; tx < CHUNK_W_TILES; tx++) {
      const idx = ty * CHUNK_W_TILES + tx;
      if (ch.solid[idx] !== 0) continue; // only for empty tiles
      const leftSolid  = tx > 0 ? (ch.solid[idx - 1] !== 0) : true;
      const rightSolid = tx < CHUNK_W_TILES - 1 ? (ch.solid[idx + 1] !== 0) : true;
      const upSolid    = ty > 0 ? (ch.solid[idx - CHUNK_W_TILES] !== 0) : true;
      const downSolid  = ty < CHUNK_H_TILES - 1 ? (ch.solid[idx + CHUNK_W_TILES] !== 0) : true;
      const x = tx * TILE_SIZE;
      const y = ty * TILE_SIZE;
      cctx.fillStyle = RIM_DARK;
      if (leftSolid)  cctx.fillRect(x, y, RIM, TILE_SIZE);
      if (rightSolid) cctx.fillRect(x + TILE_SIZE - RIM, y, RIM, TILE_SIZE);
      if (downSolid)  cctx.fillRect(x, y + TILE_SIZE - RIM, TILE_SIZE, RIM);
      if (upSolid && ty > 0) {
        // top edge gets a subtle lighter highlight strip (but not for surface row ty=0)
        const HL = Math.max(1, Math.round(TILE_SIZE * 0.10));
        cctx.fillStyle = RIM_LIGHT;
        cctx.fillRect(x, y, TILE_SIZE, HL);
        cctx.fillStyle = RIM_DARK;
      }
      // corner nubs when diagonal solid but sides are empty, to soften 45° edges
      const nub = Math.max(1, Math.round(TILE_SIZE * 0.20));
      if (!leftSolid && !upSolid) {
        const diag = (tx > 0 && ty > 0) ? (ch.solid[idx - CHUNK_W_TILES - 1] !== 0) : false;
        if (diag) cctx.fillRect(x, y, nub, nub);
      }
      if (!rightSolid && !upSolid) {
        const diag = (tx < CHUNK_W_TILES - 1 && ty > 0) ? (ch.solid[idx - CHUNK_W_TILES + 1] !== 0) : false;
        if (diag) cctx.fillRect(x + TILE_SIZE - nub, y, nub, nub);
      }
      if (!leftSolid && !downSolid) {
        const diag = (tx > 0 && ty < CHUNK_H_TILES - 1) ? (ch.solid[idx + CHUNK_W_TILES - 1] !== 0) : false;
        if (diag) cctx.fillRect(x, y + TILE_SIZE - nub, nub, nub);
      }
      if (!rightSolid && !downSolid) {
        const diag = (tx < CHUNK_W_TILES - 1 && ty < CHUNK_H_TILES - 1) ? (ch.solid[idx + CHUNK_W_TILES + 1] !== 0) : false;
        if (diag) cctx.fillRect(x + TILE_SIZE - nub, y + TILE_SIZE - nub, nub, nub);
      }
    }
  }
  ch.dirty = false;
  return cv;
}

// Convert world x,y into tile coordinates relative to groundTop
function worldToTile(w, h, x, y) {
  const groundTopY = h - world.groundH;
  const yRel = y - groundTopY; // < 0 means above ground, tiles start at 0 downwards
  const tyGlobal = Math.floor(yRel / TILE_SIZE);
  const txGlobal = Math.floor(x / TILE_SIZE);
  const cx = Math.floor(txGlobal / CHUNK_W_TILES);
  const cy = Math.floor(tyGlobal / CHUNK_H_TILES);
  const tx = txGlobal - cx * CHUNK_W_TILES;
  const ty = tyGlobal - cy * CHUNK_H_TILES;
  return { cx, cy, tx, ty, yRel, txGlobal, tyGlobal };
}

// Dig a circular brush (radius in px) at world position (x,y)
function digTilesCircle(w, h, x, y, radiusPx) {
  const groundTopY = h - world.groundH;
  if (y < groundTopY) y = groundTopY; // don't clear above ground
  const r = Math.max(1, Math.round(radiusPx));
  const x0 = Math.floor((x - r) / TILE_SIZE) * TILE_SIZE;
  const y0 = Math.floor((y - r - groundTopY) / TILE_SIZE) * TILE_SIZE + groundTopY;
  const x1 = Math.ceil((x + r) / TILE_SIZE) * TILE_SIZE;
  const y1 = Math.ceil((y + r - groundTopY) / TILE_SIZE) * TILE_SIZE + groundTopY;
  const r2 = r * r;
  for (let py = y0; py <= y1; py += TILE_SIZE) {
    for (let px = x0; px <= x1; px += TILE_SIZE) {
      const cxm = px + TILE_SIZE / 2;
      const cym = py + TILE_SIZE / 2;
      const dx = cxm - x;
      const dy = cym - y;
      if (dx * dx + dy * dy <= r2) {
        const t = worldToTile(w, h, px, py);
        if (t.tyGlobal >= 0) setTileSolid(t.cx, t.cy, t.tx, t.ty, 0);
      }
    }
  }
}

// Carve a chunky rectangular block centered at (x, y) with the given size.
function digBlockRegion(w, h, centerX, centerY, sizePx, allowSurfaceCarve = true) {
  const groundTopY = h - world.groundH;
  const half = sizePx / 2;
  const minY = Math.max(groundTopY, centerY - half);
  const maxY = centerY + half;
  const minX = centerX - half;
  const maxX = centerX + half;
  const startX = Math.floor(minX / TILE_SIZE) * TILE_SIZE;
  const endX = Math.ceil(maxX / TILE_SIZE) * TILE_SIZE;
  let startY = Math.floor((minY - groundTopY) / TILE_SIZE) * TILE_SIZE + groundTopY;
  // If the carve starts within one tile of the surface, include the surface row
  // so the top layer actually disappears.
  if (minY - groundTopY < TILE_SIZE + 0.001) startY = groundTopY;
  const endY = Math.ceil((maxY - groundTopY) / TILE_SIZE) * TILE_SIZE + groundTopY;

  // Clear surface rows to create real holes when digging from surface
  // Must match the exact horizontal range that the main loop processes
  if (allowSurfaceCarve) {
    const overlapsSurface = (centerY - half) <= groundTopY + TILE_SIZE * 2;
    if (overlapsSurface) {
      // Use same tile calculation as main loop to ensure exact match
      for (let px = startX; px <= endX; px += TILE_SIZE) {
        const txGlobal = Math.floor(px / TILE_SIZE);
        const cx = Math.floor(txGlobal / CHUNK_W_TILES);
        const tx = txGlobal - cx * CHUNK_W_TILES;
        setTileSolid(cx, 0, tx, 0, 0);
        setTileSolid(cx, 0, tx, 1, 0);
        setTileSolid(cx, 0, tx, 2, 0);
      }
    }
  }

  for (let py = startY; py <= endY; py += TILE_SIZE) {
    for (let px = startX; px <= endX; px += TILE_SIZE) {
      const t = worldToTile(w, h, px, py);
      if (t.tyGlobal >= 0) {
        setTileSolid(t.cx, t.cy, t.tx, t.ty, 0);
      }
    }
  }
}

//

// Compute how deep the ground is carved (empty tiles) at x from the surface
function tileDepthAtX(w, h, x) {
  const groundTopY = h - world.groundH;
  // scan downwards from ty=0 until first solid
  let ty = 0;
  while (true) {
    const t = worldToTile(w, h, x, groundTopY + ty * TILE_SIZE);
    if (t.tyGlobal < 0) return 0;
    const solid = getTileSolid(t.cx, t.cy, t.tx, t.ty);
    if (solid) break;
    ty++;
    if (ty * TILE_SIZE > world.groundH + GROUND_EXTRA) break;
  }
  return ty * TILE_SIZE;
}

// From an arbitrary underground world Y, measure how much empty space continues
// straight downward at x until the next solid tile. Returns depth in pixels.
function emptyDepthBelowAt(w, h, x, yWorldStart) {
  const groundTopY = h - world.groundH;
  let ty = Math.floor((yWorldStart - groundTopY) / TILE_SIZE);
  if (ty < 0) return 0;
  let depth = 0;
  // Scan tile-by-tile downward until we hit solid
  while (true) {
    const t = worldToTile(w, h, x, groundTopY + ty * TILE_SIZE);
    // Ensure chunk exists so we accurately read emptiness
    getChunk(t.cx, t.cy);
    if (t.tyGlobal < 0) break;
    const solid = getTileSolid(t.cx, t.cy, t.tx, t.ty);
    if (solid) break;
    depth += TILE_SIZE;
    ty++;
    if (depth > world.groundH + GROUND_EXTRA) break;
  }
  return depth;
}

function pruneTileChunks(camX, viewW) {
  const keepCxMinCam = Math.floor((camX - 800) / CHUNK_W) - 2;
  const keepCxMaxCam = Math.floor((camX + viewW + 800) / CHUNK_W) + 2;
  // Also keep a horizontal band centered on the player so moving left underground
  // while the camera is locked right does not prune needed chunks.
  const playerBandW = Math.max(600, Math.floor(viewW * 0.6));
  const keepCxMinPlayer = Math.floor((player.x - playerBandW) / CHUNK_W) - 1;
  const keepCxMaxPlayer = Math.floor((player.x + playerBandW) / CHUNK_W) + 1;
  for (const k of Array.from(tileChunks.keys())) {
    const [sx, sy] = k.split(',');
    const cx = parseInt(sx, 10);
    const inCamRange = (cx >= keepCxMinCam && cx <= keepCxMaxCam);
    const inPlayerRange = (cx >= keepCxMinPlayer && cx <= keepCxMaxPlayer);
    if (!inCamRange && !inPlayerRange) tileChunks.delete(k);
  }
}

// Draw carved tiles as sky-colored rectangles (only empty cells are drawn)
function drawTileCarves(ctx, w, h) {
  const groundTopY = h - world.groundH;
  const xStart = cameraX;
  const xEnd = cameraX + w;
  const yStart = groundTopY;
  // Extend vertical coverage to include whatever depth the camera can see, plus margin
  const desiredBottom = Math.max(groundTopY + world.groundH + GROUND_EXTRA, (h - cameraY) + 1200);
  const yEnd = desiredBottom;
  const cxMin = Math.floor(xStart / CHUNK_W);
  const cxMax = Math.floor(xEnd / CHUNK_W);
  const cyMin = Math.max(0, Math.floor((yStart - groundTopY) / CHUNK_H));
  const cyMax = Math.floor((yEnd - groundTopY) / CHUNK_H);
  for (let cy = cyMin; cy <= cyMax; cy++) {
    for (let cx = cxMin; cx <= cxMax; cx++) {
      // Ensure chunk exists so random caves are generated for visible area
      const ch = getChunk(cx, cy);
      const cv = rebuildChunkCanvas(cx, cy, ch, w, h);
      if (!cv) continue;
      const wx = cx * CHUNK_W;
      const wy = groundTopY + cy * CHUNK_H;
      ctx.drawImage(cv, wx, wy);
    }
  }
}

// Ensure tile chunks exist for the current visible area (so caves generate and collisions see them)
function ensureTileChunksInView(camX, w, h) {
  const groundTopY = h - world.groundH;
  // Ensure for both the visible window and a band around the player.x so
  // collision checks don't see missing chunks as solid when the camera is locked ahead.
  const band = Math.max(400, Math.floor(w * 0.5));
  const xStart = Math.min(camX - 100, player.x - band);
  const xEnd = Math.max(camX + w + 100, player.x + band);
  const yStart = groundTopY;
  const desiredBottom = Math.max(groundTopY + world.groundH + GROUND_EXTRA, (h - cameraY) + 1200);
  const yEnd = desiredBottom;
  const cxMin = Math.floor(xStart / CHUNK_W);
  const cxMax = Math.floor(xEnd / CHUNK_W);
  const cyMin = Math.max(0, Math.floor((yStart - groundTopY) / CHUNK_H));
  const cyMax = Math.floor((yEnd - groundTopY) / CHUNK_H);
  for (let cy = cyMin; cy <= cyMax; cy++) {
    for (let cx = cxMin; cx <= cxMax; cx++) {
      getChunk(cx, cy);
    }
  }
}

// === Tile collisions (side + ceiling) ===
function rectOverlapsSolid(w, h, rx, ry, rw, rh) {
  const groundTopY = h - world.groundH;
  const tx0 = Math.floor(rx / TILE_SIZE);
  const tx1 = Math.floor((rx + rw - 0.001) / TILE_SIZE);
  const ty0 = Math.floor(((ry - groundTopY) / TILE_SIZE));
  const ty1 = Math.floor(((ry + rh - groundTopY - 0.001) / TILE_SIZE));
  for (let ty = ty0; ty <= ty1; ty++) {
    if (ty < 0) continue; // above ground is empty
    for (let tx = tx0; tx <= tx1; tx++) {
      const cx = Math.floor(tx / CHUNK_W_TILES);
      const cy = Math.floor(ty / CHUNK_H_TILES);
      const itx = tx - cx * CHUNK_W_TILES;
      const ity = ty - cy * CHUNK_H_TILES;
      if (getTileSolid(cx, cy, itx, ity)) return true;
    }
  }
  return false;
}

// REFACTORED: Using new modular physics system
function resolvePlayerTileCollisions(w, h) {
  const groundTopY = h - world.groundH;
  const isUnderground = (player.y + player.h) > groundTopY;
  const horizIntent = (input.rightHeld ? 1 : 0) - (input.leftHeld ? 1 : 0);
  const preferredDir = horizIntent !== 0 ? horizIntent : Math.sign(player.vx || 0);

  // Helper to get tile solidity (wraps the tile checking logic)
  const getTileSolid = (tileX, tileY) => {
    if (tileY < 0) return false;
    const worldX = tileX * TILE_SIZE;
    const worldY = groundTopY + tileY * TILE_SIZE;
    return tileOverlapsWorldSolid(w, h, worldX, worldY, TILE_SIZE, TILE_SIZE);
  };

  let onGround = false;

  // Rest state optimization: If player has minimal velocity, no input, and against a wall,
  // skip horizontal collision to prevent jitter
  const VEL_REST_THRESHOLD = 5; // px/s - below this is considered "at rest"
  const atRestHorizontally = Math.abs(player.vx) < VEL_REST_THRESHOLD && horizIntent === 0;
  let skipHorizontalCollision = false;

  if (atRestHorizontally) {
    // Check if against a wall
    const againstLeftWall = collisionSystem.rectOverlapsSolid(
      getTileSolid,
      player.x - 1,
      player.y,
      player.w,
      player.h,
      groundTopY,
      rectOverlapsPlanks
    );
    const againstRightWall = collisionSystem.rectOverlapsSolid(
      getTileSolid,
      player.x + 1,
      player.y,
      player.w,
      player.h,
      groundTopY,
      rectOverlapsPlanks
    );
    if (againstLeftWall || againstRightWall) {
      skipHorizontalCollision = true;
      player.vx = 0; // Hard stop when resting against wall
    }
  }

  // Horizontal resolution FIRST to prevent wall-climbing (diagonal overlap interpreted as vertical)
  if (player.vx !== 0 && !skipHorizontalCollision) {
    const dir = Math.sign(player.vx);
    const hResult = playerPhysics.resolveHorizontal(
      player,
      dir,
      collisionSystem,
      getTileSolid,
      groundTopY,
      isUnderground,
      input.jumpHeld,
      rectOverlapsPlanks
    );

    player.x = hResult.x;
    player.y = hResult.y;
    // Dampen velocity instead of hard stop to reduce jitter
    if (hResult.stopVx) player.vx *= 0.1;
    if (hResult.stopVy) player.vy = Math.min(0, player.vy * 0.1);
    if (hResult.grounded) onGround = true;
  }

  // Vertical resolution (after horizontal to only handle true vertical collisions)
  if (player.vy !== 0) {
    const vResult = playerPhysics.resolveVertical(
      player,
      collisionSystem,
      getTileSolid,
      groundTopY,
      rectOverlapsPlanks
    );

    player.y = vResult.y;
    // Dampen velocity instead of hard stop to reduce jitter
    if (vResult.stopVy) player.vy *= 0.1;
    if (vResult.grounded) onGround = true;
  }

  // If there's solid support immediately below, settle firmly onto it so the
  // player doesn't hover a pixel above ground when clamp is disabled.
  const supportBelow = collisionSystem.hasGroundSupport(
    getTileSolid,
    player.x,
    player.y,
    player.w,
    player.h,
    groundTopY,
    rectOverlapsPlanks
  );

  // Check if player is against a wall (to skip settle logic and prevent jitter)
  const againstLeftWall = collisionSystem.rectOverlapsSolid(
    getTileSolid,
    player.x - 1,
    player.y,
    player.w,
    player.h,
    groundTopY,
    rectOverlapsPlanks
  );
  const againstRightWall = collisionSystem.rectOverlapsSolid(
    getTileSolid,
    player.x + 1,
    player.y,
    player.w,
    player.h,
    groundTopY,
    rectOverlapsPlanks
  );
  const againstWall = againstLeftWall || againstRightWall;

  if (supportBelow && player.vy >= 0 && !againstWall) {
    let settled = false;
    let steps = 0;
    const maxSettle = Math.max(2, Math.floor(TILE_SIZE * 0.5)); // small settle band to reduce jitter
    while (
      !collisionSystem.rectOverlapsSolid(
        getTileSolid,
        player.x,
        player.y + 1,
        player.w,
        player.h,
        groundTopY,
        rectOverlapsPlanks
      ) && steps < maxSettle
    ) {
      player.y += 1;
      steps++;
      settled = true;
    }
    if (settled || onGround) {
      player.vy = 0;
      onGround = true;
    }
  }

  // Emergency unstuck (only if stuck for multiple frames to avoid jitter from micro-overlaps)
  const centerMargin = Math.floor(Math.min(player.w, player.h) * 0.3);
  const centerW = Math.max(2, player.w - centerMargin * 2);
  const centerH = Math.max(2, player.h - centerMargin * 2);
  const isStuck = collisionSystem.rectOverlapsSolid(
    getTileSolid,
    player.x + centerMargin,
    player.y + centerMargin,
    centerW,
    centerH,
    groundTopY,
    rectOverlapsPlanks
  );

  if (isStuck) {
    playerStuckFrames++;
  } else {
    playerStuckFrames = 0;
  }

  // Only run unstuck if stuck for 3+ consecutive frames
  if (playerStuckFrames >= 3) {
    const unstuckResult = playerPhysics.unstuck(
      player,
      collisionSystem,
      getTileSolid,
      groundTopY,
      diggingSystem.isInGracePeriod(),
      preferredDir,
      isUnderground,
      rectOverlapsPlanks
    );

    player.x = unstuckResult.x;
    player.y = unstuckResult.y;

    // Reset counter after unstuck attempt
    if (unstuckResult.x !== player.x || unstuckResult.y !== player.y) {
      playerStuckFrames = 0;
    }
  }

  // Final ground check
  if (!onGround) {
    const support = collisionSystem.hasGroundSupport(
      getTileSolid,
      player.x,
      player.y,
      player.w,
      player.h,
      groundTopY,
      rectOverlapsPlanks
    );
    if (support && !collisionSystem.rectOverlapsSolid(getTileSolid, player.x, player.y, player.w, player.h, groundTopY, rectOverlapsPlanks)) {
      onGround = true;
    }
  }

  player.onGround = onGround;
}

// Helper function to check if a tile overlaps solid terrain
function tileOverlapsWorldSolid(w, h, x, y, tw, th) {
  return rectOverlapsWorldSolid(w, h, x, y, tw, th);
}

// Ledge grab: when jump is held and pressing toward a wall, if there's clear
// headroom above the lip within a small reach, pull the player up onto it.
// --- Sky platforms logic ---
function ensurePlatformCellsInView(camX, w, h) {
  if (!platformReady) return;
  ensureLegacyPlatform(h);
  const yStart = -cameraY - 200;
  const yEnd = -cameraY + h + 200;
  const cx0 = Math.floor((camX - 200) / platformChunkW);
  const cx1 = Math.floor((camX + w + 200) / platformChunkW);
  const cy0 = Math.floor(yStart / platformChunkH);
  const cy1 = Math.floor(yEnd / platformChunkH);
  for (let cy = cy0; cy <= cy1; cy++) {
    for (let cx = cx0; cx <= cx1; cx++) {
      const key = cx + ',' + cy;
      if (!platformsByCell.has(key)) platformsByCell.set(key, generatePlatformCell(cx, cy, h));
    }
  }
  // prune far away
  for (const key of Array.from(platformsByCell.keys())) {
    const [sx, sy] = key.split(',');
    const cx = parseInt(sx, 10), cy = parseInt(sy, 10);
    if (cx < cx0 - 2 || cx > cx1 + 2 || cy < cy0 - 2 || cy > cy1 + 2) platformsByCell.delete(key);
  }
}

function generatePlatformCell(cx, cy, h) {
  const rng = seededPRNG(0xA1B2C3D4 ^ ((cx * 73856093) ^ (cy * 19349663)) >>> 0);
  const list = [];
  const groundTop = h - world.groundH;
  const x0 = cx * platformChunkW;
  const y0 = cy * platformChunkH;
  // Reject cells that are too close to ground (keep platforms high in the sky only)
  const cellBottom = y0 + platformChunkH;
  if (cellBottom > groundTop - 150) return list; // no platforms near ground

  // Height-based density: more platforms the higher you go
  const cellTop = y0;
  const heightAboveGround = groundTop - cellTop;
  const heightFactor = Math.max(0, Math.min(1, heightAboveGround / (h * 2))); // 0 near ground, 1 at 2x screen height

  // Base spawn rates increased + bonus for height
  const baseChance1 = 0.65; // 65% base chance (was 40%)
  const baseChance2 = 0.45; // 45% base chance for 2nd (was 20%)
  const baseChance3 = 0.25; // 25% chance for 3rd platform (was 8%)
  const baseChance4 = 0.10; // 10% chance for 4th platform

  const heightBonus = heightFactor * 0.5; // Up to +50% at high altitudes (was 30%)

  let count = 0;
  if (rng() < baseChance1 + heightBonus) count = 1;
  if (rng() < baseChance2 + heightBonus) count = 2;
  if (rng() < baseChance3 + heightBonus) count = 3;
  if (rng() < baseChance4 + heightBonus) count = 4;

  const targetW = 180; // bigger platforms
  const aspect = platformImg.naturalWidth && platformImg.naturalHeight ? (platformImg.naturalWidth / platformImg.naturalHeight) : 2.5;
  for (let i = 0; i < count; i++) {
    const x = Math.round(x0 + 60 + rng() * (platformChunkW - 120));
    const y = Math.round(y0 + 60 + rng() * (platformChunkH - 120));
    const dw = targetW;
    const dh = Math.max(10, Math.round(targetW / aspect));
    const p = { x, y, w: dw, h: dh };
    // Chance to spawn a chest on top of the platform
    if (rng() < 0.32) {
      spawnChestOnPlatform(p);
    }
    list.push(p);
  }
  return list;
}

function ensureLegacyPlatform(h) {
  if (legacyPlatform || !platformReady) return;
  const groundTop = h - world.groundH;
  // Place near the first horizontal chunk, at a high-but-reachable sky band reminiscent of previous logic
  const baseCx = 1; // place it a bit further forward (next horizontal cell)
  const rng = seededPRNG(0xA1B2C3D4 ^ (baseCx | 0));
  const targetW = 180;
  const aspect = platformImg.naturalWidth && platformImg.naturalHeight ? (platformImg.naturalWidth / platformImg.naturalHeight) : 2.5;
  const dw = targetW;
  const dh = Math.max(10, Math.round(targetW / aspect));
  // Place within the next cell, away from edges
  const x = Math.round(baseCx * platformChunkW + platformChunkW * 0.2 + rng() * (platformChunkW * 0.6));
  // Use a sky band roughly 65–85% of the screen height above the ground top
  const yHighMin = groundTop - Math.max(220, h * 0.85);
  const yHighMax = groundTop - Math.max(140, h * 0.65);
  const y = Math.round(rng() * (yHighMax - yHighMin) + yHighMin);
  legacyPlatform = { x, y, w: dw, h: dh };
  const cx = Math.floor(x / platformChunkW);
  const cy = Math.floor(y / platformChunkH);
  const key = cx + ',' + cy;
  if (!platformsByCell.has(key)) platformsByCell.set(key, []);
  platformsByCell.get(key).push(legacyPlatform);
}

function drawPlatforms(ctx) {
  if (!platformReady) return;
  const w = world.width();
  const h = world.height();
  ensurePlatformCellsInView(cameraX, w, h);
  const yStart = -cameraY - 200;
  const yEnd = -cameraY + h + 200;
  const cx0 = Math.floor((cameraX - 100) / platformChunkW);
  const cx1 = Math.floor((cameraX + w + 100) / platformChunkW);
  const cy0 = Math.floor(yStart / platformChunkH);
  const cy1 = Math.floor(yEnd / platformChunkH);
  for (let cy = cy0; cy <= cy1; cy++) {
    for (let cx = cx0; cx <= cx1; cx++) {
      const arr = platformsByCell.get(cx + ',' + cy);
      if (!arr) continue;
      for (const p of arr) {
        ctx.drawImage(platformImg, Math.round(p.x - p.w / 2), Math.round(p.y - p.h / 2), p.w, p.h);
      }
    }
  }
  drawSkyChests(ctx);
}

function drawSkyChests(ctx) {
  if (!skyChests.length) return;
  const imgClosedReady = chestClosedReady || (chestClosedImg && chestClosedImg.naturalWidth);
  const imgOpenReady = chestOpenReady || (chestOpenImg && chestOpenImg.naturalWidth);
  let warned = false;
  for (const ch of skyChests) {
    const img = ch.open ? (imgOpenReady ? chestOpenImg : chestClosedImg) : chestClosedImg;
    const alpha = Math.max(0, Math.min(1, ch.fade));
    ctx.save();
    ctx.globalAlpha = alpha;
    if (img && img.naturalWidth) {
      ctx.drawImage(img, Math.round(ch.x), Math.round(ch.y), ch.w, ch.h);
    } else {
      // Placeholder box with '?' until art is added
      ctx.fillStyle = 'rgba(255,255,255,0.15)';
      ctx.strokeStyle = 'rgba(255,255,255,0.45)';
      ctx.lineWidth = 1;
      ctx.fillRect(Math.round(ch.x), Math.round(ch.y), ch.w, ch.h);
      ctx.strokeRect(Math.round(ch.x)+0.5, Math.round(ch.y)+0.5, ch.w-1, ch.h-1);
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.font = '16px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
      ctx.fillText('?', Math.round(ch.x + ch.w/2 - 4), Math.round(ch.y + ch.h/2 + 6));
      if (!warned) { try { console.warn('Chest images missing: add img_assets/chest.png and img_assets/chest_open.png'); } catch {} warned = true; }
    }
    ctx.restore();
  }

  // Draw underground chests
  for (const ch of undergroundChests) {
    const img = ch.open ? (imgOpenReady ? chestOpenImg : chestClosedImg) : chestClosedImg;
    const alpha = Math.max(0, Math.min(1, ch.fade));
    ctx.save();
    ctx.globalAlpha = alpha;
    if (img && img.naturalWidth) {
      ctx.drawImage(img, Math.round(ch.x), Math.round(ch.y), ch.w, ch.h);
    } else {
      // Placeholder
      ctx.fillStyle = 'rgba(255,255,255,0.15)';
      ctx.strokeStyle = 'rgba(255,255,255,0.45)';
      ctx.lineWidth = 1;
      ctx.fillRect(Math.round(ch.x), Math.round(ch.y), ch.w, ch.h);
      ctx.strokeRect(Math.round(ch.x)+0.5, Math.round(ch.y)+0.5, ch.w-1, ch.h-1);
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.font = '16px system-ui, -apple-system, Segoe UI, Roboto, sans-serif';
      ctx.fillText('?', Math.round(ch.x + ch.w/2 - 4), Math.round(ch.y + ch.h/2 + 6));
    }
    ctx.restore();
  }
}

function resolvePlatformCollisions(w, h, prevBottom) {
  // one-way platforms: only collide from above while falling
  if (player.vy < 0) return;
  // Do not resolve platforms while underground
  const groundTopWorld = h - world.groundH;
  if ((player.y + player.h) > groundTopWorld - 4) return;
  const feetNow = player.y + player.h;
  const yStart = -cameraY - 50;
  const yEnd = -cameraY + h + 50;
  const cx0 = Math.floor((cameraX - 100) / platformChunkW);
  const cx1 = Math.floor((cameraX + w + 100) / platformChunkW);
  const cy0 = Math.floor(yStart / platformChunkH);
  const cy1 = Math.floor(yEnd / platformChunkH);
  for (let cy = cy0; cy <= cy1; cy++) {
    for (let cx = cx0; cx <= cx1; cx++) {
      const arr = platformsByCell.get(cx + ',' + cy);
      if (!arr) continue;
      for (const p of arr) {
        // Compute visual top surface accounting for image transparency
        let top = p.y - p.h / 2;
        if (platformReady && platformImgBounds && platformImg.naturalHeight) {
          const scale = p.h / platformImg.naturalHeight;
          top += (platformImgBounds.top || 0) * scale;
        }
        const left = p.x - p.w / 2;
        const right = p.x + p.w / 2;
        const PLATFORM_PLAYER_OFFSET = -6; // Player offset (negative = sink into platform)
        const horizontallyOver = (player.x + player.w > left + 4) && (player.x < right - 4);
        // Account for player offset in wasAbove check (allow slightly below if standing on platform)
        const wasAbove = prevBottom <= top + Math.abs(PLATFORM_PLAYER_OFFSET) + 2;
        const isCrossing = feetNow >= top && horizontallyOver;
        if (horizontallyOver && wasAbove && isCrossing) {
          // PLAYER PLATFORM POSITION: Adjust vertical offset here (in pixels)
          // Positive values = gap between player feet and platform top
          // Negative values = player sinks into platform
          // Current: -3 pixels (player sinks 3px into platform for better visual grounding)
          player.y = top - player.h - PLATFORM_PLAYER_OFFSET;
          player.vy = 0;
          player.onGround = true;
          standingOnPlatform = true;
          currentPlatformCarry = p;
          return;
        }
      }
    }
  }
}
// --- Birds (animated sprites flying across the sky) ---
const BIRD_FRAME_COUNT = 8;
const birdFrames = [];
let birdFramesReady = 0;
for (let i = 1; i <= BIRD_FRAME_COUNT; i++) {
  const img = new Image();
  img.onload = () => { birdFramesReady++; };
  img.src = `animation_assets/bird/bird${i}.png`;
  birdFrames.push(img);
}

let birds = [];
let birdSpawnTimer = 0;
let birdNextSpawn = 6 + Math.random() * 8; // 6..14 seconds (rarer)

function spawnBird(w, h) {
  const groundTop = h - world.groundH;
  const fromLeft = Math.random() < 0.5;
  const margin = 60;
  // Choose a screen-space Y band in the top 30%, then convert to world Y using cameraY
  const yScreenMin = 20;
  const yScreenMax = Math.max(yScreenMin + 10, Math.min(h * 0.30, (groundTop + cameraY) - 40));
  const yScreen = yScreenMin + Math.random() * Math.max(1, (yScreenMax - yScreenMin));
  const y0 = Math.round(yScreen - cameraY);
  // Size birds a tad bigger: ~42–54 px tall
  const targetH = 42 + Math.random() * 12; // 42..54 px tall
  const speed = 80 + Math.random() * 90; // px/s
  const vx = (fromLeft ? 1 : -1) * speed;
  const x = fromLeft ? (cameraX - margin) : (cameraX + w + margin);
  const driftAmp = 6 + Math.random() * 8; // 6..14 px
  const driftSpeed = 0.8 + Math.random() * 0.8; // rad/s
  birds.push({ x, y0, vx, targetH, frame: 0, ftime: 0, t: 0, driftAmp, driftSpeed });
}

function spawnBirdFlock(w, h) {
  const groundTop = h - world.groundH;
  const fromLeft = Math.random() < 0.5;
  const margin = 60;
  const yScreenMin = 20;
  const yScreenMax = Math.max(yScreenMin + 10, Math.min(h * 0.30, (groundTop + cameraY) - 40));
  const yScreen = yScreenMin + Math.random() * Math.max(1, (yScreenMax - yScreenMin));
  const baseY = Math.round(yScreen - cameraY);
  const baseX = fromLeft ? (cameraX - margin) : (cameraX + w + margin);
  const dir = fromLeft ? 1 : -1;
  for (let i = 0; i < 3; i++) {
    const targetH = 42 + Math.random() * 12;
    const speed = (80 + Math.random() * 90) * (0.9 + Math.random() * 0.2);
    const vx = dir * speed;
    const x = baseX - dir * (i * 40); // staggered horizontally
    const y0 = baseY + (i - 1) * 8; // vertical fan
    const driftAmp = 6 + Math.random() * 8;
    const driftSpeed = 0.8 + Math.random() * 0.8;
    birds.push({ x, y0, vx, targetH, frame: 0, ftime: 0, t: 0, driftAmp, driftSpeed });
  }
}

function updateBirds(dt, w, h) {
  // spawn control (skip until frames are ready)
  if (birdFramesReady >= BIRD_FRAME_COUNT) {
    // Determine if sky is visibly above ground; when underground, avoid spawning
    // Consider sky visible based on the surface line in screen space
    const groundTopScreen = (h - world.groundH) + cameraY; // y of surface on screen
    const skyVisible = groundTopScreen > 60; // need some sky band available
    if (skyVisible) {
      birdSpawnTimer += dt;
      if (birdSpawnTimer >= birdNextSpawn) {
        const canFlock = birds.length <= 1 && Math.random() < 0.25; // rare flock
        if (canFlock) spawnBirdFlock(w, h);
        else if (birds.length < 2) spawnBird(w, h);
        birdSpawnTimer = 0;
        birdNextSpawn = (canFlock ? 12 : 8) + Math.random() * (canFlock ? 18 : 12); // longer wait after flocks
      }
    } else {
      // Underground view: slowly delay the next spawn further
      birdSpawnTimer = Math.min(birdSpawnTimer, birdNextSpawn * 0.5);
    }
  }
  if (!birds.length) return;
  const ANIM_FPS = 12;
  const marginCull = 160;
  for (const b of birds) {
    b.x += b.vx * dt;
    b.ftime += dt; // frame animation timer
    b.t = (b.t || 0) + dt; // drift timer
    while (b.ftime >= 1 / ANIM_FPS) {
      b.ftime -= 1 / ANIM_FPS;
      b.frame = (b.frame + 1) % BIRD_FRAME_COUNT;
    }
  }
  // cull far off-screen
  const left = cameraX - marginCull;
  const right = cameraX + w + marginCull;
  birds = birds.filter(b => b.x > left && b.x < right);
}

function drawBirds(ctx) {
  if (birdFramesReady < BIRD_FRAME_COUNT || !birds.length) return;
  for (const b of birds) {
    const img = birdFrames[b.frame];
    if (!img || !img.naturalWidth) continue;
    const s = b.targetH ? (b.targetH / img.naturalHeight) : 0.06; // fallback tiny scale
    const dw = img.naturalWidth * s;
    const dh = img.naturalHeight * s;
    const px = Math.round(b.x);
    const drift = (b.driftAmp || 0) * Math.sin((b.t || 0) * (b.driftSpeed || 1));
    const py = Math.round((b.y0 != null ? b.y0 : b.y) + drift);
    ctx.save();
    ctx.translate(px, py);
    // Invert facing: flip when moving right to match sprite orientation
    if (b.vx > 0) ctx.scale(-1, 1);
    ctx.drawImage(img, -dw / 2, -dh / 2, dw, dh);
    ctx.restore();
  }
}

function hitBirdsAtCircle(cx, cy, r) {
  let hit = false;
  for (let i = birds.length - 1; i >= 0; i--) {
    const b = birds[i];
    const drift = (b.driftAmp || 0) * Math.sin((b.t || 0) * (b.driftSpeed || 1));
    const by = b.y0 + drift;
    const dx = cx - b.x, dy = cy - by;
    const hr = r + (b.targetH || 48) * 0.5;
    if (dx * dx + dy * dy <= hr * hr) {
      particles.spawnSparks(b.x, by, 7, ['#ffffff', '#f5f0e0', '#d4c9a0']);
      birds.splice(i, 1);
      hit = true;
    }
  }
  for (let i = idleBirds.length - 1; i >= 0; i--) {
    const b = idleBirds[i];
    const drift = (b.driftAmp || 0) * Math.sin((b.t || 0) * (b.driftSpeed || 1));
    const by = b.state === 'idle' ? b.perchY : (b.y0 + drift);
    const dx = cx - b.x, dy = cy - by;
    const hr = r + (b.targetH || 42) * 0.5;
    if (dx * dx + dy * dy <= hr * hr) {
      particles.spawnSparks(b.x, by, 7, ['#ffffff', '#f5f0e0', '#d4c9a0']);
      idleBirds.splice(i, 1);
      hit = true;
    }
  }
  return hit;
}
